export interface ManualSubsection {
  id: string;
  title: string;
  content: string;
  audience: 'user' | 'admin' | 'both';
}

export interface ManualSection {
  id: string;
  title: string;
  icon: string;
  audience: 'user' | 'admin' | 'both';
  subsections: ManualSubsection[];
}

export const MANUAL_SECTIONS: ManualSection[] = [
  // ============================================================
  // 1. GETTING STARTED
  // ============================================================
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: 'fa-rocket',
    audience: 'both',
    subsections: [
      {
        id: 'logging-in',
        title: 'Logging In',
        audience: 'both',
        content: `
          <h4>Accessing the Application</h4>
          <p>Navigate to your firm&rsquo;s V7 URL (e.g., <code>https://v7.attymatter.com</code>) in your web browser.</p>

          <h5>Standard Login</h5>
          <ol>
            <li>Enter your <strong>email address</strong> as your username.</li>
            <li>Enter your <strong>password</strong>.</li>
            <li>Click <strong>Sign In</strong>.</li>
          </ol>

          <h5>Two-Factor Authentication (2FA)</h5>
          <p>If 2FA is enabled on your account, after entering your password you will be prompted for a one-time code from your authenticator app (e.g., Google Authenticator, Authy). Enter the 6-digit code and click <strong>Verify</strong>.</p>
          <div class="alert alert-info"><strong>Tip:</strong> If you lose access to your authenticator app, contact your administrator to reset your 2FA. They can disable it from the user management page.</div>

          <h5>Password Reset</h5>
          <p>Click <strong>&ldquo;Forgot your password?&rdquo;</strong> on the login page. A reset link will be emailed to your registered address. The link expires after a set period &mdash; if it expires, request a new one.</p>

          <h5>How Authentication Works</h5>
          <p>V7 uses <strong>Devise</strong> for authentication. After login, a JWT (JSON Web Token) is issued and stored in your browser&rsquo;s local storage. This token is included with every request to the server. The token expires after inactivity &mdash; if you see an unexpected login screen, your session has expired and you need to log in again.</p>
        `
      },
      {
        id: 'dhl-login',
        title: 'DHL Log in',
        audience: 'both',
        content: `
          <h4>DHL Account Credentials</h4>
          <p><a href="https://mydhl.express.dhl/us/en/home.html?login=successful#/createNewShipmentTab" target="_blank" rel="noopener noreferrer">Open the DHL shipment website</a></p>
          <table class="table table-sm table-bordered">
            <tbody>
              <tr><th>User ID</th><td><code>adonkus@Princelaw.com</code></td></tr>
              <tr><th>Password</th><td><code>4x4=Sixteen*</code></td></tr>
              <tr><th>Account No.</th><td><code>898030514</code></td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'dashboard',
        title: 'Dashboard & Home Screen',
        audience: 'both',
        content: `
          <h4>Home Screen Overview</h4>
          <p>After logging in, you land on the home dashboard. The left-side secondary menu provides these views:</p>
          <ul>
            <li><strong>Calendar</strong> &mdash; FullCalendar view showing your events and appointments in month/week/day format.</li>
            <li><strong>Reminders</strong> &mdash; Active reminders assigned to you, sorted by due date. Overdue reminders appear first.</li>
            <li><strong>Copy iCal URL</strong> &mdash; Copies the subscription URL for your calendar to the clipboard. A spinner appears while the URL is copied, followed briefly by a checkmark and <strong>iCal URL copied</strong>. The URL uses the current V7 site and includes a unique API key for authentication.</li>
          </ul>

          <h5>Dashboard Cards</h5>
          <p>The dashboard summarizes active matters, today&rsquo;s events, pending reminders, favorites, recently viewed matters, and upcoming events.</p>
          <ul>
            <li><strong>Upcoming Events</strong> uses the number of event days configured in your user settings and includes a one-day lookback so an event that ended yesterday remains visible briefly.</li>
            <li>A multi-day event is shown only for the days that fall within that dashboard window. For example, a vacation that began two weeks ago and ended yesterday appears only for yesterday, rather than filling the card with all of its older dates.</li>
            <li>Use <strong>Add</strong> in the Upcoming Events card to create a new event.</li>
          </ul>

          <h5>Quick Matter Search</h5>
          <p>The <strong>Matter #</strong> field in the top-right corner of the navbar lets you jump directly to any matter by number:</p>
          <ul>
            <li>Type the 5-digit matter number and press <strong>Enter</strong> to navigate in the current tab.</li>
            <li>The field accepts numbers from 10000 up (matter numbers are always 5 digits).</li>
          </ul>
        `
      },
      {
        id: 'navigation',
        title: 'Navigation & Menus',
        audience: 'both',
        content: `
          <h4>Main Navigation Bar (Top)</h4>
          <p>The top navbar contains dropdown menus. Hover over a menu item to open its dropdown. The menus you see depend on your permissions:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Menu</th><th>Visible To</th><th>Contains</th></tr></thead>
            <tbody>
              <tr><td><strong>Accounting</strong></td><td>Admin, Accounting group</td><td>Accounting dashboard, activity rates, bank accounts, statements, chart of accounts, entries (billings, bills, checks, deposits, etc.), escrow balances, invoices, payment obligations, payroll, upcoming events, vendors</td></tr>
              <tr><td><strong>Administration</strong></td><td>Admin, Accountant, Tech</td><td>Activities, activity/matter/rate types, AI usage logs/dashboard, changes, client intake fields, document types, email archive, environment, graphs, matter configuration (bill types, detail types, matter types, matter date types, history types, role types), misc tools, phone calls, reports, scan priorities, setting groups/types, settings, SideKiq, taxes</td></tr>
              <tr><td><strong>Contacts</strong></td><td>All users</td><td>Contact index (direct link, no dropdown)</td></tr>
              <tr><td><strong>Documents</strong></td><td>All users (expanded for editors)</td><td>Document index. Editors also see: Auto Rules (email, outgoing, redirect, todo), Document Templates (index, new, tag system ref), Stylesheets, Stylesheet Groups, Fonts</td></tr>
              <tr><td><strong>Intranet</strong></td><td>All users</td><td>Documents, Intranets (privileged), Tech References (tech/superuser), In Development (tech/superuser), Users</td></tr>
              <tr><td><strong>KB</strong></td><td>All users</td><td>KB Articles, User Manual</td></tr>
              <tr><td><strong>Matter Histories</strong></td><td>All users</td><td>Matter history index (direct link)</td></tr>
              <tr><td><strong>Matters</strong></td><td>All users</td><td>Matter index (direct link)</td></tr>
              <tr><td><strong>Services</strong></td><td>All users</td><td>Events, Faxes, Newsletters, Outgoing, Queues (Appointment, Client Intakes, Exam Requests, Oral Depo, Scan Queue), Support</td></tr>
            </tbody>
          </table>

          <h4>Secondary Menu (Context Menu)</h4>
          <p>When you open a matter, contact, or other entity, a <strong>secondary menu</strong> appears below the navbar with context-specific actions (e.g., matter tabs for History, Documents, Billing, Events, etc.).</p>

          <h4>Breadcrumbs</h4>
          <p>Below the navbar, a <strong>breadcrumb trail</strong> labeled &ldquo;History:&rdquo; shows your recent page navigation. Click any breadcrumb to jump back to that page.</p>

          <h4>Intranet Switcher (Right Side)</h4>
          <p>If you have access to multiple intranets, the current intranet&rsquo;s short name appears in the navbar&rsquo;s right side. Click it to switch. This is only visible to privileged users with linked intranet accounts.</p>
        `
      },
      {
        id: 'switching-intranets',
        title: 'Switching Intranets',
        audience: 'both',
        content: `
          <h4>Multi-Intranet Support</h4>
          <p>V7 supports multiple <strong>intranets</strong> (separate firm instances). Each intranet has completely isolated data: its own matters, contacts, billing, settings, and users.</p>

          <h5>How to Switch</h5>
          <ol>
            <li>Look for your current intranet name in the top-right of the navbar.</li>
            <li>Click the dropdown to see every intranet with an active human user account having your SSN.</li>
            <li>Click the intranet name to switch.</li>
          </ol>

          <h5>How It Works</h5>
          <ul>
            <li>Switching changes the <code>intranet_id</code> stored in your session/local storage.</li>
            <li>All data shown after switching belongs to the selected intranet only.</li>
            <li>Your user account may have different permissions in different intranets.</li>
            <li>Settings, matter types, document templates, and all configuration are per-intranet.</li>
            <li>Human accounts are associated automatically by the SSN on the user&rsquo;s contact record. Manual link records and login-name matching are not used.</li>
            <li>The SSN is normalized for matching but is never displayed by the account-association screen. Accounts without a valid nine-digit SSN are not associated across intranets.</li>
            <li>If more than one active user in the same intranet has the same SSN, that intranet is excluded because the identity is ambiguous.</li>
          </ul>
          <div class="alert alert-warning"><strong>Important:</strong> If you don&rsquo;t see the intranet switcher, your account either only exists in one intranet, lacks a valid SSN, has an ambiguous SSN match, or you don&rsquo;t have privileged status. Contact an administrator to correct the user/contact records.</div>

        `
      }
    ]
  },

  // ============================================================
  // 2. MATTERS
  // ============================================================
  {
    id: 'matters',
    title: 'Matters',
    icon: 'fa-briefcase',
    audience: 'both',
    subsections: [
      {
        id: 'matters-overview',
        title: 'Overview & Fields',
        audience: 'both',
        content: `
          <h4>What is a Matter?</h4>
          <p>A <strong>matter</strong> is the central entity in V7, representing a legal case or engagement. Every contact involvement, document, billing entry, event, and communication is linked to a matter.</p>

          <h5>Matter Fields Reference</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Values / Notes</th></tr></thead>
            <tbody>
              <tr><td><strong>Number</strong></td><td>Unique 5-digit identifier</td><td>Auto-generated per intranet, starts at 10000. Cannot be changed after creation.</td></tr>
              <tr><td><strong>Matter Type</strong></td><td>Category of case</td><td>Selected from configured types (e.g., Workers&rsquo; Comp, Personal Injury, Estate). Determines fee structure, default rates, and intake fields. See <em>Matter Type Configuration</em>.</td></tr>
              <tr><td><strong>Status</strong></td><td>Current state of the matter</td><td>3-level hierarchy. See <em>Status Hierarchy</em> below.</td></tr>
              <tr><td><strong>Custom Name</strong></td><td>Override for auto-generated case name</td><td>If blank, system auto-generates based on matter type rules (see below). If set, this name is displayed instead.</td></tr>
              <tr><td><strong>Jurisdiction</strong></td><td>Legal jurisdiction</td><td>Free text (e.g., &ldquo;Dauphin County&rdquo;, &ldquo;U.S. District Court M.D. Pa.&rdquo;).</td></tr>
              <tr><td><strong>Rate Type</strong></td><td>Fee arrangement</td><td>Inherited from matter type: Hourly, Flat Fee, Percentage, No Charge.</td></tr>
              <tr><td><strong>Referral Percent</strong></td><td>Percentage owed to referring party</td><td>Decimal (e.g., 33.33). Used for referral fee tracking.</td></tr>
              <tr><td><strong>Notes</strong></td><td>Internal notes about the matter</td><td>Free text. Visible to all matter users.</td></tr>
              <tr><td><strong>Notice</strong></td><td>Special notice or alert</td><td>Free text. Typically used for important warnings or flags.</td></tr>
              <tr><td><strong>is_private</strong></td><td>Restrict access</td><td>When true, only assigned matter users can view this matter.</td></tr>
            </tbody>
          </table>

          <h5>Case Name Auto-Generation Rules</h5>
          <p>When no custom name is set, V7 generates the case name automatically based on the matter type:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Matter Type</th><th>Generated Name Pattern</th></tr></thead>
            <tbody>
              <tr><td>Workers&rsquo; Compensation</td><td><em>Claimant</em> vs <em>Defendant/Employer</em></td></tr>
              <tr><td>Estate / Probate</td><td>Estate of <em>[Decedent name]</em></td></tr>
              <tr><td>Criminal</td><td>Commonwealth vs <em>[Defendant name]</em></td></tr>
              <tr><td>All Others (default)</td><td><em>Plaintiff</em> vs <em>Defendant</em></td></tr>
            </tbody>
          </table>
          <p>Names are derived from contact involvements with the corresponding roles (client, defendant, claimant, etc.).</p>
        `
      },
      {
        id: 'matter-statuses',
        title: 'Status Hierarchy',
        audience: 'both',
        content: `
          <h4>Matter Status System</h4>
          <p>Statuses use a 3-level ancestry-based hierarchy: <strong>top-level category &rarr; sub-status</strong>. Every matter must have a status.</p>

          <h5>Active Statuses</h5>
          <p>Matter is currently being worked on:</p>
          <ul>
            <li><strong>Pro Bono</strong> &mdash; No-fee case</li>
            <li><strong>Negotiations</strong> &mdash; Settlement discussions</li>
            <li><strong>Maintenance</strong> &mdash; Ongoing case maintenance</li>
            <li><strong>Litigation</strong>
              <ul>
                <li>Record Closed &mdash; Litigation record closed but matter still active</li>
                <li>On Appeal &mdash; Case is on appeal</li>
              </ul>
            </li>
          </ul>

          <h5>Inactive Statuses</h5>
          <p>Matter is not actively worked but not permanently closed:</p>
          <ul>
            <li><strong>Advice Only</strong> &mdash; Consultation only, no ongoing representation</li>
            <li><strong>Maintenance</strong> &mdash; Periodic check-in required</li>
            <li><strong>Ongoing Medical</strong> &mdash; Waiting on medical treatment/records</li>
            <li><strong>Referral Only</strong> &mdash; Referred to another firm</li>
          </ul>

          <h5>Perm (Permanent/Closed) Statuses</h5>
          <p>Matter is permanently closed:</p>
          <ul>
            <li><strong>Originals In File</strong> &mdash; Closed, original documents retained</li>
            <li><strong>Accounting Reject</strong> &mdash; Closed due to accounting issues</li>
            <li><strong>Collections</strong> &mdash; Sent to collections</li>
          </ul>

          <div class="alert alert-info"><strong>Tip:</strong> When filtering matters, you can filter by top-level category (Active/Inactive/Perm) or by specific sub-status. Perm matters are excluded from most default views.</div>
        `
      },
      {
        id: 'matter-exhibits',
        title: 'Exhibits',
        audience: 'both',
        content: `
          <h4>Matter Exhibits</h4>
          <p>The <strong>Exhibits</strong> section on the matter sidebar lists documents that have an exhibit number or exhibit label. The list is sorted by exhibit label and shows the exhibit title, document name, and document type.</p>

          <h5>Downloading All Exhibits</h5>
          <p>When a matter has listed exhibits, click <strong>Download Zip</strong> in the Exhibits section to download all listed exhibit files in one zip file.</p>
          <ul>
            <li>The zip includes the same exhibits shown in the sidebar list.</li>
            <li>Each file in the zip is named with the exhibit label followed by the document name.</li>
            <li>If a listed exhibit cannot be found on disk, the zip still downloads and includes a <code>_missing_exhibits.txt</code> note identifying the missing file.</li>
          </ul>

          <div class="alert alert-info"><strong>Tip:</strong> Use this when preparing exhibit packets or when you need to send all exhibits from a matter together.</div>
        `
      },
      {
        id: 'matter-details',
        title: 'Billing & Configuration Flags',
        audience: 'both',
        content: `
          <h4>Billing Flags</h4>
          <p>These flags control how billing and invoicing work for the matter:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Flag</th><th>Effect</th></tr></thead>
            <tbody>
              <tr><td><strong>do_invoice</strong></td><td>Whether this matter should be included in invoice batch runs. Turn off for matters that should not receive invoices.</td></tr>
              <tr><td><strong>email_invoice</strong></td><td>If true AND the billing contact has an email on file, invoices are sent via email with PDF attachment and online payment link when the matter uses an online payment provider. If false, invoices are printed/mailed.</td></tr>
              <tr><td><strong>auto_pay_from_escrow</strong></td><td>Automatically apply escrow funds to outstanding charges when generating invoices.</td></tr>
              <tr><td><strong>is_uncollectable</strong></td><td>Flags the matter as uncollectable. Used for reporting &mdash; does not prevent invoicing.</td></tr>
              <tr><td><strong>billing_method</strong></td><td>&ldquo;manual&rdquo; (default/manual payment handling), &ldquo;lawpay&rdquo; (LawPay-integrated invoicing), or &ldquo;payload&rdquo; (Payload-integrated invoicing). Legacy &ldquo;traditional&rdquo; values are treated as manual.</td></tr>
              <tr><td><strong>in_collections</strong></td><td>Flags the matter as sent to collections. Used for tracking and reporting.</td></tr>
            </tbody>
          </table>

          <h4>Matter Details (Key/Value Pairs)</h4>
          <p>Matter details are configurable key/value pairs linked to <strong>Detail Types</strong>. Common uses:</p>
          <ul>
            <li><strong>Flat Fee</strong> &mdash; The flat fee amount for flat-fee matters</li>
            <li><strong>Maximum Fee</strong> &mdash; Fee cap for the engagement</li>
            <li><strong>Retainer Amount</strong> &mdash; Required retainer</li>
            <li><strong>Claim Number</strong> &mdash; Insurance claim reference</li>
            <li><strong>Case Number</strong> &mdash; Court case number</li>
          </ul>
          <p>Detail types are configured per intranet under <strong>Administration &rarr; Matters &rarr; Detail Types</strong>. The values stored in matter details can be referenced in document templates using tags.</p>

          <h4>Applicable Rates</h4>
          <p>Open a matter and choose <strong>Applicable Rates</strong> from the matter menu to review the billing rates that apply to that matter&rsquo;s selected rate type.</p>
          <p>The page shows the firm default, user-specific overrides for active matter users, any matter override, and the resulting effective rate. Rates are resolved in this order:</p>
          <ol>
            <li><strong>Matter override</strong> &mdash; applies to all users on the matter.</li>
            <li><strong>User override</strong> &mdash; applies to the assigned user when no matter override exists.</li>
            <li><strong>Firm default</strong> &mdash; applies to other users when no more specific rate exists.</li>
          </ol>
          <p>Only active activities with a non-zero rate are listed. This view is read-only; change rates from the appropriate activity, user, or matter rate administration screens.</p>

          <h4>Matter Users (Team Assignment)</h4>
          <p>Each matter has assigned users with specific roles. These roles are critical not only for workflow but also for the <strong>factor compensation system</strong> (see <em>Factor System</em> in the Payroll section).</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Role</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Lead Attorney</strong></td><td>Primary attorney responsible for the matter. Can authorize billing. Receives auto-reminders from scan releases. If no separate &ldquo;Income&rdquo; user is assigned, the lead attorney is treated as the income attorney for factor purposes.</td></tr>
              <tr><td><strong>Attorney</strong></td><td>Additional attorney working on the matter.</td></tr>
              <tr><td><strong>Paralegal</strong></td><td>Paralegal assigned to the matter.</td></tr>
              <tr><td><strong>Staff</strong></td><td>Administrative staff.</td></tr>
              <tr><td><strong>Income</strong></td><td>The attorney credited with generating income on this matter. When a fee payment is received, the income attorney (and their manager) receive factor credit. If not explicitly set, defaults to the billing user.</td></tr>
              <tr><td><strong>Opener</strong></td><td>The person who <strong>brought the case to the firm</strong> &mdash; the one who &ldquo;opened the door.&rdquo; This is typically the attorney who conducted the initial consultation or signed the client. The opener receives a share of every fee payment on this matter based on their <code>opener_percent</code> in their wage base. If no opener is set and the income attorney is not the lead, the lead attorney fills the opener role.</td></tr>
            </tbody>
          </table>

          <h5>Communication Notifications</h5>
          <p>Edit a matter user and select <strong>Include in all communications</strong> when that user should receive routine email notifications generated for the matter. This setting may be changed for the lead attorney without first assigning another lead attorney.</p>
          <p>V7 requires every matter to retain a lead attorney. The warning <strong>&ldquo;Matter should have an alternative lead attorney&rdquo;</strong> appears only when someone tries to change the matter&rsquo;s sole lead attorney to another role. Assign a replacement lead attorney before making that role change.</p>

          <h5>Owners (Referral Source Owners)</h5>
          <p>The <strong>owner</strong> role is <em>not</em> a matter user type &mdash; it is determined indirectly through the matter&rsquo;s <strong>referral contact</strong>:</p>
          <ol>
            <li>A matter can have a contact with the <strong>referral</strong> role (set on the contact&rsquo;s involvement). This is the person or entity that <em>referred</em> the client to the firm.</li>
            <li>That referral contact has an <strong>&ldquo;Owned By&rdquo;</strong> field (on the Contact record) pointing to a firm user. This user is the attorney who &ldquo;owns&rdquo; the relationship with that referral source &mdash; they cultivated the referral relationship.</li>
            <li>When fee payments are received, the owner receives a share based on their <code>owner_percent</code> in their wage base.</li>
          </ol>

          <div class="alert alert-info">
            <strong>Example:</strong> Dr. Smith refers patients to the firm. Attorney Jane &ldquo;owns&rdquo; the relationship with Dr. Smith (she plays golf with him, sends holiday cards, etc.). On Dr. Smith&rsquo;s contact record, the <strong>Owned By</strong> field is set to Jane. When any matter referred by Dr. Smith receives a fee payment, Jane gets her <code>owner_percent</code> share via the factor system &mdash; even if she is not the attorney working the case.
          </div>

          <h5>How Opener &amp; Owner Affect Compensation</h5>
          <p>When a fee payment is received on a matter, the factor system distributes the money as follows:</p>
          <ol>
            <li>The <strong>income attorney</strong> (and their manager) take a share based on <code>fee_percent</code>.</li>
            <li>The <strong>opener</strong> (and their manager) take a share based on <code>opener_percent</code>, deducted from the income pool.</li>
            <li>The <strong>owner</strong> (and their manager) take a share based on <code>owner_percent</code>, also deducted from the income pool.</li>
            <li>The <strong>firm</strong> retains everything else.</li>
          </ol>
          <p>If the income attorney is also the opener or owner, duplicate factor entries are suppressed &mdash; they already receive the income share. See the <em>Factor System</em> section under Payroll for the detailed calculation algorithm and a worked example.</p>

          <div class="alert alert-warning"><strong>Important:</strong> <code>opener_percent + owner_percent</code> cannot exceed <code>fee_percent</code> in a user&rsquo;s wage base. This prevents situations where deductions exceed the income pool, which would result in negative factor amounts.</div>
        `
      },
      {
        id: 'sub-matters',
        title: 'Sub-Matters',
        audience: 'both',
        content: `
          <h4>Hierarchical Matter Structure</h4>
          <p>V7 uses an <strong>ancestry-based tree</strong> for matter hierarchies. A parent matter can have child sub-matters, which can have their own children.</p>

          <h5>Key Behaviors</h5>
          <ul>
            <li><strong>Billing aggregation</strong>: Invoices cover the entire matter family tree. When you generate an invoice for a parent matter, unbilled time from all sub-matters is included. The system uses <code>billable_subtree_ids</code> to find all related matters.</li>
            <li><strong>Independent data</strong>: Each sub-matter has its own contacts, documents, history, and status. They are separate matters that happen to be linked.</li>
            <li><strong>Orphan strategy</strong>: If a parent matter is deleted, sub-matters become top-level matters (orphans). They are not deleted.</li>
          </ul>

          <h5>Creating a Sub-Matter</h5>
          <ol>
            <li>Open the parent matter.</li>
            <li>Click <strong>Add Sub-Matter</strong> from the matter menu.</li>
            <li>The new matter will be created with the parent relationship pre-set.</li>
          </ol>

          <div class="alert alert-info"><strong>Tip:</strong> Sub-matters are useful for cases with multiple proceedings (e.g., a workers&rsquo; comp case with a separate appeal, or a personal injury case with multiple defendants).</div>
        `
      },
      {
        id: 'matter-tags',
        title: 'Tags & Classification',
        audience: 'both',
        content: `
          <h4>Using Tags</h4>
          <p>Tags provide flexible classification beyond matter types. They use the <strong>ActsAsTaggable</strong> system, allowing multiple tags per matter.</p>

          <h5>Adding Tags</h5>
          <ol>
            <li>Open the matter detail view.</li>
            <li>Find the <strong>Tags</strong> section.</li>
            <li>Type a tag name and press Enter, or select from existing tags.</li>
          </ol>

          <h5>Uses of Tags</h5>
          <ul>
            <li><strong>Filtering</strong>: Search and filter matters by tag on the matter index page.</li>
            <li><strong>Document templates</strong>: Tags can be referenced in the template tag system for conditional content.</li>
            <li><strong>Reporting</strong>: Group matters by tag for custom reports.</li>
          </ul>
        `
      },
      {
        id: 'matter-dates',
        title: 'Important Dates',
        audience: 'both',
        content: `
          <h4>Matter Date System</h4>
          <p>Each matter can have multiple dated entries, each linked to a <strong>Matter Date Type</strong> configured per intranet.</p>

          <h5>Common Date Types</h5>
          <ul>
            <li><strong>Date of Accident/Injury</strong> &mdash; When the incident occurred</li>
            <li><strong>Statute of Limitations</strong> &mdash; Filing deadline</li>
            <li><strong>Filing Date</strong> &mdash; When the case was filed</li>
            <li><strong>Hearing Date</strong> &mdash; Scheduled court hearing</li>
            <li><strong>Date of Death</strong> &mdash; For estate/wrongful death matters</li>
          </ul>

          <h5>Adding a Date</h5>
          <ol>
            <li>Open the matter and go to the <strong>Dates</strong> section.</li>
            <li>Click <strong>Add Date</strong>.</li>
            <li>Select the date type from the dropdown.</li>
            <li>Enter the date value.</li>
            <li>Click <strong>Save</strong>.</li>
          </ol>

          <p>Matter dates can be referenced in document templates using tags (e.g., <code>&lt;r:matter_date type=&quot;Date of Accident&quot;/&gt;</code>) and are collected during client intake if configured in the intake form fields.</p>

          <div class="alert alert-info"><strong>Tip:</strong> Date types are configured under <strong>Administration &rarr; Matters &rarr; Matter Date Types</strong>. Each intranet can define its own set of date types.</div>
        `
      },
      {
        id: 'matter-perming',
        title: 'Perming a File',
        audience: 'both',
        content: `
          <h4>Perming a File</h4>
          <p><strong>Perming</strong> means permanently closing a matter. A matter should be permed when it is <strong>unlikely the client will return</strong> &mdash; for example, after a C&amp;R settlement is distributed, or for no-show clients. If the client may return, use <strong>Inactive</strong> status instead.</p>

          <div class="alert alert-warning"><strong>Important:</strong> If the client does return after perming, simply set the matter back to Active, handle the issue, then re-perm when resolved. Online records are never disposed of.</div>

          <h5>Step 1: Attorney Changes Status</h5>
          <ol>
            <li>The attorney changes the matter status to a <strong>Perm</strong> sub-status in V7.</li>
            <li>A history entry must be added stating <strong>&ldquo;Matter has been reviewed and permed.&rdquo;</strong></li>
            <li>If there is an escrow balance or balance due, a history note must be entered and directed to Accounting and the appropriate recipient for review.</li>
            <li>The system automatically emails Staff that the file has been permed.</li>
            <li>The status changes to <strong>Perm Pending</strong> while awaiting Accounting approval.</li>
          </ol>

          <h5>Step 2: Accounting Review</h5>
          <p>Accounting receives a notification via the Troubleshooters email and must verify the following before the perm proceeds:</p>
          <ul>
            <li>Verify a current history from the attorney explaining why the file is being permed. If missing, email the attorney requesting one.</li>
            <li>Check perm reports are in order.</li>
            <li>Review outstanding expenses &mdash; these become the attorney&rsquo;s obligation to collect or write off.</li>
            <li>If large uncollected expenses with no history note, email the attorney.</li>
          </ul>

          <h5>Step 3: &ldquo;Do You Want File&rdquo; Letter</h5>
          <p>Staff locates the physical file, verifies all documents in the physical file are also in V7 digitally, then sends the client a letter offering them their closed file.</p>
          <ul>
            <li><strong>Individual letter:</strong> Use the template <strong>&ldquo;v5 Letter - Do You Want File - PermReady&rdquo;</strong>, created through the matter like a new client letter.</li>
            <li><strong>Bulk mailing:</strong>
              <ol>
                <li>Tag matters with <strong>&ldquo;permready&rdquo;</strong>.</li>
                <li>Use the bulk document templates <strong>&ldquo;Do you want file - PermReady&rdquo;</strong> and <strong>&ldquo;Envelope - PermReady&rdquo;</strong>.</li>
                <li>After sending, use <strong>Bulk Edit</strong> to change the tag from &ldquo;permready&rdquo; to <strong>&ldquo;PermLetterSent&rdquo;</strong>.</li>
              </ol>
            </li>
          </ul>
          <p>Set a <strong>3-week reminder</strong> to check for the client&rsquo;s response.</p>

          <div class="alert alert-danger"><strong>Do NOT</strong> send &ldquo;Do You Want File&rdquo; letters for files whose client has <strong>passed away</strong>.</div>

          <h5>Step 4: Client Response</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Client Response</th><th>Action</th></tr></thead>
            <tbody>
              <tr>
                <td><strong>Client says NO</strong> (doesn&rsquo;t want file)</td>
                <td>Make a history entry. File can be destroyed along with the letter copy.</td>
              </tr>
              <tr>
                <td><strong>No response after 3 weeks</strong></td>
                <td>File is destroyed along with the letter copy.</td>
              </tr>
              <tr>
                <td><strong>Client says YES &mdash; pick up in person</strong></td>
                <td>Client comes to the office, signs a <strong>Client File Release Form</strong>, and presents valid State ID. Scan the signed release into the matter. Change the file status to <strong>&ldquo;File With Client&rdquo;</strong>.</td>
              </tr>
              <tr>
                <td><strong>Client says YES &mdash; mail the file</strong></td>
                <td>Option to burn to CD ($25) or mail physical copy (10 cents/page). Client must return a signed File Release Form with payment first.</td>
              </tr>
              <tr>
                <td><strong>Letter returned undeliverable</strong></td>
                <td>Mark the old address as inactive. Try another address on file (reset tag to &ldquo;permready&rdquo;). If no other address exists, follow the destruction process.</td>
              </tr>
            </tbody>
          </table>

          <h5>Step 5: File Destruction / Final Disposition</h5>
          <ol>
            <li>Change the file location to <strong>&ldquo;Destroy&rdquo;</strong> in the matter Details.</li>
            <li>Add a notice: <strong>&ldquo;file destroyed&rdquo;</strong>.</li>
            <li>Archive the matter (see <em>Archiving a Matter</em> below).</li>
          </ol>

          <div class="alert alert-danger"><strong>Exception &mdash; Original Documents:</strong> If the permed file contains a <strong>Will, Power of Attorney, or Living Will</strong>, do <strong>NOT</strong> destroy the file. Place it in the Inactive Box and add a notice: <strong>&ldquo;This file contains original documents and should not be destroyed.&rdquo;</strong></div>

          <h5>Physical File Handling</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>File Status</th><th>Sticker Color</th><th>Location</th></tr></thead>
            <tbody>
              <tr><td>Active</td><td>None</td><td>Active racks downstairs</td></tr>
              <tr><td>Inactive</td><td>Red</td><td>Inactive racks upstairs</td></tr>
              <tr><td>Perm</td><td>Blue</td><td>Perm file racks upstairs</td></tr>
            </tbody>
          </table>
          <p>Update the <strong>File Location</strong> field in the matter&rsquo;s Details section whenever a file is moved. When removing a file from any rack, replace it with a colored placeholder folder (red for attorneys, yellow for staff) with the matter number and initials.</p>

          <h5>Releasing Permed Scans</h5>
          <p>Permed scans follow the same release procedure as mail scans, with these differences:</p>
          <ul>
            <li>Permed scans are designated by <strong>Priority 4</strong>.</li>
            <li>For permed scans, you do <strong>NOT</strong> need to fill in the Date Created or the Author.</li>
          </ul>
        `
      },
      {
        id: 'matter-archiving',
        title: 'Archiving a Matter',
        audience: 'both',
        content: `
          <h4>Archiving a Matter</h4>
          <p>Archiving moves a matter and <strong>all its associated data</strong> (documents, histories, billings, involvements, scans, outgoing jobs, etc.) from the active database to a separate archive database. Archived matters no longer appear in searches or default views but can be restored if needed.</p>

          <div class="alert alert-info"><strong>When to Archive:</strong> Archive a matter after the perming process is complete &mdash; the file has been destroyed or returned to the client, and all accounting is finalized. Archiving is the <em>last step</em> of the perm workflow.</div>

          <h5>How to Archive</h5>
          <ol>
            <li>Open the matter in V7.</li>
            <li>Click the <strong>Archive</strong> button on the matter page.</li>
            <li>The system verifies connectivity to the archive database and storage server before proceeding.</li>
            <li>All matter data is copied to the archive database, document files are transferred via rsync to archive storage, and originals are removed from the active system.</li>
            <li>A &ldquo;Perm Activity&rdquo; history entry is automatically created recording who archived the matter and when.</li>
          </ol>

          <h5>What Gets Archived</h5>
          <p>The following data is moved to the archive database:</p>
          <ul>
            <li><strong>Matter record</strong> (including tags and custom name)</li>
            <li><strong>Documents</strong> and document versions (files transferred to archive storage)</li>
            <li><strong>Matter histories</strong> and related emails, links, and responses</li>
            <li><strong>Billings</strong> and entry groups</li>
            <li><strong>Involvements</strong> (contacts, addresses, emails, relationships, roles)</li>
            <li><strong>Scans</strong> and scan authors</li>
            <li><strong>Outgoing jobs</strong>, documents, and envelopes</li>
            <li><strong>Reminders</strong> linked to matter histories</li>
            <li><strong>Events</strong>, bills, entries, and other associated records</li>
          </ul>
          <div class="alert alert-warning"><strong>Note:</strong> &ldquo;Perm Activity&rdquo; history entries are preserved in the active database even after archiving, so there is always a record that the matter was archived.</div>

          <h5>Partial Archive</h5>
          <p>You can archive specific record types (documents, histories, billings) before a certain date without archiving the entire matter. This is useful for reducing the size of long-running matters.</p>
          <ol>
            <li>Open the matter and click <strong>Archive</strong>.</li>
            <li>Select which record types to archive (documents, entries, matter histories, etc.).</li>
            <li>Set a <strong>before date</strong> &mdash; only records older than this date will be archived.</li>
            <li>Click <strong>Go</strong>.</li>
          </ol>

          <h5>Restoring an Archived Matter</h5>
          <p>If a client returns or a matter needs to be reopened:</p>
          <ol>
            <li>Locate the archived matter (search may need to include archived matters).</li>
            <li>Click <strong>Restore</strong>.</li>
            <li>The system copies all data back from the archive database to the active database and rsyncs document files back to active storage.</li>
            <li>Set the matter status back to <strong>Active</strong>.</li>
          </ol>
          <p>If the file was previously destroyed, change the file status to &ldquo;Active&rdquo; in Details and have the Receptionist create a new physical file.</p>

          <h5>Restore File Warnings</h5>
          <p>When a restore finds some document database records but cannot find the matching files in archive storage, V7 continues restoring the records and any other document files that are available. The matter receives an <strong>Archive restore file warning</strong> history entry listing each missing document file, and Rollbar receives the same details with the matter number, document id, filename, archive path, destination path, and file-size checks.</p>
          <div class="alert alert-warning"><strong>Important:</strong> A restore warning means the matter may contain a document row whose backing file was already missing from storage. Review the warning history entry before relying on the restored document list.</div>

          <h5>Archive and Restore Failure Notifications</h5>
          <p>V7 verifies transferred files at the location where rsync actually placed them. A successful restore therefore does not generate a failure merely because the matter remains marked archived while its files are being copied back to active storage.</p>
          <p>If a transfer command fails, or if one or more files are genuinely missing after transfer, V7 stops the operation and sends <strong>one consolidated notification</strong> for that operation. The notification identifies whether the operation was archiving or restoring, gives the source and destination, and lists every missing file path. The same command failure is reported to Rollbar for technical investigation.</p>
          <div class="alert alert-info"><strong>In plain terms:</strong> Staff should receive one useful message for a real archive or restore problem, rather than a separate false alarm for every file. Do not retry until the listed paths and the Rollbar report have been reviewed.</div>

          <h5>Technical Details</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Aspect</th><th>Detail</th></tr></thead>
            <tbody>
              <tr><td><strong>Archive database</strong></td><td>Separate database with mirrored <code>*_archived</code> tables (e.g., <code>documents_archived</code>, <code>matter_histories_archived</code>)</td></tr>
              <tr><td><strong>File storage</strong></td><td>Document files transferred via rsync to archive storage path (<code>/mnt/attymatter_storage/{intranet_id}/{matter_number}/</code>)</td></tr>
              <tr><td><strong>Concurrency</strong></td><td>A lock file (<code>archiving.lock</code>) prevents multiple archive operations from running simultaneously</td></tr>
              <tr><td><strong>Connectivity check</strong></td><td>The system verifies archive database and storage server connectivity before starting. If either is unreachable, the operation is aborted with no changes made.</td></tr>
              <tr><td><strong>Solr index</strong></td><td>Updated after archive/restore to keep search results consistent</td></tr>
            </tbody>
          </table>
        `
      }
    ]
  },

  // ============================================================
  // 3. CONTACTS
  // ============================================================
  {
    id: 'contacts',
    title: 'Contacts',
    icon: 'fa-address-book',
    audience: 'both',
    subsections: [
      {
        id: 'contacts-overview',
        title: 'Overview & Contact Types',
        audience: 'both',
        content: `
          <h4>Contact Management</h4>
          <p>Contacts represent all people and organizations in V7. They use <strong>Single Table Inheritance (STI)</strong> &mdash; the <code>type</code> field determines whether a contact is a <strong>Person</strong> or a <strong>Company</strong>.</p>

          <h5>Person Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>first_name</strong></td><td>First name</td><td>Required for persons</td></tr>
              <tr><td><strong>middle_name</strong></td><td>Middle name or initial</td><td>Optional</td></tr>
              <tr><td><strong>last_name</strong></td><td>Last name</td><td>Required for persons</td></tr>
              <tr><td><strong>qualifier</strong></td><td>Title/prefix</td><td>Mr., Mrs., Ms., The Honorable, etc.</td></tr>
              <tr><td><strong>generational_qualifier</strong></td><td>Suffix</td><td>Jr., Sr., II, III, IV, etc.</td></tr>
              <tr><td><strong>honorarium</strong></td><td>Professional suffix</td><td>Esq., Ph.D., M.D., etc.</td></tr>
              <tr><td><strong>ssn</strong></td><td>Social Security Number</td><td>Format: XXX-XX-XXXX. Used for 1099 reporting, cross-intranet user matching, and conflict checks.</td></tr>
              <tr><td><strong>dob</strong></td><td>Date of birth</td><td>Date</td></tr>
              <tr><td><strong>dod</strong></td><td>Date of death</td><td>Date (for estate/wrongful death matters)</td></tr>
              <tr><td><strong>gender</strong></td><td>Gender</td><td>&ldquo;m&rdquo; or &ldquo;f&rdquo;</td></tr>
              <tr><td><strong>employer_id</strong></td><td>Link to employer</td><td>Contact ID of the employing company</td></tr>
            </tbody>
          </table>
          <p><strong>Computed name</strong>: &ldquo;First Middle Last Gen&rdquo; (e.g., &ldquo;John A. Smith Jr.&rdquo;)</p>

          <h5>Company Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>company_name</strong></td><td>Company name</td><td>Required for companies</td></tr>
              <tr><td><strong>company_suffix</strong></td><td>Legal suffix</td><td>LLC, Inc., Corp., LLP, P.C., etc.</td></tr>
              <tr><td><strong>ein</strong></td><td>Employer ID Number</td><td>XX-XXXXXXX format. Used for 1099 reporting.</td></tr>
            </tbody>
          </table>
          <p><strong>Computed name</strong>: &ldquo;Name, Suffix&rdquo; (e.g., &ldquo;Acme Industries, LLC&rdquo;)</p>

          <h5>Special Fields (Both Types)</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>referral_source</strong></td><td>How the contact found the firm</td></tr>
              <tr><td><strong>referral_destination</strong></td><td>Where the contact was referred to</td></tr>
              <tr><td><strong>is_on_mailing_list</strong></td><td>Include in newsletter mailings</td></tr>
              <tr><td><strong>requires_1099</strong></td><td>Flag for 1099 reporting at end of year</td></tr>
              <tr><td><strong>share_password</strong></td><td>Password for client portal access (if applicable)</td></tr>
              <tr><td><strong>lawpay_contact_id</strong></td><td>Linked LawPay contact record for payment processing</td></tr>
              <tr><td><strong>url</strong></td><td>Website URL</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'contact-addresses',
        title: 'Addresses',
        audience: 'both',
        content: `
          <h4>Address System</h4>
          <p>Each contact can have <strong>multiple addresses</strong>, each with a type that determines how it&rsquo;s used.</p>

          <h5>Address Types</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Type</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><strong>Communications</strong></td><td>Default address for correspondence and letters. This is the primary address used by the system.</td></tr>
              <tr><td><strong>Billing</strong></td><td>Used for invoices and statements. If no billing address exists, falls back to Communications address.</td></tr>
              <tr><td><strong>Appointment</strong></td><td>Used for scheduling meetings and appointments.</td></tr>
              <tr><td><strong>Deposition</strong></td><td>Used for deposition notices and scheduling.</td></tr>
              <tr><td><strong>Inactive</strong></td><td>Archived address no longer in use. Not used for any correspondence.</td></tr>
              <tr><td><strong>Payment</strong></td><td>Address for payment-related correspondence.</td></tr>
              <tr><td><strong>Service</strong></td><td>Address for service of process.</td></tr>
            </tbody>
          </table>

          <h5>Default Address Resolution</h5>
          <p>When the system needs a single address for a contact (e.g., for a letter), it resolves in this order:</p>
          <ol>
            <li><strong>Communications</strong> address (if exists)</li>
            <li><strong>Billing</strong> address (fallback)</li>
            <li><strong>First active address</strong> (last resort)</li>
          </ol>

          <h5>Removing an Address</h5>
          <p>Use the delete control beside an address when it is no longer current. If the address has never been used by an event, outgoing envelope, document, or another historical record, V7 deletes it. If it has been used, V7 instead marks it <strong>Inactive</strong>, transfers current involvement selections to a suitable remaining address, and reports that the address was removed from the active list.</p>
          <p>An inactive address is hidden from the contact page, address editor, address selectors, and default-address selection. V7 retains the underlying record solely so older events, mailings, and audit history continue to show the address actually used at that time.</p>
          <div class="alert alert-warning"><strong>Last active address:</strong> V7 will not remove a living contact&rsquo;s only active address. Add the replacement address first, give it the needed address types, and then remove the obsolete address.</div>

          <div class="alert alert-info"><strong>Tip:</strong> When generating documents, the specific address used depends on the involvement. You can override which address is used for a particular matter via the involvement&rsquo;s address settings. See <em>Involvements</em>.</div>
        `
      },
      {
        id: 'contact-communications',
        title: 'Phone Numbers & Emails',
        audience: 'both',
        content: `
          <h4>Phone Numbers</h4>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Type</strong></td><td>Home, Work, Mobile, Fax</td></tr>
              <tr><td><strong>Number</strong></td><td>The phone number</td></tr>
              <tr><td><strong>Extension</strong></td><td>Optional extension</td></tr>
              <tr><td><strong>include_in_letterhead</strong></td><td>If true, this number appears in document letterhead</td></tr>
              <tr><td><strong>Position</strong></td><td>Ordering. Lower numbers appear first. The first phone number is considered the primary number.</td></tr>
            </tbody>
          </table>

          <h4>Email Addresses</h4>
          <p>Contacts can have multiple email addresses. They are ordered by <strong>position</strong> &mdash; the first email (lowest position number) is the <strong>primary email</strong> used for correspondence, invoice delivery, and notifications.</p>

          <div class="alert alert-warning"><strong>Important:</strong> The primary email address determines where invoices are sent when <code>email_invoice</code> is enabled on a matter. Make sure the correct email is in position 1.</div>
        `
      },
      {
        id: 'contact-aliases',
        title: 'Aliases (AKAs)',
        audience: 'both',
        content: `
          <h4>Also Known As (AKA) System</h4>
          <p>Contacts may have alternate names (maiden names, nicknames, former names, business names). Each AKA record stores:</p>
          <ul>
            <li><strong>Name</strong> &mdash; The alternate name</li>
            <li><strong>Type</strong> &mdash; Category (e.g., maiden name, nickname, formerly known as, trade name)</li>
          </ul>

          <h5>Where AKAs Are Used</h5>
          <ul>
            <li><strong>Conflict checks</strong>: AKAs are included in conflict check searches, so a search for &ldquo;Jane Smith&rdquo; will also find &ldquo;Jane Doe&rdquo; if &ldquo;Doe&rdquo; is listed as a maiden name AKA.</li>
            <li><strong>Contact search</strong>: AKAs are indexed in Solr for full-text search.</li>
            <li><strong>Familiar letter salutations</strong>: A <strong>Nick Name</strong> AKA is used when drafting a new letter and choosing a familiar salutation. If no Nick Name AKA is present, V7 uses the contact&rsquo;s first name.</li>
            <li><strong>Soundex matching</strong>: The system generates <code>last_name_soundex</code> and <code>company_name_soundex</code> values for fuzzy name matching (finds phonetically similar names).</li>
          </ul>

          <h5>Nicknames for Familiar Documents</h5>
          <p>When creating a person contact, ask whether the individual prefers a nickname. If they do, add it as an alias with the type <strong>Nick Name</strong>. This should be entered even when the first-name-to-nickname relationship seems obvious, because V7 uses the Nick Name alias when generating documents with the <strong>Familiar</strong> option.</p>
          <p>Enter only the nickname itself, not the person&rsquo;s last name. Common examples include:</p>
          <ul>
            <li>Elizabeth &rarr; Betty</li>
            <li>Jonathan &rarr; John or Jon</li>
            <li>William &rarr; Bill</li>
            <li>Michele &rarr; Mikey</li>
            <li>Joseph &rarr; Joe</li>
            <li>Michael &rarr; Mike</li>
          </ul>
        `
      },
      {
        id: 'searching-contacts',
        title: 'Searching Contacts',
        audience: 'both',
        content: `
          <h4>Contact Search</h4>
          <p>V7 uses <strong>Apache Solr</strong> for fast, full-text contact search. The search indexes:</p>
          <ul>
            <li>First name, middle name, last name</li>
            <li>Company name</li>
            <li>Email addresses</li>
            <li>Phone numbers</li>
            <li>AKA/alias names</li>
            <li>SSN (partial match)</li>
          </ul>

          <h5>Search Tips</h5>
          <ul>
            <li>The search is <strong>fuzzy</strong> &mdash; partial matches and similar spellings will return results.</li>
            <li>Search for a phone number (or partial number) to find contacts by phone.</li>
            <li>Soundex matching finds names that sound similar (e.g., &ldquo;Smith&rdquo; and &ldquo;Smyth&rdquo;).</li>
            <li>The contact index page also supports column-based sorting and filtering.</li>
          </ul>
        `
      },
      {
        id: 'merging-contacts',
        title: 'Merging Contacts',
        audience: 'both',
        content: `
          <h4>When to Merge Contacts</h4>
          <p>Merge contacts only after confirming that two contact records represent the <strong>same person or organization</strong>. A merge moves the duplicate contact&rsquo;s associated information to the retained contact and removes the duplicate contact record.</p>

          <div class="alert alert-warning"><strong>Important:</strong> Similar names do not establish that two contacts are duplicates. Relatives, such as a parent and child with the same name, must remain separate contacts so each person can have independent dates, roles, relationships, and documents.</div>

          <h5>Same-Matter Protection</h5>
          <p>V7 will not merge two contacts when both are already involved in the same matter. This protection prevents a merge from producing two involvement rows that point to one contact and from making edits to one displayed row unexpectedly change the other.</p>
          <p>If both records truly are duplicates, first review their roles, communication selections, relationships, notes, and document associations. Remove only the redundant involvement from the matter, then perform the contact merge. If the two rows already change together when either is edited, they are legacy duplicate involvements of the same contact; contact an administrator or Support to separate or consolidate them safely.</p>
        `
      }
    ]
  },

  // ============================================================
  // 4. INVOLVEMENTS
  // ============================================================
  {
    id: 'involvements',
    title: 'Involvements',
    icon: 'fa-user-tag',
    audience: 'both',
    subsections: [
      {
        id: 'involvements-overview',
        title: 'Overview & Roles',
        audience: 'both',
        content: `
          <h4>What is an Involvement?</h4>
          <p>An <strong>involvement</strong> is the join record that links a <strong>Contact</strong> to a <strong>Matter</strong>. There is at most one involvement per contact per matter. Each involvement can have <strong>multiple roles</strong> (via Role records).</p>
          <p>When a contact already appears on a matter, add any additional role to that contact&rsquo;s existing involvement instead of adding the contact again. V7 rejects both a new duplicate involvement and an attempt to reassign an existing involvement to a contact already on the matter. The rule applies to people, companies, and vendor contacts.</p>
          <div class="alert alert-info"><strong>Legacy records:</strong> Older imported data can contain duplicate involvements. Unrelated fields remain editable while those records are reviewed, but V7 will not permit a new action that creates another duplicate. If editing either displayed contact changes both rows, stop editing identifying information and ask an administrator or Support to separate the shared contact record.</div>

          <h5>Available Role Types</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Role</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Client</strong></td><td>The client on the matter. Typically receives invoices.</td></tr>
              <tr><td><strong>Defendant</strong></td><td>Opposing party (defendant in civil cases).</td></tr>
              <tr><td><strong>Plaintiff</strong></td><td>The party bringing the action.</td></tr>
              <tr><td><strong>Billing Contact</strong></td><td>Designated recipient of invoices. Different from client &mdash; use when a third party (e.g., insurance company) pays.</td></tr>
              <tr><td><strong>Attorney</strong></td><td>Opposing counsel or co-counsel.</td></tr>
              <tr><td><strong>Claimant</strong></td><td>For workers&rsquo; comp: the injured worker filing the claim.</td></tr>
              <tr><td><strong>Decedent</strong></td><td>For estate matters: the deceased person.</td></tr>
              <tr><td><strong>Employer</strong></td><td>The employer in workers&rsquo; comp or employment matters.</td></tr>
              <tr><td><strong>Defendant Employer</strong></td><td>Defendant who is also the employer.</td></tr>
              <tr><td><strong>Donor</strong></td><td>For trust/estate matters: the person creating the trust.</td></tr>
              <tr><td><strong>Intervenor</strong></td><td>Third party who intervenes in the case.</td></tr>
              <tr><td><strong>Vendor</strong></td><td>Service provider (process server, expert witness company, etc.).</td></tr>
              <tr><td><strong>Other</strong></td><td>Any other involvement not covered above.</td></tr>
            </tbody>
          </table>
          <p>A single involvement can hold multiple roles (e.g., a contact can be both Client and Plaintiff on the same matter).</p>

          <h5>Special Flags on Involvements</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Flag</th><th>Effect</th></tr></thead>
            <tbody>
              <tr><td><strong>is_plaintiff</strong></td><td>Used in auto-generated case names (&ldquo;Plaintiff vs Defendant&rdquo;).</td></tr>
              <tr><td><strong>is_defendant</strong></td><td>Used in auto-generated case names.</td></tr>
              <tr><td><strong>is_intervenor</strong></td><td>Marks as intervenor for case name generation.</td></tr>
              <tr><td><strong>include_spouse</strong></td><td>Include spouse name in correspondence and documents.</td></tr>
              <tr><td><strong>include_employer</strong></td><td>Include employer information in correspondence.</td></tr>
              <tr><td><strong>group_by_role</strong></td><td>Group this contact with others of the same role in document generation.</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'involvement-communications',
        title: 'Communication Preferences',
        audience: 'both',
        content: `
          <h4>Per-Matter Communication Settings</h4>
          <p>Each involvement has flags that control which communication channels to use for this contact <em>on this specific matter</em>:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Flag</th><th>Effect</th></tr></thead>
            <tbody>
              <tr><td><strong>use_email</strong></td><td>Send email communications to this contact for this matter.</td></tr>
              <tr><td><strong>use_mail</strong></td><td>Send postal mail communications.</td></tr>
              <tr><td><strong>use_fax</strong></td><td>Send fax communications.</td></tr>
              <tr><td><strong>use_phone</strong></td><td>Contact is reachable by phone.</td></tr>
              <tr><td><strong>allow_phone_text</strong></td><td>Contact has consented to receive SMS text messages.</td></tr>
            </tbody>
          </table>
          <p>These flags are per-involvement, so a contact might prefer email for one matter but mail for another.</p>

          <h4>Involvement-Specific Addresses &amp; Emails</h4>
          <p>A contact might have multiple addresses but use a specific one for a particular matter. The involvement allows you to specify:</p>
          <ul>
            <li><strong>Communication address</strong> &mdash; Which of the contact&rsquo;s addresses to use for letters on this matter.</li>
            <li><strong>Billing address</strong> &mdash; Which address to use for invoices on this matter.</li>
            <li><strong>Deposition address</strong> &mdash; For deposition notices.</li>
            <li><strong>Involvement email</strong> &mdash; Which of the contact&rsquo;s emails to use for this matter.</li>
          </ul>
          <p>On the matter page, select the map-pin icon beside an involvement address to open that location in Google Maps. The link displays the place on the map; it does not start a directions request or assume your current location as the starting point. The same behavior applies to additional addresses and addresses shown for related contacts.</p>
        `
      },
      {
        id: 'billing-contact',
        title: 'Billing Contact',
        audience: 'both',
        content: `
          <h4>How Billing Contacts Work</h4>
          <p>The <strong>billing contact</strong> is the person or entity that receives invoices for a matter. This is often the client, but can be a different party (e.g., an insurance company, a relative, or a corporate entity).</p>

          <h5>Billing Contact Resolution</h5>
          <ol>
            <li>The system looks for an involvement with the <strong>Billing Contact</strong> role.</li>
            <li>If none exists, it falls back to the <strong>Client</strong> role.</li>
            <li>The billing address is the involvement&rsquo;s billing address override, or the contact&rsquo;s billing address, or the contact&rsquo;s communications address.</li>
          </ol>

          <h5>Invoice Delivery</h5>
          <ul>
            <li>If the matter has <code>email_invoice = true</code> AND the billing contact has an email address, invoices are emailed.</li>
            <li>Otherwise, invoices are printed and mailed to the billing address.</li>
          </ul>

          <div class="alert alert-warning"><strong>Important:</strong> If no billing contact or client involvement exists, invoices cannot be generated for the matter. The system needs at least one contact to address the invoice to.</div>
        `
      }
    ]
  },

  // ============================================================
  // 5. DOCUMENTS
  // ============================================================
  {
    id: 'documents',
    title: 'Documents',
    icon: 'fa-file-alt',
    audience: 'both',
    subsections: [
      {
        id: 'document-inbox',
        title: 'Document Inbox',
        audience: 'both',
        content: `
          <h4>Reviewing Newly Arrived Documents</h4>
          <p>If you have active human user accounts with the same SSN in more than one intranet, the Document Inbox combines eligible matters from each of those intranets. You do not need to switch intranets to check each inbox. Every matter is labeled with its intranet, and opening a matter, document, scan, or history automatically activates the intranet that owns it.</p>
          <p>Open <strong>Documents &rarr; Document Inbox</strong> to review documents recently added to matters. Unified-intake files appear as soon as they arrive, before release. An item initially displays <strong>Waiting for scan queue</strong> without a scan link. After V7 creates the scan record, the same item displays <strong>Still in scan queue</strong> with an <strong>Open scan</strong> link. Keep the page open and the same line updates as intake status changes and the document is released. New Document Activity for an assigned matter also refreshes the inbox. The inbox does not replace or alter scan release, email-to-matter, client portal, file upload, matter history, or email-notification procedures.</p>
          <p>Documents are grouped by matter and ordered by the matter&rsquo;s most recent arrival. Reviewing the newest document does not move the matter lower while it still has other documents in the selected review-status view. Expand a matter to see each document&rsquo;s thumbnail, filename, description, document type, arrival source, processing status, received time, and available matter-history information.</p>
          <ul>
            <li>Each row shows a unique document, scan, and intake ID when available. A newly received row without a scan is labeled <strong>Waiting for scan queue</strong>; a staged but unreleased row is labeled <strong>Still in scan queue</strong>; and a filed row is labeled <strong>Released</strong>.</li>
            <li>Click the thumbnail, filename, or the document-specific <strong>Open document</strong> link to open a released document in a new tab. Items still in the scan queue also display their available thumbnails, including email messages; click the thumbnail or <strong>Open scan</strong> to open the scan. A generic file icon appears when no thumbnail is available. No scan link is displayed while intake processing is still creating the scan.</li>
            <li>Click <strong>Open history</strong> to open the related activity in the matter history.</li>
            <li>Click <strong>Mark reviewed</strong> after examining a document. Its button changes to <strong>Reviewed</strong>; click it again to return the item to unreviewed.</li>
            <li>Click a matter&rsquo;s <strong>Review All</strong> button to mark every reviewable, unreviewed document currently shown for that matter as reviewed. V7 asks for confirmation before changing the documents.</li>
            <li>Use <strong>Review &amp; Hide</strong> to mark one document reviewed and remove it from your visible inbox. This does not remove it from the matter.</li>
            <li>A matter with visible released documents offers <strong>Hide All</strong>. A matter with hidden documents shows the hidden count and offers <strong>Unhide All</strong>. Unhiding preserves review status and switches the inbox to the All view.</li>
            <li>Filter by unreviewed, reviewed, or all documents; choose an arrival period; or search by matter number, matter name, filename, document type, description, or source.</li>
          </ul>
          <div class="alert alert-info"><strong>Security and eligibility:</strong> The Document Inbox uses the existing V7 login, two-factor authentication, and linked-account permissions. For each intranet, it shows a matter only when that intranet&rsquo;s linked user is assigned to the matter and the assignment has <strong>Include in all communications</strong> selected. A matter does not appear merely because the user is assigned to it, has administrative access elsewhere in V7, or has an account in another intranet. Clear <strong>Include in all communications</strong> on the appropriate matter user to remove that matter from the user&rsquo;s inbox. The inbox does not create a second document repository.</div>
          <div class="alert alert-secondary"><strong>Live updates:</strong> Keep the inbox page open and it refreshes when intake arrives or changes status. It also refreshes after the live connection is established or re-established and every 60 seconds as a fallback, so a service restart does not leave the open page stale.</div>

          <h5>How the Inbox Identifies Arrivals</h5>
          <p>The inbox reads existing Document Activity history links and displays each linked document once, even when more than one history update refers to the same document. Unified-intake records provide source and processing details when available. Older workflows are identified from their existing scan, email, upload, and document records.</p>
          <p>The inbox stores review acknowledgment and optional hidden state on the linked user account belonging to the document&rsquo;s intranet. The consolidated screen presents those records together, but both states remain private to that linked identity and are never shared with other matter users.</p>

          <h5>Choosing Whether to Receive Document Emails</h5>
          <p>Each user can open their user settings, select the <strong>Profile</strong> tab, and use <strong>Receive document notifications by email</strong>. It is selected by default. Clear it to stop document-arrival emails for that user while continuing to receive new items and live updates in the Document Inbox. The choice applies to Document Activity, email-to-matter and unified intake, automatic scan/document notices, and client-portal uploads. It does not disable non-document matter-history email or operational failure notices.</p>
        `
      },
      {
        id: 'documents-overview',
        title: 'Overview & Fields',
        audience: 'both',
        content: `
          <h4>Document Management</h4>
          <p>Documents are files associated with matters &mdash; letters, pleadings, medical records, scanned documents, emails, and more.</p>
          <p>The main <strong>Documents</strong> list opens with the most recently created documents first. This uses the date and time the document was added to V7, not the document&rsquo;s authored date. If a user selects a different sort, V7 respects that selection.</p>

          <h5>Filtering a Matter&rsquo;s Documents</h5>
          <p>On a matter&rsquo;s <strong>Documents</strong> tab, select one or more document types, an author, tags, or enter content. Click <strong>Search</strong> to show documents that match those filters. Click <strong>Exclude</strong> to show documents that do <em>not</em> match the selected filters. The exclusion applies only to the selected document filters; the results always remain limited to the current matter and intranet. Pagination and column sorting preserve the active Search or Exclude mode. Click <strong>Clear Filters</strong> to remove the selections and return to the normal document list.</p>

          <h5>AI Document Tools</h5>
          <p>AI document research and drafting screens keep document and template selection together so the selected source is clear before generating or reviewing content. Use the matter context and the visible selection controls to confirm the intended document before submitting a request.</p>

          <h5>Document Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td><strong>name</strong></td><td>Document name/title</td><td>Unique per matter. Auto-prefixed by document type (e.g., &ldquo;LTR - Letter to John Smith&rdquo;).</td></tr>
              <tr><td><strong>document_type</strong></td><td>Category</td><td>Selected from configured document types. Each type has a prefix used in auto-naming.</td></tr>
              <tr><td><strong>source</strong></td><td>How the document entered the system</td><td><strong>email</strong> (from email processing), <strong>manual</strong> (user-created), <strong>scan</strong> (from scan queue), <strong>upload</strong> (file upload), <strong>system</strong> (auto-generated)</td></tr>
              <tr><td><strong>version</strong></td><td>Version number</td><td>Incremented when document is updated. Prior versions are stored as DocumentVersion records.</td></tr>
              <tr><td><strong>authored_at</strong></td><td>Date the document was authored</td><td>May differ from upload date (e.g., a letter dated last week, uploaded today).</td></tr>
              <tr><td><strong>is_editable</strong></td><td>Whether the document can be re-edited</td><td>True for HTML-based documents generated from templates and released Word/RTF files. False for uploaded PDFs.</td></tr>
              <tr><td><strong>page_count</strong></td><td>Number of pages</td><td>Auto-detected for PDFs.</td></tr>
            </tbody>
          </table>

          <h5>Document Sources Explained</h5>
          <ul>
            <li><strong>Email</strong>: Automatically created when emails are processed. The email body and attachments become documents.</li>
            <li><strong>Scan</strong>: Created when a scan is released from the scan queue (see <em>Scans</em>).</li>
            <li><strong>Upload</strong>: Manually uploaded by a user.</li>
            <li><strong>Manual</strong>: Created from a document template (generated letter, engagement letter, etc.).</li>
            <li><strong>System</strong>: Auto-generated (invoices, statements, etc.).</li>
          </ul>

          <h5>File Storage</h5>
          <p>Document files are stored on disk at: <code>[intranet.storage_path]/[matter.number]/[filename]</code>. The storage path is configured per intranet.</p>
          <p>When a document is uploaded or replaced, V7 verifies that the file was written to disk, is a regular file, and is not empty before creating document activity history, email notifications, OCR jobs, or search-index work. If storage verification fails, the upload is rejected and Rollbar receives the matter/document/path details needed for investigation.</p>
          <p>When a document is deleted, V7 logs the requested file deletion and records before/after file metadata for the main file and thumbnail. This audit trail helps distinguish a normal user-initiated document deletion from a missing backing file discovered later.</p>

          <h5>Converting PDFs to Word</h5>
          <p>On a matter&rsquo;s document list, PDF rows show a small <strong>W</strong> action icon next to the view and edit icons. Click <strong>W</strong> to create a Word <code>.docx</code> copy of that PDF. The converted Word file is saved as a new document in the same matter, preserving the original document&rsquo;s type, authors, tags, and authored date where available.</p>
          <div class="alert alert-info"><strong>Note:</strong> The conversion action appears only for PDF documents with an available file. The original PDF is not changed.</div>

          <h5>Editing Word and RTF Documents</h5>
          <p>Word (<code>.doc</code>/<code>.docx</code>) and RTF files released from the scan queue remain stored in their original file format. V7 also extracts their content into an editable document part so staff can revise the text in TinyMCE without replacing the original office file.</p>
          <ul>
            <li>Use the pencil action on the matter document list to open an editable office document.</li>
            <li>To double-space an editable document or template part, click <strong>Double</strong> beside the font-size selector in the TinyMCE toolbar. The spacing is saved with the document content.</li>
            <li>Use <strong>Save</strong> or <strong>Save &amp; Close</strong> to create a new document version. Edited Word and RTF files are written back in their original format; saving a <code>.doc</code> does not create a PDF.</li>
            <li>On the document show page, the formatted HTML preview appears below the document accordions; the right-hand accordions contain authors, details, rename, tags, and related actions.</li>
            <li>Use <strong>Download</strong> when you need the original Word or RTF file.</li>
          </ul>

          <h5>Native Browser Spell Checking</h5>
          <p>V7 asks the web browser to check spelling in every rich-text document editor, including editable matter documents, AI-created or AI-revised documents, and document template parts. A suspected misspelling is normally shown with the browser&rsquo;s standard underline. Right-click the word to use the browser&rsquo;s suggested spelling, ignore the word, or add it to the browser&rsquo;s dictionary.</p>
          <ul>
            <li>The spelling language, dictionary, suggestions, and underline style come from the browser and the computer&rsquo;s language settings; V7 does not send document text to a separate spelling service for this feature.</li>
            <li>If underlines do not appear in Safari, confirm <strong>Edit &rarr; Spelling and Grammar &rarr; Check Spelling While Typing</strong> is selected. Other browsers have an equivalent spell-check setting.</li>
            <li>Spell checking helps identify possible errors but does not change text automatically. Always proofread names, legal terms, citations, addresses, and defined terms before finalizing a document.</li>
          </ul>
        `
      },
      {
        id: 'document-versioning',
        title: 'Versioning',
        audience: 'both',
        content: `
          <h4>Document Version History</h4>
          <p>V7 tracks document versions automatically. When a document is updated:</p>
          <ol>
            <li>The current version is saved as a <strong>DocumentVersion</strong> snapshot (preserving the content, metadata, and file).</li>
            <li>The version number is incremented.</li>
            <li>The new content replaces the current document.</li>
          </ol>

          <h5>Reverting to a Previous Version</h5>
          <p>Administrators can revert a document to a previous version. The revert restores the document content and metadata from the snapshot, and the current version becomes a new version entry.</p>

          <h5>Recipients Snapshot</h5>
          <p>When a document is generated (e.g., a letter), the system stores a snapshot of the recipients&rsquo; addresses at the time of generation:</p>
          <ul>
            <li><strong>DocumentToContact</strong> &mdash; Primary recipient(s)</li>
            <li><strong>DocumentCcContact</strong> &mdash; CC recipients</li>
            <li><strong>DocumentBccContact</strong> &mdash; BCC recipients</li>
          </ul>
          <p>This means even if a contact&rsquo;s address changes later, the document retains the address it was actually sent to.</p>
        `
      },
      {
        id: 'document-sidebar',
        title: 'Document Sidebar',
        audience: 'both',
        content: `
          <h4>Using the Document Sidebar</h4>
          <p>When viewing a matter, the <strong>document sidebar</strong> on the right provides access to all matter documents:</p>
          <ul>
            <li><strong>Filter by type</strong> using the dropdown at the top.</li>
            <li><strong>Search</strong> within the sidebar to find specific documents by name.</li>
            <li><strong>Click a document</strong> to preview it in the browser (PDFs open in a new tab).</li>
            <li><strong>Generate new documents</strong> from templates using the sidebar controls.</li>
          </ul>

          <h5>Document Generation from Templates</h5>
          <p>To generate a document from a template:</p>
          <ol>
            <li>Select a <strong>Document Template</strong> from the dropdown.</li>
            <li>Select the <strong>To</strong> contact (recipient) from the involvement dropdown. This is required for letters &mdash; it populates the address block, salutation, and signature line.</li>
            <li>Choose the salutation style when the template asks for it. <strong>Familiar</strong> uses the contact&rsquo;s Nick Name AKA when available, and otherwise uses the contact&rsquo;s first name.</li>
            <li>Optionally add CC contacts.</li>
            <li>Click <strong>Preview</strong> to see a draft, or <strong>Generate</strong> to create the final document.</li>
          </ol>

          <div class="alert alert-warning"><strong>Important:</strong> The &ldquo;To&rdquo; contact is never auto-populated. You must select it from the dropdown. Without it, the address block, salutation (&ldquo;Dear X,&rdquo;), RE: line, and signature will all be blank.</div>

          <h5>Reindexing a Matter for Search</h5>
          <p>Authorized users can reindex a matter&rsquo;s documents, involvements, histories, or all three from the matter administration controls. Reindexing sends the selected matter information to Solr, which powers V7 search. A successful message confirms that the server completed the reindex.</p>
          <p>After a successful reindex, V7 refreshes any affected matter tabs that are currently open. Tabs that have not been opened are not loaded in the browser and are safely skipped; their information will load from the updated index when they are opened. A tab refresh problem is logged separately and does not change a successful Solr result into a failure message.</p>
        `
      },
      {
        id: 'ai-research',
        title: 'AI Research',
        audience: 'both',
        content: `
          <h4>AI Research Tab</h4>
          <p>AI Research is now a <strong>matter-level tab</strong>. Open a matter and click <strong>AI Research</strong> from the matter tabs. The older separate AI tools under the Documents tab have been consolidated into this single workflow.</p>

          <h5>Workflow Tabs</h5>
          <ul>
            <li><strong>Query</strong> &mdash; The first tab shown. Enter a legal research question, ask for a summary, or request that a document be drafted. The request stays in place while you switch tabs.</li>
            <li><strong>Documents</strong> &mdash; Search, filter, and select matter documents to include as context. The selected-documents panel shows what will be considered in the response.</li>
            <li><strong>Temporary Files</strong> &mdash; Drop or select a PDF, Word, RTF, OpenDocument, text, XML, or HTML file to use only for the current research. Add a description so the result can identify why the file matters. Temporary files are <strong>not added to the matter&rsquo;s document list</strong>.</li>
            <li><strong>Results</strong> &mdash; Review the answer or generated draft. After changing selected documents, return here to reconsider the response with the updated context.</li>
          </ul>

          <h5>Using Temporary Research Files</h5>
          <ul>
            <li>Open <strong>Temporary Files</strong>, drop or choose one or more supported files, and enter a description for each file.</li>
            <li>Each file may be up to 25 MB. V7 extracts readable text for the research request but does not create a matter Document record or show the file in the matter&rsquo;s Documents tab.</li>
            <li>The results begin with a <strong>Temporary documents considered</strong> section listing each filename and description and expressly stating that it is not in the matter&rsquo;s document list.</li>
            <li>Follow-up questions continue to use the extracted temporary-file text. The uploaded binary is deleted after extraction, including when extraction fails.</li>
          </ul>

          <h5>Selecting Documents</h5>
          <ul>
            <li>The document picker follows the matter&rsquo;s <strong>Documents</strong> tab, including its grouped document-type tree, standard author names and ordering, filters, pagination, and table columns.</li>
            <li>Use <strong>Filter document types</strong> in the left column to search by a type&rsquo;s name, hierarchy, or prefix. Click <strong>Apply</strong> or press Enter; V7 hides unrelated categories and automatically opens each matching group. Select one or more matching document types to filter the document list. Click <strong>Clear Text</strong> to show all type groups again. The <strong>Author</strong>, <strong>Tags</strong>, and <strong>Content</strong> filters are grouped together in the right column.</li>
            <li>The list shows 30 documents per page. Use <strong>Previous</strong> and <strong>Next</strong> to move between pages; checked documents remain selected when changing pages or filters.</li>
            <li>Click <strong>Select Visible</strong> to select the available documents on the current page.</li>
            <li>Click <strong>Refresh Documents</strong> after uploading or generating new matter documents. V7 reloads the current document list while preserving the query or follow-up text, filters, selected documents, and the active Documents tab.</li>
            <li>Use <strong>Clear Filters</strong> to return to the full document list.</li>
            <li>Click an available document&rsquo;s name or folder icon to preview it in a new browser tab. The AI Research workspace and current selections remain open.</li>
            <li>Documents whose files are unavailable or missing from storage are gray and cannot be selected. Internally generated documents remain available when V7 can render them from their saved document parts.</li>
            <li>There is no set limit on selected documents, but selecting only relevant documents will produce a more focused answer.</li>
          </ul>

          <h5>Returning to Saved Results</h5>
          <p>Completed research is saved with the research request. Refreshing the current result page reloads the same answer and discussion; it does not start a new request. Use <strong>New Request</strong> only when you intend to begin separate research.</p>

          <h5>Research vs. Document Drafting</h5>
          <p>V7 decides whether to create a document from the request itself. Ask a research question when you only want an answer. Explicitly ask to draft, prepare, create, generate, write, compose, produce, make, design, or build a document when you want a new document. For example, <em>Design a new template that will autofill the appropriate information for will and power of attorney clients</em> is treated as document creation rather than research alone.</p>
          <p>If a document is requested, choose the document type from the autocomplete/dropdown when prompted. A request containing <em>template</em> or <em>reusable form</em> automatically selects reusable-template mode; other document requests default to a one-off document for the current matter. You can change that selection before submitting. When reusable-template mode is selected, V7 asks <strong>What should this template be named?</strong> and requires a unique name before generation. This is the name users will see in the template list.</p>
          <p>Any requested document creation opens in the document editor, where it can be reviewed and edited before final use.</p>

          <h5>AI-Created Reusable Templates and Tags</h5>
          <p>When V7 generates a reusable template, it gives the AI the current V7 tag reference and instructs it to use V7&rsquo;s Radius-style tags instead of hard-coded matter information. The result is saved as a normal V7 document template and template part, so the same tag engine used by manually created templates fills the values when the template is used.</p>
          <p>After generation, the confirmation page remains open and prominently displays the saved template name and template ID. Use <strong>Edit Template</strong> to open it directly, or <strong>Return to AI Research</strong> to begin another request. V7 does not silently create an automatic template name or dismiss this confirmation.</p>
          <p>Contact tags can use any matter involvement role. For example, a template can retrieve an <strong>Agent</strong>, <strong>Substitute Agent</strong>, <strong>Executor</strong>, or <strong>Substitute Executor</strong>. Conditional tags such as <code>&lt;r:if_contact type=&quot;Substitute Agent&quot;&gt;...&lt;/r:if_contact&gt;</code> include a sentence only when the matter has a contact in that role. Review every generated template before use to confirm that the selected roles, wording, and layout match the intended practice.</p>

          <h5>Preparing a Document After Research</h5>
          <ol>
            <li>After reviewing the research answer, open the <strong>Query</strong> tab.</li>
            <li>In <strong>Follow-up or clarification</strong>, explicitly request the document, for example: <em>Prepare the petition to the Court for me.</em></li>
            <li>Click <strong>Send Follow-up</strong>.</li>
            <li>Select the appropriate document type when prompted and choose whether to create a one-off matter document or a reusable template. If you choose a reusable template, enter the unique name V7 asks for.</li>
            <li>Submit the request to generate and open the draft in the document editor.</li>
          </ol>
          <p>V7 carries the original research question, the prior research answer, and the currently selected matter documents into the drafting request. The separate <strong>Reconsider With Selected Documents</strong> button updates the research answer; it does not request document preparation.</p>

          <h5>Revising an Existing Document with AI</h5>
          <ol>
            <li>Open the matter&rsquo;s <strong>AI Research</strong> section and select the <strong>Revise a Document</strong> tab. It appears in the same tab row as <strong>Query</strong>, <strong>Documents</strong>, and <strong>Results</strong>.</li>
            <li>Search the matter document list, select one available document, and use <strong>Preview</strong> if you need to confirm its contents.</li>
            <li>Enter specific revision directions. V7 treats the selected file as document content and instructs the AI to preserve all provisions not covered by your directions.</li>
            <li>Click <strong>Create Revised Copy</strong>. The work runs in the background; the original document is never changed.</li>
            <li>When complete, V7 creates an editable copy and opens it under <strong>Documents &rarr; Editing Document</strong>.</li>
            <li>To make another change, return to <strong>AI Research</strong>, enter <strong>Additional revision directions</strong>, and click <strong>Revise Again</strong>. When that pass finishes, V7 returns you to <strong>Documents &rarr; Editing Document</strong> with the updated copy. Each completed pass creates another document version, so prior revisions remain available through V7&rsquo;s document-version history.</li>
          </ol>
          <p>Uploaded PDFs can be revised when V7 can read their stored text or extract a text layer. Documents with neither a readable file nor editable document parts are unavailable. Always review the completed revision in the normal document editor before use.</p>

          <h5>Revision Size and Time Limits</h5>
          <p>AI revision works by sending the readable text of the selected document to the AI and asking it to return a <strong>complete revised copy</strong>. V7 uses the following safeguards so an unusually large request does not run indefinitely or save an incomplete legal document:</p>
          <ul>
            <li><strong>Source document: 180,000 characters.</strong> Characters include letters, numbers, spaces, and punctuation. Page counts vary too much to give an exact page equivalent. If the readable document exceeds this limit, V7 stops before sending it and tells you to shorten or divide the document.</li>
            <li><strong>AI response: 65,536 tokens.</strong> A token is a small piece of a word, not a page or a fixed number of words. This allowance includes both the AI&rsquo;s internal reasoning and the complete revised document, so the amount of visible text available can vary from one request to another.</li>
            <li><strong>Processing time: 10 minutes.</strong> A revision may continue in the background for up to ten minutes while the AI prepares the complete copy. This is not a promise that every revision will take ten minutes; most should finish sooner.</li>
          </ul>
          <p>If the response or time limit is reached, V7 does <strong>not</strong> save the partial result. For a very long document, divide it into logical sections, revise each section, and combine the approved sections in the document editor. For a document that is within the size limit, try narrower directions in successive passes. Larger revisions can take longer and consume more AI usage than ordinary research questions.</p>

          <div class="alert alert-info"><strong>Note:</strong> AI and API usage billing is currently disabled. Usage may still be logged for administration and troubleshooting.</div>
        `
      },
      {
        id: 'estate-card-printing',
        title: 'Emergency and Gun Trust Cards',
        audience: 'both',
        content: `
          <h4>Emergency Estate-Planning Cards</h4>
          <p>For an eligible Prince Law Offices matter, the <strong>Documents &rarr; Card Printing</strong> tab prepares a two-sided, wallet-sized CR80 card. The front identifies the client and any agent, executor, or trustee shown in the matter involvements; the back has firm contact and cautionary information. This is a separate Zebra printing workflow, not the ordinary document editor or PDF print command.</p>
          <p>The tab appears only when the matter has a client, at least one will, power of attorney, or trust document, and a matching agent, executor, or trustee involvement. The presence of a document in V7 does <strong>not</strong> prove that it is signed, current, or prepared by the firm. Staff must check those facts before printing.</p>

          <h5>Prepare and Print</h5>
          <ol>
            <li>Open the matter&rsquo;s <strong>Documents &rarr; Card Printing</strong> tab. Choose the client whose name should appear.</li>
            <li>Select the current power of attorney, will, or trust as appropriate; choose <strong>Do not include</strong> for an instrument that should not appear. Check that the people and roles in the matter involvements are correct.</li>
            <li>Confirm that Prince Law Offices prepared the selected current documents and that they are not drafts or revocations. A document classified <strong>Power of Attorney/Revocation</strong> requires a separate confirmation that this particular document is a POA.</li>
            <li>Click <strong>Preview Card</strong>. Inspect <em>both</em> sides of the modal, including names, phone numbers, text fitting, and the QR code when a POA is included. The Zebra prints the back in black and white.</li>
            <li>Click <strong>Cancel</strong> to discard an unprinted preview, or <strong>Print one two-sided card</strong> to send exactly one duplex job to the Zebra ZC300. One preview cannot be printed twice.</li>
          </ol>
          <p>V7 records the print attempt before sending it. If printing times out or the result is uncertain, <strong>check the physical card and Zebra job status before trying again</strong>; an automatic retry could issue a duplicate card. The POA QR link becomes active only after the printer confirms completion.</p>

          <h5>What the POA QR Code Does</h5>
          <p>The code leads to a request page, <strong>not a POA download</strong>. The page confirms only that the firm prepared a POA for the named client. It asks the requester for a name, email or phone number, and reason for requesting a copy. V7 stores the request and queues notices to Warren by text, Matrix, and email. Staff must independently verify identity, authority, and the current signed instrument before releasing a copy. The card page shows recent requests and whether each notice channel was sent. <strong>Revoke QR</strong> disables an issued card&rsquo;s request link; it does not erase requests already received.</p>

          <h5>Gun Trust Wallet Cards</h5>
          <p>Matters with a client, a <strong>Firearms Trust</strong> document, and current <strong>Trustee</strong> involvements use the same <strong>Documents &rarr; Card Printing</strong> tab, including CRDF matters. The color front contains Prince Law Offices, P.C., a large centered FICG logo and firm contact information. The black-and-white back lists the current trustees beneath the trust name and includes a QR code. Beneficiaries and substitute trustees are excluded.</p>
          <p>Up to six trustees use landscape artwork; larger rosters use portrait. Names wrap as needed, and individual phone numbers appear when available and space permits. Names take priority over phone numbers. If the full roster cannot fit legibly, V7 stops the preview instead of leaving anyone off.</p>
          <p>Review the latest firearms trust and the trustee involvements before previewing. Confirm that anyone holding the card QR may open the document without signing in. Unlike an emergency POA card, this QR opens the latest <strong>Firearms Trust</strong> PDF directly. It resolves the document on every scan, ordered by document date (or creation date when undated), then creation date and ID. Uploading a newer firearms trust changes what existing cards open; review the document type and date carefully. Do not classify an unrelated document as a Firearms Trust.</p>
          <p>The preview offers <strong>Print one two-sided card</strong> or <strong>Print N cards &mdash; one per trustee</strong>. N is the number of distinct current trustees. V7 rechecks that count before printing; if it changed, preview again. The batch contains identical two-sided cards sharing one QR link, so revoking that link disables every copy. Either print button consumes the preview; it cannot be submitted a second time.</p>
          <p>The link stays inactive until the Zebra confirms printing, and <strong>Revoke QR</strong> disables it. Revocation cannot recall copies already downloaded. If the latest document is unavailable, V7 displays an unavailable message instead of serving an older trust. Printing still uses a single duplex job, with the FICG logo in color and the trustee roster in black and white.</p>

          <h5>Administrator: Install the Zebra SDK on Storage</h5>
          <p>The Zebra card-printing SDK and Java runtime belong on <strong>storage</strong>, the print server. Do not install them on <strong>primal2</strong>, the V7 web server. The ZC300 card printer is at <code>10.1.1.138</code>. The existing CUPS queue called <strong>Zebra</strong> points to a different label printer at <code>10.1.1.98</code> and must not be used for these cards.</p>
          <p>Use the verified Zebra Card SDK for Java 3.01.211 release files and the tested Java 21 runtime. The temporary test copies on storage are under <code>/tmp/zc300-sdk.u6pDrn</code>; temporary files may disappear after reboot, so keep the installed copy under <code>/opt/zc300</code>. If the temporary copy is gone, obtain the same release from Zebra&rsquo;s official SDK distribution and verify its contents before installing it. Run these commands <strong>on storage</strong> as an administrator:</p>
          <pre>install -d -m 0755 /opt/zc300
cp -a /tmp/zc300-sdk.u6pDrn/jre/usr/lib/jvm/java-21-openjdk-amd64 /opt/zc300/jre
cp -a /tmp/zc300-sdk.u6pDrn/LinkOS_SDK_PC-Card_Java_v3.01.211/demos/release /opt/zc300/sdk
test -x /opt/zc300/jre/bin/java
test -r /opt/zc300/sdk/ZSDK_CARD_API.jar
/opt/zc300/jre/bin/java -version</pre>
          <p>Keep <strong>all sibling JARs</strong> in the SDK release directory; copying just <code>ZSDK_CARD_API.jar</code> is insufficient. The app defaults to <code>/opt/zc300/jre/bin/java</code>, <code>/opt/zc300/sdk/ZSDK_CARD_API.jar</code>, SSH host <code>storage</code>, and printer URI <code>TCP:10.1.1.138</code>. Override those only when necessary with <code>ESTATE_CARD_JAVA_BIN</code>, <code>ESTATE_CARD_SDK_JAR</code>, <code>ESTATE_CARD_PRINTER_HOST</code>, and <code>ESTATE_CARD_PRINTER_URI</code> in the V7 service environment.</p>
          <p>The production V7 process runs as <code>www-data</code> on primal2, so root&rsquo;s SSH access is <strong>not</strong> sufficient. Configure a dedicated, unprivileged <code>v7cardprint</code> user on storage, with a key belonging to <code>www-data</code> on primal2. Do not copy root&rsquo;s private key into the web account. The configured storage SSH port is <code>47911</code>. On a replacement setup, create the account and key with:</p>
          <pre># On storage, as administrator
useradd -m -s /bin/bash v7cardprint
install -d -m 0700 -o v7cardprint -g v7cardprint /home/v7cardprint/.ssh

# On primal2, as administrator
install -d -m 0700 -o www-data -g www-data /var/www/.ssh
sudo -u www-data -H ssh-keygen -t ed25519 -N '' -f /var/www/.ssh/v7_card_print</pre>
          <p>Install <code>/var/www/.ssh/v7_card_print.pub</code> as storage&rsquo;s <code>/home/v7cardprint/.ssh/authorized_keys</code>, owned by <code>v7cardprint</code> with mode <code>0600</code>. Prefix that public-key line with <code>from=&quot;10.1.1.8&quot;,restrict</code> to allow only primal2 and disable forwarding or interactive terminals. Keep the <strong>private</strong> key only on primal2, owned by <code>www-data</code> with mode <code>0600</code>.</p>
          <p>Create <code>/var/www/.ssh/config</code> for <code>www-data</code> on primal2 with the settings below. Pin storage&rsquo;s verified SSH host key in <code>/var/www/.ssh/known_hosts</code>; do not disable host-key checking. Both files must be readable by <code>www-data</code>.</p>
          <pre>Host storage
  HostName 10.1.1.11
  Port 47911
  User v7cardprint
  IdentityFile /var/www/.ssh/v7_card_print
  IdentitiesOnly yes
  BatchMode yes
  StrictHostKeyChecking yes
  UserKnownHostsFile /var/www/.ssh/known_hosts</pre>
          <p>From <strong>primal2</strong>, verify that the V7 service can use noninteractive, key-based SSH to storage and read the installed files:</p>
          <pre>sudo -u www-data -H ssh -o BatchMode=yes storage 'test -x /opt/zc300/jre/bin/java &amp;&amp; test -r /opt/zc300/sdk/ZSDK_CARD_API.jar'
sudo -u www-data -H ssh -o BatchMode=yes storage 'nc -z -w 3 10.1.1.138 9100'</pre>
          <p>Both commands should exit successfully; the second checks that storage can reach the printer. A production deployment must also install the V7 gems, run the card tables migration, and build the Angular manual. After deployment, test with one approved sample card and inspect both physical sides before staff rely on the workflow. Never test with a real client&rsquo;s confidential data unless its use has been approved.</p>
        `
      },
      {
        id: 'document-types',
        title: 'Document Types',
        audience: 'both',
        content: `
          <h4>Document Type System</h4>
          <p>Every document must have a <strong>document type</strong>. Types are configured per intranet under <strong>Administration &rarr; Documents &rarr; Document Types</strong>.</p>

          <h5>How Types Are Used</h5>
          <ul>
            <li><strong>Auto-naming</strong>: Each type has a prefix (e.g., &ldquo;LTR&rdquo; for letters, &ldquo;INV&rdquo; for invoices). Document names are auto-generated as &ldquo;PREFIX - Description&rdquo;.</li>
            <li><strong>Scan queue</strong>: Scanners assign a document type when filing scans.</li>
            <li><strong>Auto rules</strong>: Auto email/reminder/outgoing rules trigger based on document type (e.g., send notification when a &ldquo;Medical Record&rdquo; is filed).</li>
            <li><strong>Filtering</strong>: Documents can be filtered by type in the document index and sidebar.</li>
            <li><strong>Template association</strong>: Document templates are linked to a document type so generated documents are properly categorized.</li>
          </ul>

          <h5>Common Document Types</h5>
          <p>Correspondence, Pleading, Motion, Brief, Medical Record, Police Report, Invoice, Statement, Signed Fee Agreement, Discovery, Deposition Transcript, Court Order, and many more. The exact types available depend on your intranet&rsquo;s configuration.</p>
        `
      }
    ]
  },

  // ============================================================
  // 6. CLIENT PORTAL
  // ============================================================
  {
    id: 'client-portal',
    title: 'Client Portal',
    icon: 'fa-user-shield',
    audience: 'both',
    subsections: [
      {
        id: 'portal-overview',
        title: 'Portal Overview',
        audience: 'both',
        content: `
          <h4>What the Client Portal Is</h4>
          <p>The client portal is a public-facing page where contacts on a matter can sign in to <strong>view documents shared with them, upload documents to the firm, and (if they are the client) see their billing balance and make payments</strong>. It lives at <code>https://v7.attymatter.com/share_sessions/new</code> (firm-specific URL) and is reached from any fileshare email.</p>

          <h5>Who Can See What</h5>
          <p>The portal shows different content depending on who is viewing it:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Audience</th><th>What They See</th></tr></thead>
            <tbody>
              <tr>
                <td><strong>Internal V7 staff</strong> (logged into V7 in the same browser)</td>
                <td>The entire portal for the matter &mdash; every document that has been shared, every portal upload, and the Billing &amp; Payments tab. Used for support and preview.</td>
              </tr>
              <tr>
                <td><strong>Clients on the matter</strong> (contact in the <code>client</code> role)</td>
                <td>All documents shared with them, all documents any party has uploaded to the portal for this matter, and the Billing &amp; Payments tab (balance, unbilled, charges, payments, and online payment links when available).</td>
              </tr>
              <tr>
                <td><strong>Non-client contacts</strong> (opposing counsel, experts, other involved parties)</td>
                <td>Only the documents actually shared with them and only the documents they themselves uploaded. No Billing &amp; Payments tab.</td>
              </tr>
            </tbody>
          </table>

          <h5>Tabs on the Portal</h5>
          <ul>
            <li><strong>Documents &amp; Media</strong> &mdash; Lists shared documents (grouped by fileshare date) and any documents the viewer has uploaded. Clicking a document name downloads it; multi-select lets the viewer bulk-download a ZIP.</li>
            <li><strong>Upload a Document</strong> &mdash; File upload form. Uploads are stamped with the uploader as the apparent author, sourced as <code>client_portal</code>, and logged to the matter history. When unified document intake is enabled, uploads wait in the scan/release queue so staff can confirm the final matter, document type, author, date, and notes before filing.</li>
            <li><strong>Billing &amp; Payments</strong> (clients and staff only) &mdash; Shows balance due, trust/escrow on file, online payment options for the matter&rsquo;s selected provider, and collapsible sections for unbilled time, charges, payments received, and <strong>All</strong> activity. If the matter has escrow transactions, it also shows a collapsed <strong>Escrow Activity</strong> section with the date, description, category, and amount of each transaction; matters without escrow activity do not show that section. Closed sections show a right-pointing arrow; the arrow turns downward only after the section is opened. The All section combines charges and payments, starts with the newest activity first, and includes a button to reverse the date order. When online payment is applicable, PayLoad matters show a <strong>Pay Now</strong> button that obtains a current PayLoad checkout session when clicked, while LawPay matters use the stored LawPay invoice payment link (or the firm&rsquo;s configured LawPay page when no invoice-specific link exists). Manual and Traditional matters do not display a fabricated online-payment link; the portal instead directs the client to contact the office.</li>
          </ul>

          <h5>Uploading from a Phone or Tablet</h5>
          <ol>
            <li>Open <strong>Upload a Document</strong> and tap anywhere in the upload area to open the standard iOS or Android file picker.</li>
            <li>Select the file, choose its document type, and tap <strong>Upload Document</strong>. The percentage beside “Uploading” shows transfer progress.</li>
            <li>Keep the portal open until the success message appears. Files may be up to <strong>100 MB</strong>.</li>
            <li>If the connection is interrupted, the portal keeps the selected file and displays <strong>Retry Upload</strong>. Reconnect and retry; V7 uses the upload token to avoid creating a second copy when the first upload actually succeeded.</li>
          </ol>
          <p>The portal uses the device&rsquo;s normal file chooser and does not force camera capture, preserving compatibility with both iOS and Android. All primary upload and document-type controls use mobile-sized touch targets.</p>

          <h5>Document Access Enforcement</h5>
          <p>The portal enforces access <em>server-side</em>, not just by hiding links. A non-client who tries to guess a document URL will get a 404 unless the document was explicitly shared with them (via a fileshare) or they uploaded it themselves.</p>
        `
      },
      {
        id: 'portal-view-as-client',
        title: 'View as Client (Internal Preview Link)',
        audience: 'both',
        content: `
          <h4>Previewing the Portal as Staff</h4>
          <p>Every matter page includes a <strong>Portal</strong> choice in the matter navigation bar between <strong>New</strong> and <strong>Queues</strong>. It opens a new browser tab signed in as the matter&rsquo;s primary client contact, so staff can continue working in V7 while previewing exactly what the client sees. The matter header&rsquo;s <strong>View as Client</strong> link provides the same preview function.</p>

          <p>The preview works even if no document or media has been shared yet. It remains tied to the requested matter and client if another browser tab switches the staff session between PLO, CRDF, or another linked intranet. Within a staff preview, <strong>All Matters</strong> lists every matter in the same intranet where the impersonated contact has the Client role. For a contact who signs into the portal normally, All Matters remains limited to matters that have actually been shared with that contact.</p>

          <div class="alert alert-info">
            <strong>Tip:</strong> This is the fastest way to sanity-check what a client will see after you share something, without asking the client to log in. The session is marked as an impersonation, logs to matter history, and survives across page navigations within the portal.
          </div>

          <h5>What Happens Behind the Scenes</h5>
          <ul>
            <li>The impersonation is recorded as a matter history entry: &ldquo;[attorney name] logged into client portal as [client name] to preview client view.&rdquo;</li>
            <li>The link is visible on every matter. If the matter has no client contact, you&rsquo;ll see a &ldquo;no client&rdquo; notice instead of the portal.</li>
            <li>Impersonation does <em>not</em> trigger a 2FA challenge &mdash; you&rsquo;re already authenticated to V7, so the portal trusts you.</li>
            <li>To stop impersonating, log out of the portal or close the tab.</li>
          </ul>
        `
      },
      {
        id: 'portal-welcome-letter',
        title: 'Portal Welcome and Payment Letter',
        audience: 'both',
        content: `
          <h4>Creating the Client&rsquo;s Portal Letter</h4>
          <p>From the matter&rsquo;s <strong>Documents</strong> tab, create a new document and select the template named <strong>Client Portal and Payment Instructions</strong>. This is a normal editable letter based on each firm&rsquo;s established miscellaneous-letter format. It includes the letterhead, date, inside address, RE line, salutation, closing, signature, and footer used by that firm.</p>

          <p>The template defaults the letter recipient to the matter&rsquo;s <strong>bill-paying contact</strong>. If the matter has no separate bill-paying contact, it uses the client. The inside address and salutation are filled only after that recipient is added to the document, so confirm the displayed recipient before saving or sending the letter.</p>

          <h5>Information Filled Automatically</h5>
          <ul>
            <li>The matter-specific portal sign-in link. Production letters use <code>https://v7.attymatter.com</code>; development letters use the development server at <code>http://10.1.1.112:3000</code>.</li>
            <li>The recipient&rsquo;s portal login email and temporary portal password.</li>
            <li>PayLoad instructions and a link to the portal&rsquo;s Billing &amp; Payments tab when the matter uses PayLoad. The client clicks <strong>Pay Now</strong> there to receive a current checkout session.</li>
            <li>LawPay instructions and the matter&rsquo;s LawPay link when the matter uses LawPay.</li>
            <li>Instructions to mail a check when the matter is configured for manual or traditional payment rather than an online provider.</li>
          </ul>

          <div class="alert alert-warning"><strong>Protect the letter:</strong> It contains portal credentials. Verify the recipient and address before sending it, and do not place it in an unrestricted shared location.</div>

          <h5>Preview and Page Layout</h5>
          <p>Click <strong>Preview</strong> to create a fresh PDF; V7 marks preview responses as non-cacheable so the browser does not display an older rendering. The PDF renderer measures the real header and footer and reserves their space on every page. The closing and signature are kept together. If the complete signature block cannot fit above the footer, V7 moves that entire block to the next page instead of allowing the closing or signature to overlap the footer.</p>
          <p>Portal hyperlinks embedded in a PDF must be complete web addresses. During PDF generation, V7 converts older or browser-cached relative portal links such as <code>/share_sessions/new</code> (and invalid <code>file:///share_sessions/new</code> links) to the correct full address for the current environment. This prevents a PDF viewer from treating the portal as a file on the user&rsquo;s computer. Existing editable letters receive the correction when their PDF is regenerated.</p>

          <h5>Administrator Installation Notes</h5>
          <p>The template and its body are database records, not merely files in the deployed release. After first deploying this feature to an environment, run <code>bundle exec rake templates:install_client_portal_letter</code> in that environment. The task is safe to rerun. It creates or updates the <strong>Client Portal and Payment Instructions</strong> template and its body part for each supported firm, preserves the firm&rsquo;s existing miscellaneous-letter furniture and stylesheets, and applies the <code>default-to-billing-contact</code> template tag.</p>
          <p>The underlying tag reference must include <code>matter_portal_link</code>, <code>matter_payment_link</code>, <code>portal_login_email</code>, <code>portal_password</code>, <code>payment_provider</code>, and the conditional <code>if_payment_provider</code> tag. These tags resolve within the current matter and intranet; they must never borrow a payment provider, invoice, contact, or portal route from another firm.</p>
        `
      },
      {
        id: 'portal-2fa',
        title: 'Two-Factor Authentication (2FA)',
        audience: 'both',
        content: `
          <h4>How Portal Login Works</h4>
          <p>Every client-side portal login now requires a <strong>two-step sign-in</strong>:</p>
          <ol>
            <li>The contact enters their email address and the share password you&rsquo;ve assigned to their contact record.</li>
            <li>If those are correct, we email them a <strong>6-digit one-time code</strong>. They enter it on the next screen to complete the sign-in.</li>
          </ol>
          <p>The code expires in <strong>10 minutes</strong> and becomes invalid after <strong>5 wrong attempts</strong>, at which point the contact must request a new one via the <strong>Resend email</strong> link on the verify page.</p>

          <h5>Email Is the Required Channel</h5>
          <p>Every portal-enabled contact receives their code by email. The From address is the firm&rsquo;s sending address; the subject is &ldquo;Your client portal verification code.&rdquo; If a contact has no email address on file they cannot sign in to the portal &mdash; share passwords alone are no longer sufficient.</p>

          <h5>SMS Is Optional</h5>
          <p>On the verify page, contacts see an additional <strong>&ldquo;Text me the code instead&rdquo;</strong> button when <em>both</em> of the following are true:</p>
          <ul>
            <li>The contact has a mobile phone number on their contact record.</li>
            <li>At least one of the contact&rsquo;s involvements has <strong>&ldquo;Use phone for text notifications&rdquo;</strong> checked (see <em>Enabling SMS for a contact</em> below).</li>
          </ul>
          <p>We deliberately do <em>not</em> offer SMS-only 2FA &mdash; not every client has a text-enabled number, and Telnyx <code>STOP</code> replies would lock the client out of their own portal. Email is always the fallback.</p>

          <h5>Staff Are Not Prompted</h5>
          <p>If you&rsquo;re signed in to V7 as staff and you navigate to the portal (or use <strong>View as Client</strong>), the portal recognizes your session and skips 2FA. 2FA only gates the contact-email-and-password login path.</p>

          <h5>What If a Client Can&rsquo;t Receive the Code?</h5>
          <ul>
            <li><strong>Email delay</strong> &mdash; The OTP is sent synchronously when they submit the password, so it should arrive within seconds. If they wait and it still hasn&rsquo;t arrived, have them click <strong>Resend email</strong> and check spam.</li>
            <li><strong>Wrong email on file</strong> &mdash; Update their contact&rsquo;s primary email in V7; the next OTP goes to the new address.</li>
            <li><strong>Expired code</strong> &mdash; Codes expire in 10 minutes. If they come back tomorrow, they&rsquo;ll need a fresh code &mdash; they can just sign in again.</li>
            <li><strong>Locked out after 5 wrong tries</strong> &mdash; The code is invalidated; <strong>Resend email</strong> issues a new one.</li>
          </ul>

          <h5>Email Formatting and Login Matching</h5>
          <p>V7 automatically removes invisible spaces, tabs, and line breaks from the beginning and end of contact email addresses and stores addresses in lowercase. Portal login is case-insensitive and also ignores accidental surrounding whitespace typed by the contact. File-share destinations use the same normalized address. This prevents a copied email address containing hidden whitespace from looking correct in V7 while repeatedly returning the contact to the login page.</p>
          <p>If a contact still returns to the login page without reaching the 6-digit-code screen, confirm that the email shown in the fileshare message belongs to the same contact selected as the fileshare recipient. Use <strong>Forgot password?</strong> when the address is correct but the share password is uncertain.</p>
        `
      },
      {
        id: 'portal-enabling-sms',
        title: 'Enabling SMS for a Contact',
        audience: 'both',
        content: `
          <h4>Where the SMS Opt-In Lives</h4>
          <p>SMS is controlled per-involvement, not per-contact. When you add or edit an involvement on a matter, the form includes a checkbox labeled <strong>&ldquo;Use phone for text notifications (eg: for reminders)&rdquo;</strong>. That single checkbox is the authoritative opt-in and it serves double duty:</p>
          <ul>
            <li>Text reminders about events and deadlines.</li>
            <li>Portal 2FA codes sent via SMS (when the contact requests them on the verify page).</li>
          </ul>

          <div class="alert alert-info">
            <strong>Where to find it:</strong> Matter page &rarr; Involvements &rarr; click an involvement to edit &rarr; the checkbox is in the main form area. You can also set it when adding a new involvement.
          </div>

          <h5>What the Checkbox Means Legally</h5>
          <p>Checking this box is the firm&rsquo;s record that the contact consented to receiving text messages from us for that matter. Because Telnyx normalizes opt-out behavior, a single <strong>STOP</strong> reply from any contact wipes the checkbox across <em>all</em> their involvements &mdash; the same opt-out propagates to portal 2FA automatically. You don&rsquo;t have to disable it in two places.</p>

          <h5>Requirements for the SMS Button to Appear</h5>
          <p>On the portal verify page, the &ldquo;Text me the code instead&rdquo; option only appears if <strong>all three</strong> conditions hold:</p>
          <ol>
            <li>The contact record has a phone number whose type is <strong>Mobile</strong> (landlines are not offered &mdash; they can&rsquo;t receive SMS).</li>
            <li>At least one involvement for that contact has the text-notifications checkbox enabled.</li>
            <li>The contact has not replied <strong>STOP</strong> to any firm text message (which would have cleared the checkbox everywhere).</li>
          </ol>

          <h5>Turning SMS Back On After a STOP</h5>
          <p>If a contact replies <strong>STOP</strong> and later wants to receive texts again, they can reply <strong>START</strong> to the Telnyx number. That re-enables the checkbox across all their involvements. Alternatively, a staff member can re-check the box manually on the involvement form.</p>
        `
      },
      {
        id: 'portal-sharing-docs',
        title: 'Sharing Documents to the Portal',
        audience: 'both',
        content: `
          <h4>Sending a Fileshare</h4>
          <p>From a document or from the matter page, click the <strong>Share</strong> action to send one or more documents to a contact. You can choose any involvement on the matter as the recipient; the system stores an &ldquo;outgoing document&rdquo; record and emails a link.</p>
          <p>The link lands the contact on the portal login page. After they sign in and complete 2FA, the document appears on the <strong>Documents &amp; Media</strong> tab, grouped with any other shares from the same send.</p>

          <h5>What the Client Receives</h5>
          <p>The email contains a link to the portal plus some context about the send. The document itself is <em>not</em> attached &mdash; clients must sign in to retrieve it. This keeps the audit trail: every download is logged to matter history with the client&rsquo;s IP and timestamp.</p>

          <h5>Client-Uploaded Documents</h5>
          <p>When a client uploads a document through the portal:</p>
          <ul>
            <li>A matter history entry is created: &ldquo;[client name] uploaded &lsquo;[filename]&rsquo; via the client portal.&rdquo;</li>
            <li>The original uploaded file is preserved as the intake source.</li>
            <li>When unified document intake is enabled, reviewable document files appear in the scan/release queue; media files that cannot be converted to PDF are released directly to the matter as uploaded documents.</li>
            <li>The client is recorded as the apparent author, but staff can correct the author before release.</li>
            <li>The client&rsquo;s suggested document type appears as a suggestion. Staff can accept it or select a different final type.</li>
            <li>When staff release the item, V7 creates the final document and then runs the normal document-type rules.</li>
          </ul>
          <div class="alert alert-info"><strong>Supported upload types:</strong> PDF, Word (<code>.doc</code>/<code>.docx</code>), RTF, text, XML, common image formats, common video/audio formats such as <code>.mp4</code>, <code>.mov</code>, <code>.mp3</code>, and supported archives. Word and RTF files are filed in their original format and opened as editable TinyMCE documents after release; text, XML, and images use a PDF review derivative; media files are preserved and filed directly.</div>
        `
      }
    ]
  },

  // ============================================================
  // 7. COMMUNICATION (MATTER HISTORY)
  // ============================================================
  {
    id: 'communication',
    title: 'Communication',
    icon: 'fa-comments',
    audience: 'both',
    subsections: [
      {
        id: 'matter-history-entries',
        title: 'Matter History System',
        audience: 'both',
        content: `
          <h4>What is Matter History?</h4>
          <p>Matter history is the <strong>timeline of everything that happens on a matter</strong> &mdash; phone calls, emails, notes, document activity, and system events. It creates the complete audit trail for the case.</p>

          <h5>Matter History Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td><strong>matter_history_type</strong></td><td>Category of entry</td><td><strong>Manual</strong> (user-created notes, calls, etc.), <strong>Document Activity</strong> (auto-logged when documents are created/filed), <strong>Conflict Check</strong> (auto-logged conflict check results), <strong>Perm Activity</strong> (auto-logged when matter is permanently closed)</td></tr>
              <tr><td><strong>title</strong></td><td>Subject line</td><td>Brief summary of the entry</td></tr>
              <tr><td><strong>message</strong></td><td>Full content</td><td>HTML content (rich text). Supports formatting, links, and embedded content.</td></tr>
              <tr><td><strong>color_value</strong></td><td>Visual indicator</td><td>Color code for the entry in the history timeline. Used for visual differentiation.</td></tr>
              <tr><td><strong>hidden</strong></td><td>Visibility flag</td><td>If true, the entry is hidden from the default view. Useful for internal notes that shouldn&rsquo;t clutter the timeline.</td></tr>
              <tr><td><strong>notify_accounting</strong></td><td>Alert flag</td><td>If true, sends a notification to the accounting team about this entry.</td></tr>
              <tr><td><strong>created_by</strong></td><td>Author</td><td>User who created the entry.</td></tr>
            </tbody>
          </table>

          <h5>Threading (Replies)</h5>
          <p>Matter history entries support <strong>threading</strong> using an ancestry-based hierarchy. A reply to a history entry becomes a child of the original entry. This allows conversations and follow-ups to be tracked as threads.</p>

          <h5>Attachments</h5>
          <p>History entries can have:</p>
          <ul>
            <li><strong>Email attachments</strong> (matter_history_emails) &mdash; Associated email records</li>
            <li><strong>Links</strong> (matter_history_links) &mdash; Links to related documents or external URLs</li>
            <li><strong>Responses</strong> (matter_history_responses) &mdash; Tracked responses and follow-ups</li>
          </ul>

          <h5>Deletion</h5>
          <p>History entries use <strong>soft delete</strong> &mdash; they are marked with <code>deleted_by</code> and <code>deleted_at</code> but not actually removed from the database. Deleted entries can be restored by an administrator.</p>

          <h5>Reminders on History</h5>
          <p>You can set a reminder on any matter history entry. The reminder will link back to the specific history entry when it fires.</p>
        `
      },
      {
        id: 'sending-emails',
        title: 'Sending Emails',
        audience: 'both',
        content: `
          <h4>Email from a Matter</h4>
          <ol>
            <li>Open the matter and go to <strong>History</strong>.</li>
            <li>Click <strong>New Email</strong> (or use the email action in the sidebar).</li>
            <li>Select recipients from the matter&rsquo;s involvements or type email addresses manually.</li>
            <li>Compose using the <strong>TinyMCE</strong> rich text editor.</li>
            <li>Attach documents from the matter&rsquo;s document list if needed.</li>
            <li>Click <strong>Send</strong>.</li>
          </ol>

          <h5>What Happens After Sending</h5>
          <ul>
            <li>A matter history entry of type &ldquo;Document Activity&rdquo; is automatically created.</li>
            <li>The email is delivered via the firm&rsquo;s mail server (Postfix relay to the mail server at 10.1.1.11).</li>
            <li>The email is archived in the <strong>Email Archive</strong> (searchable from Administration).</li>
            <li>If delivery fails (mail server down, invalid address), the email queues in Postfix and will retry.</li>
          </ul>
        `
      },
      {
        id: 'notification-emails',
        title: 'Email Notifications',
        audience: 'both',
        content: `
          <h4>Automatic Email Notifications</h4>
          <p>When a matter history entry is created, the system can automatically email notifications to the matter&rsquo;s assigned users:</p>
          <ul>
            <li>Notifications are sent to users configured in the matter&rsquo;s notification settings.</li>
            <li>The notification includes the history entry title and content.</li>
            <li>When a document is linked, the notification identifies the document by name and includes its type, author, authored date, and any notes or description that are available. The document name opens the released file directly.</li>
            <li>Each notification also includes <strong>View the related history and its linked documents</strong>. This opens the matter, expands the complete history thread, scrolls to the exact entry or reply referenced by the notification, and highlights it so staff can review the surrounding conversation and use its document links.</li>
            <li>Document and source links in notifications are complete web addresses, so they continue to work when the message is opened outside V7.</li>
            <li>Delivery is asynchronous via Sidekiq background jobs &mdash; there may be a brief delay.</li>
          </ul>
          <div class="alert alert-info"><strong>Troubleshooting:</strong> If notifications aren&rsquo;t being received, check: (1) the mail server is running, (2) Sidekiq background jobs are processing, (3) the user has a valid email address, (4) the entry&rsquo;s notification settings are enabled. If a document link in an older notification does not open, use <strong>View the related history and its linked documents</strong>, then open the document from the history entry.</div>
        `
      },
      {
        id: 'phone-calls',
        title: 'Phone Call Logging',
        audience: 'both',
        content: `
          <h4>Logging Phone Calls</h4>
          <ol>
            <li>Open the relevant matter.</li>
            <li>Add a new matter history entry.</li>
            <li>Record who called, the topic, and detailed notes of the conversation.</li>
            <li>Click <strong>Save</strong>.</li>
          </ol>
          <p>A centralized phone call log is available at <strong>Administration &rarr; Phone Calls</strong>, which shows all logged calls across all matters.</p>
        `
      }
    ]
  },

  // ============================================================
  // 6b. OUTGOING MAIL
  // ============================================================
  {
    id: 'outgoing-mail',
    title: 'Outgoing Mail',
    icon: 'fa-envelope',
    audience: 'both',
    subsections: [
      {
        id: 'outgoing-overview',
        title: 'Outgoing Mail Queue',
        audience: 'both',
        content: `
          <h4>Outgoing Mail System</h4>
          <p>All physical mail, faxes, and emailed documents are processed through the <strong>Outgoing Mail Queue</strong> (<strong>Services &rarr; Outgoing</strong>). The queue manages the entire lifecycle of outgoing correspondence from document generation through final mailing.</p>
          <div class="alert alert-warning"><strong>Important:</strong> PLO and CRDF have separate outgoing mail queues. Always check both intranets for pending mail.</div>

          <h5>Queue Terminology</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
            <tbody>
              <tr><td><strong>Waiting</strong></td><td>Job has NOT been printed yet. Select &ldquo;Waiting&rdquo; from the status filter to see only unprinted jobs.</td></tr>
              <tr><td><strong>Ready</strong></td><td>Job has been printed and is ready to process.</td></tr>
              <tr><td><strong>Take</strong></td><td>Click to claim a job. Your name appears in the Processor column so others know you&rsquo;re working on it.</td></tr>
              <tr><td><strong>Finish</strong></td><td>Click when the job is fully processed and mailed. Moves it out of the active queue.</td></tr>
              <tr><td><strong>Priority (Red)</strong></td><td>High-priority job (e.g., overnight mailing). Process these first, ahead of regular mail.</td></tr>
            </tbody>
          </table>

          <h5>Daily Workflow</h5>
          <ol>
            <li>Go to <strong>Services &rarr; Outgoing</strong>. Check PLO first, then switch to CRDF.</li>
            <li>Filter by <strong>Waiting</strong> status. Click <strong>&ldquo;Print xx jobs&rdquo;</strong> (top left) to print all waiting jobs. <em>Only press this button once</em> &mdash; it may spin for a moment.</li>
            <li>Retrieve printed letters, envelopes, and copies from the designated printer. Keep them in print order.</li>
            <li>Work from the <strong>bottom of the list upward</strong> (oldest jobs first). Check page 2 for older items.</li>
            <li>Click <strong>Take</strong> on each job to claim it.</li>
            <li>Read the job instructions and the yellow separator page for each print job.</li>
            <li>Assemble the mailing: sign letters (your initials behind the attorney&rsquo;s name), stuff envelopes, attach enclosures.</li>
            <li>Weigh and apply postage via the postal machine.</li>
            <li>Click <strong>Finish</strong> when done.</li>
            <li>Place completed mail in the outgoing bin near reception.</li>
          </ol>

          <h5>Yellow Separator Pages</h5>
          <p>Each print job is separated by a <strong>yellow page</strong> containing processing instructions. The yellow page only prints when there is an attachment for a print job. <strong>&ldquo;Special Instruction Attachments&rdquo;</strong> (instructions only, no physical attachment) do NOT produce a yellow page &mdash; the instructions appear only in the outgoing job&rsquo;s instruction section on screen.</p>

          <h5>If Items Are Missing</h5>
          <p>If you&rsquo;re missing letters, attachments, or envelopes, click the <strong>Resend</strong> button to reprint any part of the job. If resend fails, contact Support. Note: resending faxes requires creating an entirely new outgoing job.</p>

          <h5>Document Check Before a Job Is Created</h5>
          <p>Before V7 creates an outgoing job, it rebuilds editable documents from their saved contents and verifies that the main document and every selected attachment have a readable, non-empty file. Document names may contain spaces.</p>
          <p>If V7 cannot create or find one of the files, it displays the affected document&rsquo;s name and <strong>does not create the outgoing job, send file-share notices, or add recipients</strong>. Return to the document, confirm that it is saved and can be previewed, and then try again. If the problem continues, contact Support with the document and matter numbers.</p>
          <div class="alert alert-info"><strong>Data protection:</strong> A failed PDF conversion does not replace a previously valid PDF with a blank or partial file.</div>

          <h5>File Share Tracking</h5>
          <p>File share jobs preserve the original sent time for audit purposes. In the list of outgoing jobs, an ID shown in <strong class="text-danger">red</strong> means that at least one shared item in that job has not been downloaded by its intended recipient. Ask the attorney for direction before taking further action.</p>
          <p>On the outgoing job&rsquo;s page, shared items use a <strong>Resend</strong> action instead of the general resend control. Clicking <strong>Resend</strong> sends that particular line-item notification again to the intended recipient. V7 records the date and time of the resend separately and preserves the original sent time.</p>

          <h5>Postage Rules (as of 2/16/2022)</h5>
          <ul>
            <li>Any outgoing mail with postage cost higher than <strong>$2.00</strong> is charged to the client matter.</li>
            <li>For thick envelopes: weigh, set meter, run empty envelope through meter, then re-stuff and seal.</li>
            <li>For oversized packages, use large green-and-white first-class envelopes or brown mailing paper. Check USPS.com for rates.</li>
          </ul>

          <h5>Signing Letters</h5>
          <p>Most letters include the attorney&rsquo;s signature automatically. If for some reason the signature is missing, sign the attorney&rsquo;s name followed by <strong>&ldquo;Esq./(your initials)&rdquo;</strong>.</p>

          <h5>File Copies</h5>
          <ul>
            <li>Always retain one copy for the file unless instructed otherwise.</li>
            <li>Mark file copies with an <strong>orange marker</strong> on the lower right-hand corner.</li>
            <li>Write the Matter # on the bottom left of copies that don&rsquo;t have one on the front page.</li>
            <li>Attachments provided via email have already been scanned &mdash; no need to print an extra file copy.</li>
          </ul>

          <h5>Proof Preview</h5>
          <p>When <strong>Include Proof</strong> is selected, open the <strong>Proof</strong> tab to choose the proof template, proof date, and type served. Click <strong>Preview</strong> after entering both required fields.</p>
          <p>The proof preview includes every recipient listed on the Recipients tab, including multiple CC recipients. If the preview does not open, verify that a proof date and type served are entered and that all recipients appear in the Proof Recipients list.</p>
        `
      },
      {
        id: 'certified-mail',
        title: 'Certified Mail & Special Handling',
        audience: 'both',
        content: `
          <h4>Certified Mail</h4>
          <p>Instructions are posted beside the postal machine, and forms are in the supply cabinet.</p>
          <ol>
            <li>Fill out the certified mail form per the posted instructions.</li>
            <li>Write the <strong>file number on the green card</strong> and the white slip (so returned items can be matched to the file).</li>
            <li>Note the <strong>tracking number</strong> on the file copy of the letter.</li>
            <li>Place in the outgoing mail bin.</li>
            <li>Set aside the file copy and wait for the white slip to be returned from the Post Office (usually within a day or two).</li>
            <li>When the white slip is returned, the incoming mail person scans it and gives it to the outgoing mail person to attach to the file copy.</li>
            <li>When the green card is returned, the incoming mail person scans it and gives it to the outgoing mail person.</li>
            <li>Once both the white slip and green card are attached to the file copy, it can be filed.</li>
          </ol>
          <div class="alert alert-info"><strong>Tracking:</strong> If several days pass without the white slip returning, go to USPS.com and enter the tracking number from the file copy. If that doesn&rsquo;t work, call the Bechtelsville Post Office.</div>

          <h5>Charging Postage to Matters</h5>
          <p>When sending certified mail or any mail with additional charges, send a <strong>history note to accounting</strong> so they can apply charges to the matter:</p>
          <pre>POSTAGE: [Documents description] sent to [Recipients], sent via certified mail, $6.48. [Date] - [Staff name]</pre>

          <h4>Certificate of Mailing</h4>
          <ol>
            <li>Complete the Certificate of Mailing form (found in the supply cabinet or inside the supply cabinet door).</li>
            <li>Make a <strong>copy</strong> of the Certificate.</li>
            <li>Both original and copy must be <strong>stamped at the Post Office</strong> &mdash; do NOT use the postal machine.</li>
            <li>The Post Office must affix postage AND a red postmark where indicated. Courts may reject certificates without both.</li>
            <li>Cost: approximately $1.30 each &times; 2 = $2.60 (check current rates).</li>
            <li>Place <strong>&ldquo;PHOTOCOPY&rdquo;</strong> in the mailing envelope. Bring the original back to the office and attach to the file copy.</li>
            <li>Scan the Certificate of Mailing, print, write the Matter # on it, and give to the incoming mail person to code and scan.</li>
          </ol>

          <h4>Appeal Mailing Tracking Procedure</h4>
          <p>For any appeal sent via certified, return receipt, or any form of overnight/express delivery:</p>
          <ol>
            <li>Document the tracking number in a matter history note.</li>
            <li>Set a <strong>daily reminder</strong> to check delivery status.</li>
            <li>If the mailing is not delivered <strong>4 business days prior to the appeal deadline</strong>, notify via history: the lead attorney, the submitting attorney (if different), and WHP.</li>
            <li>If no response by the following morning, immediately call those notified, seek guidance, and document all conversations via history.</li>
            <li>The outgoing mail person must also notify the backup staff member (via copied history) of the matter, tracking number, and circumstances.</li>
          </ol>

          <h4>Overnight / Priority Mail</h4>
          <ul>
            <li>USPS overnight delivery is guaranteed by <strong>6:00 PM next day</strong> (no longer mid-day). For earlier delivery, use another service.</li>
            <li>Minimum overnight cost via USPS: approximately <strong>$25.50</strong> (weight-dependent).</li>
            <li>Mark the outgoing job as <strong>&ldquo;Priority&rdquo;</strong> AND send an email/call to the outgoing mail person.</li>
            <li>All overnight mailings must be in the queue by <strong>Noon</strong> (Post Office closes 12&ndash;2 PM).</li>
            <li>If after Noon, the attorney is personally responsible for taking the mailing to a registered FedEx or UPS center.</li>
          </ul>

          <h4>Return Mail Handling</h4>
          <ol>
            <li>The returned envelope and original contents are scanned.</li>
            <li>If a forwarding address is provided, resend the item and add a history entry.</li>
            <li>If no address can be found, add a detailed history entry notifying the attorney that it was NOT resent.</li>
            <li>Original letter and envelope go on the &ldquo;to file&rdquo; pile.</li>
            <li>If the returned letter was about closing/picking up a file, return the original to the person who wrote the letter.</li>
          </ol>
        `
      },
      {
        id: 'outgoing-court-filings',
        title: 'Court Filing Procedures',
        audience: 'both',
        content: `
          <h4>Workers&rsquo; Compensation Bureau Filings</h4>
          <div class="alert alert-danger"><strong>Bureau Requirements:</strong> All filings with the Bureau of Workers&rsquo; Compensation must be <strong>ORIGINALS, IN COLOR, and DOUBLE-SIDED</strong>. The Bureau will return non-compliant filings.</div>

          <h5>Standard WC Petition Filing</h5>
          <ul>
            <li>Original to the Bureau (double-sided, in color)</li>
            <li>Copy to employer, carrier, and opposing counsel (if any)</li>
            <li>Proof of Service to all parties</li>
            <li>Copy and Proof of Service to the file</li>
            <li>DO NOT send a copy to the defense attorney unless instructed</li>
            <li><strong>DO NOT STAPLE</strong> papers going to the Bureau</li>
          </ul>
          <p>This applies to: Claim Petitions, Penalty Petitions, Review, Termination, Modification, Suspension, Reinstatement, Set Aside, and C&amp;R Approval petitions.</p>
          <p>All petitions must be <strong>signed by the attorney</strong> prior to filing (exception: Penalty Petitions do not require client signature).</p>

          <h5>Utilization Review Request</h5>
          <ul>
            <li>Original (in color) plus <strong>eight (8) copies</strong> to the Bureau &mdash; all double-sided</li>
            <li>Copy to doctor/provider, employer, carrier, and opposing counsel</li>
            <li>Attach a copy of signed Medical Authorization from the Claimant</li>
            <li>Proof of Service to all parties</li>
            <li>Send to: Medical Treatment Review Section (see Bureau of Workers&rsquo; Compensation contacts)</li>
            <li>The <strong>Bureau Code</strong> (4-digit number) MUST be included. If not listed for the carrier, email <code>emadigan@state.pa.us</code> with: carrier name, client name, SSN, date of injury, and employer name.</li>
          </ul>

          <h5>WC Subpoenas</h5>
          <ol>
            <li>Subpoenas are paperclipped to a letter to the Judge requesting signature and seal.</li>
            <li>Include a <strong>stamped, self-addressed envelope</strong> for return. Weigh the subpoenas to determine return postage.</li>
            <li>Write the attorney&rsquo;s initials and file number on the lower left of the return envelope (e.g., &ldquo;WP #10711&rdquo;).</li>
            <li>Per firm policy: <strong>ALL subpoenas must be sent via Certified Mail</strong> unless the attorney explicitly says otherwise.</li>
          </ol>

          <h5>WC Record Depositions</h5>
          <p>Assemble in order: Cover letter &rarr; Notice of Records Deposition &rarr; Subpoena &rarr; List of Requested Documents &rarr; Affidavit of Records Custodian. Staple all five pages, sign originals, stamp first page: <strong>&ldquo;Remember to Return Affidavit.&rdquo;</strong> Send originals via certified mail.</p>

          <h5>WC Appeal Board Filings</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Filing Type</th><th>Copies</th><th>Recipients</th></tr></thead>
            <tbody>
              <tr><td>Brief</td><td>Original + 5 copies (bound)</td><td>WCAB, Defense Counsel, file</td></tr>
              <tr><td>Appeal from Judge&rsquo;s Decision</td><td>Original + 4 copies</td><td>WCAB + attach Judge&rsquo;s Decision, copy to client/defense/Judge</td></tr>
              <tr><td>Counsel Fee Approval (501)</td><td>Original + 4 copies</td><td>Appeal Board + attach Fee Agreement, copy to claimant/OC/employer/carrier</td></tr>
            </tbody>
          </table>

          <h5>Commonwealth Court Filings</h5>
          <ul>
            <li><strong>Brief</strong>: Original + 15 copies (single-sided only, total of 16). Eight copies of reproduced record (unless excused in forma pauperis). All must be <strong>bound</strong>. Original on good paper to color printer.</li>
            <li><strong>Petition for Review</strong>: Original + 1 copy (no binding, cover staples with tape). Copy to client, OC, WCAB, PA Attorney General.</li>
            <li>Check <strong>Commonwealth Court filing fees</strong> before processing.</li>
          </ul>

          <h5>Letters to Judge</h5>
          <ul>
            <li>Original to Judge with Proof of Service</li>
            <li>Copy to Opposing Counsel with Proof of Service</li>
            <li>Copy to file</li>
            <li>Dates on Proof of Service and postmark must be the <strong>same</strong></li>
          </ul>

          <h5>Civil Complaint Filing</h5>
          <p>All documents must be reprinted <strong>one-sided</strong> for court filing.</p>
          <ol>
            <li>Cover letter (not attached to packets)</li>
            <li>Notice to Defend or Scheduling Order (1 original + 5 copies)</li>
            <li>Complaint (1 original + 5 copies)</li>
            <li>Verification (1 original + 5 copies)</li>
            <li>Staple in order: Notice/Scheduling Order &rarr; Complaint &rarr; Verification</li>
            <li>Family Cover Sheet (1 original + 1 copy, not stapled)</li>
            <li>Certification of Address (1 original + 1 copy)</li>
            <li>Return envelopes: 1 SASE addressed to opposing party + 1 addressed to PLO. Use the Prothonotary&rsquo;s address as return address (apply blank white labels over PLO return address).</li>
          </ol>

          <h5>HIPAA Authorization / Medical Releases</h5>
          <ul>
            <li>Releases go out with the <strong>client&rsquo;s signature only</strong>.</li>
            <li>Leave expiration date blank. Date the release line next to the client&rsquo;s signature with the mailing date.</li>
            <li>If the provider returns it requesting the attorney also sign, notify the attorney to sign and resend.</li>
            <li>Print releases from: Intranet &rarr; Matter &rarr; Documents &rarr; Medical Releases.</li>
          </ul>
        `
      },
      {
        id: 'outgoing-fax-email',
        title: 'Faxing & Emailing Documents',
        audience: 'both',
        content: `
          <h4>Sending a Fax</h4>
          <div class="alert alert-warning"><strong>Cost Alert:</strong> Faxing costs approximately <strong>$1.00 per page</strong>. Do NOT fax large document sets. If the recipient has email, use email instead. If neither email nor fax is feasible, use regular mail.</div>
          <p>PLO does not have a physical fax machine. All faxes are sent electronically through <strong>Phaxio</strong> via the outgoing mail system.</p>

          <h5>How to Send a Fax</h5>
          <ol>
            <li>Ensure the recipient contact has a <strong>fax number</strong> on file and is attached to the matter.</li>
            <li>If including attachments, they must be <strong>scanned/uploaded into the matter BEFORE</strong> generating the fax.</li>
            <li>Go to the Matter &rarr; click <strong>New Document</strong>.</li>
            <li>For Document Type, select <strong>Miscellaneous Letter</strong>.</li>
            <li>Tag the document and select your signature.</li>
            <li>Select the recipient contact.</li>
            <li>Check <strong>&ldquo;Send By Fax&rdquo;</strong> (and optionally &ldquo;Send By Mail&rdquo; for dual delivery).</li>
            <li>If including attachments, check <strong>&ldquo;Include Attachments&rdquo;</strong>.</li>
            <li>Complete the letter body, then click <strong>Preview</strong> to review.</li>
            <li>Click <strong>Send</strong>.</li>
            <li>On the Outgoing Transaction screen, click the <strong>Documents</strong> tab to select which documents to attach.</li>
            <li>Click <strong>Submit</strong>. The fax will queue and send immediately without further action.</li>
          </ol>
          <p>If the fax fails to send, both the composer and the outgoing mail person receive a <strong>Fax Failure</strong> email notification.</p>

          <h4>Emailing Documents from a Matter</h4>
          <ol>
            <li>Go to the Matter &rarr; <strong>New &rarr; Document</strong>.</li>
            <li>Select <strong>Miscellaneous Letter</strong> (or appropriate letter type).</li>
            <li>Select the recipient contact (must have an email address on file).</li>
            <li>Check <strong>&ldquo;Send By Email&rdquo;</strong>.</li>
            <li>Complete the letter body.</li>
            <li>Click <strong>Send</strong>, then select documents to attach from the Documents tab.</li>
            <li>Click <strong>Submit</strong>.</li>
          </ol>
          <div class="alert alert-info"><strong>Note:</strong> A hard copy of the cover letter always prints for the file, regardless of delivery method (fax or email).</div>

          <h4>Typing an Envelope</h4>
          <p>Go to the matter, find the contact, and click the <strong>envelope icon</strong> next to their name. A confirmation message appears at the top and the envelope prints on the envelope printer.</p>
        `
      },
      {
        id: 'outgoing-special-items',
        title: 'Special Outgoing Items',
        audience: 'both',
        content: `
          <h4>New Client Letters</h4>
          <p>New client letters should include:</p>
          <ul>
            <li>A <strong>business card</strong> of the attorney the client will meet with</li>
            <li>An appropriate <strong>brochure</strong> for the case type (e.g., Firearms Law brochure for gun trust, Family Law for divorce). If no specific brochure exists, use the PLO general brochure.</li>
          </ul>

          <h4>Gun Trust Assembly (CRDF)</h4>
          <ol>
            <li>Click <strong>Take</strong> on the outgoing job and check instructions.</li>
            <li>Verify the page count matches the trust document.</li>
            <li>Staple the cover page (usually 2 pages).</li>
            <li>Attach a backer to the trust and staple. Keep pages in order.</li>
            <li>Include: firearms brochure, general brochure, and attorney&rsquo;s business card.</li>
            <li>Paper clip brochures and card to the cover letter in front of the trust.</li>
            <li>Place the entire package in the large white addressed envelope.</li>
          </ol>

          <h4>Fee Agreements</h4>
          <p>Generate from: Intranet &rarr; Matter &rarr; New &rarr; Documents &rarr; type &ldquo;Fee&rdquo; &rarr; select the correct fee agreement. The attorney will instruct whether to sign the document before sending.</p>

          <h4>Original Documents Bin</h4>
          <ul>
            <li>Original documents waiting to be sent are kept in the <strong>&ldquo;Original Documents&rdquo; bin</strong> on the bookshelf in the printer area.</li>
            <li><strong>Weekly review:</strong> Check the bin and send a history note reminder to the attorney if a document has been sitting there.</li>
            <li>If placed in error or not being sent, move to the &ldquo;to be filed&rdquo; box and document via history.</li>
            <li>When an attorney needs an original delivered to a specific office, document the request, the delivering staff member, and the delivery confirmation via history notes.</li>
          </ul>
        `
      }
    ]
  },

  // ============================================================
  // 7. CALENDAR & EVENTS
  // ============================================================
  {
    id: 'calendar',
    title: 'Calendar & Events',
    icon: 'fa-calendar-alt',
    audience: 'both',
    subsections: [
      {
        id: 'calendar-overview',
        title: 'Calendar View',
        audience: 'both',
        content: `
          <h4>Using the Calendar</h4>
          <p>The calendar uses <strong>FullCalendar</strong> and provides month, week, day, multi-day, and intranet-agnostic views:</p>
          <ul>
            <li>Access from <strong>Home &rarr; Calendar</strong> (secondary menu) or <strong>Services &rarr; Events</strong>.</li>
            <li>Click any event to view details or edit.</li>
            <li>Click an empty time slot to create a new event.</li>
            <li>Drag events to reschedule them.</li>
            <li>Day, week, and month views include events on every displayed date, including the final day of the visible range.</li>
            <li><strong>Agnostic</strong> uses the multi-day list layout and combines events for every attorney and staff member currently selected in the calendar filters. V7 matches each selected person&rsquo;s active human user accounts across the intranets available to you by SSN. Each event is labeled with its owning intranet. Use the plus/minus control to expand or collapse the event details. Expanding an event, or opening its event or matter link, activates the owning intranet first; the event itself remains stored only in its assigned intranet.</li>
          </ul>

          <h5>iCal Subscription</h5>
          <p>Click <strong>Copy iCal URL</strong> in the home secondary menu, wait for the spinner to change to the <strong>iCal URL copied</strong> confirmation, and then paste the URL into the subscription-calendar feature in Outlook, Google Calendar, or Apple Calendar. The generated URL uses the V7 host from which you are working and includes a unique API key salt for authentication &mdash; do not share it. If copying fails, V7 displays an error instead of leaving the progress indicator running.</p>

          <h5>Time Zones &amp; DST</h5>
          <p>Events are stored in <strong>UTC</strong> and displayed in your local time zone. Daylight Saving Time transitions are handled automatically.</p>
        `
      },
      {
        id: 'creating-events',
        title: 'Creating Events',
        audience: 'both',
        content: `
          <h4>Event Fields</h4>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td><strong>Name</strong></td><td>Event title</td><td>Required</td></tr>
              <tr><td><strong>Event Type</strong></td><td>Category</td><td>See event types below</td></tr>
              <tr><td><strong>Start / End</strong></td><td>Date and time</td><td>All-day events: duration &ge; 14 hours</td></tr>
              <tr><td><strong>Notes</strong></td><td>Description or details</td><td>Free text</td></tr>
              <tr><td><strong>Address</strong></td><td>Location</td><td>Required for Appointment type. Selected from contact addresses.</td></tr>
              <tr><td><strong>Reminder</strong></td><td>Minutes before event</td><td>Optional. Sets a pre-event reminder notification.</td></tr>
              <tr><td><strong>Matter</strong></td><td>Associated matter</td><td>Optional. Links event to a specific case.</td></tr>
              <tr><td><strong>Attendees</strong></td><td>Users and/or contacts</td><td>Firm users (via involvements) and &ldquo;All users&rdquo; option.</td></tr>
            </tbody>
          </table>

          <h5>Event Types</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Type</th><th>Requirements</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Appointment</strong></td><td>Address required</td><td>In-person meeting at a specific location.</td></tr>
              <tr><td><strong>Phone Conference</strong></td><td>Phone contact and number required</td><td>Phone call event. Specify <strong>Initiator</strong>: &ldquo;You&rdquo; (you call them) or &ldquo;Contact&rdquo; (they call you).</td></tr>
              <tr><td><strong>To-Do</strong></td><td>None</td><td>Task-like event. Can be converted to a Reminder.</td></tr>
              <tr><td><strong>New Client</strong></td><td>None</td><td>New client consultation or onboarding event.</td></tr>
            </tbody>
          </table>

          <h5>Recurrence</h5>
          <p>Events can recur using <strong>iCal RRULE</strong> syntax (powered by IceCube). Supported patterns:</p>
          <ul>
            <li><strong>DAILY</strong> &mdash; Every day or every N days</li>
            <li><strong>WEEKLY</strong> &mdash; With BYDAY (e.g., every Mon/Wed/Fri)</li>
            <li><strong>MONTHLY</strong> &mdash; By day of month (BYMONTHDAY) or by position (BYSETPOS, e.g., &ldquo;2nd Tuesday&rdquo;)</li>
            <li><strong>YEARLY</strong> &mdash; Annual recurrence</li>
          </ul>
        `
      },
      {
        id: 'upcoming-events',
        title: 'Upcoming Events & Hearings',
        audience: 'both',
        content: `
          <h4>Quick Schedule Views</h4>
          <p><strong>Accounting &rarr; Upcoming Events</strong> is a list-format view for quick scanning. It combines upcoming events and hearings into one chronological report.</p>
          <ul>
            <li>Use the <strong>Lead Attorney</strong> selector to view another attorney&rsquo;s upcoming event report.</li>
            <li>The four filter checkboxes are checked by default: <strong>Hide repeating events</strong>, <strong>Hide all-day events</strong>, <strong>Hide personal events</strong>, and <strong>Hide doctor appointments</strong>.</li>
            <li>Changing a checkbox refreshes the report immediately; the <strong>Search</strong> button is still available after changing the lead attorney.</li>
            <li>Uncheck a filter to include that category in the report.</li>
          </ul>
          <p>These views show the event name, date/time, matter number, and any notes.</p>
        `
      },
      {
        id: 'scheduling-policies',
        title: 'Scheduling Policies',
        audience: 'both',
        content: `
          <h4>Appointment Scheduling Rules</h4>

          <h5>Next-Day Appointment Policy</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Scenario</th><th>Action Required</th></tr></thead>
            <tbody>
              <tr><td>Appointment within <strong>24 hours</strong></td><td>Must <strong>call the attorney to get permission</strong> before scheduling</td></tr>
              <tr><td>More than 24 hours but <strong>next business day</strong></td><td>May schedule but must <strong>call and notify</strong> the attorney</td></tr>
              <tr><td><strong>Beyond next business day</strong></td><td>Schedule freely &mdash; no permission or notification needed</td></tr>
            </tbody>
          </table>
          <p><strong>Example:</strong> It is 1 PM. Scheduling for 11 AM tomorrow = ask permission. Scheduling for 3 PM tomorrow = notify attorney. Scheduling for Wednesday (two days out) = no action needed.</p>

          <h5>Location &amp; Travel Rules</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Location</th><th>Rule</th></tr></thead>
            <tbody>
              <tr><td><strong>Philadelphia or Harrisburg</strong></td><td>Do NOT schedule anything else that day</td></tr>
              <tr><td><strong>Malvern (Chester Co.)</strong></td><td>Do NOT schedule anything before <strong>3:00 PM</strong> (testimony may run late)</td></tr>
              <tr><td><strong>Non-home office</strong></td><td>Allow <strong>1 hour</strong> at the end of last appointment before scheduling next attorney in same room</td></tr>
              <tr><td><strong>Any location</strong></td><td>Don&rsquo;t schedule at 9 AM sharp &mdash; prefer 9:30 or 10:00 AM for travel time</td></tr>
            </tbody>
          </table>

          <h5>General Scheduling Best Practices</h5>
          <ul>
            <li><strong>Group appointments</strong> together during the day. Don&rsquo;t scatter one at 9 AM and another at 3 PM.</li>
            <li>Group same-office appointments on one day rather than making the attorney travel multiple times per week.</li>
            <li>Allow <strong>1 hour for lunch</strong> when there are back-to-back appointments.</li>
            <li>Check with the attorney before scheduling anything <strong>after 5 PM</strong>.</li>
            <li>For appointments at non-PLO offices, write the location in <strong>ALL CAPS</strong> in the event &ldquo;What&rdquo; field (e.g., &ldquo;Client appt. in BECHTELSVILLE Office w/Scott Jones (10711)&rdquo;).</li>
            <li>For conference room needs, add &ldquo;<strong>CONFERENCE ROOM NEEDED</strong>&rdquo; in ALL CAPS.</li>
            <li>For home visits or agreed locations, include the city in ALL CAPS (e.g., &ldquo;Client appt. HOME VISIT LANCASTER&rdquo;).</li>
          </ul>

          <h5>Phone Conference Events</h5>
          <p>Include the phone number in both the &ldquo;What&rdquo; field and the Description. The Description creates a clickable hyperlink on mobile devices:</p>
          <pre>Phone Conference w/Scott Jones Call: 610-845-3803</pre>

          <h5>Event Duration Defaults</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Event Type</th><th>Default Duration</th></tr></thead>
            <tbody>
              <tr><td>Existing client appointment</td><td>30 minutes (attorney may override)</td></tr>
              <tr><td>New client consultation</td><td>30 minutes (internal block may be longer)</td></tr>
              <tr><td>Firearms Trust new client</td><td>1 hour phone conference</td></tr>
              <tr><td>Hearing (no end time specified)</td><td>3 hours</td></tr>
              <tr><td>Call of List / Oral Argument</td><td>All day</td></tr>
              <tr><td>Judge telephone conference</td><td>1 hour</td></tr>
              <tr><td>Vocational Rehabilitation meeting</td><td>1.5 hours (client meets attorney 15 min before)</td></tr>
              <tr><td>Deposition (&ldquo;Their&rdquo;)</td><td>2 hours + travel time</td></tr>
            </tbody>
          </table>

          <h5>Rescheduling &amp; Cancellation</h5>
          <ul>
            <li>When events are rescheduled, mark the old event <strong>INACTIVE</strong> with a brief note explaining why. Create a <strong>new event</strong> for the new date.</li>
            <li>Do NOT reuse a cancelled event slot for a subsequent date &mdash; leave the cancelled event as-is (inactive).</li>
            <li>For WC hearing continuances requested by opposing counsel: check with the attorney for objections before agreeing. If no objection, OC sends a letter to the Judge. Call the client with the new date. Change the calendar and add a history note.</li>
          </ul>

          <h5>Attorney Order in Events</h5>
          <p>When adding multiple attorneys to an event, always add them in order: <strong>Lead Attorney &rarr; Attorney &rarr; Attorney</strong>. This ensures the lead attorney appears first.</p>
        `
      },
      {
        id: 'placekeeper-events',
        title: 'Placekeeper Events',
        audience: 'both',
        content: `
          <h4>What Is a Placekeeper?</h4>
          <p>A <strong>Placekeeper</strong> is a special event type used to block time on an attorney&rsquo;s calendar when we know something is supposed to occur (hearing, appointment), but there is a <strong>deficiency with representation status</strong> &mdash; typically a missing signed fee agreement, missing retainer, or unpaid balance.</p>

          <h5>When Placekeepers Are Created</h5>
          <ol>
            <li><strong>During intake</strong>: A prospective client mentions an upcoming hearing but hasn&rsquo;t had their consultation or decided to hire the attorney yet.</li>
            <li><strong>During scan release</strong>: A notice comes in for a matter where we haven&rsquo;t received a signed fee agreement and/or retainer, or there is an outstanding balance, or insufficient escrow.</li>
          </ol>

          <h5>Placekeeper Timeline</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Time Before Event</th><th>Action</th></tr></thead>
            <tbody>
              <tr><td><strong>1 week before</strong></td><td>Send the <strong>Placekeeper form letter</strong> to the client: notify them of the issue (missing fee agreement, retainer, or balance due) and that we will not represent them if not corrected. We may seek to withdraw if representation has already begun.</td></tr>
              <tr><td><strong>48 hours before</strong></td><td>If issue is <strong>NOT rectified</strong>: disable the Placekeeper event to clear the attorney&rsquo;s calendar. Add a history note titled &ldquo;Placekeeper inactivated&rdquo; with tags: <em>Placekeeper, cancelled appointment</em>, and the relevant issue (<em>no fee agreement</em>, <em>no retainer</em>, or <em>insufficient escrow</em>).</td></tr>
              <tr><td><strong>After 48-hour deadline</strong></td><td>If the client rectifies the situation after the deadline but before the event, <strong>seek permission from Warren</strong> (NOT the attorney) to reactivate.</td></tr>
            </tbody>
          </table>

          <div class="alert alert-warning"><strong>If attorney has already entered appearance:</strong> Do NOT unilaterally cancel. Discuss with WHP or Joshua (if WHP unavailable). Do not schedule regular appointments unless a payment plan is in place and the client is cooperating, or the missing fee agreement has been received.</div>

          <h5>Overdue Balance Matters</h5>
          <p>For matters where work has been performed but the client is no longer paying balances or replenishing retainer, notify Warren via a history note titled &ldquo;Missing Fee Agreement/No Retainer/Overdue Balance&rdquo; so he can review the matter and decide whether to withdraw the Firm.</p>

          <h5>Placekeepers During Intake</h5>
          <p>If during a new client intake a court date or hearing is already scheduled in the near future:</p>
          <ul>
            <li>Advise the client that the consult fee is NOT a retainer &mdash; it only reserves time to discuss the matter.</li>
            <li>The attorney will advise whether the firm will handle the case.</li>
            <li>Due to the short timeline, the client should come prepared to pay the retainer.</li>
            <li>List the event as a <strong>Placekeeper</strong>. Convert to a regular event once the attorney collects retainer and signed fee agreement.</li>
          </ul>
        `
      },
      {
        id: 'deposition-scheduling',
        title: 'Deposition & Hearing Scheduling',
        audience: 'both',
        content: `
          <h4>Deposition Scheduling</h4>
          <p>There are two types of depositions: <strong>&ldquo;Our&rdquo; depositions</strong> (we are deposing someone) and <strong>&ldquo;Their&rdquo; depositions</strong> (opposing counsel is deposing our client).</p>

          <h5>&ldquo;Our&rdquo; Depositions</h5>
          <p>Chris handles &ldquo;Our&rdquo; deposition scheduling: calling the client, booking a court reporter if necessary, and sending confirming letters.</p>

          <h5>&ldquo;Their&rdquo; Depositions</h5>
          <p>When opposing counsel calls to schedule a deposition of our client:</p>
          <ol>
            <li>Collect: contact person&rsquo;s name, opposing counsel&rsquo;s name, phone/extension/email, client name, person to be deposed, full address of deposition location, time, and tentative dates.</li>
            <li>Check the attorney&rsquo;s calendar for availability. If none of the proposed dates work, relay this to opposing counsel.</li>
            <li>Agree on a <strong>tentative date</strong> &mdash; do NOT confirm yet.</li>
            <li>Confirm with the client that the date works, then call opposing counsel back to confirm.</li>
            <li>Verify that opposing counsel will send a confirmation letter.</li>
            <li>Enter the event in the attorney&rsquo;s calendar (allow <strong>2 hours</strong> plus travel time).</li>
            <li>Call our client to notify them of the scheduled deposition.</li>
          </ol>
          <div class="alert alert-info"><strong>Limit:</strong> Schedule only <strong>2 depositions per week per attorney</strong>. Consult the attorney before scheduling a 3rd in the same week.</div>

          <h5>Social Security Hearings</h5>
          <ol>
            <li>The Judge&rsquo;s office calls to schedule (judges only sit a few dates per month at each location).</li>
            <li>Look up the client name and verify the correct matter number before agreeing to any date.</li>
            <li>Check the attorney&rsquo;s calendar and pick the best date/time. Try to schedule on dates when the attorney is already in the area or has an open day.</li>
            <li>If scheduling multiple hearings from the same call, try to put them on the same day.</li>
            <li>Add to the calendar with: Judge&rsquo;s name, contact person, phone number in the <strong>Description</strong>.</li>
            <li>Call the client to inform them. Leave a message if unavailable. Let them know a formal Hearing Notice will arrive 2&ndash;4 weeks before.</li>
          </ol>

          <h5>WC Hearings</h5>
          <ul>
            <li>Initial hearing notices arrive via First Class Mail.</li>
            <li>Phone calls come for conflicts requiring rescheduling or quick settlement hearing (C&amp;R) scheduling.</li>
            <li>If opposing counsel requests a continuance: check with the attorney for objections before choosing a new date. If no objection, notify OC and they send a letter to the Judge.</li>
            <li>Always <strong>call the client</strong> with any new date and update the calendar and matter history.</li>
          </ul>

          <h5>Mediation &amp; Settlement Conferences</h5>
          <ul>
            <li>Both counsel must be present in person.</li>
            <li>Clients can sometimes attend by phone, but someone with <strong>settlement authority</strong> must be available (even if by phone).</li>
          </ul>

          <h5>Vocational Rehabilitation Meetings</h5>
          <ol>
            <li>A letter requesting a meeting usually arrives first. The attorney decides whether to approve or object.</li>
            <li>If approved: call the client for unavailable dates, then coordinate with the rehab person.</li>
            <li>Pick a <strong>tentative date</strong> &mdash; confirm with the client first, then confirm with the rehab person.</li>
            <li>The attorney is ALWAYS present at these meetings. Schedule 1.5 hours, with the client arriving 15 minutes early.</li>
            <li>Call the client <strong>2 days before</strong> as a reminder.</li>
          </ol>
        `
      }
    ]
  },

  // ============================================================
  // 8. REMINDERS & TODOS
  // ============================================================
  {
    id: 'reminders',
    title: 'Reminders & Todos',
    icon: 'fa-bell',
    audience: 'both',
    subsections: [
      {
        id: 'reminders-overview',
        title: 'Overview & Fields',
        audience: 'both',
        content: `
          <h4>Reminders System</h4>
          <p>Reminders notify users about upcoming deadlines, follow-ups, and tasks. They can be created manually or generated automatically by auto-reminder rules (triggered when scans are released).</p>

          <h5>Reminder Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td><strong>topic</strong></td><td>Subject line</td><td>Brief summary (e.g., &ldquo;Follow up on medical records&rdquo;)</td></tr>
              <tr><td><strong>body</strong></td><td>Full description</td><td>Detailed instructions or notes</td></tr>
              <tr><td><strong>start_date</strong></td><td>When the reminder activates</td><td>Date/time. The reminder fires on this date.</td></tr>
              <tr><td><strong>end_date</strong></td><td>Expiration date</td><td>Optional. After this date the reminder is no longer active.</td></tr>
              <tr><td><strong>rrule</strong></td><td>Recurrence rule</td><td>iCal RRULE for repeating reminders (same syntax as events).</td></tr>
              <tr><td><strong>one_time_only</strong></td><td>Single delivery flag</td><td>If true, reminder fires once and doesn&rsquo;t repeat.</td></tr>
              <tr><td><strong>snoozed_till</strong></td><td>Snooze until</td><td>If snoozed, reminder reappears at this date/time.</td></tr>
            </tbody>
          </table>

          <h5>Delivery Methods</h5>
          <p>Reminders can be delivered through multiple channels (configurable per reminder):</p>
          <ul>
            <li><strong>Browser Notification</strong> &mdash; Default. Shows a notification in the V7 interface.</li>
            <li><strong>Email</strong> &mdash; Sends an email to the user.</li>
            <li><strong>Element (Matrix)</strong> &mdash; Sends a message via the Matrix/Element chat system.</li>
            <li><strong>Phone Text (SMS)</strong> &mdash; Sends an SMS via Telnyx (if configured).</li>
          </ul>

          <h5>Reminder Actions</h5>
          <ul>
            <li><strong>Dismiss</strong> &mdash; Marks the reminder as opened/acknowledged.</li>
            <li><strong>Snooze</strong> &mdash; Reschedules to 7:00 AM tomorrow.</li>
            <li><strong>Reset</strong> &mdash; Clears all state (opened, snoozed) and restores the reminder to its original state.</li>
          </ul>

          <h5>What Can Have Reminders</h5>
          <p>Reminders are polymorphic (reminderable) and can be attached to: Contact, Document, Event, Matter, MatterHistory, or User.</p>
        `
      },
      {
        id: 'auto-reminders',
        title: 'Auto-Generated Reminders',
        audience: 'both',
        content: `
          <h4>Auto-Reminder Rules</h4>
          <p>Auto-reminder rules automatically create reminders when scans are released. They are configured by document type:</p>
          <ul>
            <li><strong>Trigger</strong>: A scan of a specific document type is released (filed to a matter).</li>
            <li><strong>duration_after</strong>: Number of days after the release to fire the reminder.</li>
            <li><strong>Assigned to</strong>: The matter&rsquo;s <strong>lead attorney</strong> by default.</li>
          </ul>
          <p>For example: &ldquo;When a Medical Record is filed, create a reminder for the lead attorney 30 days later to follow up.&rdquo;</p>
          <p>Configure auto-reminder rules at <strong>Documents &rarr; Auto Rules &rarr; Auto Reminder Rules</strong>.</p>

          <h5>Reminder Groups</h5>
          <p>Auto-generated reminders from the same rule are grouped together in a <strong>reminder group</strong>. This allows batch management &mdash; you can dismiss or snooze all reminders in a group at once.</p>
        `
      }
    ]
  },

  // ============================================================
  // 9. SCANS
  // ============================================================
  {
    id: 'scans',
    title: 'Scans',
    icon: 'fa-qrcode',
    audience: 'both',
    subsections: [
      {
        id: 'scan-queue',
        title: 'Scan Queue & Workflow',
        audience: 'both',
        content: `
          <h4>Scan Processing Workflow</h4>
          <p>Scans follow a <strong>state machine workflow</strong> from arrival to filing:</p>
          <ol>
            <li><strong>Pending</strong> &mdash; Scan has arrived and is waiting to be processed.</li>
            <li><strong>Processing</strong> &mdash; Scanner operator is reviewing and classifying the scan.</li>
            <li><strong>Scanner Reviewing</strong> &harr; <strong>Attorney Reviewing</strong> &mdash; Dispute workflow (see below).</li>
            <li><strong>Finished</strong> &mdash; Scan has been released (filed to matter as a document).</li>
          </ol>

          <h5>Unified Queue Across Intranets</h5>
          <p>The queue combines scans from the signed-in person&rsquo;s active intranet accounts when those accounts have the same SSN. Each card displays an intranet badge. Counts and live updates also include all of those accounts.</p>
          <p><strong>The scan and resulting documents never move between intranets.</strong> Opening a card activates its owning intranet account, and V7 applies that account&rsquo;s scan permissions, matter assignments, priority restrictions, document types, matters, and storage location. A scan can be released only within its assigned intranet.</p>

          <h5>Processing a Scan</h5>
          <ol>
            <li>Navigate to <strong>Services &rarr; Queues &rarr; Scan Queue</strong>.</li>
            <li>Click a scan to open the preview.</li>
            <li>Set the <strong>matter number(s)</strong> to file the scan to (scans can be filed to multiple matters).</li>
            <li>Select the <strong>document type</strong>.</li>
            <li>Set the <strong>authored on</strong> date (the date on the document, not the scan date).</li>
            <li>Add <strong>document authors</strong> (who wrote/signed the document).</li>
            <li>Set <strong>amount</strong> if applicable (e.g., a check amount).</li>
            <li>For an uploaded spreadsheet, choose whether to file the <strong>spreadsheet and PDF</strong> (the default), the <strong>spreadsheet only</strong>, or the <strong>PDF only</strong>.</li>
            <li>Click <strong>Release</strong> to file the scan.</li>
          </ol>

          <h5>Scan Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>scanned_by</strong></td><td>User who scanned the document</td></tr>
              <tr><td><strong>checked_out_at / checked_out_by</strong></td><td>Locks the scan so only one user processes it at a time</td></tr>
              <tr><td><strong>authored_on</strong></td><td>Date on the actual document (e.g., letter date)</td></tr>
              <tr><td><strong>document_type_id</strong></td><td>Document type classification</td></tr>
              <tr><td><strong>priority</strong></td><td>Processing priority level (configured per intranet)</td></tr>
              <tr><td><strong>page_count</strong></td><td>Number of pages in the scanned document</td></tr>
              <tr><td><strong>amount</strong></td><td>Dollar amount if the document contains a payment or charge</td></tr>
              <tr><td><strong>notes</strong></td><td>Processing notes from the scanner operator</td></tr>
            </tbody>
          </table>

          <h5>Multi-Matter Filing</h5>
          <p>A single scan can be filed to <strong>multiple matters</strong> simultaneously. The system creates separate Document records for each matter, all linked back to the same scan.</p>

          <h5>File Storage</h5>
          <p>Scan files are stored at: <code>{intranet.storage_path}/scans/{scan_id}.pdf</code></p>
        `
      },
      {
        id: 'scan-disputes',
        title: 'Scan Dispute Process',
        audience: 'both',
        content: `
          <h4>Dispute Workflow</h4>
          <p>When the scanner and releaser disagree about a scan&rsquo;s classification:</p>
          <ol>
            <li><strong>Scanner</strong> sets the matter, document type, authors, and date.</li>
            <li><strong>Releaser</strong> reviews and may change classifications. If they disagree with the scanner&rsquo;s choices, the scan goes to <strong>Scanner Reviewing</strong>.</li>
            <li><strong>Scanner</strong> reviews the releaser&rsquo;s changes. If they still disagree, it goes to <strong>Attorney Reviewing</strong>.</li>
            <li><strong>Attorney</strong> makes the final decision.</li>
          </ol>
          <p>The system automatically compares scanner vs. releaser selections for: authors, authored date, document type, and matter numbers. Differences are highlighted as &ldquo;problems.&rdquo;</p>
        `
      },
      {
        id: 'scan-release-effects',
        title: 'What Happens on Release',
        audience: 'both',
        content: `
          <h4>When a Scan is Released</h4>
          <p>Releasing a scan triggers several actions:</p>
          <ol>
            <li><strong>Document creation</strong>: A Document record is created in the target matter(s).</li>
            <li><strong>File copy</strong>: The scan PDF is copied to the matter&rsquo;s document storage directory.</li>
            <li><strong>History record</strong>: A &ldquo;Scan released&rdquo; Document Activity entry records the releaser, document, notes, and a link to the filed document.</li>
            <li><strong>Auto-reminder rules</strong>: If any auto-reminder rules match the document type, reminders are created for the lead attorney.</li>
            <li><strong>Auto-email rules</strong>: If configured, notification emails are sent.</li>
            <li><strong>Auto-outgoing rules</strong>: If configured, outgoing jobs (fax, mail) are created.</li>
          </ol>

          <h5>Scan Priorities</h5>
          <p>Priority levels are configured per intranet at <strong>Administration &rarr; Scan Priorities</strong>. Higher-priority scans appear first in the queue. Priorities can be set per source (e.g., faxes get a higher priority than regular mail).</p>
        `
      },
      {
        id: 'digital-document-intake',
        title: 'Digital Document Intake',
        audience: 'both',
        content: `
          <h4>Email and Portal Files in Document Intake</h4>
          <p>Unified document intake lets digital files move through a shared review-and-release workflow. Most document-like files enter the scan queue for final review. Media files that cannot be converted to PDF are preserved and filed directly to the matter with their intake history and notifications.</p>

          <h5>Where Intake Items Come From</h5>
          <ul>
            <li><strong>Matter email</strong> &mdash; meaningful body text and attachments forwarded to a matter address are separate intake items. Body text and document-like attachments are staged for review; a supported media attachment can be released directly.</li>
            <li><strong>Client portal uploads</strong> &mdash; files uploaded by a client can be staged for review before they appear as final matter documents; supported media files are filed directly.</li>
            <li><strong>Incoming Phaxio faxes</strong> &mdash; received fax PDFs can be staged directly as pending scan queue items for staff to identify the matter and release.</li>
            <li><strong>ScanMail / physical scans</strong> &mdash; physical paper scans continue to use the scan queue, with their scanner-entered type and metadata.</li>
          </ul>

          <h5>What Staff See</h5>
          <ul>
            <li><strong>Intake Source</strong> &mdash; shows whether the item came from MM2 email, the client portal, ScanMail, or another source.</li>
            <li><strong>Original file</strong> &mdash; opens the preserved original attachment/upload in a new tab.</li>
            <li><strong>Original email</strong> &mdash; for MM2 items, opens the archived email in read-only view.</li>
            <li><strong>Email part</strong> &mdash; identifies body text separately from file attachments. The body is rendered as a readable PDF for release review while the original email remains archived.</li>
            <li><strong>Suggested type</strong> &mdash; shows the suggested document type when V7 can infer one from the file name, requested portal type, or extracted text. When a suggestion exists, the release form starts with that type selected.</li>
            <li><strong>Status history</strong> &mdash; shows intake progress such as received, classifying, waiting for release, released, or failed.</li>
          </ul>

          <h5>Supported File Types</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Type</th><th>Release Handling</th></tr></thead>
            <tbody>
              <tr><td>PDF</td><td>Copied directly to the scan queue for release.</td></tr>
              <tr><td>Word / RTF (<code>.doc</code>, <code>.docx</code>, <code>.rtf</code>)</td><td>Shown as a PDF review derivative in the intake queue. V7 retains a stable copy of the original attachment through release, then files it in the original office format with an editable TinyMCE document part.</td></tr>
              <tr><td>Spreadsheets (<code>.xlsx</code>, <code>.ods</code>)</td><td>Shown as a PDF review derivative using an adaptive print layout. V7 preserves reasonable print settings; otherwise it detects each sheet&rsquo;s populated dimensions and column widths, chooses portrait or landscape and letter, legal, or tabloid paper, limits horizontal page splitting while preserving readable text, repeats the first row, and leaves vertical pagination unlimited. On release, staff can file both the original spreadsheet and PDF (default), only the spreadsheet, or only the PDF. The uploaded spreadsheet is never modified.</td></tr>
              <tr><td>Apple Pages (<code>.pages</code>)</td><td>Converted with LibreOffice to a PDF review derivative for the intake queue. The original Pages file remains available from the intake history but is not editable inside V7.</td></tr>
              <tr><td>Text / XML (<code>.txt</code>, <code>.xml</code>)</td><td>Rendered to a readable PDF for release review. The original file stays linked from the history.</td></tr>
              <tr><td>Images (<code>.jpg</code>, <code>.jpeg</code>, <code>.png</code>, <code>.tif</code>, <code>.tiff</code>)</td><td>Converted to PDF for release review.</td></tr>
              <tr><td>Media (<code>.mp4</code>, <code>.mov</code>, <code>.m4v</code>, <code>.avi</code>, <code>.webm</code>, <code>.mp3</code>, <code>.wav</code>, <code>.m4a</code>, and related audio/video formats)</td><td>Released directly as uploaded matter documents. V7 copies the original media file into the matter folder, links it to the intake matter history, records authors/type/notes/tags, and sends the normal intake release notice. Media files do not create scan queue items and are not converted to PDF.</td></tr>
              <tr><td>Archives (<code>.zip</code>, <code>.tar</code>, <code>.tar.gz</code>, <code>.tgz</code>, <code>.gz</code>, <code>.zlib</code>, <code>.zz</code>)</td><td>Treated as containers. V7 preserves the original archive and expands safe supported files into separate intake items. Document-like files use the scan queue path; supported media entries are released directly.</td></tr>
            </tbody>
          </table>

          <h5>Temporary Browser File Names</h5>
          <p>Some browsers upload a valid document with a temporary name instead of its normal extension. For example, a Word file may arrive as <code>42685.sb-...</code>, <code>.download</code>, <code>.crdownload</code>, <code>.part</code>, <code>.tmp</code>, or with no extension at all. V7 now examines the file itself in those limited cases. If the contents identify a supported type, V7 restores a normal file name and content type before sending the item through the usual intake workflow.</p>
          <p>The intake record keeps the original name and type together with the detected name and type for auditing. V7 does not guess from arbitrary unfamiliar extensions, and a file whose contents are unknown or unsafe is still rejected. A failed item&rsquo;s source file remains preserved so staff or support can correct and retry it without asking for another upload.</p>

          <h5>Archive Attachments</h5>
          <p>Archive files are not released as a single document. The archive itself remains preserved as the parent intake item for audit purposes. Each safe file inside the archive becomes its own child intake item, with its own suggested type, status history, and release decision. Supported media inside an archive follows the same direct-release path as standalone media.</p>
          <p>If an archive contains files that should not be processed, those files are still named in the matter history with a rejection reason. Examples include executable or script files, encrypted entries, nested archives, unsafe paths, unsupported file types, system metadata files, empty files, and files over the size limit.</p>
          <p>Archive expansion uses safety limits of <strong>100 entries</strong>, <strong>50 MB per file</strong>, and <strong>250 MB total extracted content</strong>. When the 100-entry limit is reached, V7 stops inspecting the archive and records that the remaining entries were not inspected. Files that are inspected but fall outside the safety rules are rejected and listed in the history rather than silently ignored.</p>

          <h5>Incoming Faxes</h5>
          <p>Incoming Phaxio faxes usually arrive before anyone has selected a matter. V7 therefore creates a pending scan queue item and links it to a Phaxio intake record. Staff open the queue item, choose the correct matter, document type, author, date, and notes, and then release it like any other scan.</p>
          <p>If Phaxio repeats the same callback, V7 reuses the existing intake/scan item instead of creating a duplicate queue card.</p>

          <h5>Matter History</h5>
          <p>The early matter history entry shows intake progress before release. After release, the history shows the final filed document link, who released it, when it was released, source links, suggestions, and per-attachment details.</p>

          <div class="alert alert-warning"><strong>Important:</strong> Document-type rules run on the final type selected during release. For example, a court order scheduling a hearing should trigger reminder rules only after staff release it with the correct final document type.</div>
        `
      },
      {
        id: 'sinatra-scanner',
        title: 'Sinatra Scanner (Desktop App)',
        audience: 'both',
        content: `
          <h4>Sinatra Scanner</h4>
          <p>The <strong>Sinatra Scanner</strong> is a standalone desktop application that serves as the scan intake interface. It runs separately from V7 and communicates with the V7 backend via API. Scanner operators use this app to select PDFs from a shared folder, attach metadata (matter, document type, author, date), and submit them for processing in V7.</p>

          <div class="alert alert-info"><strong>Key Point:</strong> The Sinatra Scanner is a <em>data capture</em> tool only. It does not store documents or perform OCR &mdash; all processing, storage, and indexing happens on the V7 server after submission.</div>

          <h5>How It Works</h5>
          <ol>
            <li>PDFs are placed in a shared <strong>scans folder</strong> (e.g., from a physical scanner or fax server).</li>
            <li>The scanner operator opens the Sinatra Scanner app in their browser.</li>
            <li>The home page shows all PDFs in the scans folder with thumbnail previews.</li>
            <li>Click a PDF to open the <strong>upload form</strong>.</li>
            <li>Fill in the required metadata (see below).</li>
            <li>Click <strong>Upload</strong> &mdash; the PDF and metadata are sent to V7, and the file is deleted from the scans folder.</li>
            <li>The scan appears in V7&rsquo;s <strong>Scan Queue</strong> for review and release.</li>
          </ol>

          <h5>Upload Form Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Required</th></tr></thead>
            <tbody>
              <tr><td><strong>Scan Priority</strong></td><td>Dropdown of priorities from V7 (e.g., Receipt, Email, Letter, Fax). Determines processing order in V7&rsquo;s scan queue.</td><td>Yes</td></tr>
              <tr><td><strong>Matter(s)</strong></td><td>Search and select one or more matters by number or name (autocomplete). The scan will be filed to all selected matters.</td><td>Yes (at least 1)</td></tr>
              <tr><td><strong>Author(s)</strong></td><td>Search for document authors (autocomplete, filtered to contacts involved in the selected matters).</td><td>Yes (at least 1)</td></tr>
              <tr><td><strong>Document Type</strong></td><td>Hierarchical search tree of document types. Click a category to see its sub-types.</td><td>Yes</td></tr>
              <tr><td><strong>Authored On</strong></td><td>The date on the document itself (not the scan date). Date picker format: MM/DD/YYYY. Must be between 1900 and 2099.</td><td>Yes</td></tr>
              <tr><td><strong>Notes</strong></td><td>Description of the document for reference.</td><td>Yes</td></tr>
              <tr><td><strong>Amount</strong></td><td>Dollar amount on the document (e.g., a check). Only shown when scan priority is <strong>Receipt</strong> (priority 1).</td><td>Only for receipts</td></tr>
            </tbody>
          </table>

          <h5>Multi-Intranet Support</h5>
          <p>The scanner supports multiple law firms (intranets). Each intranet has its own API key. When multiple intranets are configured, buttons appear at the top of the screen to switch between them. The active intranet determines which matters, document types, and priorities are available.</p>
        `
      },
      {
        id: 'sinatra-scanner-setup',
        title: 'Scanner Setup & Configuration',
        audience: 'admin',
        content: `
          <h4>Setting Up the Sinatra Scanner</h4>

          <h5>Prerequisites</h5>
          <ul>
            <li><strong>Ruby 3.0+</strong> (managed via rbenv or Homebrew)</li>
            <li><strong>ImageMagick</strong> &mdash; for PDF thumbnail generation</li>
            <li><strong>GhostScript</strong> &mdash; required by ImageMagick for PDF processing</li>
          </ul>

          <h5>Installation (macOS)</h5>
          <ol>
            <li>Install dependencies: <code>brew install imagemagick ghostscript</code></li>
            <li>Navigate to the scanner directory and run: <code>bundle install</code></li>
          </ol>

          <h5>Running the Scanner</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Command</th><th>Mode</th><th>V7 Server</th><th>Config File</th></tr></thead>
            <tbody>
              <tr><td><code>./run_app</code></td><td>Production</td><td>https://v7.attymatter.com</td><td><code>scanner.plocfg</code></td></tr>
              <tr><td><code>./run_app_dev</code></td><td>Development</td><td>http://localhost:3000</td><td><code>scanner_dev.plocfg</code></td></tr>
            </tbody>
          </table>
          <p>The app logs to <code>logs/app.log</code> and also outputs to the console.</p>

          <h5>Configuration</h5>
          <p>Configuration is managed through the <strong>Settings page</strong> within the scanner app itself. Click the gear icon or navigate to the settings page.</p>

          <table class="table table-sm table-bordered">
            <thead><tr><th>Setting</th><th>Description</th><th>Example</th></tr></thead>
            <tbody>
              <tr><td><strong>Server URL</strong></td><td>The V7 server URL. All API requests go to <code>{url}/scans_api/</code>.</td><td><code>https://v7.attymatter.com</code></td></tr>
              <tr><td><strong>Scans Folder</strong></td><td>Path to the shared folder where scanned PDFs are placed.</td><td><code>/opt/shared/public/storage/1/scans/</code></td></tr>
              <tr><td><strong>API Keys</strong></td><td>One API key per intranet. Obtained from V7 user settings.</td><td>See &ldquo;Adding an API Key&rdquo; below.</td></tr>
            </tbody>
          </table>

          <h5>Config File Location</h5>
          <p>Settings are stored in an AES-128-CBC encrypted file:</p>
          <ul>
            <li><strong>macOS</strong>: <code>~/Library/Application Support/plo_scans/scanner.plocfg</code></li>
            <li><strong>Linux</strong>: <code>~/plo_scans/scanner.plocfg</code></li>
          </ul>
          <p>In development mode, the file is <code>scanner_dev.plocfg</code> in the same directory.</p>

          <h5>Adding an API Key</h5>
          <ol>
            <li>In V7, go to the user&rsquo;s profile and copy their <strong>API key</strong>.</li>
            <li>In the scanner settings page, enter the <strong>API key</strong> and click <strong>Verify</strong>.</li>
            <li>If valid, the system returns the intranet name and user name. Click <strong>Add</strong>.</li>
            <li>The key is saved to the encrypted config file, associated with the intranet name.</li>
          </ol>
          <p>Multiple API keys can be configured for different intranets. Remove an API key by clicking the delete button next to it on the settings page.</p>

          <h5>Authentication &amp; Security</h5>
          <ul>
            <li>The scanner app has <strong>no user login</strong> &mdash; access is controlled by whoever can reach the scanner&rsquo;s URL on the network.</li>
            <li>All API calls are authenticated via <strong>API key + HMAC signature</strong>. The signature includes a timestamp and uses a triple SHA1 digest chain to prevent tampering and replay attacks.</li>
            <li>SSL certificate verification is currently disabled for internal servers with self-signed certificates.</li>
          </ul>

          <h5>Troubleshooting</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Problem</th><th>Likely Cause</th><th>Solution</th></tr></thead>
            <tbody>
              <tr><td>No thumbnails showing</td><td>ImageMagick or GhostScript not installed</td><td>Run <code>brew install imagemagick ghostscript</code> (macOS) or install via package manager</td></tr>
              <tr><td>404 on upload</td><td>API key is invalid or expired</td><td>Re-verify the API key in scanner settings. Check V7 to ensure the user is still active.</td></tr>
              <tr><td>Connection refused</td><td>V7 server is down or server URL is wrong</td><td>Check the server URL in settings. Verify V7 is running.</td></tr>
              <tr><td>No files in scanner</td><td>Scans folder path is wrong or empty</td><td>Check the scans folder path in settings. Ensure PDFs are being placed there by the scanner hardware.</td></tr>
              <tr><td>Upload succeeds but scan not in V7</td><td>V7 API error or Sidekiq not running</td><td>Check V7 server logs. Ensure Sidekiq is running for background processing.</td></tr>
              <tr><td>&ldquo;Internal server error&rdquo; on upload</td><td>V7 backend error processing the scan</td><td>Check V7 server logs for the detailed error. Common cause: storage directory permissions.</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'scan-sorting-codes',
        title: 'Document Sorting & Scanning Codes',
        audience: 'both',
        content: `
          <h4>Sorting Code System</h4>
          <p>When processing incoming mail and scans, staff marks each document with an alphabetical code that identifies the document type. These codes help the scanner and releaser quickly classify documents into the correct document types in V7.</p>
          <div class="alert alert-info"><strong>Note:</strong> The full code list is maintained in the FAQ system. Below is a summary of the top-level categories and the most commonly used codes.</div>

          <h5>Top-Level Code Categories</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Code</th><th>Category</th><th>Key Sub-codes</th></tr></thead>
            <tbody>
              <tr><td><strong>AA</strong></td><td>Family Law</td><td>AAA Support, AAB Divorce, AAC Custody, AAD Adoption, AAG PFA</td></tr>
              <tr><td><strong>AB</strong></td><td>Real Estate</td><td>ABA Sales/Lease, ABC Deeds, ABD Appraisal, ABE Mortgage, ABG Title Search</td></tr>
              <tr><td><strong>AC</strong></td><td>Business Law</td><td>ACA Corporations, ACB Misc., ACC Agreements, ACE IRS Forms</td></tr>
              <tr><td><strong>AD</strong></td><td>Wills &amp; Estates</td><td>ADA Will, ADC Orphans Court, ADD Assets, ADE Tax Docs, ADJ POA, ADK Living Will</td></tr>
              <tr><td><strong>AE</strong></td><td>Workers&rsquo; Compensation</td><td>AEA Agreements/C&amp;R, AEB Bills, AEC Pleadings, AED Filings, AEF Notices</td></tr>
              <tr><td><strong>AF</strong></td><td>Social Security</td><td>AFA SS Notice (hearings), AFB SS Forms, AFC Bar Code Page, AFF Medical Evidence Summary</td></tr>
              <tr><td><strong>AG</strong></td><td>Civil</td><td>AGA Notices, AGB Pleadings, AGC Lien Docs, AGE Discovery, AGG Filings</td></tr>
              <tr><td><strong>AH</strong></td><td>Criminal</td><td>AHA Arrest Docs, AHB Preliminary Hearing, AHD Trial, AHE Sentencing, AHI Search Warrants</td></tr>
              <tr><td><strong>AI</strong></td><td>Pardons</td><td>AIA Application for Clemency</td></tr>
              <tr><td><strong>M</strong></td><td>Trusts</td><td>MA Agreements, MB Pet Trust</td></tr>
              <tr><td><strong>N</strong></td><td>Firearms Law</td><td>NA ATF, NB PA State Police, NC Questionnaires, NF Firearms Trust</td></tr>
              <tr><td><strong>O</strong></td><td>Other</td><td>(Catch-all)</td></tr>
              <tr><td><strong>P</strong></td><td>Immigration</td><td>PA Notices, PB Filings, PC Petitions, PD Orders</td></tr>
              <tr><td><strong>Q</strong></td><td>Personal Injury</td><td>QA Medical Bills, QB Rule 26 Info</td></tr>
              <tr><td><strong>R</strong></td><td>Intellectual Property</td><td>RA Trademarks, RB Copyrights, RC Patents</td></tr>
            </tbody>
          </table>

          <h5>Cross-Cutting Document Codes</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Code</th><th>Category</th><th>Key Sub-codes</th></tr></thead>
            <tbody>
              <tr><td><strong>B</strong></td><td>Costs</td><td>BA Costs (also copies of our checks), BB Civil Action Costs</td></tr>
              <tr><td><strong>C</strong></td><td>Letters</td><td>CA From Attorney, CB From Carrier, CC From Employer, CE Other Letters (CEA Client, CEB Judge, CEC Doctor, CED SS, CEI Government), CI Our Letters</td></tr>
              <tr><td><strong>D</strong></td><td>Miscellaneous</td><td>DA Handwritten, DB Interoffice, DC Releases, DD Employer Records, DE Federal/Tax, DI Bank Statements, DK Research, DM Pictures</td></tr>
              <tr><td><strong>E</strong></td><td>Orders</td><td>EA Interlocutory, EB Judge&rsquo;s Decision, EC Board Order, ED CW Court, EE Supreme Court, EF Civil/Criminal</td></tr>
              <tr><td><strong>F</strong></td><td>Reports</td><td>FA Medical Reports (FAA Our Records, FAB IME/IRE, FAD NARTW), FB Surveillance, FC Wage, FD Vocational, FI Police</td></tr>
              <tr><td><strong>G</strong></td><td>Testimony</td><td>GA Hearing, GB Medical, GC Other, GD Witness Statement</td></tr>
              <tr><td><strong>H</strong></td><td>Fees &amp; Payments</td><td>HA Regular Fee, HB Prepaid Expense, HF Client Checks/Cash, HG Our Invoices, HH Fee Agreements (HHA Gun Trust, HHB General Signed), HI Credit Card Transactions</td></tr>
              <tr><td><strong>L</strong></td><td>Procedural</td><td>LA Enter/Withdraw Appearance, LB Service, LC Answer/Response, LD Hearing Notices, LE Cancellations, LF Applications</td></tr>
            </tbody>
          </table>

          <h5>Internal &amp; Administrative Codes</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Code</th><th>Category</th><th>Key Sub-codes</th></tr></thead>
            <tbody>
              <tr><td><strong>J</strong></td><td>Internal (Firm)</td><td>JA Advertising, JB Credit Cards, JC Donations, JD Dues/Membership, JE Government Notices, JF Insurance, JG Internet Bills, JH Phone Bills, JI Rents, JJ Supplies, JK Gasoline, JL Services (JLA Accounting, JLAA-JLAX Payroll sub-codes), JN Utility Bills, JR Taxes (JRA 941, JRB PA-W3, etc.)</td></tr>
              <tr><td><strong>K</strong></td><td>Internal (UnConundrum)</td><td>KA Bills, KB Income, KC Bank Statements</td></tr>
            </tbody>
          </table>

          <h5>Common Code Usage Notes</h5>
          <ul>
            <li><strong>HH</strong> (Fee Agreements) is the preferred code for all fee agreements, regardless of matter type. Do NOT use AEEA or AABCA.</li>
            <li><strong>LD</strong> (Hearing Notices) is the preferred code for hearing notices. Do NOT use AEFB.</li>
            <li><strong>LC</strong> (Answer/Response) is the preferred code for answers. Do NOT use AABAD.</li>
            <li>Codes marked with <strong>AEDOA</strong> (Continuance Granted) = <strong>remove hearing from calendar</strong>.</li>
            <li><strong>Tax documents</strong>: Do NOT scan 1099 forms.</li>
            <li><strong>Permed scans</strong> are designated by Priority 4. For permed scans, you do NOT need to fill in Date Created or Author.</li>
          </ul>

          <h5>How Codes Map to Document Types</h5>
          <p>The alphabetical codes correspond to document types in V7. When releasing a scan, the releaser selects the V7 document type that matches the code written on the physical document. The code system ensures consistent classification across all staff members.</p>
        `
      }
    ]
  },

  // ============================================================
  // 10. BILLING & TIME ENTRY
  // ============================================================
  {
    id: 'billing',
    title: 'Billing & Time Entry',
    icon: 'fa-clock',
    audience: 'both',
    subsections: [
      {
        id: 'time-entries',
        title: 'Recording Time & Billing Fields',
        audience: 'both',
        content: `
          <h4>Billing Record Fields</h4>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td><strong>activity_id</strong></td><td>Type of work performed</td><td>Selected from Activities list (Attorney, Paralegal, Document Preparation, etc.)</td></tr>
              <tr><td><strong>matter_id</strong></td><td>Which matter this time is for</td><td>Required</td></tr>
              <tr><td><strong>user_id</strong></td><td>Who performed the work</td><td>Defaults to current user</td></tr>
              <tr><td><strong>quantity</strong></td><td>Time in decimal hours</td><td>e.g., 1.5 = 1 hour 30 minutes. Attorney activities default to minimum 0.2 (12 min).</td></tr>
              <tr><td><strong>quantity_actual</strong></td><td>Actual time spent</td><td>May differ from billed quantity (e.g., actual 2.5 hrs, billed 2.0 hrs)</td></tr>
              <tr><td><strong>rate</strong></td><td>Hourly rate</td><td>Auto-populated from activity rate lookup (see precedence below)</td></tr>
              <tr><td><strong>description</strong></td><td>Work description</td><td>Appears on invoices. Write clear, professional descriptions.</td></tr>
              <tr><td><strong>rendered_at</strong></td><td>Date work was performed</td><td>Date (not the date the entry was created)</td></tr>
              <tr><td><strong>is_billable</strong></td><td>Whether to include on invoices</td><td>Toggle to exclude non-billable time from invoicing</td></tr>
              <tr><td><strong>is_authorized</strong></td><td>Approved for invoicing</td><td>Only authorized entries are included in invoices</td></tr>
              <tr><td><strong>is_rejected</strong></td><td>Rejected by reviewer</td><td>Rejected entries are excluded from billing</td></tr>
              <tr><td><strong>is_hourly</strong></td><td>Hourly vs flat</td><td>Determined by activity type and matter rate type</td></tr>
            </tbody>
          </table>

          <h5>Billing Statuses</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Status</th><th>Meaning</th></tr></thead>
            <tbody>
              <tr><td><strong>Pending</strong></td><td>Billable but not yet authorized. Waiting for review.</td></tr>
              <tr><td><strong>Authorized</strong></td><td>Approved and ready for invoicing.</td></tr>
              <tr><td><strong>Billed</strong></td><td>Converted to a Bill record (included on an invoice).</td></tr>
              <tr><td><strong>Not Billable</strong></td><td>Marked as non-billable. Tracked but not invoiced.</td></tr>
              <tr><td><strong>Rejected</strong></td><td>Rejected by a reviewer. Not invoiced.</td></tr>
            </tbody>
          </table>

          <h5>Authorization</h5>
          <p><strong>Lead attorneys</strong> and <strong>accountants</strong> can authorize billing entries. Other matter users can create entries but cannot authorize them.</p>
        `
      },
      {
        id: 'activity-rates',
        title: 'Activities & Rate Lookup',
        audience: 'both',
        content: `
          <h4>Activity Types</h4>
          <p>Activities define the categories of billable work. Some are <strong>static</strong> (cannot be deleted):</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Activity</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td>Attorney</td><td>Standard attorney time. Min 0.2 hours (12 min).</td></tr>
              <tr><td>Attorney-Federal</td><td>Federal case attorney time. Min 0.2 hours.</td></tr>
              <tr><td>Document Preparation</td><td><em>Static.</em> Typically for clerical document work.</td></tr>
              <tr><td>Flat Fee</td><td>No rate/time/total &mdash; quantity always 1.</td></tr>
              <tr><td>Lead Attorney</td><td>Lead attorney time. Min 0.2 hours.</td></tr>
              <tr><td>Lead Attorney-Federal</td><td>Federal case lead attorney time.</td></tr>
              <tr><td>No Charge</td><td><em>Static.</em> Rate=0, not billable.</td></tr>
              <tr><td>Paralegal</td><td>Paralegal time.</td></tr>
              <tr><td>Paralegal-Federal</td><td>Federal case paralegal time.</td></tr>
              <tr><td>Staff</td><td>Administrative staff time.</td></tr>
            </tbody>
          </table>

          <h4>Rate Lookup Precedence</h4>
          <p>When a billing entry is created, the rate is resolved in this order (first match wins):</p>
          <ol>
            <li><strong>Matter-specific rate</strong> &mdash; Custom rate set for this activity on this specific matter.</li>
            <li><strong>User-specific rate</strong> &mdash; Rate set for this user for this activity type.</li>
            <li><strong>Intranet ActivityMatterRate</strong> &mdash; Default rate for this activity + matter type combination.</li>
            <li><strong>Intranet-level default</strong> &mdash; Global default rate for this activity.</li>
          </ol>

          <h5>Editing Activity/Matter Rates</h5>
          <p>To edit a default ActivityMatterRate, open the Activity Matter Rate record and click <strong>Edit</strong>. The record page shows the activity, matter type, and rate type as context. The editable fields are:</p>
          <ul>
            <li><strong>Rate</strong> &mdash; the default amount used when this ActivityMatterRate is selected by the lookup.</li>
            <li><strong>Global</strong> &mdash; when checked, the rate can act as an intranet-level default instead of applying only to a specific matter type.</li>
          </ul>
          <p>Click <strong>Save</strong> to apply the change. New billing entries use the updated rate after it is saved; existing billing entries are not automatically recalculated.</p>

          <h5>Rate Type Behavior</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Rate Type</th><th>Behavior</th></tr></thead>
            <tbody>
              <tr><td><strong>Hourly</strong></td><td>Rate &times; Time = Total. Standard time-based billing.</td></tr>
              <tr><td><strong>Flat Fee</strong></td><td>No rate, no time, quantity=1. Billed as a single fixed amount.</td></tr>
              <tr><td><strong>No Fee</strong></td><td>Rate=0. Entry tracked but not billable (pro bono).</td></tr>
              <tr><td><strong>Percentage</strong></td><td>No rate, quantity=1. Used for contingency fee matters.</td></tr>
            </tbody>
          </table>
          <p>Access rate configuration from <strong>Accounting &rarr; Activity Rates</strong>.</p>
        `
      },
      {
        id: 'billing-adjustments',
        title: 'Billing Adjustments',
        audience: 'both',
        content: `
          <h4>Adjusting Billing Entries</h4>
          <p><strong>BillingAdjustment</strong> records modify the amount of a billing entry without changing the original. Uses include:</p>
          <ul>
            <li>Discounts (reduce the billed amount)</li>
            <li>Write-offs (partially or fully write off a charge)</li>
            <li>Corrections (adjust for errors)</li>
          </ul>
          <p>Adjustments are tracked separately from the original entry, maintaining a clear audit trail.</p>
          <h5>Payment Reversal Notices</h5>
          <p>When a payment is reversed, V7 emails the reversal details to every address configured under the firm&rsquo;s <strong>Accounting &rarr; Email Address</strong> settings. If no accounting recipient is configured, V7 sends the notice to the user who performed the reversal and records a warning in the application log.</p>
        `
      },
      {
        id: 'billing-import',
        title: 'Billing Import (from External Software)',
        audience: 'admin',
        content: `
          <h4>Importing Billing Entries from External Software</h4>
          <p>The Billing Import tool lets you bring time entries from an attorney&rsquo;s outside billing software directly into V7 as unbilled billing entries, ready for invoicing through the normal workflow.</p>
          <p>Access it from <strong>Accounting &rarr; Entries &rarr; Billings Import</strong>, or from the matter&rsquo;s Accounting secondary menu.</p>

          <h5>Step-by-Step</h5>
          <ol>
            <li>
              <strong>Enter the matter number</strong> and click <strong>Lookup</strong>. The system will confirm the matter name and populate the attorney and activity lists.
            </li>
            <li>
              <strong>Select the attorney</strong> from the dropdown. Only attorneys assigned to the matter appear.
            </li>
            <li>
              <strong>Select the activity type</strong> (e.g., Lead Attorney, Attorney, Paralegal).
            </li>
            <li>
              <strong>Rate</strong> is automatically populated based on the activity rate lookup for that attorney and matter. Lead attorneys and accountants may edit the rate; other users see it as read-only.
            </li>
            <li>
              <strong>Upload the CSV file</strong> by dragging and dropping it onto the drop zone, or clicking to browse. Only <code>.csv</code> files are accepted.
            </li>
            <li>
              Click <strong>Import Billings</strong>. The system will detect the file format automatically and import the entries.
            </li>
          </ol>

          <div class="alert alert-info">
            <strong>What happens to the imported entries?</strong> Each entry is created as an unbilled billing record (<code>bill_id = 0</code>) linked to the selected matter and attorney. Lead attorneys&rsquo; own entries are automatically authorized. Other entries are created as pending and must be authorized before invoicing.
          </div>

          <h5>Duplicate Detection</h5>
          <p>The importer skips any row where a billing with the same <strong>date, hours, and description</strong> already exists on the matter for that attorney. Re-running the same import is safe &mdash; duplicates are counted in the &ldquo;Skipped&rdquo; total.</p>

          <h5>Already-Billed Rows</h5>
          <p>Rows that the source software has already invoiced are automatically skipped. The import summary shows how many were skipped for this reason. For software that does not export a billed/unbilled indicator (see table below), export only unbilled entries from within that software before importing.</p>

          <h5>Supported Formats</h5>
          <p>The system automatically detects the source software from the CSV column headers. No configuration is needed &mdash; just drop in the file.</p>
          <table class="table table-sm table-bordered">
            <thead>
              <tr><th>Software</th><th>Billed Detection</th><th>Notes</th></tr>
            </thead>
            <tbody>
              <tr><td><strong>Clio</strong></td><td>Bill # column</td><td>Full detection. Export the time entry detail report (not the billing summary).</td></tr>
              <tr><td><strong>MyCase</strong></td><td>Status = Billed</td><td>Full detection.</td></tr>
              <tr><td><strong>PracticePanther</strong></td><td>Billing Status column</td><td>Full detection.</td></tr>
              <tr><td><strong>TimeSolv</strong></td><td>Status column</td><td>Full detection.</td></tr>
              <tr><td><strong>Sage Timeslips</strong></td><td>Billing Status column</td><td>Full detection.</td></tr>
              <tr><td><strong>PCLaw</strong></td><td>None &mdash; filter first</td><td>Shared format with Time Matters and Juris. Filter to unbilled in PCLaw before exporting (Data Entry &rarr; Register &rarr; Time &rarr; Unbilled).</td></tr>
              <tr><td><strong>Time Matters</strong></td><td>None &mdash; filter first</td><td>Same export format as PCLaw. Filter to unbilled before exporting.</td></tr>
              <tr><td><strong>Juris</strong></td><td>None &mdash; filter first</td><td>Same export format as PCLaw/Time Matters. Filter to unbilled before exporting.</td></tr>
              <tr><td><strong>AbacusLaw</strong></td><td>None &mdash; filter first</td><td>Export only unbilled time tickets from AbacusLaw.</td></tr>
              <tr><td><strong>CosmoLex</strong></td><td>None &mdash; filter first</td><td>Billing-Type reflects billability, not invoice status. Export WIP (work in progress) entries only.</td></tr>
              <tr><td><strong>Tabs3</strong></td><td>None &mdash; filter first</td><td>Export only unbilled entries from Tabs3 before importing.</td></tr>
            </tbody>
          </table>

          <div class="alert alert-warning">
            <strong>Unsupported format?</strong> If the system cannot identify the file format, it will display the column headers it found along with what each supported format requires. Contact your system administrator with a sample file to have support added.
          </div>

          <h5>Reversing an Import</h5>
          <p>The <strong>Previous Imports</strong> table at the bottom of the page lists all past imports. An import can be reversed as long as <strong>none of its entries have been invoiced yet</strong>.</p>
          <ul>
            <li>Imports with all entries still unbilled show a green <strong>Active</strong> badge and a <strong>Reverse</strong> button.</li>
            <li>Imports where one or more entries have been invoiced show a yellow <strong>Invoiced (locked)</strong> badge &mdash; these cannot be reversed. Void the invoice first if a reversal is needed.</li>
            <li>Already-reversed imports show a grey <strong>Reversed</strong> badge.</li>
          </ul>
          <p>Reversing an import <strong>permanently deletes</strong> the billing entries created by that import. This cannot be undone.</p>
        `
      }
    ]
  },

  // ============================================================
  // 11. INVOICES & STATEMENTS
  // ============================================================
  {
    id: 'invoices',
    title: 'Invoices & Statements',
    icon: 'fa-file-invoice-dollar',
    audience: 'both',
    subsections: [
      {
        id: 'generating-invoices',
        title: 'Generating Invoices',
        audience: 'both',
        content: `
          <h4>Invoice vs Statement</h4>
          <table class="table table-sm table-bordered">
            <thead><tr><th></th><th>Invoice</th><th>Statement</th></tr></thead>
            <tbody>
              <tr><td><strong>Scope</strong></td><td>Covers entire matter family tree (parent + all sub-matters)</td><td>Single matter only</td></tr>
              <tr><td><strong>Content</strong></td><td>New unbilled charges since last invoice</td><td>Complete account history with running balance</td></tr>
              <tr><td><strong>Aggregation</strong></td><td>Aggregates billing across sub-matters</td><td>No aggregation</td></tr>
            </tbody>
          </table>

          <h4>Creating an Invoice</h4>
          <ol>
            <li>Navigate to <strong>Accounting &rarr; INVOICES+</strong> (recommended) or <strong>Accounting &rarr; Entries &rarr; Invoices</strong>.</li>
            <li>The INVOICES+ page shows matters with unbilled activity since a configurable date.</li>
            <li>Select matters to invoice.</li>
            <li>Review charges, expenses, and payments.</li>
            <li>Click <strong>Create Invoices</strong>. V7 queues the invoice run in Sidekiq and returns to the invoice list immediately; the background job creates PDFs, sends or prints invoices, and creates LawPay/PayLoad links without holding the browser request open.</li>
          </ol>
          <p>If another invoice run is already active, V7 blocks the new run and asks you to wait. This prevents overlapping bulk invoice jobs from double-invoicing the same billings.</p>
          <p>If V7 cannot queue the run, it returns to the invoice screen with an error instead of continuing to spin. No invoices or bills are created by a failed queue attempt; correct the reported problem and submit the run again.</p>

          <h5>Associated Matters</h5>
          <p>V7 treats a parent matter and all of its associated child matters as one invoice group. The monthly and manual invoice runs inspect the <strong>entire group</strong> for an outstanding balance or new unbilled activity. An invoice is therefore created when billable activity exists on a child matter even if the parent matter has no charges of its own. The generated invoice records and document copies remain linked to the appropriate matters in the group.</p>
          <div class="alert alert-warning"><strong>Troubleshooting:</strong> Do not remove a valid matter association merely to make an invoice run. If an associated matter has authorized, billable activity but no invoice PDF is created, leave the association intact and report the parent and child matter numbers so Accounting or Technical Support can review the group.</div>

          <h5>Outgoing Invoice Totals</h5>
          <p>Each generated invoice (OutgoingInvoice) tracks these amounts:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Meaning</th></tr></thead>
            <tbody>
              <tr><td><strong>total_previous</strong></td><td>Amount previously invoiced (running total of prior invoices)</td></tr>
              <tr><td><strong>total_current</strong></td><td>New charges on this invoice</td></tr>
              <tr><td><strong>total_escrow</strong></td><td>Escrow/trust funds held</td></tr>
              <tr><td><strong>total_expense</strong></td><td>Reimbursable expenses</td></tr>
              <tr><td><strong>total_payments</strong></td><td>Payments received</td></tr>
              <tr><td><strong>total_due</strong></td><td>Net balance due (excludes escrow)</td></tr>
            </tbody>
          </table>
          <p>Invoice records include both <code>created_at</code> and <code>updated_at</code>. Older invoices were backfilled with <code>updated_at = created_at</code>; later invoice edits, withdrawals, and provider-status updates advance <code>updated_at</code> through normal Rails timestamp handling.</p>

          <h4>Invoice Delivery</h4>
          <ul>
            <li><strong>Email</strong>: If the matter has <code>email_invoice = true</code> AND billing contact has an email &rarr; PDF sent via email with the selected online payment provider link (LawPay or PayLoad).</li>
            <li><strong>Mail</strong>: Otherwise &rarr; printed and sent via postal mail.</li>
          </ul>

          <h5>Payments Received Outside PayLoad</h5>
          <p>When a check, cash payment, or payment from another provider is entered in V7 and <strong>applied to billed fees or expenses</strong>, V7 closes the obsolete PayLoad invoice so the client is not asked to pay the same balance again. If a balance remains, V7 replaces the PayLoad request with one for the remaining amount. Money recorded only as an escrow contribution does not close an invoice; the update occurs when escrow is actually disbursed and applied to billed charges.</p>
          <div class="alert alert-info"><strong>What staff should do:</strong> Record the outside receipt normally and allocate it to the correct charges. Do not create a pretend PayLoad transaction. The PayLoad update runs in the background after the accounting transaction commits, and a temporary provider failure does not undo the receipt.</div>
          <div class="alert alert-warning"><strong>If PayLoad cannot be updated:</strong> V7 keeps the accounting receipt, marks the invoice with a PayLoad settlement-sync error, reports the failure to technical monitoring, and retries the background job. Until a retry succeeds, verify the PayLoad request before asking the client to use it.</div>
        `
      },
      {
        id: 'lawpay-payments',
        title: 'LawPay Payment Integration',
        audience: 'both',
        content: `
          <h4>How LawPay Links Work</h4>
          <p>V7 generates payment links differently based on the invoice type:</p>

          <h5>Single-Matter Email Invoice (Invoice API)</h5>
          <ul>
            <li>V7 creates a <strong>LawPay Invoice</strong> via the API with line items showing each charge.</li>
            <li>Contact information is synced to LawPay (name, address, email).</li>
            <li>The client sees a detailed breakdown of what they&rsquo;re paying for.</li>
            <li>PDF attachment is included with the LawPay email.</li>
          </ul>

          <h5>Multi-Matter or Mailed Invoice (Hosted Payment Page)</h5>
          <ul>
            <li>A <strong>Hosted Payment Page (HPP)</strong> URL is generated with the matter reference and amount pre-filled.</li>
            <li>URL format: <code>https://secure.lawpay.com/pages/[firm]/tr-pay-link?reference=[matter]&amp;amount=[total_due]</code></li>
            <li>No line-item detail &mdash; just a total amount.</li>
            <li>Contact info is NOT synced (generic payment page).</li>
          </ul>

          <h4>Matter Settings for Staff</h4>
          <ul>
            <li><strong>Billing Method</strong>: choose <strong>LawPay</strong> for LawPay-integrated payment links or <strong>PayLoad</strong> for PayLoad-integrated invoice links. Use <strong>Traditional (V7)</strong> for manual payment handling.</li>
            <li><strong>Email Invoice</strong>: keep checked when V7 should email the client the firm invoice with the selected provider&rsquo;s payment link.</li>
            <li><strong>LawPay Deposit Account</strong>: leave blank for the automatic operating LawPay account, or select a specific LawPay-enabled bank account such as <strong>Escrow Account (Escrow)</strong> when the payment should deposit to trust/IOLTA.</li>
          </ul>
          <div class="alert alert-info"><strong>LawPay enabled switch:</strong> If <code>lawpay.enabled</code> is explicitly set to <code>false</code>, <code>0</code>, <code>no</code>, <code>off</code>, or <code>disabled</code>, V7 treats LawPay matters as manual during invoicing and does not process LawPay callbacks, workers, refunds, or manual queue posting.</div>
          <div class="alert alert-warning"><strong>Important:</strong> The matter <strong>Type</strong> does not decide whether LawPay deposits to operating or escrow. A trust-drafting matter is still operating unless the LawPay Deposit Account is set to an escrow LawPay account.</div>

          <h4>Administrative Bank Setup</h4>
          <p>Administrators control which V7 bank accounts appear in the LawPay Deposit Account picker from <strong>Accounting &rarr; Bank Accounts</strong>.</p>
          <ul>
            <li>Enter the matching LawPay live and/or test account ID under <strong>Remote payment accounts</strong>. No separate LawPay checkbox is required.</li>
            <li>Set <code>is_escrow = true</code> only for trust/IOLTA/escrow LawPay accounts; operating accounts should have <code>is_escrow = false</code>.</li>
            <li>Keep the bank and mapping active. The matter picker uses mappings for the firm&rsquo;s current live/test environment.</li>
          </ul>
          <div class="alert alert-info"><strong>Tip:</strong> API credentials, OAuth tokens, webhook token, and <code>test_mode</code> are configured in <strong>Settings</strong> under the <code>lawpay</code> group. Deposit routing uses the V7 <strong>Bank Account</strong> records, not the old <code>trust_account_id</code>/<code>operating_account_id</code> settings.</div>
        `
      },
      {
        id: 'lawpay-testing',
        title: 'LawPay Testing',
        audience: 'admin',
        content: `
          <h4>What Test Mode Means</h4>
          <p>When the V7 <code>lawpay.test_mode</code> setting is <code>true</code>, V7 sends LawPay API calls to the LawPay sandbox. V7 may still send the invoice email from the real V7 environment, but the payment page and receipt belong to LawPay&rsquo;s test account and do not move real money.</p>
          <div class="alert alert-warning"><strong>Important:</strong> Test-mode invoices and payments cannot be converted into live LawPay payments later. To test live processing, turn test mode off only after confirming the live LawPay credentials, bank account IDs, and webhook URL.</div>

          <h4>Choose the Webhook Target First</h4>
          <p>The webhook target determines where V7 records the payment after LawPay accepts it.</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Test Goal</th><th>LawPay Webhook URL</th><th>What Gets Updated</th></tr></thead>
            <tbody>
              <tr>
                <td>Production V7 with LawPay sandbox</td>
                <td><code>https://v7.attymatter.com/lawpay/webhook/&lt;webhook_token&gt;</code></td>
                <td>Production V7 receives the callback and can create the <code>LawpayPayment</code> record.</td>
              </tr>
              <tr>
                <td>Localhost V7 through ngrok</td>
                <td><code>https://agrostographical-nonpursuant-loralee.ngrok-free.dev/lawpay/webhook</code></td>
                <td>Only the local Rails server receives the callback. Production V7 will not record the payment.</td>
              </tr>
            </tbody>
          </table>
          <p>Use <code>bundle exec rake lawpay:webhook_urls</code> or <code>bundle exec rake lawpay:show_settings</code> to confirm the production tokenized URL.</p>

          <h4>Localhost Test with ngrok</h4>
          <ol>
            <li>Start V7 locally on port 3000: <code>bundle exec rails s -b 0.0.0.0 -p 3000</code>.</li>
            <li>Start the reserved ngrok tunnel: <code>ngrok http --url=agrostographical-nonpursuant-loralee.ngrok-free.dev 3000</code>.</li>
            <li>Set the LawPay sandbox webhook URL to <code>https://agrostographical-nonpursuant-loralee.ngrok-free.dev/lawpay/webhook</code>.</li>
            <li>Confirm the tunnel reaches V7 before making a payment:
              <pre><code>curl -i -X POST https://agrostographical-nonpursuant-loralee.ngrok-free.dev/lawpay/webhook \\
  -H 'Content-Type: application/json' \\
  -H 'ngrok-skip-browser-warning: true' \\
  --data '{"type":"ping","data":{}}'</code></pre>
              A healthy local callback returns <code>{"received":true}</code>.</li>
            <li>Create or select a LawPay test invoice, follow its payment link, and pay a small amount.</li>
            <li>Check the local V7 <strong>LawPay Payments</strong> screen or the newest <code>LawpayPayment</code> row to confirm the callback arrived.</li>
          </ol>
          <div class="alert alert-info"><strong>Shortcut:</strong> To run ngrok detached during a longer test window, use <code>screen -dmS lawpay-ngrok ngrok http --url=agrostographical-nonpursuant-loralee.ngrok-free.dev 3000</code>.</div>

          <h4>Production V7 Test with LawPay Sandbox</h4>
          <ol>
            <li>Confirm <code>lawpay.test_mode = true</code> for the target intranet.</li>
            <li>Confirm LawPay sandbox is configured with the tokenized production V7 webhook URL.</li>
            <li>Create the V7 invoice and follow the LawPay payment link from the email.</li>
            <li>Pay a small amount using a sandbox card.</li>
            <li>Confirm the payment appears in LawPay sandbox and in V7 as a <code>LawpayPayment</code> with processing status <code>unprocessed</code> until bookkeeping processing is completed. Sandbox card payments may arrive as <code>transaction.authorized</code> with <code>auto_capture=true</code>; V7 records that as the accepted payment event.</li>
          </ol>
          <p>If the sandbox webhook is still pointed at ngrok, production V7 will not record the callback. If ngrok is not running, LawPay may accept the payment but V7 will not receive anything.</p>

          <h4>Testing Checklist and Watch Points</h4>
          <ul>
            <li><strong>Before testing:</strong> verify the LawPay webhook target. Ngrok means the callback records only in local V7; the tokenized <code>v7.attymatter.com</code> URL means production V7 records it.</li>
            <li><strong>Mode must match:</strong> sandbox/test transactions can only be found, refunded, or voided with sandbox credentials. Live transactions can only be found, refunded, or voided with live credentials. If V7 has <code>lawpay.test_mode=false</code>, a refund for an old sandbox transaction will fail because the live API cannot see it.</li>
            <li><strong>Local setup:</strong> keep Rails on port 3000, keep ngrok pointed at port 3000, and run local Rails/Sidekiq with <code>HOST_DEVL=10.1.1.5</code> to avoid local DNS failures against <code>mx2.princelaw.com</code>.</li>
            <li><strong>After payment:</strong> check ngrok request history at <code>http://127.0.0.1:4040</code>, then check the newest <code>LawpayPayment</code> row for <code>amount</code>, <code>invoice_reference</code>, <code>matter_id</code>, <code>processing_status</code>, and <code>receipt_id</code>.</li>
            <li><strong>Expected sandbox payload:</strong> card payments may arrive as <code>transaction.authorized</code> with <code>auto_capture=true</code>, integer cents such as <code>1000</code> for $10.00, and references like <code>Matter #10711</code>.</li>
            <li><strong>Worker failures:</strong> if <code>processing_status</code> remains <code>unprocessed</code>, inspect Sidekiq retries and logs. A database/DNS/pool failure means the webhook was received, but the receipt/deposit posting did not finish.</li>
            <li><strong>Notices:</strong> LawPay posting failures should generate a <strong>LawPay Payment Posting Failed</strong> email with the post-failure V7 status, including whether a receipt or entry group was created.</li>
          </ul>

          <h4>Manual Processing from the LawPay Queue</h4>
          <p>The <strong>LawPay Payment Queue</strong> is a fallback for callbacks V7 received but could not safely post automatically. Use it only when a <code>LawpayPayment</code> is still <code>unprocessed</code>, usually because the matter or LawPay bank account could not be resolved during webhook processing.</p>
          <ul>
            <li>Manual processing requires selecting the correct matter. A matched contact is helpful, but the matter is what drives bookkeeping.</li>
            <li>Manual processing uses the same <code>LawpayPaymentProcessor</code> path as the webhook. It creates the LawPay payment entry group, deposit child, receipt child, ledgers, and payment bills.</li>
            <li>Do not create a generic receipt for a LawPay payment. V7 blocks <code>lawpay_payment_id</code> from the generic receipt create path so LawPay payments cannot be marked processed without the LawPay-specific bookkeeping tree.</li>
            <li>If the payment is already <code>processed</code>, the queue must not offer manual processing. Use the <strong>View LawPay Entry Group</strong> link to inspect the posted tree.</li>
          </ul>

          <h4>Refund Testing Notes</h4>
          <ul>
            <li>Refunds are submitted to LawPay first; V7 records bookkeeping only after LawPay sends the refund callback.</li>
            <li>Refund failures leave the original V7 receipt/payment intact and set <code>refund_failed=true</code> with the reason returned by LawPay.</li>
            <li>If the reason is <code>Requested resource not found</code> or similar, first verify that <code>lawpay.test_mode</code> matches the mode that created the charge and that the charge ID belongs to the same LawPay account.</li>
            <li>For an authorized charge that has not settled, LawPay may require voiding instead of refunding. After settlement, use the refund flow.</li>
          </ul>

          <h4>Card Entry Notes</h4>
          <p>Password managers may visually fill LawPay&rsquo;s hosted card fields without completing LawPay&rsquo;s secure tokenization. If LawPay says <strong>Card Number is required</strong> or <strong>CVV is required</strong> even though values are visible, clear those fields and type the card number and CVV manually.</p>

          <h4>Security Notes</h4>
          <ul>
            <li>Do not store ngrok credentials, one-time codes, API secrets, or LawPay passwords in this manual.</li>
            <li>If credentials or recovery codes are exposed during troubleshooting, rotate them after the test window.</li>
            <li>Use the tokenized production webhook URL for production-facing tests whenever the payment must be recorded in production V7.</li>
          </ul>
        `
      },
      {
        id: 'lawpay-staff-procedures',
        title: 'LawPay Staff Procedures',
        audience: 'both',
        content: `
          <h4>LawPay Access</h4>
          <p>The following staff members have access to LawPay for Quick Bill emails and payment processing: <strong>Angi, Tammy, Chris, Michele, and Howard</strong>. Administration login credentials are in a separate restricted FAQ.</p>
          <div class="alert alert-warning"><strong>Important:</strong> PLO and CRDF have <strong>separate LawPay accounts</strong>. Always verify you are in the correct account before processing.</div>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Entity</th><th>Payment Link</th></tr></thead>
            <tbody>
              <tr><td><strong>PLO</strong></td><td><code>https://secure.lawpay.com/pages/princelaw/trust</code></td></tr>
              <tr><td><strong>CRDF</strong></td><td><code>https://secure.lawpay.com/pages/civilrightsdefensefirm/trust</code></td></tr>
            </tbody>
          </table>
          <p>Clients can also pay from the firm website by clicking <strong>&ldquo;Make a Payment&rdquo;</strong> (top right of homepage).</p>

          <h4>Sending a Quick Bill Email</h4>
          <ol>
            <li>Log in to LawPay.</li>
            <li>Click the green <strong>&ldquo;Quick Bill&rdquo;</strong> button (top right).</li>
            <li>Select <strong>Trust</strong> payment page.</li>
            <li>Enter: amount, matter number + client name, client&rsquo;s email address.</li>
            <li>Add a message in the email body if appropriate. Include a note asking the client to <strong>sign their receipt</strong> (can be done with mouse, touchpad, or smartphone screen).</li>
            <li>Click <strong>Send</strong>.</li>
          </ol>
          <p>When the client completes payment, a receipt is emailed to <code>accounting@princelaw.com</code> and uploaded to the scan queue.</p>

          <h4>Manual Credit Card Entry (In-Person)</h4>
          <div class="alert alert-danger"><strong>Required:</strong> A <strong>signed Credit Card Authorization form</strong> must be received BEFORE entering any manual transaction when the client is not present. For in-person payments, print the receipt and have the client sign it.</div>
          <ol>
            <li>Log in to LawPay. Click <strong>&ldquo;Charge&rdquo;</strong> on the left sidebar.</li>
            <li>Select <strong>&ldquo;Accept Payment&rdquo;</strong> under the <strong>Trust Payment</strong> header. <em>Always use Trust Payment &mdash; NEVER use Invoice Payment.</em></li>
            <li>Enter: amount, matter # + client name, billing information, email address, card information.</li>
            <li>Click <strong>Submit Payment</strong>.</li>
            <li>If successful, the receipt appears. For in-person payments, print the receipt and have the client sign.</li>
            <li>Give the signed receipt to the incoming mail person for scanning. Notify accounting.</li>
          </ol>
          <p>Receipts are automatically emailed to the client and to accounting. Failed charges also generate notifications.</p>
          <p>If a manual charge fails, contact the client directly to advise them and discuss alternative payment methods.</p>

          <h4>Voiding a Transaction</h4>
          <p>Voiding is only possible <strong>BEFORE end-of-day processing</strong>. After that, a refund must be issued by Angi/Admin.</p>
          <ol>
            <li>Click on the transaction receipt to void.</li>
            <li>Select <strong>&ldquo;Void Transaction&rdquo;</strong> from the right-side menu.</li>
            <li>Follow the prompts.</li>
          </ol>

          <h4>Resending a Receipt</h4>
          <ol>
            <li>Click on the transaction receipt.</li>
            <li>Click <strong>&ldquo;Resend Receipt&rdquo;</strong> from the right-side menu.</li>
            <li>Verify the email address and click <strong>Resend</strong>.</li>
          </ol>

          <h4>Credit Card Authorization Form</h4>
          <p>If a client refuses to pay directly (via website or email link), a <strong>signed credit card authorization form</strong> is required before any staff member processes a payment:</p>
          <ul>
            <li>For existing clients: create the form in the client&rsquo;s matter.</li>
            <li>For new intakes (no matter yet): use the blank form in <strong>Matter 10799</strong>.</li>
            <li>If the client does not return a signed form, no credit card payment will be processed. Advise them they may pay via cash or check, but their appointment will be delayed until payment is received.</li>
          </ul>
        `
      },
      {
        id: 'lawpay-workflow',
        title: 'LawPay Workflow (Technical)',
        audience: 'admin',
        content: `
          <p>End-to-end flow showing how V7 integrates with the LawPay/AffiniPay (8am) API for invoice creation, client payment, webhook processing, and bookkeeping. Code lives in <code>app/services/lawpay_service.rb</code>, <code>app/services/lawpay_invoice_service.rb</code>, and <code>app/controllers/lawpay_*_controller.rb</code>.</p>

          <div class="alert alert-warning">
            <strong>Implementation status:</strong> the bank-account routing model and webhook bookkeeping described below reflect the current LawPay design. Payments are stored as <code>LawpayPayment</code> rows and processed through <code>LawpayPaymentProcessor</code> into the LawPay entry-group tree. Refund bookkeeping remains webhook-driven so V7 does not reverse books until LawPay confirms the refund.
          </div>

          <h4>1. One-Time Setup (per intranet)</h4>
          <p>Credentials &amp; webhook config stored as <code>Setting</code> rows under <code>group='lawpay'</code>:</p>
          <ul>
            <li><code>oauth_client_id</code> / <code>oauth_client_secret</code> &mdash; preferred auth (OAuth 2.0 client_credentials, scope <code>payments</code>)</li>
            <li><code>api_key</code> / <code>api_secret</code> &mdash; fallback (Basic auth, secret as username)</li>
            <li><code>test_api_key</code> / <code>test_api_secret</code> / <code>test_merchant_id</code> &mdash; sandbox credentials used by the webhook/transaction service when <code>test_mode = true</code></li>
            <li><code>api_key_test</code> / <code>api_secret_test</code> &mdash; sandbox credential names used by the invoice/contact service when <code>test_mode = true</code>; if blank, that service falls back to <code>api_key</code> / <code>api_secret</code></li>
            <li><code>merchant_id</code></li>
            <li><code>webhook_token</code> &mdash; per-intranet, embedded in webhook URL</li>
            <li><code>webhook_secret</code> &mdash; HMAC-SHA256 signature verification</li>
            <li><code>test_mode</code> &mdash; <code>true</code>|<code>false</code>, toggles sandbox vs production</li>
          </ul>
          <p>Webhook URL registered in LawPay portal: <code>https://v7.attymatter.com/lawpay/webhook/&lt;webhook_token&gt;</code></p>

          <p><strong>Bank account mapping</strong>: each eligible V7 <code>BankAccount</code> has a <code>payment_provider_bank_accounts</code> row containing:</p>
          <ul>
            <li><code>provider = lawpay</code> and the applicable <code>environment</code> (<code>live</code> or <code>test</code>)</li>
            <li><code>remote_account_id</code> &mdash; the corresponding LawPay-side account ID returned by LawPay</li>
            <li><code>purpose</code> &mdash; <code>operating</code> or <code>escrow</code>, derived from the V7 bank&rsquo;s Escrow flag</li>
          </ul>
          <p>Together these create a relational map between V7 banks and LawPay&rsquo;s deposit destinations. Selection lists are scoped automatically by <code>intranet_id</code> &mdash; a CRDF matter never sees PLO bank accounts.</p>

          <h4>2. Send Invoice (Firm Action)</h4>
          <p>Staff clicks &ldquo;Send via LawPay&rdquo; on an Invoice &rarr; <code>LawpayInvoiceService#create_invoice</code>:</p>
          <ol>
            <li><strong>Resolve target bank</strong>: <code>matter.lawpay_bank_account</code> returns the matter&rsquo;s chosen <code>BankAccount</code> (per-matter override, else an active mapped LawPay operating account). Matter type is not used for deposit routing; a trust-drafting matter is still operating unless an escrow LawPay account is explicitly selected or an escrow-specific flow requests <code>escrow: true</code>. The service sends the mapping&rsquo;s <code>remote_account_id</code> to LawPay.</li>
            <li><strong>Sync contact</strong>: <code>POST /contacts</code> (or <code>PATCH</code> if already synced). Stores <code>contact.lawpay_contact_id</code>.</li>
            <li><strong>Build line items</strong> from unbilled <code>Bills</code> on the matter (charges, expenses, plus previous balance only on the FIRST LawPay invoice for the matter &mdash; LawPay tracks its own running total after that).</li>
            <li><strong>Create invoice</strong>: <code>POST /invoices</code> with <code>{contact_id, bank_account_id, line_items, due_date, invoice_number, reference: "Matter #&lt;num&gt;", notes (incl. 3% surcharge disclosure)}</code>. Returns <code>lawpay_invoice_id</code> and <code>payment_uri</code>.</li>
            <li><strong>Publish</strong>: <code>POST /invoices/:id/messages</code> &mdash; transitions <code>draft</code> &rarr; <code>sent</code> and triggers LawPay&rsquo;s email to the client.</li>
            <li><strong>Update V7 invoice</strong>: <code>lawpay_invoice_id</code>, <code>lawpay_status='sent'</code>, the chosen V7 <code>BankAccount</code>, <code>lawpay_payment_url</code>.</li>
          </ol>

          <h4>3. Client Payment</h4>
          <p>Client receives email &rarr; clicks payment link &rarr; LawPay-hosted portal &rarr; enters card/bank info. 3% surcharge auto-applied to credit card payments. Funds settle to whichever LawPay account ID we passed in step 2 (i.e., the LawPay counterpart of the V7 bank chosen by the matter).</p>

          <h4>4. Webhook &rarr; V7</h4>
          <p>LawPay POSTs to <code>/lawpay/webhook/&lt;token&gt;</code> &rarr; <code>LawpayWebhooksController#callback</code>:</p>
          <ol>
            <li>Look up intranet by webhook token.</li>
            <li>Verify HMAC-SHA256 signature against <code>webhook_secret</code>.</li>
            <li>Route by event type:
              <ul>
                <li><code>invoice.*</code> &rarr; <code>LawpayInvoiceService#process_invoice_webhook</code></li>
                <li>otherwise (charge/payment/refund) &rarr; <code>LawpayService#process_webhook</code></li>
              </ul>
            </li>
            <li>Acknowledge webhook (200) and enqueue a Sidekiq job for the heavy bookkeeping work <em>(planned)</em> &mdash; webhooks should ack fast and processing should be retryable.</li>
          </ol>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Event Type</th><th>V7 Action</th></tr></thead>
            <tbody>
              <tr><td><code>invoice.sent</code></td><td><code>lawpay_status='sent'</code></td></tr>
              <tr><td><code>invoice.viewed</code></td><td><code>lawpay_status='viewed'</code>, sets <code>lawpay_viewed_at</code></td></tr>
              <tr><td><code>invoice.paid</code> / <code>invoice.payment.completed</code></td><td><code>lawpay_status='paid'</code>, sets <code>lawpay_paid_at</code>, enqueue full bookkeeping (step 5)</td></tr>
              <tr><td><code>invoice.partial_payment</code></td><td><code>lawpay_status='partial'</code></td></tr>
              <tr><td><code>invoice.overdue</code></td><td><code>lawpay_status='overdue'</code></td></tr>
              <tr><td><code>invoice.voided</code></td><td><code>lawpay_status='void'</code></td></tr>
              <tr><td><code>charge.succeeded</code> / <code>transaction.completed</code> / <code>payment.completed</code></td><td><code>create_payment_record(data)</code> &mdash; row in <code>lawpay_payments</code>; enqueue bookkeeping</td></tr>
              <tr><td><code>charge.failed</code> / <code>transaction.failed</code> / <code>payment.failed</code></td><td><code>log_failed_payment</code></td></tr>
              <tr><td><code>refund.created</code> / <code>refund.completed</code></td><td><code>handle_refund_notification</code></td></tr>
            </tbody>
          </table>

          <h4>5. V7 Bookkeeping (full double-entry, four-level tree)</h4>
          <p>Webhook handler stores the raw event as a <code>LawpayPayment</code> row (<code>processing_status='unprocessed'</code>, full JSON payload, payer info, card brand/last 4, transaction date) and dedupes by <code>lawpay_transaction_id</code>. Then a <code>LawpayPaymentProcessor</code> Sidekiq job <em>(planned)</em> resolves the V7 records and creates the entry-group structure below.</p>
          <p><strong>Lookup back to V7 bank:</strong> webhook reports the LawPay account ID the funds were deposited to. V7 maps it back via <code>BankAccount.find_by(intranet_id:, lawpay_account_id:)</code>.</p>
          <p><strong>Four-level tree</strong> &mdash; ties V7 Invoice &harr; LawPay payment(s) &harr; refund(s) &harr; bookkeeping artifacts in one walkable structure. New entry_group_types: <code>lawpay_invoice</code>, <code>lawpay_payment</code>, <code>lawpay_refund</code>:</p>
          <pre style="background:#f5f5f5; padding:10px; font-size:11px; line-height:1.5; border-radius:4px;">Great-grandparent EG (type='lawpay_invoice', parent_id=nil)
│   created when V7 sends the invoice (step 2)
│   description: "LawPay Invoice INV-<v7_invoice.id> for Matter #<num>"
│   info: { v7_invoice_id, lawpay_invoice_id, payment_url, status }
│   (no entries — container only)
│
├── Grandparent EG (type='lawpay_payment', parent_id=invoice_eg.id)
│   │   created when payment webhook arrives; one per payment event
│   │   info: { lawpay_transaction_id, lawpay_charge_id, processed_at }
│   │   (no entries — container only)
│   │
│   ├── Child EG (type='deposit', parent_id=payment_eg.id)
│   │   ├── Deposit       (bank=target_bank, amount=+X)
│   │   ├── Ledger debit  (asset COA, +X)         ← bank balance up
│   │   └── Ledger credit (in-transit COA, +X)    ← closes in-transit
│   │
│   └── Child EG (type='receipt', parent_id=payment_eg.id)
│       ├── Receipt       (contact, deposit_id=<sibling deposit.id>,
│       │                  invoice_id=<v7_invoice.id>,
│       │                  for_entry_id=<lawpay_payment EG.id>, amount=+X)
│       ├── Payment Bill  ← ONE PER CHARGE the payment applies to (see allocation below)
│       │     (bill_type='payment', amount=+portion, matter_id,
│       │      paid_bill_id=<charge_bill.id>,
│       │      invoice_id=<v7_invoice.id>,
│       │      for_entry_id=<Receipt.id>)
│       ├── Ledger debit  (in-transit COA, +X)    ← opens in-transit
│       └── Ledger credit (income or held-in-escrow COA, +X)
│
└── Grandparent EG (type='lawpay_refund', parent_id=invoice_eg.id) — sibling of payment, ONE PER REFUND
    │   created when refund webhook arrives
    │   info: { lawpay_refund_id, refund_amount, original_lawpay_transaction_id }
    │
    ├── Child EG (type='check', parent_id=refund_eg.id)
    │   ├── Check         (bank=target_bank, amount=+R)
    │   ├── Ledger credit (asset COA, +R)         ← bank balance down
    │   └── Ledger debit  (in-transit COA, +R)
    │
    └── Child EG (type='receipt_reversal', parent_id=refund_eg.id)
        ├── Receipt-rev   (amount=-R, for_entry_id=<original Receipt.id>,
        │                  description: "for reverse of...")
        ├── Refund Bill   ← ONE PER PAYMENT BILL being un-applied
        │     (bill_type='payment', amount=-portion, matter_id,
        │      paid_bill_id=<original charge.id>,
        │      invoice_id=<v7_invoice.id>,
        │      for_entry_id=<original Payment Bill.id>)   ← KEY: pairs refund to original allocation
        ├── Ledger credit (in-transit COA, +R)
        └── Ledger debit  (income or held-in-escrow COA, +R)</pre>
          <p>For trust/escrow matters the receipt-side credit lands on <code>held in escrow</code> (COA 37) accompanied by a <code>bill_type='escrow'</code> Bill on the same child EG &mdash; same pattern V7 uses for non-LawPay escrow allocations.</p>

          <h5>Payment Allocation (a payment is bill-agnostic)</h5>
          <p>LawPay tells us the gross amount paid; it does not break down which charge bills got paid. V7 must allocate using its existing pattern (see <code>app/models/ledger.rb:175</code>): one Receipt + N Payment Bills, each tagged with <code>paid_bill_id</code> + <code>for_entry_id</code> + <code>invoice_id</code>.</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>On</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><code>invoice_id</code></td><td>Receipt + Payment Bills</td><td>Carries the V7 Invoice forward; ties everything to the great-grandparent EG via the V7 Invoice record</td></tr>
              <tr><td><code>paid_bill_id</code></td><td>Payment Bill</td><td>The charge bill this Payment Bill applies to. One Payment Bill per charge being paid.</td></tr>
              <tr><td><code>for_entry_id</code></td><td>Payment Bill</td><td>The Receipt that funded this Payment Bill (back-pointer for tracing partial allocations)</td></tr>
              <tr><td><code>deposit_id</code></td><td>Receipt</td><td>The physical Deposit that brought the money to the bank</td></tr>
              <tr><td><code>for_entry_id</code></td><td>Receipt</td><td>The <code>lawpay_payment</code> grandparent EG (back-pointer to the LawPay event)</td></tr>
            </tbody>
          </table>
          <p><strong>Allocation algorithm (LIFO with manual override at refund time):</strong></p>
          <ol>
            <li>Find unpaid charge Bills on the V7 Invoice, ordered by <code>created_at</code> ASC (FIFO &mdash; oldest charge paid first by convention).</li>
            <li>Walk through each charge; create one Payment Bill applying the remaining receipt amount or the charge&rsquo;s outstanding amount, whichever is smaller.</li>
            <li>Stop when the receipt amount is exhausted; remaining charges stay unpaid.</li>
          </ol>
          <p>For partial payments the receipt is one entry, but it spawns multiple Payment Bills. The matter&rsquo;s outstanding balance recomputes naturally via summation: each charge&rsquo;s effective balance = charge.amount + sum(its Payment Bills + its Refund Bills).</p>

          <h5>Refund Flow &amp; Allocation Preservation</h5>
          <p>When a refund webhook arrives, the <code>lawpay_refund</code> grandparent is created as a sibling of the original <code>lawpay_payment</code> grandparent (same <code>lawpay_invoice</code> parent). The refund EG&rsquo;s <code>receipt_reversal</code> child contains <strong>one Refund Bill per Payment Bill being un-applied</strong>. Each Refund Bill carries:</p>
          <ul>
            <li><code>amount = -portion</code> (negative, restores client&rsquo;s outstanding balance)</li>
            <li><code>paid_bill_id = original charge.id</code> (so the charge&rsquo;s &ldquo;amount paid&rdquo; sum drops correctly)</li>
            <li><code>for_entry_id = original Payment Bill.id</code> (back-pointer pinpointing exactly which earlier allocation is being reversed)</li>
            <li>Description prefixed with &ldquo;for reverse of...&rdquo; for human readability</li>
          </ul>
          <p><strong>Critical:</strong> refund entries do <strong>not</strong> set <code>reverse_entry_id</code>. The originals stay active in <code>rr_scope</code>; refunds net via summation. This preserves the audit trail (you can see both the original $1500 receipt AND the $500 refund AND the $1000 net position by reading the ledger) and makes partial refunds work uniformly with full refunds.</p>
          <p><strong>Refund allocation policy</strong>: LIFO by default (most-recent payment refunded first), with manual override at refund time. The UI presents the allocation breakdown before the firm clicks Refund, allowing override if a specific payment should be refunded out-of-order.</p>

          <h5>Per-payment processing (inside one DB transaction)</h5>
          <ol>
            <li>Resolve target bank via <code>BankAccount.find_by(intranet_id:, lawpay_account_id:)</code>.</li>
            <li>Find or create great-grandparent <code>lawpay_invoice</code> EG (linked to V7 Invoice).</li>
            <li>Create grandparent <code>lawpay_payment</code> EG under it.</li>
            <li>Create <code>deposit</code> child EG: Deposit + asset/in-transit ledger pair.</li>
            <li>Create <code>receipt</code> child EG: Receipt with proper FK populated.</li>
            <li>Run allocation algorithm: for each charge being paid, create Payment Bill with <code>paid_bill_id</code>/<code>for_entry_id</code>/<code>invoice_id</code> set.</li>
            <li>Create receipt-side ledger pair (debit in-transit, credit offset COA; for escrow add the bill_type=&lsquo;escrow&rsquo; Bill).</li>
            <li>Update <code>LawpayPayment</code>: <code>processing_status='processed'</code>, <code>receipt_id</code>, <code>bank_account_id</code>, <code>entry_group_id</code>=lawpay_payment grandparent.</li>
          </ol>
          <p><strong>Net effect</strong>: client&rsquo;s balance reduced precisely by the right amount per charge (Payment Bills), money lands in the chosen V7 bank (Deposit + asset ledger), in-transit fully zeroes, all double-entry invariants hold. Existing dashboards handle this transparently because the entry-bearing egs match non-LawPay flows; only entry-less container egs are new.</p>
          <p><strong>Reconciliation guards</strong> for <code>accounting_audit.rake</code>:</p>
          <ul>
            <li>Every <code>lawpay_payment</code> grandparent must have exactly one <code>deposit</code> child + one <code>receipt</code> child, both non-empty</li>
            <li>Every <code>lawpay_refund</code> grandparent must have exactly one <code>check</code> child + one <code>receipt_reversal</code> child</li>
            <li>For each Payment Bill, sum of Refund Bills with same <code>for_entry_id</code> must not exceed the Payment Bill amount</li>
            <li><code>LawpayPayment.refund_pending=true</code> for &gt;24h without webhook confirmation &mdash; flagged in daily digest</li>
          </ul>

          <h4>6. Refund Initiation (Firm-Side)</h4>
          <p>Firm clicks Refund on a <code>LawpayPayment</code> in V7:</p>
          <ol>
            <li><code>LawpayService#refund_payment(payment, amount, reason, user)</code> presents the allocation breakdown (which Payment Bills will be reversed under LIFO) and lets the user adjust.</li>
            <li><code>POST /v1/charges/{chargeId}/refund</code> to LawPay with <code>{amount_cents, reason}</code>.</li>
            <li>On API success: <code>LawpayPayment.refund_pending=true</code>, <code>refund_amount</code>, planned allocation persisted in <code>info</code>. <strong>No bookkeeping yet.</strong></li>
            <li>LawPay processes &rarr; sends <code>refund.created</code> / <code>refund.completed</code> webhook.</li>
            <li>Webhook handler creates the <code>lawpay_refund</code> EG + entries (step 5 structure). Sets <code>refund_transaction_id</code>, clears <code>refund_pending</code>.</li>
            <li>If LawPay rejects the refund (e.g., card expired, refund window passed): <code>refund_failed=true</code> + reason captured. No V7 entries created. Daily digest flags it for follow-up.</li>
          </ol>
          <div class="alert alert-warning">
            <strong>Mode/account check:</strong> the refund API can only see charges created in the same LawPay environment and account. A sandbox charge refunded while <code>lawpay.test_mode=false</code> will look like a missing resource to the live API. Verify <code>lawpay.test_mode</code>, the secret key, and the LawPay account ID before assuming V7 bookkeeping is wrong.
          </div>
          <p><strong>Client-initiated chargebacks</strong>: only the webhook fires (no firm-initiated API call). Same code path on the inbound side.</p>

          <h4>7. Reversal Policy (LawPay Entries)</h4>
          <div class="alert alert-danger">
            <strong>Manual reversal is disabled for any entry inside a <code>lawpay_invoice</code>, <code>lawpay_payment</code>, or <code>lawpay_refund</code> tree.</strong> A V7-only reversal would silently drift V7 books from LawPay records (V7 says &ldquo;reversed,&rdquo; LawPay says &ldquo;paid&rdquo; &mdash; the customer&rsquo;s card is never refunded).
          </div>
          <p>The reversal-pair-enforcement service (<a href="#error-detection">see error-detection</a>) walks up the eg tree before allowing any reversal. If any ancestor is <code>lawpay_invoice</code> / <code>lawpay_payment</code> / <code>lawpay_refund</code>, the action is blocked and a dialog points the user to the two correct paths:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Goal</th><th>Tool</th></tr></thead>
            <tbody>
              <tr><td>Refund the customer&rsquo;s card AND adjust V7 books</td><td><strong>LawPay Refund</strong> (refund flow &rarr; API &rarr; webhook &rarr; bookkeeping). Single source of truth.</td></tr>
              <tr><td>Adjust V7 books only (chargeback already handled externally, settlement, write-off, accounting cleanup)</td><td><strong>Journal Entry</strong> (Ledger Adjustment EG with full bill/ledger structure under Accounting &rarr; Adjustments). Auditable, doesn&rsquo;t pretend the LawPay charge was reversed.</td></tr>
            </tbody>
          </table>
          <p><strong>What &ldquo;disable&rdquo; means concretely:</strong> the Reverse button on entries inside LawPay trees never enters the reversal flow. There is no &ldquo;force&rdquo; or &ldquo;override&rdquo; option &mdash; if the firm has a legitimate need that the Refund button can&rsquo;t address (e.g., LawPay refund failed because the card expired), they create a Journal Entry instead. The Journal Entry path captures a typed reason, attributes the user, and surfaces in the daily digest&rsquo;s anomaly section so finance can review.</p>
          <p>Three kinds of state mismatch the daily digest flags:</p>
          <ul>
            <li><code>LawpayPayment.refund_pending=true</code> for &gt;24h with no <code>refund_transaction_id</code> &mdash; refund initiated but webhook missing</li>
            <li><code>LawpayPayment.refund_failed=true</code> &mdash; refund rejected; needs Journal Entry follow-up if firm intends to credit by other means</li>
            <li><code>lawpay_refund</code> EG exists but <code>refund_transaction_id</code> is nil &mdash; bookkeeping ran without API confirmation (shouldn&rsquo;t happen with the webhook-driven design, but defense-in-depth)</li>
          </ul>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Entry type</th><th>Reverse button</th><th>Refund button</th><th>Journal Entry</th></tr></thead>
            <tbody>
              <tr><td>Non-LawPay</td><td>✓ with pair-enforcement</td><td>&mdash;</td><td>✓</td></tr>
              <tr><td>LawPay payment / receipt</td><td>✗ disabled</td><td>✓ primary undo</td><td>✓ for V7-only adjustments</td></tr>
              <tr><td>LawPay refund</td><td>✗ disabled (refund-the-refund: rare; use Journal Entry)</td><td>&mdash;</td><td>✓</td></tr>
              <tr><td>LawPay invoice grandparent</td><td>✗ &mdash; voiding handled at LawPay level via webhook</td><td>&mdash;</td><td>&mdash;</td></tr>
            </tbody>
          </table>

          <h4>API Notes</h4>
          <div class="alert alert-info">
            <strong>Two base URLs:</strong>
            <ul class="mb-0">
              <li><code>LawpayInvoiceService</code> uses <code>https://api.8am.com</code> (no <code>/v1</code> prefix) &mdash; per 8am&rsquo;s QuickBill docs for contacts/invoices.</li>
              <li><code>LawpayService</code> uses <code>https://api.8am.com/v1</code> for charges/refunds/transactions.</li>
            </ul>
            Sandbox equivalents: <code>https://api-sandbox.8am.com</code>. Both correct for their respective endpoints.
          </div>
          <p><strong>OAuth lifecycle:</strong> <code>ensure_oauth_token!</code> auto-refreshes via <code>refresh_token</code> grant when <code>oauth_token_expires_at &lt;= now</code>. Tokens persisted to Settings (<code>oauth_access_token</code>, <code>oauth_refresh_token</code>, <code>oauth_token_expires_at</code>).</p>

          <h4>Schema Touch Points</h4>
          <ul>
            <li><code>payment_provider_bank_accounts</code> &mdash; provider/environment/purpose mapping from each remote payment account ID to a V7 bank</li>
            <li><code>bank_accounts.is_lawpay</code> and <code>bank_accounts.lawpay_account_id</code> &mdash; retained temporarily as legacy fallbacks; they are no longer shown in bank setup</li>
            <li><code>matters.lawpay_bank_account_override_id</code> (FK to <code>bank_accounts</code>, nullable)</li>
            <li><code>entry_groups.mixed_intranet</code> (boolean, default false) <em>(planned)</em> &mdash; explicit flag for split-pay egs containing entries from multiple intranets; required to bypass cross-intranet validation</li>
            <li><code>entry_group_types</code> seeds: <code>lawpay_invoice</code>, <code>lawpay_payment</code>, <code>lawpay_refund</code> <em>(planned)</em> &mdash; the new four-level tree types</li>
            <li><code>contacts.lawpay_contact_id</code></li>
            <li><code>invoices.lawpay_invoice_id</code>, <code>lawpay_status</code>, <code>lawpay_sent_at</code>, <code>lawpay_viewed_at</code>, <code>lawpay_paid_at</code>, <code>lawpay_bank_account_id</code>, <code>lawpay_payment_url</code>, <code>lawpay_invoice_eg_id</code> <em>(planned)</em> &mdash; FK to the <code>lawpay_invoice</code> great-grandparent EG</li>
            <li><code>lawpay_payments</code> table &mdash; transaction record (unique on <code>lawpay_transaction_id</code>), indexed by intranet/contact/processing_status; gains <code>receipt_id</code>, <code>bank_account_id</code>, <code>entry_group_id</code> (the <code>lawpay_payment</code> grandparent), <code>refund_pending</code>, <code>refund_failed</code>, <code>refund_failure_reason</code> after processing <em>(planned)</em></li>
            <li>Entry FK usage (existing fields, no schema change): <code>paid_bill_id</code> on Payment Bills points at the charge being paid; <code>for_entry_id</code> on Payment Bills points at the funding Receipt; <code>for_entry_id</code> on Refund Bills points at the original Payment Bill being un-applied; <code>invoice_id</code> carries the V7 Invoice through Receipt and all its Payment/Refund Bills.</li>
          </ul>
        `
      }
    ]
  },

  // ============================================================
  // 12. RECEIPTS & PAYMENTS
  // ============================================================
  {
    id: 'receipts',
    title: 'Receipts & Payments',
    icon: 'fa-receipt',
    audience: 'both',
    subsections: [
      {
        id: 'paying-payables',
        title: 'Paying Payables',
        audience: 'both',
        content: `
          <h4>Choosing the Bank Account for Payables</h4>
          <ol>
            <li>Open <strong>Accounting &rarr; Payables</strong> and click <strong>Make payment</strong>.</li>
            <li>Select the vendor charges to pay and confirm each payment amount.</li>
            <li>Use <strong>Pay from</strong> to choose the general bank account for this payment run. V7 initially selects the default general bank account when one is configured.</li>
            <li>Review the selected bank&rsquo;s current balance, total payment, and projected balance, then click <strong>Create Check(s)</strong>.</li>
          </ol>
          <p>The selected account applies to every check created in that payment run. Only active general checking accounts for the current intranet are available; escrow, cash, credit-card, PayPal, payroll, tax, inactive, and other intranets&rsquo; accounts cannot be selected. V7 validates the selection again when the checks are created.</p>
          <div class="alert alert-warning"><strong>No account listed:</strong> A bank account must be active and configured as a general checking account before V7 will enable check creation.</div>
        `
      },
      {
        id: 'entry-types',
        title: 'Entry Types (STI Hierarchy)',
        audience: 'both',
        content: `
          <h4>Understanding Entry Types</h4>
          <p>All financial transactions in V7 use a <strong>Single Table Inheritance (STI)</strong> model called <strong>Entry</strong>. The <code>type</code> field determines the entry type:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Type</th><th>Description</th><th>Amount Convention</th></tr></thead>
            <tbody>
              <tr><td><strong>Receipt</strong></td><td>Payment received from client</td><td>Positive (money in)</td></tr>
              <tr><td><strong>Bill</strong></td><td>Charge to client</td><td>Negative (money owed)</td></tr>
              <tr><td><strong>Check</strong></td><td>Payment issued by firm</td><td>Negative (money out)</td></tr>
              <tr><td><strong>Deposit</strong></td><td>Bank deposit grouping receipts</td><td>Positive</td></tr>
              <tr><td><strong>Ledger</strong></td><td>General ledger entry</td><td>Varies (debit/credit pair)</td></tr>
              <tr><td><strong>Factor</strong></td><td>Factoring entry</td><td>Varies</td></tr>
              <tr><td><strong>ToBePaid</strong></td><td>Accounts payable (vendor bills)</td><td>Negative (money owed)</td></tr>
              <tr><td><strong>Receivable</strong></td><td>Accounts receivable</td><td>Positive (money owed to firm)</td></tr>
              <tr><td><strong>Wage</strong></td><td>Payroll wage entry</td><td>Negative (money out)</td></tr>
              <tr><td><strong>Referral</strong></td><td>Referral fee payment tracking</td><td>Varies</td></tr>
            </tbody>
          </table>

          <h5>Entry Statuses</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Status Code</th><th>Meaning</th></tr></thead>
            <tbody>
              <tr><td><strong>O</strong></td><td>Outstanding &mdash; Not yet reconciled</td></tr>
              <tr><td><strong>C</strong></td><td>Cleared &mdash; Matched with the bank statement</td></tr>
              <tr><td><strong>R</strong></td><td>Reserved &mdash; Check number reserved but not yet recorded as a final check</td></tr>
              <tr><td><strong>V</strong></td><td>Voided &mdash; Entry has been voided</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'recording-receipts',
        title: 'Recording Receipts',
        audience: 'both',
        content: `
          <h4>Receipt Workflow</h4>
          <ol>
            <li>Navigate to <strong>Accounting &rarr; Entries &rarr; Receipts</strong>.</li>
            <li>Click <strong>New Receipt</strong>.</li>
            <li>Select the <strong>matter</strong>.</li>
            <li>Enter the <strong>amount</strong>.</li>
            <li>Select the <strong>allocation type</strong>:
              <ul>
                <li><strong>Fee</strong> &mdash; Payment toward legal fees</li>
                <li><strong>Flat Fee</strong> &mdash; Flat fee payment</li>
                <li><strong>Expense</strong> &mdash; Expense reimbursement</li>
                <li><strong>Escrow (fee/cost/other)</strong> &mdash; Funds deposited to trust/escrow</li>
                <li><strong>Contingent</strong> &mdash; Contingency fee payment</li>
                <li><strong>Weekly</strong> &mdash; Weekly payment arrangement</li>
                <li><strong>Variable</strong> &mdash; Variable amount payment</li>
              </ul>
            </li>
            <li>Select the <strong>bank account</strong>.</li>
            <li>Add reference info (check number, transaction ID).</li>
            <li>Click <strong>Save</strong>.</li>
          </ol>

          <h5>Overpayments</h5>
          <p>If a receipt amount exceeds the outstanding balance, the excess is <strong>automatically moved to escrow</strong>.</p>

          <h5>Deposit Workflow</h5>
          <p>Receipts are recorded first with <code>deposit_id = null</code>. Later, the bookkeeper groups related receipts into a <strong>Deposit</strong> record to match the actual bank deposit.</p>

          <h5>Reversals</h5>
          <p>Entries can be reversed using the <strong>Reverse</strong> action on an entry group. This creates mirror-image entries with opposite amounts for every entry in the group. The original entries are marked with a <code>reverse_entry_id</code> pointing to the reversing entry. The <code>rr_ledgers</code> and <code>rr_receipts</code> scopes filter out reversed entries so they don&rsquo;t affect reports or balances.</p>

          <h6>Reversal Rules</h6>
          <ul>
            <li>Reversals operate at the <strong>entry group</strong> level &mdash; all entries in the group are reversed together.</li>
            <li>The reversed entry group is marked with <code>is_reversed = true</code>.</li>
            <li>Both the original entries and the reversing entries remain in the database for audit trail purposes.</li>
            <li>To view reversed entries, uncheck &ldquo;Hide Reversed&rdquo; in the filter options.</li>
          </ul>
          <div class="alert alert-warning"><strong>Important:</strong> When reversing a bank transfer, always reverse from the <strong>parent entry group</strong> (not a child). Reversing a child only reverses one side of the transfer, leaving the other side unbalanced.</div>

          <h5>Restoring Reversed Entry Groups</h5>
          <p>A reversed entry group can be restored to its original state using the <strong>Restore</strong> action. The restore process:</p>
          <ol>
            <li>Retrieves the original entry values from the PaperTrail version history (going back to the last version before <code>reverse_entry_id</code> was set).</li>
            <li>Saves each original entry with its pre-reversal values.</li>
            <li>Deletes the reversing entries (the mirror-image entries created during reversal).</li>
            <li>If any restored entry is a <strong>Receipt</strong> that was part of a <strong>Deposit</strong>, recalculates the deposit amount as the sum of all non-reversed receipts in that deposit.</li>
            <li>Marks the entry group as <code>is_reversed = false</code>.</li>
          </ol>

          <h6>Restore Considerations</h6>
          <ul>
            <li>The restore relies on PaperTrail version history. If versions have been pruned, the restore may not have complete data.</li>
            <li>If the reversed entry group was part of an <strong>invoice</strong>, the associated invoice and matter history records are also restored (marked as not deleted).</li>
            <li>Billing associations are re-linked: if an entry had a <code>billing_id</code>, the billing record&rsquo;s <code>bill_id</code> is updated to point back to the restored entry.</li>
            <li>Deposit amounts are recalculated from their receipts after restore. Verify the deposit amount is correct after restoring receipt entry groups.</li>
          </ul>
          <div class="alert alert-danger"><strong>Caution:</strong> Reversing and restoring should be done carefully. Each reversal and restore creates version history entries. Multiple reverse/restore cycles on the same entry group can make the version history complex and harder to audit.</div>
        `
      },
      {
        id: 'bill-types',
        title: 'Bill Types',
        audience: 'both',
        content: `
          <h4>Bill Type Reference</h4>
          <p>Bills (charges to clients) come in several types:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Bill Type</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>charge</strong></td><td>Standard charge</td></tr>
              <tr><td><strong>time</strong></td><td>Time-based charge (hours &times; rate)</td></tr>
              <tr><td><strong>fixed fee</strong></td><td>Flat fee charge</td></tr>
              <tr><td><strong>expense</strong></td><td>Reimbursable expense</td></tr>
              <tr><td><strong>payment</strong></td><td>Payment applied to balance</td></tr>
              <tr><td><strong>expense payment</strong></td><td>Payment applied to expenses</td></tr>
              <tr><td><strong>escrow</strong></td><td>Escrow/trust transaction</td></tr>
              <tr><td><strong>credit</strong></td><td>Credit applied to account</td></tr>
              <tr><td><strong>variable fee</strong></td><td>Variable fee charge</td></tr>
            </tbody>
          </table>
          <p>Bill types are configured at <strong>Administration &rarr; Matters &rarr; Bill Types</strong>.</p>
        `
      }
    ]
  },

  // ============================================================
  // 13. SUPPORT QUEUE
  // ============================================================
  {
    id: 'support',
    title: 'Support Queue',
    icon: 'fa-headset',
    audience: 'both',
    subsections: [
      {
        id: 'support-tickets',
        title: 'Support Queue System',
        audience: 'both',
        content: `
          <h4>Internal Support Tickets</h4>
          <p>The support queue tracks internal IT and system requests.</p>

          <h5>Support Queue Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>name</strong></td><td>Queue name (e.g., &ldquo;IT Support&rdquo;, &ldquo;Billing Issues&rdquo;)</td></tr>
              <tr><td><strong>description</strong></td><td>Queue description and purpose</td></tr>
            </tbody>
          </table>

          <h5>Ticket Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Subject</strong></td><td>Brief description of the issue</td></tr>
              <tr><td><strong>Description</strong></td><td>Detailed explanation</td></tr>
              <tr><td><strong>Condition</strong></td><td>Ticket state (e.g., UnAssigned, In Progress, Resolved)</td></tr>
              <tr><td><strong>Queue</strong></td><td>Which support queue this ticket belongs to</td></tr>
            </tbody>
          </table>

          <h5>Support Groups</h5>
          <p>Firm users are organized into <strong>support groups</strong> that define which team members handle tickets in each queue. Navigate to <strong>Services &rarr; Support</strong> to access your queues.</p>
        `
      }
    ]
  },

  // ============================================================
  // 14. APPOINTMENT REQUESTS
  // ============================================================
  {
    id: 'appointments',
    title: 'Appointment Requests',
    icon: 'fa-calendar-check',
    audience: 'both',
    subsections: [
      {
        id: 'appointment-queue',
        title: 'Appointment Request Queue',
        audience: 'both',
        content: `
          <h4>Managing Appointment Requests</h4>
          <p>The appointment request queue tracks incoming consultation and meeting requests.</p>

          <h5>Request States</h5>
          <ul>
            <li><strong>Pending</strong> &mdash; New request, not yet contacted</li>
            <li><strong>Making Contact</strong> &mdash; Attempting to reach the requester</li>
            <li><strong>To Be Rescheduled</strong> &mdash; Contact made but appointment needs rescheduling</li>
          </ul>

          <h5>Processing a Request</h5>
          <ol>
            <li>Navigate to <strong>Services &rarr; Queues &rarr; Appointment Requests</strong>.</li>
            <li>Click a request to view details (contact info, reason, requested date).</li>
            <li>Contact the requester and schedule an appointment.</li>
            <li>Update the request status.</li>
            <li>If accepted, create a new event and optionally start client intake.</li>
          </ol>

          <h5>Configuration</h5>
          <p>The <strong>responsible person</strong> for appointment requests is configured in Settings under <code>appointment &rarr; responsible_person_id</code>. This setting can hold multiple user IDs.</p>

          <h5>Event Type Flags</h5>
          <p>Event types can have these flags that affect the appointment workflow:</p>
          <ul>
            <li><strong>requires_fee_agreement</strong> &mdash; A signed fee agreement must be obtained</li>
            <li><strong>requires_retainer</strong> &mdash; A retainer payment must be collected</li>
          </ul>
        `
      }
    ]
  },

  // ============================================================
  // 15. CLIENT INTAKE
  // ============================================================
  {
    id: 'client-intake',
    title: 'Client Intake',
    icon: 'fa-user-plus',
    audience: 'both',
    subsections: [
      {
        id: 'intake-process',
        title: 'Processing New Clients',
        audience: 'both',
        content: `
          <h4>Client Intake Workflow</h4>
          <p>The client intake process guides new client onboarding:</p>
          <ol>
            <li>Navigate to <strong>Services &rarr; Queues &rarr; Client Intakes</strong>.</li>
            <li>View pending intake forms.</li>
            <li>Review the submitted information (contact details, case type, description).</li>
            <li>Run a <strong>conflict check</strong> before accepting the client.</li>
            <li>If no conflicts, create a new matter and contact from the intake.</li>
          </ol>
          <p>Client intake forms can be customized with different fields per matter type. See <strong>Configuring Intake Forms</strong> below.</p>
        `
      },
      {
        id: 'intake-consultation-fees',
        title: 'Consultation Fees & Fee Collection',
        audience: 'both',
        content: `
          <h4>Consultation Fee Schedule</h4>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Consultation Type</th><th>PLO Fee</th><th>CRDF Fee</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td>Phone Conference</td><td>$100.00</td><td>$200.00</td><td>Non-refundable</td></tr>
              <tr><td>Office Visit</td><td>$100.00</td><td>$200.00</td><td>Non-refundable</td></tr>
              <tr><td>Federal, Mental Health Firearms, Maryland</td><td colspan="2">$200.00</td><td>Non-refundable</td></tr>
              <tr><td>Gun Trust (CRDF)</td><td>&mdash;</td><td>$300.00</td><td>Applied to the $850 flat fee if retained</td></tr>
            </tbody>
          </table>
          <p>The consultation covers the first <strong>30 minutes</strong>. Fees are deducted from future work if the client retains the attorney.</p>
          <div class="alert alert-warning"><strong>Exceptions:</strong> Workers&rsquo; Compensation, Social Security, Self Defense, Estate Law, and Bar Association Referrals do NOT require an upfront consultation fee.</div>
          <p>Any request to waive a consultation fee must have <strong>Warren&rsquo;s approval</strong> and be documented in the matter.</p>

          <h4>Fee Collection Policy</h4>
          <div class="alert alert-danger"><strong>Critical Rules:</strong>
            <ul>
              <li>Intake staff provides the LawPay link for <strong>new client consultation fees ONLY</strong>.</li>
              <li><strong>NO staff member</strong> may take a charge for a retainer payment or provide the LawPay link for retainer payments.</li>
              <li><strong>NO staff member</strong> may discuss the credit card policy with a client.</li>
              <li>For retainer payments: advise clients they can pay via check or money order to the Bechtelsville Office, or cash at Bechtelsville or Reading.</li>
              <li>The LawPay link MAY be used for <strong>eCheck payments only</strong>. Make this explicitly clear.</li>
              <li>Any client insisting on credit/debit card for a retainer must be directed to <strong>Angi</strong>.</li>
            </ul>
          </div>

          <h4>Scheduling After Payment</h4>
          <ol>
            <li><strong>DO NOT</strong> block time on the attorney&rsquo;s schedule using matter 10799 or any holding matter. Time is not blocked until the consultation fee is paid.</li>
            <li>Tell the client: &ldquo;Once the consult fee has been paid, we will call back to confirm receipt and schedule the appointment.&rdquo;</li>
            <li>Do NOT promise a specific date before payment &mdash; the slot may no longer be available.</li>
            <li>Once fee is paid and a time agreed upon, <strong>then</strong> enter the event.</li>
            <li>If the matter is in CRDF, also add the event to the PLO calendar (enter in the attorney&rsquo;s personal matter for scanning).</li>
          </ol>

          <h5>No-Show Policy</h5>
          <p>If a client no-shows and doesn&rsquo;t call within 10 minutes of the appointment time (where staff attempted to confirm the appointment in advance and the attorney called at the designated time, documented in history), staff is NOT to reschedule without payment of an additional consultation fee, unless approved by the lead attorney.</p>

          <h5>Email Inquiry Response Template</h5>
          <p>When responding to email/website inquiries from potential new clients:</p>
          <blockquote>Thank you for contacting our office. My name is [staff name], I am a New Client Intake Paralegal at Prince Law Offices, P.C. We have received your request for information regarding [Matter type] Law in the state of Pennsylvania. Our Firm charges a Consultation fee, which is NON REFUNDABLE. You may reach us at 1-888-313-0416 to schedule the consultation.</blockquote>
          <p>Job applicants, internship inquiries, and business partnership requests should be forwarded to Jan.</p>
        `
      },
      {
        id: 'intake-will-power-of-attorney-fees',
        title: 'Will and Power of Attorney Fees',
        audience: 'both',
        content: `
          <div class="alert alert-info"><strong>Living Will:</strong> A Living Will is a standard section of our Durable Power of Attorney. The additional $100.00 fee applies only if the client wants a separate Living Will document.</div>

          <h4>Individual Fees</h4>
          <table class="table table-sm table-bordered">
            <tbody>
              <tr><th>Simple Will for one person</th><td>$300.00</td></tr>
              <tr><th>Power of Attorney for one person</th><td>$300.00</td></tr>
              <tr><th>Separate Living Will document</th><td>Additional $100.00</td></tr>
            </tbody>
          </table>

          <h4>Husband and Wife Fees</h4>
          <table class="table table-sm table-bordered">
            <tbody>
              <tr><th>Simple Wills for husband and wife</th><td>$500.00</td></tr>
              <tr><th>Powers of Attorney for husband and wife</th><td>$500.00</td></tr>
              <tr><th>Separate Living Will documents</th><td>Additional $100.00</td></tr>
            </tbody>
          </table>

          <h4>Living Will Fees</h4>
          <table class="table table-sm table-bordered">
            <tbody>
              <tr><th>Living Will for one person</th><td>$100.00</td></tr>
              <tr><th>Living Wills for husband and wife</th><td>$150.00</td></tr>
            </tbody>
          </table>

          <h4>Updates and Additional Work</h4>
          <ul>
            <li><strong>Simple Will Updates:</strong> If an existing PLO Will client would like to have their Will updated, and PLO originally created the Will, there is no charge.</li>
            <li><strong>Beyond a Simple Will or Standard Power of Attorney:</strong> The $350.00 hourly rate applies.</li>
          </ul>
        `
      },
      {
        id: 'intake-contact-standards',
        title: 'Contact Data Entry Standards',
        audience: 'both',
        content: `
          <h4>Creating a New Client Contact</h4>
          <p>The first thing to ask a new client is their <strong>name and phone number</strong> in case the call drops.</p>

          <h5>Contact Search</h5>
          <ol>
            <li>Type the first three letters of first name + first three letters of last name, then Search.</li>
            <li>If you have a phone number, try searching by that as well (catches misspelled names).</li>
            <li>If the person appears, verify their information is correct and check associated matters to avoid duplicates.</li>
          </ol>

          <h5>Name Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Standard</th></tr></thead>
            <tbody>
              <tr><td><strong>Display Name</strong></td><td>Full legal name. If the person goes by a different name (e.g., Richard &rarr; Rich), add the preferred name as an <strong>alias</strong> with type &ldquo;assumed name&rdquo; and note it in the matter (&ldquo;Goes By Rich&rdquo;).</td></tr>
              <tr><td><strong>First Name</strong></td><td>Must be the legal first name</td></tr>
              <tr><td><strong>Middle Name</strong></td><td>Can be full middle name or initial (add a period if initial only)</td></tr>
              <tr><td><strong>Last Name</strong></td><td>Must be the legal last name</td></tr>
              <tr><td><strong>Qualifier</strong></td><td>Must be used unless Honorarium is used</td></tr>
              <tr><td><strong>Gender</strong></td><td>Must choose male or female</td></tr>
              <tr><td><strong>Date of Birth</strong></td><td>Use whenever possible</td></tr>
              <tr><td><strong>SSN</strong></td><td>Only request from WC, SS, and Estate contacts</td></tr>
            </tbody>
          </table>

          <h5>Dual Last Names</h5>
          <p>For contacts with hyphenated or dual last names (e.g., Carlos Mateo-Ventura):</p>
          <ul>
            <li>Ask if they prefer a shortened name (e.g., Carlos Mateo).</li>
            <li>Enter the <strong>full legal name</strong> as the primary name.</li>
            <li>Add the shortened form as an <strong>alias</strong> of type &ldquo;assumed name.&rdquo;</li>
            <li>If a court document arrives under a different form of the name, update the contact&rsquo;s name to match the court document and add the previous form as an alias.</li>
          </ul>

          <h5>Owner Assignment</h5>
          <ul>
            <li>For new clients: make the <strong>lead attorney</strong> the Owner of the contact.</li>
            <li>If the client already exists without an owner, use the original lead attorney. If that attorney is no longer with the firm, use the original lead attorney who is still current.</li>
            <li>If an Owner is already assigned, <strong>do NOT change it</strong> (even if it shows a former attorney).</li>
            <li><strong>Exception:</strong> Joshua must be made Owner of any contacts referred via pafirearmslawyer.com, even if he is not the lead attorney.</li>
          </ul>

          <h5>Quick Address Entry</h5>
          <p>The address dialog includes a text area at the top. You can paste a full address (copied from a website, email, etc.) and click <strong>&ldquo;Quick Search&rdquo;</strong> to auto-fill fields using geo data. It can identify city, state, zip, and county from a street address + zip code.</p>
          <p>Address number formatting: spell out numbers 1&ndash;10 (e.g., &ldquo;123 Second Avenue&rdquo;), use digits for numbers above 10 (e.g., &ldquo;3400 42nd Street&rdquo;).</p>

          <h5>Caller Is Not the Client</h5>
          <p>If a family member calls on behalf of the client, conduct the intake but let the caller know staff needs to speak directly with the client to arrange the appointment (unless the client is detained).</p>
        `
      },
      {
        id: 'intake-special-matters',
        title: 'Court Appointed & Pro Bono Matters',
        audience: 'both',
        content: `
          <h4>Court Appointed Matters</h4>
          <ol>
            <li><strong>No matter can be created</strong> until a copy of the <strong>Order from the Court</strong> appointing the attorney is received.</li>
            <li>Any paperwork received before the Order is held for processing.</li>
            <li>Once the Order is received:</li>
          </ol>
          <ul>
            <li>Create the matter and set status to <strong>Court Appointed</strong>.</li>
            <li>Set the <strong>billing contact</strong> to the Court Administration office for the appointing county.</li>
            <li>Add <strong>Angi</strong> to the matter to receive all communications.</li>
          </ul>

          <h4>Pro Bono Matters</h4>
          <ul>
            <li>Matter is created when paperwork/information is received from the attorney (no court order required).</li>
            <li>Set the matter as <strong>Pro Bono</strong>.</li>
            <li>Set fee type to <strong>No Fee</strong> (instead of hourly/flat fee).</li>
            <li>Enter a notice in matter details: <strong>&ldquo;Pro Bono Matter.&rdquo;</strong></li>
            <li>Maximum of <strong>4 Pro Bono matters per year per attorney</strong>.</li>
          </ul>

          <h4>Firearms Law New Clients (CRDF)</h4>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Case Type</th><th>Assignment</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td>LTCF/PICS Denials</td><td>Joshua (default); Dillon if Joshua is unavailable within 30-day appeal window</td><td>&mdash;</td></tr>
              <tr><td>Mental Health Firearms</td><td>Dillon</td><td>&mdash;</td></tr>
              <tr><td>Gun Trust</td><td>Varies</td><td>$300 consult (applied to $850 flat fee). FFL new clients: 1 hour phone conference.</td></tr>
              <tr><td>Game Law</td><td>PLO (not CRDF)</td><td>Assigned to Eric or Dillon</td></tr>
              <tr><td>Out-of-State Gun Trust</td><td>Do NOT schedule</td><td>Tell client Mr. Prince will contact them within 24 hours</td></tr>
              <tr><td>Trust Amendments (former Odom clients)</td><td>Dillon</td><td>&mdash;</td></tr>
              <tr><td>Pardons &amp; Expungements</td><td>Dillon</td><td>&mdash;</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'intake-form-config',
        title: 'Configuring Intake Forms',
        audience: 'admin',
        content: `
          <h4>Client Intake Field Configuration</h4>
          <p>Intake forms are configured per matter type. Each matter type can have its own set of intake fields that determine what information is collected from new clients.</p>

          <h5>Accessing Intake Field Settings</h5>
          <ol>
            <li>Navigate to <strong>Administration &rarr; Client Intake Fields</strong>.</li>
            <li>Select the <strong>matter type</strong> you want to configure from the dropdown.</li>
            <li>The current fields for that matter type will be displayed in order.</li>
          </ol>

          <h5>Available Field Types</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field Type</th><th>Description</th><th>Data Collected</th></tr></thead>
            <tbody>
              <tr><td><strong>Matter Date Type</strong></td><td>Collects a date value linked to a matter date type (e.g., Date of Injury, Filing Date)</td><td>Date</td></tr>
              <tr><td><strong>Matter Detail Type</strong></td><td>Collects a text value linked to a detail type (e.g., Case Number, Claim Number)</td><td>Text</td></tr>
              <tr><td><strong>Contact</strong></td><td>Links a contact to the matter via an involvement (with typeahead search)</td><td>Contact lookup</td></tr>
              <tr><td><strong>Role Type Contacts</strong></td><td>Links a contact with a specific role type (e.g., Opposing Counsel, Judge)</td><td>Contact + Role</td></tr>
              <tr><td><strong>Matter Note</strong></td><td>Collects a free-text note that is saved to the matter&rsquo;s notes field</td><td>Textarea</td></tr>
              <tr><td><strong>Matter Jurisdiction</strong></td><td>Collects the jurisdiction for the matter</td><td>Text</td></tr>
              <tr><td><strong>Instruction</strong></td><td>Displays a read-only instruction or label on the form (no data collected)</td><td>Label only</td></tr>
              <tr><td><strong>Instruction Confirm</strong></td><td>Displays an instruction with a checkbox the user must check to confirm</td><td>Checkbox</td></tr>
              <tr><td><strong>Email Confirm</strong></td><td>Displays a confirmation checkbox with an associated email body template</td><td>Checkbox + Email</td></tr>
              <tr><td><strong>Group Heading</strong></td><td>Displays a section heading to visually organize the form</td><td>Header only</td></tr>
            </tbody>
          </table>

          <h5>Adding a Field</h5>
          <ol>
            <li>Select the <strong>field type</strong> from the dropdown.</li>
            <li>For types that reference existing records (Matter Date Type, Matter Detail Type, Role Type Contacts), select the specific record from the second dropdown.</li>
            <li>Enter a <strong>label</strong> &mdash; this is what the user sees on the intake form.</li>
            <li>Set <strong>Allowed to Change</strong> to control who can edit the field: Attorney, Staff, Attorney and Staff, or Attorney and Paralegal.</li>
            <li>Click <strong>Add</strong>.</li>
          </ol>

          <h5>Reordering Fields</h5>
          <p>Fields appear on the intake form in the order they are listed. Drag and drop fields to reorder them, or use the position controls.</p>

          <h5>Tips</h5>
          <ul>
            <li>Use <strong>Group Heading</strong> fields to organize long forms into logical sections.</li>
            <li>Use <strong>Instruction</strong> fields to provide guidance to staff filling out the form.</li>
            <li>Use <strong>Instruction Confirm</strong> for mandatory acknowledgments (e.g., &ldquo;I have verified the client&rsquo;s identity&rdquo;).</li>
            <li>When cloning matter types between intranets, you can optionally include client intake fields.</li>
            <li>Each matter type has its own independent set of intake fields, so different case types can collect different information.</li>
          </ul>
        `
      }
    ]
  },

  // ============================================================
  // 16. NEWSLETTERS
  // ============================================================
  {
    id: 'newsletters',
    title: 'Newsletters',
    icon: 'fa-newspaper',
    audience: 'both',
    subsections: [
      {
        id: 'creating-newsletters',
        title: 'Creating & Sending Newsletters',
        audience: 'both',
        content: `
          <h4>Newsletter System</h4>
          <p>V7 integrates with the <strong>Smailer</strong> email campaign system for newsletter distribution.</p>

          <h5>How It Works</h5>
          <ol>
            <li>Navigate to <strong>Services &rarr; Newsletters</strong>.</li>
            <li>Click <strong>New Newsletter</strong>.</li>
            <li>Compose content using the TinyMCE rich text editor.</li>
            <li>Select the <strong>mailing list</strong> (MailingList record).</li>
            <li>Preview the newsletter.</li>
            <li>Click <strong>Send</strong> to create a <strong>MailCampaign</strong> and distribute.</li>
          </ol>

          <h5>Mailing Lists</h5>
          <p>Recipients are contacts with <code>is_on_mailing_list = true</code>. The system automatically handles:</p>
          <ul>
            <li><strong>Unsubscriptions</strong>: Contacts can unsubscribe via a link in the newsletter. The unsubscribe page does not require login.</li>
            <li><strong>Bounce handling</strong>: Invalid email addresses are tracked.</li>
          </ul>

          <h5>Configuration</h5>
          <p>The <strong>Matter for newsletters</strong> setting (under <code>management</code>) links newsletter activity to a specific matter number for tracking and billing purposes.</p>
        `
      }
    ]
  },

  // ============================================================
  // 17. CONFLICT CHECKS
  // ============================================================
  {
    id: 'conflicts',
    title: 'Conflict Checks',
    icon: 'fa-search-plus',
    audience: 'both',
    subsections: [
      {
        id: 'running-conflicts',
        title: 'Running a Conflict Check',
        audience: 'both',
        content: `
          <h4>Conflict Check System</h4>
          <p>Conflict checks identify potential conflicts of interest before taking on new clients or matters.</p>

          <h5>Running a Check</h5>
          <ol>
            <li>Navigate to the conflict check page (accessible from client intake or Administration).</li>
            <li>Enter names of all parties: clients, opposing parties, witnesses, employers, etc.</li>
            <li>Click <strong>Search</strong>.</li>
            <li>The system searches across: contacts, AKAs, involvements, matters &mdash; across <strong>all intranets</strong>.</li>
            <li>Review results. Matches show the contact name, matter number, and involvement role.</li>
          </ol>

          <h5>Government Contacts Excluded from Potential Conflicts</h5>
          <p>The checker does not report a potential conflict for recognized Pennsylvania or U.S. government contacts, regardless of whether the contact is listed as a defendant, client, plaintiff, or another involvement role. This prevents frequent government parties from creating routine conflict alerts or freezing a matter.</p>
          <p>Examples include the Commonwealth of Pennsylvania and its named agencies (such as the PA Game Commission, Department of Labor and Industry, Bureau of Workers&rsquo; Compensation, and Workers&rsquo; Compensation Appeal Board/WCAB), the U.S. government and its named agencies, and the Social Security Administration/SSA. Common abbreviations and punctuation variations are recognized.</p>
          <p>The exemption is based on the contact&rsquo;s name. An agency entered under an unrelated or unfamiliar name may still appear in results; ask an administrator to add that name to the rule. Private organizations with similar words in their names are not intentionally excluded. Continue to review the parties and results as part of the firm&rsquo;s normal conflict-check process.</p>

          <h5>Conflict Check Record</h5>
          <p>Each conflict check creates a <strong>ConflictCheck</strong> record with:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>search_criteria</strong></td><td>JSON of the names/terms searched</td></tr>
              <tr><td><strong>results</strong></td><td>JSON of the search results</td></tr>
              <tr><td><strong>conflicts_found</strong></td><td>Number of potential conflicts found</td></tr>
              <tr><td><strong>approved</strong></td><td>Whether the check was approved (conflicts waived)</td></tr>
              <tr><td><strong>approved_by_id</strong></td><td>User who approved (supervising attorney)</td></tr>
              <tr><td><strong>approved_at</strong></td><td>When the approval occurred</td></tr>
            </tbody>
          </table>

          <h5>Approval Workflow</h5>
          <p>If conflicts are found:</p>
          <ol>
            <li>An <strong>approval_token</strong> is generated (32-character random string).</li>
            <li>An approval email is sent to the supervising attorney with a unique approval URL.</li>
            <li>The supervising attorney clicks the link to approve (waive the conflict).</li>
          </ol>

          <h5>Matter History Integration</h5>
          <p>Conflict check results are automatically logged in matter history as a &ldquo;Conflict Check&rdquo; type entry, creating a permanent record of the check and its outcome.</p>
        `
      }
    ]
  },

  // ============================================================
  // 18. ACCOUNTING ADMINISTRATION
  // ============================================================
  {
    id: 'accounting-admin',
    title: 'Accounting Administration',
    icon: 'fa-calculator',
    audience: 'admin',
    subsections: [
      {
        id: 'accounting-dashboard',
        title: 'Accounting Dashboard',
        audience: 'admin',
        content: `
          <h4>Understanding the Accounting Dashboard</h4>
          <p>The accounting dashboard (<strong>Accounting &rarr; Accounting</strong>) shows 4 reconciliation sections. Understanding these is critical for maintaining balanced books.</p>

          <h5>1. Bank Balances</h5>
          <p>For each bank account, two numbers are compared:</p>
          <ul>
            <li><strong>Ledger Balance</strong> &mdash; Calculated from the Chart of Accounts: debit minus credit for the bank&rsquo;s asset account.</li>
            <li><strong>Bank Balance</strong> &mdash; Calculated from entry records: total deposits minus total checks.</li>
          </ul>
          <p><strong>Difference should be $0.00.</strong> If not, there are entries in the ledger that don&rsquo;t have corresponding deposits/checks, or vice versa.</p>

          <h5>2. Checks In Transit</h5>
          <p>Compares:</p>
          <ul>
            <li><strong>In-Transit Ledger</strong> &mdash; Balance in the &ldquo;Receipt In Transit [bank name]&rdquo; asset account.</li>
            <li><strong>Outstanding Receipts</strong> &mdash; Total of receipts not yet grouped into a deposit (deposit_id is null).</li>
          </ul>
          <p><strong>These should match.</strong> A mismatch means receipts exist without corresponding in-transit ledger entries, or the in-transit account has stale balances.</p>

          <h5>3. Held In Escrow</h5>
          <p>A 3-way reconciliation for trust/escrow accounts:</p>
          <ul>
            <li><strong>Ledger</strong> &mdash; Escrow account balance in the Chart of Accounts</li>
            <li><strong>Bills</strong> &mdash; Sum of escrow-type bills across all matters</li>
            <li><strong>Bank Balance</strong> &mdash; Actual escrow bank account balance</li>
          </ul>
          <p><strong>All three should match.</strong> A discrepancy means either the ledger, the bill records, or the bank are out of sync.</p>

          <h5>4. Overall Ledger Balance</h5>
          <p>Total credits minus total debits across ALL accounts. In double-entry bookkeeping, <strong>this must be exactly $0.00</strong>. Any non-zero amount means the books are out of balance.</p>

          <h5 class="text-danger">Troubleshooting Imbalances</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Section Off</th><th>Where to Look</th></tr></thead>
            <tbody>
              <tr><td>Bank Balances</td><td>Check for entries in the bank&rsquo;s asset COA that don&rsquo;t have matching deposit/check records. Look for unreconciled transactions.</td></tr>
              <tr><td>Checks In Transit</td><td>Check for receipts without deposit_id that don&rsquo;t have in-transit ledger entries. May indicate a receipt was created without proper ledger posting.</td></tr>
              <tr><td>Held In Escrow</td><td>Compare escrow bills against the escrow COA account. Look for escrow receipts that weren&rsquo;t properly posted to the escrow bill type.</td></tr>
              <tr><td>Overall Ledger</td><td>Find the most recent unbalanced entry group. Use Entry Groups page, sort by date descending, and check for groups where debits &ne; credits. Also check for single-sided ledger entries (debit without credit).</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'chart-of-accounts',
        title: 'Chart of Accounts',
        audience: 'admin',
        content: `
          <h4>Chart of Accounts Structure</h4>
          <p>The COA uses a hierarchical tree (ancestry-based). Accounts have 5 types:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Type</th><th>Normal Balance</th><th>Examples</th></tr></thead>
            <tbody>
              <tr><td><strong>Assets</strong></td><td>Debit &minus; Credit</td><td>Cash In Bank, Receipt In Transit, Accounts Receivable, Escrow</td></tr>
              <tr><td><strong>Liabilities</strong></td><td>Credit &minus; Debit</td><td>Accounts Payable, Taxes Payable, Client Trust</td></tr>
              <tr><td><strong>Income</strong></td><td>Credit &minus; Debit</td><td>Legal Fees, Interest Income, Referral Fees</td></tr>
              <tr><td><strong>Expenses</strong></td><td>Debit &minus; Credit</td><td>Salaries, Rent, Office Supplies, Postage</td></tr>
              <tr><td><strong>Equity</strong></td><td>Credit &minus; Debit</td><td>Owner&rsquo;s Equity, Retained Earnings</td></tr>
            </tbody>
          </table>

          <h5>Static Accounts</h5>
          <p>There are <strong>38 static accounts</strong> that cannot be renamed or deleted. These are core accounts required by the system (e.g., Fee Income, Escrow, Trust, standard expense categories).</p>

          <h5>Auto-Created Accounts</h5>
          <p>When a <strong>bank account</strong> is created, the system automatically creates two COA entries:</p>
          <ul>
            <li><strong>&ldquo;Cash In Bank [name]&rdquo;</strong> &mdash; Asset account for the bank&rsquo;s balance</li>
            <li><strong>&ldquo;Receipt In Transit [name]&rdquo;</strong> &mdash; Asset account for undeposited receipts</li>
          </ul>
        `
      },
      {
        id: 'bank-accounts',
        title: 'Bank Accounts',
        audience: 'admin',
        content: `
          <h4>Bank Account Fields</h4>
          <div class="alert alert-info"><strong>Use Accounting &rarr; Bank Accounts &rarr; Add New Bank Account.</strong> The creation workflow now saves the bank, its cash asset account, its receipt-in-transit account, and its internal bank matter in one transaction. If any required part fails, nothing is retained, preventing partially configured bank accounts.</div>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td><strong>name</strong></td><td>Display name</td><td>e.g., &ldquo;General Operating&rdquo;, &ldquo;IOLTA Trust&rdquo;</td></tr>
              <tr><td><strong>bank_number</strong></td><td>Routing number</td><td>9-digit ABA routing number</td></tr>
              <tr><td><strong>account_number</strong></td><td>Account number</td><td>Bank account number</td></tr>
              <tr><td><strong>Active status</strong></td><td>Controls whether the account can be used for new transactions.</td><td>For an existing account, use <strong>Disable</strong> or <strong>Enable</strong> on the Bank Accounts list. Inactive accounts and their history are retained, but they are excluded from new receipts, deposits, checks, transfers, and payment-provider routing.</td></tr>
              <tr><td><strong>is_default</strong></td><td>Default operating account</td><td>One per firm. Selecting it warns which bank will lose the role and allows the user to cancel before transferring the Default role.</td></tr>
              <tr><td><strong>is_escrow</strong></td><td>Trust/IOLTA account</td><td>Escrow transactions are directed to this account.</td></tr>
              <tr><td><strong>is_payroll</strong></td><td>Payroll account</td><td>Used for payroll check writing. Selecting it warns which bank will lose the role and allows cancellation before transferring Payroll.</td></tr>
              <tr><td><strong>is_tax</strong></td><td>Tax account</td><td>Used for tax payments. Selecting it warns which bank will lose the role and allows cancellation before transferring Tax.</td></tr>
              <tr><td><strong>is_cash</strong></td><td>Cash account</td><td>For cash transactions.</td></tr>
              <tr><td><strong>is_credit_card</strong></td><td>Credit card account</td><td>Treated differently from checking accounts.</td></tr>
              <tr><td><strong>is_paypal</strong></td><td>PayPal account</td><td>For PayPal transactions.</td></tr>
              <tr><td><strong>check_printer_destination</strong></td><td>CUPS printer name</td><td>Printer to use when printing checks from this account.</td></tr>
              <tr><td><strong>last_check_number</strong></td><td>Last check number used</td><td>Auto-incremented when checks are printed.</td></tr>
              <tr><td><strong>Remote payment accounts</strong></td><td>Optional account IDs assigned by LawPay and PayLoad.</td><td>Leave all provider IDs blank when the bank does not use online payments. When an ID is entered, use separate live and test IDs for each provider. Before saving a new or changed ID, V7 uses the matching environment&rsquo;s credentials to confirm that it is accessible. PayLoad IDs must be processing accounts; LawPay IDs must match the bank&rsquo;s operating/escrow type. If the ID is already mapped to another V7 bank, V7 identifies that bank and allows the user to cancel or transfer the mapping. Unchanged IDs are not rechecked on every edit.</td></tr>
              <tr><td><strong>Automatic receipt routing</strong></td><td>Derived from the transaction and account type.</td><td>V7 routes operating/escrow checks and cash using the Escrow and Cash flags, routes card and PayPal receipts using their flags, and routes LawPay receipts through the active provider mapping. Incoming PayLoad receipts post first to the active bank account marked <strong>Credit Card</strong>; PayLoad&rsquo;s processing-account ID separately controls where PayLoad settles the funds.</td></tr>
            </tbody>
          </table>

          <h4>Creating an Operational Bank Account</h4>
          <ol>
            <li>Select the bank/company contact and enter the account identity, routing number, account number, check number, and printer destination.</li>
            <li>Set the account-type flags. For example, an IOLTA checking account is Escrow but not Cash, Credit Card, or PayPal.</li>
            <li>Enter any LawPay or PayLoad live/test IDs. LawPay mappings route LawPay receipts to the matching bank. PayLoad mappings select the remote settlement account for invoices and retainers; incoming PayLoad receipts still post to the V7 account marked <strong>Credit Card</strong>.</li>
            <li>V7 automatically makes every active non-asset operational chart account available for check allocation. Allocation availability is many-to-many, so the app does not move or change a chart account&rsquo;s legacy bank ownership. Asset, Cash in Bank, and Receipt in Transit accounts remain excluded.</li>
            <li>Click <strong>Create Complete Bank Account</strong>. V7 creates and validates the Cash in Bank account, Receipt in Transit account, and internal matter before reporting success.</li>
            <li>Confirm that the Bank Accounts list shows the green <strong>Ready</strong> badge. An Incomplete badge identifies a legacy account with missing or mismatched dependencies.</li>
          </ol>
          <div class="alert alert-warning"><strong>Avoid ambiguous account types:</strong> If multiple active banks match the same ordinary receipt type, V7 uses the matching Default bank when available; otherwise it stops and asks for the bank configuration to be clarified rather than silently posting to the wrong account.</div>

          <h4>Disabling or Re-enabling a Bank Account</h4>
          <p>Use <strong>Disable</strong> when an account is closed or should no longer receive new activity. Disabling does not delete the bank, its documents, its ledger history, or its LawPay/PayLoad settings.</p>
          <ol>
            <li>Open <strong>Accounting &rarr; Bank Accounts</strong> and click <strong>Disable</strong> beside the account.</li>
            <li>Read the confirmation, then continue. V7 checks the account before changing it.</li>
            <li>If the account is Default, Payroll, or Tax, transfer that role to another account first.</li>
            <li>Resolve any undeposited receipts, open deposits, unprinted checks, or reserved checks reported by V7, then try again.</li>
            <li>After disabling, review any warning about the affected receipt route. A warning identifies either a route with no remaining active destination or multiple matching active accounts without a Default.</li>
          </ol>
          <p>To restore an account, select <strong>Show Inactive Accounts</strong>, click <strong>Refresh</strong>, and then click <strong>Enable</strong>. V7 restores its Cash in Bank and Receipt in Transit accounts. Existing active LawPay/PayLoad mappings become usable again; mappings that were already inactive remain inactive.</p>
          <div class="alert alert-info"><strong>Why the checks exist:</strong> V7 will not disable an account while unfinished money movement still depends on it. This prevents a receipt, deposit, or check from becoming stranded and prevents the application from silently choosing the wrong bank.</div>

          <h5>Accounting behavior</h5>
          <ul>
            <li>Checks credit the bank&rsquo;s Cash in Bank asset account and use the selected operational chart account for the other side.</li>
            <li>Receipts debit the bank&rsquo;s Receipt in Transit account. Deposits credit Receipt in Transit and debit Cash in Bank.</li>
            <li>Receipt routes and all selectable records are scoped to the current intranet; one firm cannot route money to another firm&rsquo;s bank.</li>
          </ul>

          <h4>Bank Transfers</h4>
          <p>Transferring between bank accounts creates a 3-level entry group hierarchy:</p>
          <ol>
            <li><strong>Parent</strong> entry group (name: &ldquo;transfer&rdquo;)</li>
            <li><strong>Check child</strong> &mdash; Debit from source bank</li>
            <li><strong>Deposit child</strong> &mdash; Credit to destination bank</li>
          </ol>
          <p>Uses the &ldquo;in transit&rdquo; accounts as intermediaries.</p>
          <div class="alert alert-danger"><strong>Critical:</strong> When reversing a transfer, always reverse the <strong>parent</strong> entry group. Reversing a child only reverses one side, leaving the other side dangling (money disappears into the in-transit account).</div>
        `
      },
      {
        id: 'ledger-system',
        title: 'Double-Entry Ledger',
        audience: 'admin',
        content: `
          <h4>How the Ledger Works</h4>
          <p>V7 uses <strong>double-entry bookkeeping</strong>. Every transaction creates at least one debit and one credit ledger entry. The total of all debits must equal the total of all credits (the fundamental accounting equation).</p>

          <h5>Key Concepts</h5>
          <ul>
            <li><strong>Debit</strong>: Increases assets/expenses, decreases liabilities/income/equity.</li>
            <li><strong>Credit</strong>: Increases liabilities/income/equity, decreases assets/expenses.</li>
            <li><strong>rr_ledgers</strong>: A database scope that excludes reversed entries. All reports and balances use this scope to show only &ldquo;real&rdquo; entries.</li>
          </ul>

          <h5>Entry Groups</h5>
          <p>Related entries are organized into <strong>entry groups</strong>:</p>
          <ul>
            <li>Each entry group must balance (total debits = total credits).</li>
            <li>Entry groups support hierarchy (parent/child) for complex transactions.</li>
            <li>Reversals are performed at the entry group level &mdash; all entries in the group are reversed together.</li>
          </ul>
          <p>Access from <strong>Accounting &rarr; Entries &rarr; Ledgers</strong> or <strong>Entry Groups</strong>.</p>
        `
      },
      {
        id: 'checks',
        title: 'Checks',
        audience: 'admin',
        content: `
          <h4>Creating a Disbursement or Check</h4>
          <div class="alert alert-info"><strong>Starting point:</strong> Open <strong>Accounting &rarr; Checks</strong> and click <strong>New Disbursement / Check</strong>. A disbursement is the accounting transaction. It may produce a printed check or represent an electronic/direct withdrawal.</div>

          <h5>Field Reference</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>What to enter</th><th>Behavior and validation</th></tr></thead>
            <tbody>
              <tr><td><strong>Matter</strong></td><td>The matter responsible for the expense.</td><td>Required. Search by matter number or name and select a result. Selecting a matter loads its involved contacts for Payee, supplies the matter used for the first allocation, and controls matter history and client-charge posting. Changing the matter clears any previously selected payee.</td></tr>
              <tr><td><strong>Payee</strong></td><td>The person or company receiving payment.</td><td>Required. Disabled until a Matter is selected. Type part of a name and select a suggestion from that matter&rsquo;s involvements. Suggestions show the involvement role, such as <em>Client</em>, <em>Employer</em>, or <em>Vendor</em>. Free text is not accepted because V7 needs a contact record and address.</td></tr>
              <tr><td><strong>Bank Account</strong></td><td>The V7 bank account from which money is paid.</td><td>Required. The autocomplete contains active bank accounts. It determines the Cash in Bank ledger account, available allocation accounts, next check number, check printer, and whether escrow controls apply.</td></tr>
              <tr><td><strong>Escrow category</strong></td><td>The displayed escrow source when an escrow bank is selected.</td><td>Appears only when the selected matter has eligible escrow funds in the selected escrow bank. V7 prevents spending more than the matter&rsquo;s available escrow balance.</td></tr>
              <tr><td><strong>Amount</strong></td><td>The total amount of the disbursement.</td><td>Required and must be greater than zero. In allocation mode, the sum of all allocations must exactly equal this amount; the remaining amount is displayed and Save stays disabled until it reaches zero.</td></tr>
              <tr><td><strong>Description</strong></td><td>A concise business purpose for the payment.</td><td>Required. Stored on the check/disbursement, ledger entries, matter history, outgoing print job, and related client charge where applicable. Include invoice dates or service periods when useful.</td></tr>
              <tr><td><strong>Charge Client</strong></td><td>Select when the allocated expense should be billed back to the client.</td><td>Applied to the allocation. V7 creates the appropriate client expense/charge in addition to the bank and expense ledger entries. Do not select for firm overhead.</td></tr>
              <tr><td><strong>Direct Withdrawal</strong></td><td>Select for ACH, debit, electronic withdrawal, or another payment that does not use check stock.</td><td>Mutually exclusive with Print. V7 records check number zero and does not queue a physical check.</td></tr>
              <tr><td><strong>Print</strong></td><td>Select when V7 should create a printable physical check.</td><td>Selected by default and mutually exclusive with Direct Withdrawal. The bank&rsquo;s next check number, printer destination, and authorized signature are used.</td></tr>
            </tbody>
          </table>

          <h5>Action Buttons</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Action</th><th>Purpose</th><th>When enabled</th></tr></thead>
            <tbody>
              <tr><td><strong>Save</strong></td><td>Posts the completed disbursement.</td><td>Matter, Payee, Bank Account, Description, and valid allocations are present; in allocation mode, the unallocated remainder is zero.</td></tr>
              <tr><td><strong>Allocate</strong></td><td>Opens allocation mode so the total can be assigned to one or more matters and chart accounts.</td><td>Matter, Payee, Bank Account, positive Amount, and Description are present. The first allocation defaults to the selected matter and full amount.</td></tr>
              <tr><td><strong>Add</strong></td><td>Adds the current allocation row to the allocation list.</td><td>An allocation Matter, operational Chart of Account, positive allocation Amount, Payee, Bank, and Description are present.</td></tr>
              <tr><td><strong>Reserve</strong></td><td>Switches to reserve mode or creates a reserved check after its responsible Matter User is selected.</td><td>Use only when a physical check number is needed before the final amount/allocation is known.</td></tr>
              <tr><td><strong>Cancel</strong></td><td>Returns to Checks without posting the form.</td><td>Always available.</td></tr>
            </tbody>
          </table>

          <h5>Allocation Fields</h5>
          <p>Click <strong>Allocate</strong> whenever an expense must identify its accounting category, be charged to a client, or be divided among matters/accounts.</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Allocation Matter</strong></td><td>Defaults to the original Matter. Select a different matter when part of the payment belongs elsewhere. Each allocation creates matter-specific ledger/history consequences.</td></tr>
              <tr><td><strong>Chart of Account</strong></td><td>The expense or other operational account receiving the debit. Only active, non-asset accounts configured as available to the selected bank appear. Cash in Bank and Receipt in Transit accounts are intentionally excluded.</td></tr>
              <tr><td><strong>Allocation Amount</strong></td><td>The portion assigned to this matter/account. It cannot exceed the remaining unallocated total.</td></tr>
              <tr><td><strong>Charge Client / Direct Withdrawal / Print</strong></td><td>Confirm the transaction treatment before adding allocations. Charge Client may vary by allocation; payment method applies to the disbursement.</td></tr>
            </tbody>
          </table>

          <h5>Standard Workflow</h5>
          <ol>
            <li>Select the Matter, then select the Payee from that matter&rsquo;s involvement suggestions.</li>
            <li>Select the Bank Account, enter Amount and Description, and choose Print or Direct Withdrawal.</li>
            <li>Click <strong>Allocate</strong>. Confirm the default Matter, select the Chart of Account, adjust the allocation Amount if needed, and select Charge Client when appropriate.</li>
            <li>Click <strong>Add</strong>. Repeat for additional matters/accounts until the remaining amount is zero.</li>
            <li>Review the check preview, allocations, total, payment method, and client-charge choices. Click <strong>Save</strong>.</li>
          </ol>

          <div class="alert alert-warning"><strong>Escrow warning:</strong> A bank-to-bank transfer out of an escrow bank moves cash but does not, by itself, release a client&rsquo;s Held in Escrow liability. Use a matter-level escrow disbursement or the appropriate escrow workflow. Otherwise the Accounting Dashboard will correctly report a difference between escrow cash and Held in Escrow.</div>

          <h5>What Save Creates</h5>
          <ul>
            <li>A balanced disbursement entry group containing the Check and ledger entries.</li>
            <li>A credit to the selected bank&rsquo;s Cash in Bank account and debit(s) to the allocation account(s).</li>
            <li>Matter history and, when Charge Client is selected, the client expense/charge.</li>
            <li>An outgoing print job when Print is selected; no print job for Direct Withdrawal.</li>
          </ul>

          <h5>Troubleshooting</h5>
          <ul>
            <li><strong>Payee is disabled:</strong> select a Matter result, not merely typed text.</li>
            <li><strong>No Payee suggestions:</strong> confirm the Matter was selected from its search results (not merely typed), then confirm the intended contact is an involvement on that matter. Selecting the Matter loads the Payee suggestions; typing in Payee filters that matter-scoped list.</li>
            <li><strong>Allocate is disabled:</strong> confirm Matter, Payee, Bank, positive Amount, and Description are all valid selected values.</li>
            <li><strong>Add is disabled:</strong> confirm the allocation Matter was selected from the finder, then select an operational Chart of Account and enter a positive allocation Amount.</li>
            <li><strong>Add is disabled:</strong> select an allocation Matter and Chart of Account and enter an amount no greater than the remainder.</li>
            <li><strong>Save is disabled after allocating:</strong> the allocation total does not yet equal the disbursement Amount.</li>
            <li><strong>Wrong or missing check number:</strong> confirm Print versus Direct Withdrawal and the bank&rsquo;s Last Check Number configuration.</li>
          </ul>

          <h5>Check Printing</h5>
          <ul>
            <li>Checks print to the bank account&rsquo;s configured <code>check_printer_destination</code> (CUPS printer).</li>
            <li>The <strong>authorized signature</strong> image is automatically included (from the intranet president&rsquo;s uploaded signature file).</li>
            <li>Check numbers auto-increment from <code>last_check_number</code>.</li>
            <li>The check is rendered as HTML, converted to PDF via wkhtmltopdf, then sent to the CUPS print server.</li>
          </ul>

          <h5>Reserving a Check</h5>
          <p>Use a reserved check when a physical check needs to be printed or handed out before the final amount and accounting allocation are known.</p>
          <ol>
            <li>Navigate to <strong>Accounting &rarr; Entries &rarr; Checks</strong>.</li>
            <li>Click <strong>New Check</strong>.</li>
            <li>Select the <strong>Matter</strong> and <strong>Bank Account</strong>.</li>
            <li>Click the first <strong>Reserve</strong> button to switch the form into reserve mode. The heading changes to <strong>Create Reserve Check</strong>.</li>
            <li>Select the <strong>Matter User</strong> who will receive or be responsible for the reserved check.</li>
            <li>Enter or confirm the <strong>Payee</strong> if known. If the payee is not known yet, it can be filled in later when the reserved check is recorded.</li>
            <li>Enter a clear <strong>Description</strong>. If left blank, V7 uses a default description identifying the user and matter number.</li>
            <li>Click the second <strong>Reserve</strong> button. V7 creates the check with reserved status, assigns the next check number from the selected bank account, creates the matter history entry, and queues the outgoing check for printing.</li>
          </ol>

          <h5>Recording a Reserved Check</h5>
          <ol>
            <li>Return to <strong>Accounting &rarr; Entries &rarr; Checks &rarr; New Check</strong>.</li>
            <li>In <strong>Outstanding Reserved Checks</strong>, click the reserved check number you want to complete.</li>
            <li>V7 loads the reserved check, matter, bank, check number, and payee if one was set.</li>
            <li>Enter the final <strong>Amount</strong>, <strong>Description</strong>, and accounting allocation(s).</li>
            <li>Click <strong>Save</strong>. V7 changes the reserved check from reserved to open, posts the ledger entries, updates the matter history, and creates any client charge or escrow bill required by the allocation.</li>
          </ol>

          <div class="alert alert-warning"><strong>Important:</strong> Do not create a separate new check for a check number that was already reserved. Always select it from <strong>Outstanding Reserved Checks</strong> so V7 reuses the reserved number and keeps the ledger, history, outgoing check, and reservation record tied together.</div>
        `
      },
      {
        id: 'bank-reconciliation',
        title: 'Reconciling Checkbooks',
        audience: 'admin',
        content: `
          <h4>Reconciling Checkbooks / Bank Statements</h4>
          <p>Use the Bank Statements screen to reconcile each V7 checkbook against the bank&rsquo;s statement. Reconciliation changes matched checks and deposits from <strong>Outstanding</strong> (<code>O</code>) to <strong>Cleared</strong> (<code>C</code>), leaving unmatched items outstanding for follow-up.</p>

          <h5>Before You Start</h5>
          <ul>
            <li>Make sure all checks, deposits, transfers, and electronic withdrawals for the statement period have been entered in V7.</li>
            <li>For trust or escrow accounts, confirm matter-level escrow bills/receipts are entered before reconciling.</li>
            <li>Download the bank statement in <strong>OFX format</strong> when available.</li>
          </ul>

          <h5>Reconciliation Process</h5>
          <ol>
            <li>Navigate to <strong>Accounting &rarr; Bank Statements</strong>.</li>
            <li>Upload the bank statement file using <strong>Upload OFX Report</strong>.</li>
            <li>Open the uploaded statement and select the date tolerance. The default window is usually enough; increase it only when the bank posted transactions several days away from the V7 entry date.</li>
            <li>Click <strong>Clear Statement</strong>. V7 attempts to match deposits, checks, transfers, and automatic withdrawals by amount, date window, and check/reference number.</li>
            <li>Review the clearing report. Confirm which entries cleared, which were already cleared, which were voided, and which were not found.</li>
            <li>Investigate discrepancies before considering the checkbook reconciled.</li>
            <li>Generate and retain the reconciliation report for the statement.</li>
          </ol>

          <h5>How Matching Works</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Bank Transaction</th><th>V7 Matching Behavior</th></tr></thead>
            <tbody>
              <tr><td><strong>Deposits</strong></td><td>Matched to V7 deposits by amount and date range.</td></tr>
              <tr><td><strong>Checks with check numbers</strong></td><td>Matched to V7 checks by check number, then verified against the amount.</td></tr>
              <tr><td><strong>Electronic withdrawals / ACH</strong></td><td>Matched to checks with check number <code>0</code> by amount and date range. Reconciliation rules may create or classify recurring withdrawals when configured.</td></tr>
              <tr><td><strong>Transfers</strong></td><td>Matched against transfer checks/deposits and cleared when the matching side is found.</td></tr>
            </tbody>
          </table>

          <h5>Reviewing Exceptions</h5>
          <ul>
            <li><strong>Not found</strong> &mdash; The bank shows a transaction that V7 could not match. Enter the missing check/deposit/transfer or adjust the date window if the V7 entry exists outside the search range.</li>
            <li><strong>Wrong amount</strong> &mdash; A check number matched, but the amount differs. Do not clear it until the V7 entry or bank issue is corrected.</li>
            <li><strong>Voided</strong> &mdash; The bank transaction matched a voided V7 entry. Review immediately; the bank may have paid a check that V7 marks void.</li>
            <li><strong>Already cleared</strong> &mdash; The V7 entry was already cleared by a prior statement. Confirm the same transaction was not imported twice.</li>
          </ul>

          <h5>Troubleshooting</h5>
          <ul>
            <li><strong>Statement has no bank account</strong> &mdash; V7 matches an OFX file to a V7 bank account by the OFX <code>BANKID</code> and <code>ACCTID</code>. The V7 bank account&rsquo;s <strong>Bank Number</strong> must match the bank id/routing number in the OFX file, and the <strong>Account Number</strong> must match the account id. V7 can tolerate leading zero differences in the account number, but the bank id must be accurate.</li>
            <li><strong>Bank changed or merged</strong> &mdash; A bank conversion, merger, or new online banking platform may change the OFX <code>BANKID</code>/<code>FID</code> even when the underlying checking account number stays the same. If reconciliation suddenly cannot associate an OFX file with the correct bank account, open the OFX file and compare its <code>BANKID</code> to the V7 bank account&rsquo;s <strong>Bank Number</strong>.</li>
            <li><strong>Where to update it</strong> &mdash; Go to <strong>Accounting &rarr; Bank Accounts</strong>, click the pencil icon next to the affected account, update <strong>Bank Number</strong> to the bank id shown in the OFX file, confirm the <strong>Account Number</strong>, and click <strong>Save</strong>. Repeat for each affected checkbook, such as General, Escrow, or Tax.</li>
          </ul>

          <div class="alert alert-warning"><strong>Important:</strong> Do not force a checkbook to reconcile by changing cleared statuses without understanding the difference. Outstanding items should either be legitimate uncleared checks/deposits or corrected before the reconciliation is treated as complete.</div>

          <h5>After Reconciliation</h5>
          <ul>
            <li>Compare the statement ending balance to V7&rsquo;s cleared balance plus legitimate outstanding checks/deposits.</li>
            <li>Use <strong>Accounting &rarr; Accounting</strong> to confirm bank balances and, for trust accounts, escrow balances remain in agreement.</li>
            <li>Run the reconciliation report when a deeper comparison is needed.</li>
          </ul>

          <h5>Reports</h5>
          <p>Available financial reports from <strong>Administration &rarr; Reports</strong>:</p>
          <ul>
            <li><strong>Balance Sheet</strong> &mdash; Assets, liabilities, and equity at a point in time.</li>
            <li><strong>Profit &amp; Loss (P&amp;L)</strong> &mdash; Income vs expenses. Supports up to 4-year comparison.</li>
            <li><strong>Ledger Detail</strong> &mdash; Line-item transactions per account.</li>
            <li><strong>End of Year Reports</strong> &mdash; Comprehensive summaries by year. Can be sent to accountant, self, or both.</li>
          </ul>
          <p>Time frames available: this year, last year, 2-5 years ago, before this year, before today. All major reports support CSV export.</p>
        `
      },
      {
        id: '1099-reporting',
        title: '1099 Reporting',
        audience: 'admin',
        content: `
          <h4>Annual 1099 Reports</h4>
          <p>V7 can generate a 1099 report listing all vendors/contacts who were paid $600 or more during a calendar year. The report is emailed as an HTML table showing each recipient, their tax ID, and total payments.</p>

          <h5>Requirements for a Contact to Appear on the 1099 Report</h5>
          <p>All three conditions must be met:</p>
          <ol>
            <li><strong>Contact &rarr; requires_1099</strong> must be checked (on the contact&rsquo;s edit page).</li>
            <li><strong>Chart of Account &rarr; requires_1099</strong> must be checked on the expense account(s) used to pay them (under <strong>Accounting &rarr; Chart of Accounts</strong>).</li>
            <li><strong>Total payments &ge; $600</strong> for the selected year (summed across all qualifying entries).</li>
          </ol>
          <p class="text-danger"><strong>Common issue:</strong> A contact is paid over $600 but does not appear on the report. This almost always means the chart of account used for the payment does not have <em>requires_1099</em> enabled. Check the expense account under Accounting &rarr; Chart of Accounts and enable the flag.</p>

          <h5>How It Works (Technical)</h5>
          <ol>
            <li>The system finds all <strong>Ledger</strong> entries in the selected year where the chart of account has <code>requires_1099 = true</code>.</li>
            <li>It traces each ledger back to its parent <strong>Entry</strong> (check, bill, etc.).</li>
            <li>It filters those entries to only include contacts where <code>requires_1099 = true</code>.</li>
            <li>Results are grouped by contact and summed. Only contacts with totals exceeding $599 are included.</li>
          </ol>

          <h5>Generating the Report</h5>
          <ol>
            <li>Navigate to <strong>Administration &rarr; Misc Tools</strong> (requires Accountant or Admin group).</li>
            <li>Under the <strong>Send 1099 Reports</strong> section, enter the recipient email address.</li>
            <li>Select the year (last year, this year, 2 years ago, or 3 years ago).</li>
            <li>Click <strong>Send</strong>. The report will be emailed to the specified address.</li>
          </ol>

          <h5>Report Contents</h5>
          <p>The emailed report includes a table with:</p>
          <ul>
            <li><strong>Recipient</strong> &mdash; Contact name and billing address</li>
            <li><strong>Tax ID</strong> &mdash; EIN (preferred) or SSN from the contact record</li>
            <li><strong>Total for Year</strong> &mdash; Sum of all qualifying payments</li>
          </ul>

          <h5>Setup Checklist</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Step</th><th>Where</th><th>What to Do</th></tr></thead>
            <tbody>
              <tr><td>1</td><td>Contact edit page</td><td>Check <strong>Requires 1099</strong> for each vendor/payee who should receive a 1099</td></tr>
              <tr><td>2</td><td>Contact edit page</td><td>Enter the contact&rsquo;s <strong>EIN</strong> or <strong>SSN</strong> (needed for the report)</td></tr>
              <tr><td>3</td><td>Accounting &rarr; Chart of Accounts</td><td>Check <strong>Requires 1099</strong> on all expense accounts used for 1099-reportable payments (e.g., referral fees, contractor payments, property maintenance)</td></tr>
            </tbody>
          </table>
        `
      }
    ]
  },

  // ============================================================
  // 19. PAYROLL
  // ============================================================
  {
    id: 'payroll',
    title: 'Payroll',
    icon: 'fa-hand-holding-usd',
    audience: 'admin',
    subsections: [
      {
        id: 'payroll-processing',
        title: 'Processing Payroll',
        audience: 'admin',
        content: `
          <h4>Running Payroll</h4>
          <p>Navigate to <strong>Accounting &rarr; Payroll</strong> to access the payroll form.</p>

          <h5>Payroll Form</h5>
          <p>The form shows all active, reportable users (excludes suspended users, non-reportable users, and users with no factor and base &le; 0). For each user, you see:</p>
          <ul>
            <li><strong>Name</strong> &mdash; The user&rsquo;s name</li>
            <li><strong>Pay</strong> checkbox &mdash; Whether to include this user in this payroll run</li>
            <li><strong>Hours</strong> &mdash; Pre-populated from the user&rsquo;s default hours; editable per pay period</li>
            <li><strong>Factors Outstanding</strong> &mdash; Current factor balance (for factor-based users)</li>
          </ul>

          <h5>Payroll Options</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Option</th><th>Description</th><th>When to Use</th></tr></thead>
            <tbody>
              <tr><td><strong>Pay Date</strong></td><td>The date for this payroll run (appears on checks and entries).</td><td>Always required.</td></tr>
              <tr><td><strong>Max WIT</strong></td><td>Maximize withheld income tax for this pay period.</td><td>When an employee requests extra withholding.</td></tr>
              <tr><td><strong>Max Pension</strong></td><td>Maximize pension contributions (up to annual limits).</td><td>Year-end catch-up contributions.</td></tr>
              <tr><td><strong>Attorney Hours</strong></td><td>Calculate attorney wages using hourly rate &times; hours instead of base salary. Useful for tracking attorney billable time value.</td><td>When you want to pay attorneys by actual hours worked.</td></tr>
              <tr><td><strong>Ignore Deductions</strong></td><td>Skip all deductions for this payroll run.</td><td>Bonus checks or special payments.</td></tr>
            </tbody>
          </table>

          <h5>Processing Flow</h5>
          <ol>
            <li>The system creates a parent <strong>Entry Group</strong> for the entire payroll run.</li>
            <li>For each user being paid, it creates a child entry group containing:
              <ul>
                <li>A <strong>Wage</strong> entry with the net pay amount</li>
                <li><strong>WageRecord</strong> entries for: gross, net, hours, rate, taxable income, and each deduction category</li>
                <li>A <strong>Check</strong> entry (or direct deposit) for disbursement</li>
                <li><strong>Ledger</strong> entries for the double-entry accounting (wage expense debit, cash credit)</li>
              </ul>
            </li>
            <li>Tax calculations: Federal, State, FICA, Medicare, WIT &mdash; each creates wage records and ledger entries.</li>
            <li>Pension: Employee contribution deducted, employer match calculated and recorded.</li>
            <li>Health insurance: Premiums deducted up to monthly caps.</li>
            <li>Deductions: Auto-pay deductions applied (unless &ldquo;Ignore Deductions&rdquo; is checked).</li>
            <li><strong>Payment Obligations</strong> created for tax liabilities and benefit payments.</li>
            <li>If <code>transfer_to_payroll_bank</code> is &ldquo;1&rdquo;, a bank transfer is automatically created to move payroll funds.</li>
          </ol>

          <div class="alert alert-warning">
            <strong>Payroll validation errors:</strong> If payroll cannot be completed, V7 keeps the submitted form values and displays the underlying error above the form. Correct the identified configuration and run payroll again. No payroll entries are retained from the failed attempt because the transaction is rolled back.
          </div>

          <h5>Gross Wage Calculation</h5>
          <ul>
            <li><strong>Hourly employees</strong>: <code>hourly_rate &times; hours</code>, with 1.5&times; overtime over 40 hours (non-attorneys only).</li>
            <li><strong>Salaried employees</strong>: Uses <code>current_wage_base.base</code>.</li>
            <li><strong>Attorneys with factors</strong>: Base salary plus factor wage (if <code>add_to_base</code> is checked) OR the higher of base or factor wage (if unchecked). See <em>Factor System</em> section for details.</li>
            <li><strong>Contractors</strong>: Factor outstanding only &mdash; no base, no hours, no taxes.</li>
          </ul>

          <h5>Payroll-Related Settings</h5>
          <p>These settings (under the <code>accounting</code> group) control payroll calculations:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Setting</th><th>Description</th><th>Format</th></tr></thead>
            <tbody>
              <tr><td><strong>pension_match_percent</strong></td><td>Employer pension match percentage</td><td>Float (e.g., 3.0 = 3%)</td></tr>
              <tr><td><strong>pension_match_max_percent</strong></td><td>Max percentage of salary to match</td><td>Float</td></tr>
              <tr><td><strong>max_pension_amount</strong></td><td>Maximum pension contribution per period</td><td>Decimal</td></tr>
              <tr><td><strong>max_pension_addition</strong></td><td>Maximum additional pension contribution</td><td>Decimal</td></tr>
              <tr><td><strong>pension_contact_id</strong></td><td>Contact ID of the pension provider</td><td>Integer (Contact ID)</td></tr>
              <tr><td><strong>wit_exemption_per_dependant</strong></td><td>Withholding tax exemption per dependent</td><td>Decimal</td></tr>
              <tr><td><strong>health_premium_contact_id</strong></td><td>Health insurance premium provider</td><td>Integer (Contact ID)</td></tr>
              <tr><td><strong>health_contribution_contact_id</strong></td><td>Health contribution provider</td><td>Integer (Contact ID)</td></tr>
              <tr><td><strong>health_savings_contact_id</strong></td><td>HSA provider</td><td>Integer (Contact ID)</td></tr>
              <tr><td><strong>health_flex_contact_id</strong></td><td>Flex spending provider</td><td>Integer (Contact ID)</td></tr>
              <tr><td><strong>transfer_to_payroll_bank</strong></td><td>Auto-transfer to payroll bank</td><td>&ldquo;1&rdquo; or blank</td></tr>
              <tr><td><strong>transfer_to_tax_bank</strong></td><td>Auto-transfer to tax bank</td><td>&ldquo;1&rdquo; or blank</td></tr>
              <tr><td><strong>payroll_contact_id</strong></td><td>Payroll provider contact</td><td>Integer (Contact ID)</td></tr>
              <tr><td><strong>payroll_is_direct</strong></td><td>Direct deposit vs check</td><td>&ldquo;1&rdquo; = direct deposit</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'staff-attendance-review',
        title: 'Staff Attendance Review',
        audience: 'admin',
        content: `
          <h4>Reviewing Staff Attendance</h4>
          <p>Open the staff attendance report from the attendance administration links. The combined report includes only the PLO and CRDF intranets and only active staff members who logged in during the selected week. Use Previous week, Next week, and Current week to change the review period. Filter by employee to review that employee&rsquo;s complete attendance history, newest first.</p>
          <p>Each employee appears once. When activity exists in both PLO and CRDF, V7 combines it and reports the earliest login and latest logout for the workday. Lunch punches are shown only when the employee explicitly chooses the lunch option during logout; ordinary logout/login activity is not treated as lunch. A rebooted or restored browser session remains available in the login audit trail but does not create an additional payroll attendance row.</p>
          <p><strong>Automatic heartbeat:</strong> while a staff member is signed in, V7 periodically records that the application is still open. This happens only for staff accounts and only one open V7 tab in each browser sends the heartbeat. Other tabs automatically take over if the active tab closes or stops responding. Routine heartbeats update presence only; payroll calculations run when the attendance record is first created or when an actual clock event changes the workday.</p>

          <h5>Review Actions</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Action</th><th>Use</th><th>Payroll effect</th></tr></thead>
            <tbody>
              <tr><td><strong>Forgive all</strong></td><td>Accept the entire calculated missed-time amount as paid time.</td><td>Restores all calculated minutes through an approved attendance adjustment.</td></tr>
              <tr><td><strong>Adjust</strong></td><td>Restore only the entered five-minute increment.</td><td>Restores the approved minutes; any remaining missed time remains unpaid.</td></tr>
              <tr><td><strong>Ignore</strong></td><td>Mark the line as reviewed without changing the calculated payroll amount.</td><td>No payroll change. This is an audit/review disposition.</td></tr>
            </tbody>
          </table>
          <p>A reason is required for every action. V7 records the action, the manager who took it, and the date and time in the <strong>Review</strong> column. The source intranet remains available for audit purposes even though PLO and CRDF are combined in the report.</p>
        `
      },
      {
        id: 'staff-leave',
        title: 'Staff Leave',
        audience: 'admin',
        content: `
          <h4>Staff Leave Requests</h4>
          <p>Open <strong>Accounting &rarr; Leave</strong> to review staff leave balances, submit a leave request, or approve pending requests. The Accounting submenu opens Leave filtered to the signed-in user; managers can then choose another employee from the selector, while non-managers see only their own leave page.</p>

          <h5>Leave Cycles and Entitlement</h5>
          <ul>
            <li>Leave is tracked by each employee&rsquo;s anniversary cycle, from the anniversary date through the day before the next anniversary.</li>
            <li>Paid personal leave is available after one year of service. Employees with fewer than five completed years receive 7 days per cycle; employees with five or more completed years receive 14 days per cycle.</li>
            <li>V7 calculates the active cycle from the selected employee&rsquo;s anniversary date. The page URL may include a year for navigation, but the selected employee&rsquo;s anniversary cycle controls the balance display.</li>
            <li>Requests are made in half-day increments. V7 calculates the end date across the employee&rsquo;s business days.</li>
            <li>Unpaid time off can be requested from the same page but does not consume paid personal-day entitlement.</li>
          </ul>

          <h5>Approvals and Audit Trail</h5>
          <ul>
            <li>Submitted requests start as <strong>Pending</strong>. Manager approval changes the status to <strong>Approved</strong> and stores the approving manager and timestamp.</li>
            <li>Approved leave syncs to the calendar when available. If calendar sync fails, the request remains visible with the sync error so it can be reviewed.</li>
            <li>When a manager acts on their own leave page, V7 redirects back to the same leave page without adding a redundant employee filter to the URL. Acting on another employee keeps that employee selected.</li>
          </ul>

        `
      },
      {
        id: 'wage-reports',
        title: 'Wage Reports',
        audience: 'admin',
        content: `
          <h4>Payroll Reports</h4>
          <ul>
            <li><strong>Wage Reports</strong> (<strong>Administration &rarr; Reports &rarr; Wage Reports</strong>) &mdash; Employee wage summaries by period. Used for tax filing and year-end reporting.</li>
            <li><strong>Workers&rsquo; Compensation Report</strong> (<strong>Administration &rarr; Reports &rarr; Workers&rsquo; Compensation</strong>) &mdash; WC reporting data required by insurance carriers.</li>
          </ul>
          <p>Reports can be generated for: this year, last year, or a specific date range. CSV export is available.</p>
        `
      }
    ]
  },

  // ============================================================
  // 20. SYSTEM ADMINISTRATION
  // ============================================================
  {
    id: 'system-admin',
    title: 'System Administration',
    icon: 'fa-cogs',
    audience: 'admin',
    subsections: [
      {
        id: 'user-management',
        title: 'User Management',
        audience: 'admin',
        content: `
          <h4>Managing Users</h4>
          <p>Navigate to <strong>Intranet &rarr; Users</strong> to manage accounts.</p>

          <h5>User Groups &amp; Permissions</h5>
          <p>Access is controlled by group membership. Users can belong to multiple groups:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Group</th><th>Access Granted</th></tr></thead>
            <tbody>
              <tr><td><strong>admin</strong></td><td>Full administrative access. Can manage users, settings, and all system features.</td></tr>
              <tr><td><strong>accounting</strong></td><td>Accounting menu and all financial features (billing, invoicing, receipts, bank accounts, ledger, payroll).</td></tr>
              <tr><td><strong>tech</strong></td><td>Technical/development features: code coverage, environment info, in-development pages, tech references.</td></tr>
              <tr><td><strong>editor</strong></td><td>Document template editing: auto rules, doc templates, stylesheets, fonts.</td></tr>
            </tbody>
          </table>

          <h5>Special User Roles (Method-Based)</h5>
          <p>Beyond groups, users have role flags checked in code:</p>
          <ul>
            <li><strong>is_priviledged?</strong> &mdash; Can see linked intranets, vendor access, intranet management.</li>
            <li><strong>is_superuser?</strong> &mdash; Can manage setting groups/types.</li>
            <li><strong>is_admin?</strong> &mdash; Can access settings page.</li>
            <li><strong>is_accountant?</strong> &mdash; Can access taxes and administration menu.</li>
            <li><strong>is_tech?</strong> &mdash; Technical features and environment access.</li>
            <li><strong>is_owner?</strong> &mdash; Firm owner &mdash; code coverage, environment.</li>
            <li><strong>is_editor?</strong> &mdash; Document template management.</li>
          </ul>

          <div class="alert alert-warning"><strong>Important:</strong> Always <strong>deactivate</strong> departed users rather than deleting them. Deletion would break audit trails, matter history records, and billing entries that reference the user.</div>
        `
      },
      {
        id: 'adding-attorney',
        title: 'Adding a New Attorney',
        audience: 'admin',
        content: `
          <h4>Adding a New User (Attorney)</h4>
          <p>Navigate to <strong>Intranet &rarr; Users</strong> and click <strong>New User</strong>. The following fields are required or important when setting up an attorney:</p>

          <h5>Basic Information</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>Login</strong></td><td>Username for sign-in. Must be unique within the intranet.</td><td>String (typically email address)</td></tr>
              <tr><td><strong>Email</strong></td><td>Email address. Used for password resets, notifications, and reminders.</td><td>Valid email</td></tr>
              <tr><td><strong>Password</strong></td><td>Initial password. User can change later.</td><td>Minimum 6 characters</td></tr>
              <tr><td><strong>Extension</strong></td><td>Phone extension within the firm.</td><td>Integer</td></tr>
              <tr><td><strong>Time Zone</strong></td><td>User&rsquo;s time zone for calendar and event display.</td><td>Default: &ldquo;Eastern Time (US &amp; Canada)&rdquo;</td></tr>
              <tr><td><strong>Color Code</strong></td><td>Color shown in calendar and UI for this user.</td><td>Hex color (e.g., <code>#FF0000</code>), max 10 chars</td></tr>
            </tbody>
          </table>

          <h5>Attorney-Specific Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>Attorney Number</strong></td><td>Bar license / attorney ID number.</td><td>Integer</td></tr>
              <tr><td><strong>Admittance Date</strong></td><td>Date admitted to the bar. Used to calculate years of experience for wage base lookup.</td><td>Date (MM/DD/YYYY)</td></tr>
              <tr><td><strong>Of Counsel</strong></td><td>Mark if the attorney is &ldquo;Of Counsel&rdquo; (affiliated but not a partner/associate).</td><td>Checkbox (boolean)</td></tr>
              <tr><td><strong>Include in Letterhead</strong></td><td>Whether to include this attorney in firm letterhead when generating documents.</td><td>Checkbox (boolean)</td></tr>
              <tr><td><strong>Letterhead Position</strong></td><td>Order in which this attorney appears on the letterhead.</td><td>Integer (lower = earlier)</td></tr>
              <tr><td><strong>Owner</strong></td><td>Whether this user is a firm owner/partner. Affects factor calculations and certain permissions.</td><td>Checkbox (boolean)</td></tr>
            </tbody>
          </table>

          <h5>Group Membership</h5>
          <p>Assign the user to the appropriate groups. For an attorney, at minimum add to:</p>
          <ul>
            <li><strong>attorney</strong> &mdash; Required. Enables attorney-specific features (lead attorney assignment, factor calculations, activity rates).</li>
          </ul>
          <p>Optionally also add to:</p>
          <ul>
            <li><strong>admin</strong> &mdash; Full administrative access.</li>
            <li><strong>accounting</strong> &mdash; Access to accounting features and billing authorization.</li>
            <li><strong>management</strong> &mdash; Supervisory features.</li>
            <li><strong>reportable</strong> &mdash; Include in payroll reports and payroll processing screens.</li>
            <li><strong>owner / owners</strong> &mdash; Firm owner permissions.</li>
          </ul>

          <h5>Employment &amp; Hierarchy</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>Start Date</strong></td><td>Employment start date. Used to determine the current wage base year and experience level.</td><td>Date</td></tr>
              <tr><td><strong>Parent (Manager)</strong></td><td>The supervising attorney. Critical for factor calculations &mdash; the manager receives the difference between their fee percentage and their subordinate&rsquo;s fee percentage. Also enforces the rule that a subordinate&rsquo;s percentages cannot exceed their manager&rsquo;s.</td><td>Select another user</td></tr>
              <tr><td><strong>Chart of Account</strong></td><td>The chart of account used for this user&rsquo;s wage expense entries in payroll.</td><td>Select a COA entry (typically a wage expense account)</td></tr>
              <tr><td><strong>Matter</strong></td><td>The user&rsquo;s personal &ldquo;profit center&rdquo; matter. Tracks the user&rsquo;s billings and financial activity.</td><td>Select a matter</td></tr>
              <tr><td><strong>Terminated On</strong></td><td>Set when a user leaves the firm. Deactivates the user without deleting records.</td><td>Date (blank if active)</td></tr>
              <tr><td><strong>Rehired On</strong></td><td>Set if a terminated user returns to the firm.</td><td>Date</td></tr>
            </tbody>
          </table>

          <h5>Contact Record</h5>
          <p>Every user is linked to a <strong>Contact</strong> (Person) record. This contact record holds the user&rsquo;s name, addresses, phone numbers, and email addresses. When creating a new user, ensure a corresponding contact record exists or is created. The contact is also what receives factor entries &mdash; factors are tracked on the <em>contact</em>, not the user directly.</p>

          <h5>Steps to Add an Attorney</h5>
          <ol>
            <li>Create a <strong>Contact</strong> (Person) record with the attorney&rsquo;s name, address, phone, and email.</li>
            <li>Create the <strong>User</strong> record, linking it to the contact.</li>
            <li>Add the user to the <strong>attorney</strong> group (and any other applicable groups).</li>
            <li>Set the <strong>attorney number</strong> and <strong>admittance date</strong>.</li>
            <li>Set the <strong>start date</strong> and <strong>parent (manager)</strong>.</li>
            <li>Configure <strong>payroll fields</strong> (see Payroll Options below).</li>
            <li>Create <strong>wage base</strong> records for each year of employment (see Wage Base &amp; Shares).</li>
            <li>Assign as <strong>Lead Attorney</strong> or <strong>Attorney</strong> on their matters.</li>
          </ol>

          <div class="alert alert-info"><strong>Tip:</strong> If you are adding an attorney who has been practicing for several years, create wage base records for each &ldquo;year&rdquo; of their employment to track their percentage progression. Year 1 = first year at the firm, etc.</div>
        `
      },
      {
        id: 'contract-employees-1099-contractors',
        title: '1099 Contractors / Contract Employees',
        audience: 'admin',
        content: `
          <h4>Setting Up a Contract Employee or 1099 Contractor</h4>
          <p>Use these requirements when a person is paid through payroll as an independent contractor rather than as a W-2 employee.</p>

          <div class="alert alert-warning">
            <strong>The controlling setting is group membership.</strong> A user is a 1099 contractor only when the user belongs to the <strong>contractor</strong> group. Selecting <strong>Contract Labor</strong> as the Chart of Account, checking <strong>Of Counsel</strong>, or entering &ldquo;contractor&rdquo; in Notes does <em>not</em> classify the user as a contractor for payroll.
          </div>

          <h5>Required Setup Checklist</h5>
          <ol>
            <li>Keep the user <strong>Active</strong> and remove the user from the <strong>suspended</strong> group.</li>
            <li>Add the user to the <strong>contractor</strong> group. This makes <code>is_contractor?</code> true and controls the payroll tax and deduction treatment.</li>
            <li>Add the user to the <strong>reportable</strong> group so the user is eligible to appear on the payroll screen.</li>
            <li>If the contractor is an attorney, also keep the user in the <strong>attorney</strong> group. The attorney group controls legal and factor features; it does not change the 1099 classification.</li>
            <li>Set <strong>Chart of Account</strong> to <strong>Contract Labor</strong> so the gross payment is posted to the correct expense account. This is required for correct bookkeeping, but the account alone does not make the user a contractor.</li>
            <li>Link the user to the correct contact and personal/profit-center matter used for payroll entries.</li>
            <li>For a factor-paid attorney contractor, set <strong>Factor Percent</strong> above zero, maintain a current wage-base record (a zero base is appropriate when there is no guaranteed base payment), and confirm that positive factor activity exists for the pay period. Contractor payroll pays the full factor amount even though a positive Factor Percent is needed to enable factor participation.</li>
          </ol>

          <h5>How to Verify the Setup</h5>
          <ul>
            <li>The user appears on the payroll form.</li>
            <li>The application identifies the user as a contractor.</li>
            <li>The expense account is <strong>Contract Labor</strong>.</li>
            <li>The user is not treated as an employee for EIT, FICA, Medicare, WIT, pension, health, or other employee deductions.</li>
          </ul>
          <p>If payroll attempts to calculate employee taxes or deductions for the contractor, stop and verify membership in the <strong>contractor</strong> group before creating payroll.</p>

          <h5>Contractor Payroll Treatment</h5>
          <ul>
            <li>No employee tax withholding is calculated.</li>
            <li>No employee deductions are processed.</li>
            <li>A factor-paid contractor receives 100% of the available factor balance.</li>
            <li>Payment and ledger descriptions identify the transaction as an independent-contractor payment.</li>
          </ul>
        `
      },
      {
        id: 'payroll-options',
        title: 'Payroll Options (Per User)',
        audience: 'admin',
        content: `
          <h4>User Payroll Configuration</h4>
          <p>Each user has payroll-related fields on their user record that control how they are compensated:</p>

          <h5>Compensation Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>Hourly Rate</strong></td><td>Hourly wage rate. If set (and wage base = 0), the user is paid hourly.</td><td>Float (e.g., 25.00)</td></tr>
              <tr><td><strong>Hours</strong></td><td>Default hours per pay period. Pre-populated on the payroll form.</td><td>Float (e.g., 40.0 for full-time)</td></tr>
              <tr><td><strong>Factor Percent</strong></td><td>Percentage of factor outstanding paid out each pay period. If &gt; 0, the user participates in the factor system. Contractors get 100% of their factor outstanding.</td><td>Float (0&ndash;100). 0 = no factor participation.</td></tr>
            </tbody>
          </table>

          <h5>Hourly vs. Salaried Determination</h5>
          <p>The system determines compensation type automatically:</p>
          <ul>
            <li><strong>Hourly</strong>: <code>hourly_rate &gt; 0</code> AND <code>current_wage_base.base == 0</code></li>
            <li><strong>Salaried/Factor</strong>: <code>current_wage_base.base &gt; 0</code> (hourly rate is ignored)</li>
          </ul>
          <p>Hourly employees get <strong>1.5&times; overtime</strong> for hours over 40 per week (non-attorneys are exempt from this calculation &mdash; attorneys do not receive overtime).</p>

          <h5>Tax &amp; Withholding Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>Dependants</strong></td><td>Number of tax dependents. Used for withholding tax calculation (WIT exemption = dependants &times; <code>wit_exemption_per_dependant</code> setting). A blank value is treated as zero dependents so it does not prevent payroll from being calculated.</td><td>Integer (0+)</td></tr>
              <tr><td><strong>Is Married</strong></td><td>Marital status for tax bracket purposes.</td><td>Checkbox (boolean)</td></tr>
              <tr><td><strong>EIT</strong></td><td>Earned Income Tax rate (local tax). This field must contain a rate for every employee subject to the configured EIT tax; a blank value stops payroll and identifies the employee and missing field.</td><td>Decimal percentage (precision 4, scale 2), e.g., 1.00 for 1%</td></tr>
            </tbody>
          </table>

          <h5>Pension &amp; Retirement</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>Pension Percent</strong></td><td>Employee&rsquo;s pension contribution as a percentage of gross pay.</td><td>Float (0&ndash;100), e.g., 6.0 = 6%</td></tr>
              <tr><td><strong>Roth IRA Percent</strong></td><td>Roth IRA contribution percentage of gross pay.</td><td>Float (0&ndash;100)</td></tr>
            </tbody>
          </table>
          <p>The employer match is controlled by the system-wide settings <code>pension_match_percent</code> and <code>pension_match_max_percent</code>. Annual contribution limits are enforced via <code>max_pension_amount</code> and <code>max_pension_addition</code> (catch-up for age 50+).</p>

          <h5>Health Insurance</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>Health Premium Per Pay</strong></td><td>Employee&rsquo;s health insurance premium deducted each paycheck.</td><td>Decimal (e.g., 150.00)</td></tr>
              <tr><td><strong>Health Premium Per Month</strong></td><td>Total monthly premium cap. Once the monthly total is reached, no more is deducted.</td><td>Decimal</td></tr>
              <tr><td><strong>Health Cost Per Pay</strong></td><td>Employer health cost per paycheck.</td><td>Decimal</td></tr>
              <tr><td><strong>Health Cost Per Month</strong></td><td>Employer health cost monthly cap.</td><td>Decimal</td></tr>
              <tr><td><strong>Health Savings</strong></td><td>Health Savings Account (HSA) deduction per pay period.</td><td>Decimal</td></tr>
              <tr><td><strong>Health Flex</strong></td><td>Flexible Spending Account (FSA) deduction per pay period.</td><td>Decimal</td></tr>
              <tr><td><strong>Is Other Comp</strong></td><td>Whether the user receives other compensation (affects tax calculations).</td><td>Checkbox (boolean)</td></tr>
            </tbody>
          </table>

          <div class="alert alert-info">For contract employees and independent contractors, see the separate <strong>1099 Contractors / Contract Employees</strong> topic in this Payroll section.</div>
        `
      },
      {
        id: 'wage-base-shares',
        title: 'Wage Base & Shares',
        audience: 'admin',
        content: `
          <h4>Wage Base Records</h4>
          <p>Each attorney/salaried user has one or more <strong>Wage Base</strong> records that define their compensation structure over time. The system uses the most recent effective wage base (by <code>effective_date</code>) that is before the current date.</p>

          <h5>Wage Base Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format / Values</th></tr></thead>
            <tbody>
              <tr><td><strong>Year</strong></td><td>Year number of employment (Year 1, Year 2, etc.). Must be unique per user. This represents the year of employment, <em>not</em> the calendar year.</td><td>Integer (1, 2, 3&hellip;)</td></tr>
              <tr><td><strong>Effective Date</strong></td><td>When this wage base takes effect. The system uses the latest effective date before today.</td><td>Date</td></tr>
              <tr><td><strong>Base</strong></td><td>Base salary per pay period. If &gt; 0, the user is salaried. If 0 and hourly_rate is set, the user is hourly.</td><td>Float (e.g., 2500.00 per pay period)</td></tr>
              <tr><td><strong>Fee Percent</strong></td><td>Percentage of fee income allocated to this user via the factor system. This is the primary &ldquo;share&rdquo; percentage.</td><td>Float (0&ndash;100)</td></tr>
              <tr><td><strong>Expense Percent</strong></td><td>Percentage of expense-related income allocated to this user.</td><td>Float (0&ndash;100)</td></tr>
              <tr><td><strong>Opener Percent</strong></td><td>Percentage allocated to this user for matters they opened (brought to the firm). Deducted from the income attorney&rsquo;s share.</td><td>Float (0&ndash;100)</td></tr>
              <tr><td><strong>Owner Percent</strong></td><td>Percentage allocated to this user as the referral source owner. Deducted from the income attorney&rsquo;s share.</td><td>Float (0&ndash;100)</td></tr>
              <tr><td><strong>Payroll Expense Percent</strong></td><td>Payroll expense allocation percentage.</td><td>Float (0&ndash;100)</td></tr>
              <tr><td><strong>Add to Base</strong></td><td>If checked, factor income is <em>added to</em> the base salary. If unchecked, gross pay = whichever is higher: base or factor income.</td><td>Checkbox (boolean)</td></tr>
              <tr><td><strong>Include Base in Factor</strong></td><td>Whether to include base salary in factor calculations.</td><td>Checkbox (boolean)</td></tr>
              <tr><td><strong>Payroll Expense Only on Factor</strong></td><td>Only apply payroll expenses when factor income is involved.</td><td>Checkbox (boolean)</td></tr>
            </tbody>
          </table>

          <h5>Validation Rules</h5>
          <ul>
            <li>All percentage fields must be between <strong>0 and 100</strong>.</li>
            <li><strong>opener_percent + owner_percent</strong> cannot exceed <strong>fee_percent</strong>. This prevents negative income situations.</li>
            <li>A subordinate&rsquo;s percentages <strong>cannot exceed their manager&rsquo;s</strong> equivalent percentages. The system compares wage bases by years of experience (adjusted for start date differences).</li>
            <li><strong>Year</strong> must be unique per user.</li>
          </ul>

          <h5>How Wage Base Progression Works</h5>
          <p>Attorneys typically start with lower percentages (e.g., Year 1: 20% fee) and increase over time (Year 5: 40% fee). Create a wage base for each year of employment:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Year</th><th>Effective Date</th><th>Base</th><th>Fee %</th><th>Opener %</th><th>Owner %</th></tr></thead>
            <tbody>
              <tr><td>1</td><td>01/01/2024</td><td>2,000</td><td>20</td><td>5</td><td>5</td></tr>
              <tr><td>2</td><td>01/01/2025</td><td>2,200</td><td>25</td><td>7</td><td>7</td></tr>
              <tr><td>3</td><td>01/01/2026</td><td>2,500</td><td>30</td><td>10</td><td>10</td></tr>
            </tbody>
          </table>
          <p>The system automatically selects the wage base with the latest <code>effective_date</code> that is before today.</p>

          <h5>Manager Equivalence</h5>
          <p>When comparing a subordinate&rsquo;s wage base to their manager&rsquo;s, the system adjusts for start date differences. If User A started 3 years before User B, then User B&rsquo;s Year 1 is compared to User A&rsquo;s Year 4 equivalent. This ensures a more experienced manager always has higher percentages than their subordinates at the same experience level.</p>
        `
      },
      {
        id: 'factor-system',
        title: 'Factor System',
        audience: 'admin',
        content: `
          <h4>Understanding Factors</h4>
          <p>The <strong>factor system</strong> is the firm&rsquo;s compensation-sharing mechanism. When money comes in (fee payments, expense payments) or wages are paid, the system creates <strong>Factor</strong> entries that allocate portions of that amount to the people who earned it. Factors accumulate as an outstanding balance until consumed during payroll.</p>

          <div class="alert alert-info"><strong>Key Concept:</strong> Factors track how much each person has &ldquo;earned&rdquo; but not yet been paid. During payroll, a percentage of the outstanding factor balance (the user&rsquo;s <code>factor_percent</code>) is paid out as income.</div>

          <h5>Viewing Factors</h5>
          <p>Open the Factors report from the Accounting menu to review factor entries for a user or attorney. Each row may show both the factor description and the description of the accounting entry that generated it. Older or reversed entries can have no source description; V7 leaves that secondary description blank while continuing to display the factor row and the rest of the report.</p>

          <h5>When Factors Are Created</h5>
          <p>Factors are generated automatically when receipts are allocated. The system calls <code>Factor.make_factors_for()</code> with a type (fee, expense, or wage) and distributes the amount among the involved parties.</p>

          <h5>Factor Roles</h5>
          <p>Each receipt creates factor entries for up to 7 parties:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Role</th><th>Who</th><th>How Determined</th></tr></thead>
            <tbody>
              <tr><td><strong>Income</strong></td><td>The attorney who did the work</td><td>The matter&rsquo;s &ldquo;Income&rdquo; user (from MatterUser), or the billing user</td></tr>
              <tr><td><strong>Income Manager</strong></td><td>The income attorney&rsquo;s supervisor</td><td>The income user&rsquo;s <code>parent</code> (manager hierarchy)</td></tr>
              <tr><td><strong>Opener</strong></td><td>The person who brought the case to the firm</td><td>The matter&rsquo;s &ldquo;Opener&rdquo; user (from MatterUser). If none and user isn&rsquo;t lead, falls back to lead attorney.</td></tr>
              <tr><td><strong>Opener Manager</strong></td><td>The opener&rsquo;s supervisor</td><td>The opener&rsquo;s <code>parent</code></td></tr>
              <tr><td><strong>Owner</strong></td><td>The person who &ldquo;owns&rdquo; the referral source</td><td>The matter&rsquo;s referral contact&rsquo;s <code>owned_by_user</code></td></tr>
              <tr><td><strong>Owner Manager</strong></td><td>The owner&rsquo;s supervisor</td><td>The owner&rsquo;s <code>parent</code></td></tr>
              <tr><td><strong>Intranet (Firm)</strong></td><td>The firm itself</td><td>Whatever is left after all individual shares</td></tr>
            </tbody>
          </table>

          <h5>Factor Allocation Types</h5>
          <p>Factor entries have an <strong>allocation type</strong> that describes what kind of income they represent:</p>
          <ul>
            <li><strong>fee</strong> &mdash; From fee payments (the primary attorney income stream)</li>
            <li><strong>expense</strong> &mdash; From expense payments</li>
            <li><strong>wage</strong> &mdash; From wage payments (negative factors, consuming outstanding balance)</li>
            <li><strong>escrow</strong> &mdash; Escrow movements are <em>not</em> factored (explicitly excluded)</li>
          </ul>

          <h5>How Factor Amounts Are Calculated</h5>
          <p>For a given receipt amount (e.g., $10,000 fee payment):</p>
          <ol>
            <li><strong>Income amount</strong>: The higher of the income attorney&rsquo;s fee_percent or their manager&rsquo;s fee_percent, applied to the total. For example, if the income attorney has 30% and their manager has 50%, the income &ldquo;pool&rdquo; is 50% &times; $10,000 = $5,000.</li>
            <li><strong>Opener deduction</strong>: The opener&rsquo;s opener_percent is deducted from the income pool. Capped at 50% of the income amount.</li>
            <li><strong>Owner deduction</strong>: The owner&rsquo;s owner_percent is deducted from the income pool. Also capped at 50% of income amount.</li>
            <li><strong>Income split</strong>: The remaining income amount is split between the income attorney and their manager based on the ratio of their fee percentages.</li>
            <li><strong>Opener split</strong>: Opener amount is split between opener and opener&rsquo;s manager based on their opener percentages.</li>
            <li><strong>Owner split</strong>: Owner amount is split between owner and owner&rsquo;s manager based on their owner percentages.</li>
            <li><strong>Firm remainder</strong>: Everything left goes to the intranet (firm) factor.</li>
          </ol>

          <div class="alert alert-warning"><strong>Validation:</strong> The sum of all factor entries always equals the original receipt amount exactly. If the math doesn&rsquo;t balance to the penny, the system raises an error and the transaction is rolled back.</div>

          <h5>Deduplication Rules</h5>
          <ul>
            <li>If the <strong>income attorney is also the opener</strong>, no separate opener factor is created (they already get the income share).</li>
            <li>If the <strong>income attorney is also the owner</strong>, no separate owner factor is created.</li>
            <li>If the <strong>opener is the same as the income user</strong>, opener records are suppressed.</li>
          </ul>

          <h5>Factor Outstanding &amp; Payroll</h5>
          <p>A user&rsquo;s <strong>factor outstanding</strong> is the sum of all their non-reversed factor amounts. During payroll:</p>
          <ul>
            <li><strong>Salaried attorneys</strong>: <code>factor_wage = (factor_percent / 100) &times; factor_outstanding</code>. If <strong>add_to_base</strong> is checked, this is added to the base salary. Otherwise, gross pay = the higher of base salary or factor wage.</li>
            <li><strong>Contractors</strong>: <code>factor_wage = factor_outstanding</code> (100% payout).</li>
            <li>The paid-out factor amount creates negative (wage-type) factor entries that reduce the outstanding balance.</li>
          </ul>

          <h5>Factor Example</h5>
          <p>Suppose a $10,000 fee is received on a matter where:</p>
          <ul>
            <li>Income Attorney (Alice): fee 30%, opener 5%, owner 5%</li>
            <li>Alice&rsquo;s Manager (Bob): fee 50%, opener 10%, owner 10%</li>
            <li>Opener (Carol): opener 8%</li>
            <li>No separate owner</li>
          </ul>
          <p>The income pool = 50% &times; $10,000 = $5,000. The opener gets 8% &times; $10,000 = $800. Alice&rsquo;s income (after opener deduction) = $5,000 &minus; $500 = $4,500, split 30/50 between Alice ($2,700) and Bob ($1,800). Carol gets $800. The firm gets $10,000 &minus; $2,700 &minus; $1,800 &minus; $800 = $4,700.</p>

          <h5>Enabling/Disabling Factors</h5>
          <p>The factor system is controlled by the <code>accounting.use_factor</code> setting. If set to &ldquo;1&rdquo;, factors are active. If blank or &ldquo;0&rdquo;, no factor calculations occur during payroll.</p>
        `
      },
      {
        id: 'deductions',
        title: 'Deductions',
        audience: 'admin',
        content: `
          <h4>User Deductions</h4>
          <p>Deductions are recurring amounts withheld from a user&rsquo;s paycheck for various purposes (garnishments, loans, repayments, etc.). Each deduction is tracked with a total owed and payments made against it.</p>

          <h5>Deduction Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Format</th></tr></thead>
            <tbody>
              <tr><td><strong>Name</strong></td><td>Description of the deduction (e.g., &ldquo;Office key replacement&rdquo;, &ldquo;Bar dues advance&rdquo;).</td><td>String</td></tr>
              <tr><td><strong>Amount</strong></td><td>Amount deducted per pay period.</td><td>Decimal</td></tr>
              <tr><td><strong>Total with Interest</strong></td><td>Total amount owed (including any interest). Deductions stop when this amount is fully paid.</td><td>Decimal</td></tr>
              <tr><td><strong>Contact</strong></td><td>The payee contact for this deduction (who receives the withheld money).</td><td>Contact ID</td></tr>
              <tr><td><strong>Chart of Account</strong></td><td>The ledger account for tracking the deduction liability.</td><td>COA ID</td></tr>
              <tr><td><strong>Is Auto Pay</strong></td><td>If checked, this deduction is automatically applied each payroll. If unchecked, it must be manually applied.</td><td>Checkbox</td></tr>
            </tbody>
          </table>

          <p>The remaining balance (<strong>outstanding</strong>) = total_with_interest &minus; total paid to date. Deductions automatically stop when the outstanding balance reaches zero.</p>

          <div class="alert alert-info"><strong>Tip:</strong> During payroll processing, you can choose <strong>&ldquo;Ignore Deductions&rdquo;</strong> to skip all deductions for that pay period (e.g., for a bonus check).</div>
        `
      },
      {
        id: 'settings',
        title: 'Settings Reference',
        audience: 'admin',
        content: `
          <h4>Settings System Architecture</h4>
          <p>Settings use a <strong>3-level hierarchy</strong>:</p>
          <ol>
            <li><strong>SettingGroup</strong> &mdash; Top-level category (e.g., &ldquo;accounting&rdquo;, &ldquo;lawpay&rdquo;, &ldquo;management&rdquo;)</li>
            <li><strong>SettingType</strong> &mdash; Individual setting definition (e.g., &ldquo;pension_match_percent&rdquo;)</li>
            <li><strong>Setting</strong> &mdash; The actual value, stored <strong>per intranet</strong></li>
          </ol>
          <p><strong>All values are strings.</strong> The calling code interprets them as numbers, booleans, IDs, etc. as needed.</p>

          <h5>Access Control</h5>
          <ul>
            <li><strong>Settings page</strong> (<code>/settings</code>) &mdash; Admin-only access</li>
            <li><strong>Setting Groups/Types management</strong> &mdash; Superuser-only (except index view)</li>
          </ul>

          <h4>Setting Groups Reference</h4>

          <h5>ai</h5>
          <p>Controls the provider used by V7&rsquo;s AI Research and AI document drafting tools.</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Setting</th><th>Description</th><th>Format</th></tr></thead>
            <tbody>
              <tr><td><code>provider</code></td><td>Primary AI provider. Use <code>claude</code> for Anthropic Claude or <code>openai</code> for OpenAI. Blank, missing, or invalid values fall back to <code>claude</code>.</td><td><code>claude</code> or <code>openai</code></td></tr>
              <tr><td><code>openai_model</code></td><td>OpenAI model to use when <code>ai.provider = openai</code>. Leave this set to the approved firm model unless testing a replacement.</td><td>OpenAI model name</td></tr>
            </tbody>
          </table>
          <p>Provider API keys are not stored in the V7 Settings table. They are configured in the server environment/credentials: Claude uses <code>ANTHROPIC_API_KEY</code>; OpenAI uses <code>OPENAI_API_KEY</code>.</p>

          <h5>accounting</h5>
          <p>Controls payroll and financial calculations. See <em>Payroll</em> section for the full list of accounting settings.</p>
          <ul>
            <li><code>use_factor</code> &mdash; Enable/disable factoring features (&ldquo;1&rdquo; or blank)</li>
          </ul>

          <h5>lawpay</h5>
          <p>LawPay payment processing integration:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Setting</th><th>Description</th><th>Format</th></tr></thead>
            <tbody>
              <tr><td><code>enabled</code></td><td>Master LawPay switch. Blank or missing means enabled. Set to <code>false</code>, <code>0</code>, <code>no</code>, <code>off</code>, or <code>disabled</code> to stop LawPay API calls, callbacks, workers, refunds, and manual payment processing. Matters marked <code>billing_method = "lawpay"</code> invoice as manual while disabled.</td><td>String boolean</td></tr>
              <tr><td><code>api_key</code></td><td>LawPay live API key</td><td>String</td></tr>
              <tr><td><code>api_secret</code></td><td>LawPay live API secret</td><td>String</td></tr>
              <tr><td><code>test_api_key</code> / <code>test_api_secret</code></td><td>LawPay sandbox credentials used by the webhook/transaction service when <code>test_mode = true</code></td><td>String</td></tr>
              <tr><td><code>api_key_test</code> / <code>api_secret_test</code></td><td>LawPay sandbox credential names used by the invoice/contact service when <code>test_mode = true</code>. If blank, that service falls back to <code>api_key</code> / <code>api_secret</code>.</td><td>String</td></tr>
              <tr><td><code>merchant_id</code></td><td>LawPay merchant ID</td><td>String</td></tr>
              <tr><td><code>test_merchant_id</code></td><td>LawPay sandbox merchant ID used by the webhook/transaction service when <code>test_mode = true</code></td><td>String</td></tr>
              <tr><td><code>test_mode</code></td><td>Enable test/sandbox mode</td><td>&ldquo;true&rdquo; or &ldquo;false&rdquo;</td></tr>
              <tr><td><code>webhook_token</code></td><td>Token embedded in the V7 webhook URL registered with LawPay</td><td>Random string</td></tr>
              <tr><td><code>webhook_secret</code></td><td>Optional HMAC signature secret for webhook verification</td><td>String</td></tr>
              <tr><td><code>oauth_client_id</code> / <code>oauth_client_secret</code></td><td>Preferred OAuth credentials for invoice/contact API authentication</td><td>String</td></tr>
              <tr><td><code>oauth_access_token</code> / <code>oauth_refresh_token</code> / <code>oauth_token_expires_at</code></td><td>Stored OAuth token values maintained by V7 after authentication</td><td>Token strings / ISO timestamp</td></tr>
              <tr><td><code>trust_account_id</code></td><td>Legacy fallback only. Current routing uses the active LawPay escrow mapping on a V7 bank.</td><td>String (LawPay bank ID)</td></tr>
              <tr><td><code>operating_account_id</code></td><td>Legacy fallback only. Current routing uses the active LawPay operating mapping on a V7 bank.</td><td>String (LawPay bank ID)</td></tr>
            </tbody>
          </table>
          <p><strong>Activation:</strong> LawPay is selected by the matter&rsquo;s <code>billing_method = "lawpay"</code> only when <code>lawpay.enabled</code> is not explicitly off. If LawPay is disabled, V7 treats LawPay matters as manual for invoicing and acknowledges callbacks without posting new LawPay bookkeeping.</p>

          <h5>payload</h5>
          <p>Payload payment processing integration:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Setting</th><th>Description</th><th>Format</th></tr></thead>
            <tbody>
              <tr><td><code>enabled</code></td><td>Master Payload switch. Blank or missing means enabled for explicitly selected Payload matters/settings. Truthy values (<code>1</code>, <code>true</code>, <code>yes</code>, <code>enabled</code>, <code>payload</code>) also select Payload as the global invoice provider. Set to <code>false</code>, <code>0</code>, <code>no</code>, <code>off</code>, or <code>disabled</code> to stop Payload invoice/checkout-link calls, retainer links, callbacks, receipt posting workers, deposit polling, refunds, and reconciliation. Matters marked <code>billing_method = "payload"</code> invoice as manual while disabled.</td><td>String boolean</td></tr>
              <tr><td><code>provider</code></td><td>Global provider selector when set to <code>payload</code>. Checked after matter-level billing method.</td><td><code>payload</code> or blank</td></tr>
              <tr><td><code>test_mode</code></td><td>Use Payload test credentials and test processing accounts when <code>true</code>.</td><td>&ldquo;true&rdquo; or &ldquo;false&rdquo;</td></tr>
              <tr><td><code>api_key</code></td><td>Payload live API key</td><td>String</td></tr>
              <tr><td><code>test_api_key</code></td><td>Payload test API key. Also makes the deposit poller consider Payload configured.</td><td>String</td></tr>
              <tr><td><code>api_url</code></td><td>Optional Payload API URL override. Leave blank for the client default (<code>https://api.payload.com</code>). Blank strings are treated as absent so an empty setting does not break Payload requests.</td><td>URL string</td></tr>
              <tr><td><code>processing_account_id</code></td><td>Legacy/default processing account fallback for invoice payments.</td><td>Payload account ID</td></tr>
              <tr><td><code>general_processing_account_id</code></td><td>Live general/operating processing account for invoice payments.</td><td>Payload account ID</td></tr>
              <tr><td><code>test_processing_account_id</code></td><td>Test processing account fallback for invoice payments.</td><td>Payload account ID</td></tr>
              <tr><td><code>test_general_processing_account_id</code></td><td>Test general/operating processing account for invoice payments. Preferred over <code>test_processing_account_id</code>.</td><td>Payload account ID</td></tr>
              <tr><td><code>iolta_processing_account_id</code></td><td>Live IOLTA/trust processing account for retainer requests.</td><td>Payload account ID</td></tr>
              <tr><td><code>test_iolta_processing_account_id</code></td><td>Test IOLTA/trust processing account for retainer requests.</td><td>Payload account ID</td></tr>
              <tr><td><code>allow_card_payments</code></td><td>Whether Payload checkout permits card payments. Blank defaults to enabled.</td><td>&ldquo;true&rdquo; or &ldquo;false&rdquo;</td></tr>
              <tr><td><code>allow_bank_account_payments</code></td><td>Whether Payload checkout permits bank-account payments. Blank defaults to enabled.</td><td>&ldquo;true&rdquo; or &ldquo;false&rdquo;</td></tr>
              <tr><td><code>webhook_token</code></td><td>Token used for Payload webhook routing/verification where configured.</td><td>Random string</td></tr>
            </tbody>
          </table>
          <p><strong>Activation:</strong> Payload is selected first by <code>matter.billing_method = "payload"</code>, unless <code>payload.enabled</code> is explicitly off. Payload invoice selection also requires the current intranet to have a Payload API key and a general/operating processing account configured. If the matter does not choose a provider, V7 then checks <code>payments.provider</code>, <code>payment_provider.provider</code>, <code>payload.provider</code>, and finally a truthy <code>payload.enabled</code>. The deposit poller runs only when Payload is enabled and an intranet has <code>payload.api_key</code> or <code>payload.test_api_key</code>.</p>
          <p><strong>Receipt posting:</strong> Paid Payload callbacks create or update <code>PayloadPayment</code> rows first, then post V7 accounting receipts through <code>PayloadPaymentProcessorWorker</code>. Incoming PayLoad money is first credited to the firm&rsquo;s active V7 bank account marked <strong>Credit Card</strong>. The processing-account ID returned by PayLoad identifies the later settlement destination; it must not replace the Credit Card clearing account on the receipt. If no active Credit Card account is configured, V7 falls back to the active live/test processing-account mapping. Payments posted to an escrow-designated Credit Card account are treated as one-matter escrow receipts.</p>

          <h5>phaxio</h5>
          <p>Fax service integration:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Setting</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>api_key</code></td><td>Phaxio API key (production)</td></tr>
              <tr><td><code>api_secret</code></td><td>Phaxio API secret (production)</td></tr>
              <tr><td><code>callback_token</code></td><td>Webhook callback verification token</td></tr>
              <tr><td><code>test_api_key</code></td><td>Phaxio API key (test mode)</td></tr>
              <tr><td><code>test_api_secret</code></td><td>Phaxio API secret (test mode)</td></tr>
              <tr><td><code>test_callback_token</code></td><td>Test webhook callback token</td></tr>
            </tbody>
          </table>

          <h5>management</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Setting</th><th>Description</th><th>Format</th></tr></thead>
            <tbody>
              <tr><td><code>delinquent threshold manager</code></td><td>Dollar threshold for flagging matters as delinquent</td><td>Decimal (e.g., &ldquo;500.00&rdquo;)</td></tr>
              <tr><td><code>Matter for newsletters</code></td><td>Matter number to track newsletter activity</td><td>Integer (matter number)</td></tr>
            </tbody>
          </table>

          <h5>appointment</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Setting</th><th>Description</th><th>Format</th></tr></thead>
            <tbody>
              <tr><td><code>responsible_person_id</code></td><td>User(s) responsible for processing appointment requests</td><td>Integer (user ID). Can hold multiple values.</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'linked-accounts',
        title: 'Linked Accounts & Intranets',
        audience: 'admin',
        content: `
          <h4>Multi-Intranet Architecture</h4>
          <p>V7 supports multiple <strong>intranets</strong> (firm instances) within the same application:</p>
          <ul>
            <li>Each intranet has its own: matters, contacts, documents, billing, settings, templates, users, bank accounts, and chart of accounts.</li>
            <li>Data is isolated by <code>intranet_id</code> foreign keys throughout the database.</li>
            <li><strong>Associated accounts</strong> allow the same human user to access multiple intranets.</li>
          </ul>

          <h5>Managing Intranets</h5>
          <p>Navigate to <strong>Intranet &rarr; Linked Accounts</strong> (privileged access required) to review automatically associated identities:</p>
          <ul>
            <li>Every human user must have a valid SSN on the user&rsquo;s contact record.</li>
            <li>A missing or invalid legacy SSN can be corrected once. After a valid nine-digit SSN is saved for a user, V7 prevents it from being changed through the application.</li>
            <li>V7 normalizes the SSN and automatically associates active accounts with the same SSN across intranets.</li>
            <li>Non-human accounts have no SSN and therefore receive no cross-intranet association.</li>
            <li>The SSN is not displayed. The page shows the person and associated intranets.</li>
            <li>More than one active account with the same SSN in a single intranet is ambiguous. V7 fails closed by excluding that intranet until the duplicate is corrected.</li>
            <li>The obsolete <code>linked_accounts</code> table has been removed. There is no manual association record that can grant access.</li>
          </ul>

          <h5>Cross-Intranet Features</h5>
          <ul>
            <li><strong>Conflict checks</strong> search across all intranets the user has access to.</li>
            <li><strong>Matter type cloning</strong> can copy matter types between intranets.</li>
            <li><strong>Matter cloning</strong> can clone entire matters to a different intranet (maps users by SSN matching).</li>
          </ul>
        `
      }
    ]
  },

  // ============================================================
  // 21. MATTER TYPE CONFIGURATION
  // ============================================================
  {
    id: 'matter-types',
    title: 'Matter Type Configuration',
    icon: 'fa-briefcase',
    audience: 'admin',
    subsections: [
      {
        id: 'managing-matter-types',
        title: 'Managing Matter Types',
        audience: 'admin',
        content: `
          <h4>Matter Type Fields</h4>
          <p>Navigate to <strong>Administration &rarr; Matters &rarr; Matter Types</strong>.</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th><th>Notes</th></tr></thead>
            <tbody>
              <tr><td><strong>name</strong></td><td>Type name</td><td>e.g., &ldquo;Personal Injury&rdquo;, &ldquo;Workers&rsquo; Compensation&rdquo;, &ldquo;Estate&rdquo;</td></tr>
              <tr><td><strong>consult_fee</strong></td><td>Default consultation fee</td><td>Decimal (e.g., 250.00). Used in appointment/intake workflow.</td></tr>
              <tr><td><strong>retainer</strong></td><td>Default retainer amount</td><td>Decimal. Suggested retainer for new matters of this type.</td></tr>
              <tr><td><strong>is_flat_fee</strong></td><td>Flat fee allowed</td><td>Checkbox. If checked, matters of this type can use flat fee billing.</td></tr>
              <tr><td><strong>is_hourly</strong></td><td>Hourly billing allowed</td><td>Checkbox. Standard time-based billing.</td></tr>
              <tr><td><strong>is_no_charge</strong></td><td>Pro bono / no charge</td><td>Checkbox. No fees charged.</td></tr>
              <tr><td><strong>is_percentage</strong></td><td>Contingency allowed</td><td>Checkbox. Percentage-based fee arrangement.</td></tr>
              <tr><td><strong>invoice_by_default</strong></td><td>Auto-enable invoicing</td><td>If true, new matters of this type default to <code>do_invoice = true</code>.</td></tr>
              <tr><td><strong>primary_activity_id</strong></td><td>Default billing activity</td><td>Activity used for new billing entries on this matter type.</td></tr>
              <tr><td><strong>regarding</strong></td><td>Boilerplate &ldquo;regarding&rdquo; text</td><td>Used in engagement letters/fee agreements as the &ldquo;RE:&rdquo; description.</td></tr>
              <tr><td><strong>we_will_do</strong></td><td>Boilerplate scope of work</td><td>Used in engagement letters as the description of services.</td></tr>
              <tr><td><strong>graph_group</strong></td><td>Grouping for graphs</td><td>Groups multiple matter types together in attorney performance graphs.</td></tr>
              <tr><td><strong>graph_label</strong></td><td>Display label on graphs</td><td>How this type appears on graph legends.</td></tr>
            </tbody>
          </table>

          <h5>Activity/Matter/Rate Types</h5>
          <p>The <code>activity_matter_rates</code> join table connects matter types to rate types and activities, defining which billing rates apply to each matter type. Configure at <strong>Administration &rarr; Activity/Matter/Rate Types</strong>.</p>
        `
      },
      {
        id: 'cloning-matter-types',
        title: 'Cloning Matter Types',
        audience: 'admin',
        content: `
          <h4>Cloning Between Intranets</h4>
          <p>Matter types can be cloned between intranets for consistency across related firms.</p>

          <h5>Clone To (export to another intranet)</h5>
          <ol>
            <li>Select matter types using the checkboxes in the table.</li>
            <li>In the <strong>Clone To</strong> sidebar, select the target intranet.</li>
            <li>Optionally include:
              <ul>
                <li><strong>Client intake fields</strong> &mdash; Copy the intake form configuration</li>
                <li><strong>Accounting dependencies</strong> &mdash; Copy activity/matter/rate type associations</li>
              </ul>
            </li>
            <li>Click <strong>Go</strong>.</li>
          </ol>

          <h5>Clone From (import from another intranet)</h5>
          <ol>
            <li>In the <strong>Clone From</strong> sidebar, select the source intranet.</li>
            <li>Select the matter type to import.</li>
            <li>Same optional includes as Clone To.</li>
            <li>Click <strong>Go</strong>.</li>
          </ol>
        `
      }
    ]
  },

  // ============================================================
  // 22. DOCUMENT TEMPLATES
  // ============================================================
  {
    id: 'doc-templates',
    title: 'Document Templates',
    icon: 'fa-file-code',
    audience: 'admin',
    subsections: [
      {
        id: 'template-overview',
        title: 'Template System Overview',
        audience: 'admin',
        content: `
          <h4>Document Template Architecture</h4>
          <p>The template system has two layers:</p>
          <ul>
            <li><strong>DocTemplate</strong> (low-level) &mdash; Defines the physical document structure: logo, letterhead, watermark, stylesheets, margins. Contains <strong>DocParts</strong> for structural sections (to, cc, re, salutation, signature, close, body).</li>
            <li><strong>DocumentTemplate</strong> (high-level) &mdash; User-facing template that optionally links to a DocTemplate. Associates with a matter type, document type, and activity.</li>
          </ul>

          <h5>DocTemplate Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>name</strong></td><td>Unique name per intranet</td></tr>
              <tr><td><strong>document_type</strong></td><td>Document type for generated documents</td></tr>
              <tr><td><strong>matter_type</strong></td><td>Which matter type this template is for (or all)</td></tr>
              <tr><td><strong>is_letter</strong></td><td>Whether this generates a letter (with To/From/Re format)</td></tr>
              <tr><td><strong>is_global</strong></td><td>Available to all intranets (vs. intranet-specific)</td></tr>
              <tr><td><strong>require_envelope</strong></td><td>Generate an envelope when producing the document</td></tr>
              <tr><td><strong>require_proof</strong></td><td>Generate a proof of service/mailing</td></tr>
              <tr><td><strong>send_check</strong></td><td>Accompany with a check</td></tr>
              <tr><td><strong>merge_parts</strong></td><td>Merge multiple parts into one document</td></tr>
              <tr><td><strong>watermark_start_page</strong></td><td>Page to start the letterhead watermark (usually 2 for letters)</td></tr>
              <tr><td><strong>logo_id</strong></td><td>Logo for the letterhead/watermark</td></tr>
              <tr><td><strong>stylesheets</strong></td><td>CSS stylesheets for formatting</td></tr>
            </tbody>
          </table>

          <h5>Document Generation Flow</h5>
          <ol>
            <li>User selects template and recipients in the document sidebar.</li>
            <li>Template content is processed: <strong>tag substitution</strong> replaces <code>&lt;r:tag/&gt;</code> placeholders with real data.</li>
            <li>DocParts are assembled into the document structure.</li>
            <li>HTML is rendered with stylesheets.</li>
            <li>HTML is converted to PDF via <strong>wkhtmltopdf</strong>.</li>
            <li>If a watermark/letterhead is configured, it&rsquo;s composited onto the PDF via <strong>pdftk</strong>.</li>
          </ol>
        `
      },
      {
        id: 'template-parts',
        title: 'Template Parts (DocParts)',
        audience: 'admin',
        content: `
          <h4>DocPart Types</h4>
          <p>DocParts are the building blocks of a template. Each part has a <strong>type</strong> that determines where it appears in the generated document:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Part Type</th><th>Location</th><th>Example Content</th></tr></thead>
            <tbody>
              <tr><td><strong>to</strong></td><td>Recipient address block</td><td>Contact name and address</td></tr>
              <tr><td><strong>cc</strong></td><td>CC line</td><td>Copy recipients</td></tr>
              <tr><td><strong>re</strong></td><td>RE: line</td><td>Subject/regarding line</td></tr>
              <tr><td><strong>salutation</strong></td><td>Greeting</td><td>&ldquo;Dear Mr. Smith,&rdquo;</td></tr>
              <tr><td><strong>signature</strong></td><td>Signature block</td><td>Signer name and title</td></tr>
              <tr><td><strong>close</strong></td><td>Closing</td><td>&ldquo;Very truly yours,&rdquo;</td></tr>
              <tr><td><strong>(body)</strong></td><td>Main content</td><td>The letter/document body text</td></tr>
            </tbody>
          </table>

          <p>Parts can contain tags for dynamic content. Create and manage parts from <strong>Documents &rarr; Document Templates</strong>.</p>
        `
      },
      {
        id: 'tag-reference',
        title: 'Tag System Reference',
        audience: 'admin',
        content: `
          <h4>Template Tag System</h4>
          <p>Tags use the <strong>Radius</strong> tag syntax: <code>&lt;r:tag_name attribute=&quot;value&quot;/&gt;</code></p>
          <p>Access the full interactive reference from <strong>Documents &rarr; Document Templates &rarr; Tag system ref</strong>.</p>

          <h5>Tag Categories</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Category</th><th>Examples</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Firm</strong></td><td><code>&lt;r:firm_name/&gt;</code>, <code>&lt;r:firm_address/&gt;</code>, <code>&lt;r:firm_phone/&gt;</code></td><td>Firm/intranet information</td></tr>
              <tr><td><strong>Matter</strong></td><td><code>&lt;r:matter_number/&gt;</code>, <code>&lt;r:matter_name/&gt;</code>, <code>&lt;r:matter_type/&gt;</code></td><td>Matter fields and dates</td></tr>
              <tr><td><strong>Contact</strong></td><td><code>&lt;r:contact type=&quot;client&quot;&gt;</code>, <code>&lt;r:contacts type=&quot;client, plaintiff&quot;&gt;</code></td><td>Contact by role type (supports multiple comma-separated types with deduplication)</td></tr>
              <tr><td><strong>Involvement</strong></td><td><code>&lt;r:client_name/&gt;</code>, <code>&lt;r:defendant_name/&gt;</code></td><td>Role-specific contact information</td></tr>
              <tr><td><strong>Billing</strong></td><td><code>&lt;r:total_due/&gt;</code>, <code>&lt;r:escrow_balance/&gt;</code></td><td>Financial data</td></tr>
              <tr><td><strong>Date</strong></td><td><code>&lt;r:today/&gt;</code>, <code>&lt;r:today format=&quot;%B %d, %Y&quot;/&gt;</code></td><td>Date formatting (Ruby strftime)</td></tr>
              <tr><td><strong>Conditional</strong></td><td><code>&lt;r:if_role role=&quot;defendant&quot;&gt;...&lt;/r:if_role&gt;</code></td><td>Conditional content based on roles, values, etc.</td></tr>
              <tr><td><strong>Detail</strong></td><td><code>&lt;r:detail type=&quot;Flat Fee&quot;/&gt;</code></td><td>Matter detail values</td></tr>
              <tr><td><strong>Matter Date</strong></td><td><code>&lt;r:matter_date type=&quot;Date of Accident&quot;/&gt;</code></td><td>Matter date values by type</td></tr>
              <tr><td><strong>User</strong></td><td><code>&lt;r:lead_attorney/&gt;</code>, <code>&lt;r:paralegal/&gt;</code></td><td>Assigned user information</td></tr>
              <tr><td><strong>History</strong></td><td><code>&lt;r:last_history/&gt;</code></td><td>Matter history references</td></tr>
              <tr><td><strong>Sentence</strong></td><td><code>&lt;r:sentence role=&quot;client&quot; field=&quot;name&quot;/&gt;</code></td><td>Comma-separated list of contacts by role</td></tr>
            </tbody>
          </table>

          <h5>Multi-Type Contact Tags</h5>
          <p>Contact tags (<code>&lt;r:contact&gt;</code>, <code>&lt;r:contacts&gt;</code>, <code>&lt;r:if_contact&gt;</code>, <code>&lt;r:unless_contact&gt;</code>) support <strong>multiple comma-separated types</strong>. Duplicates are automatically eliminated so each contact appears only once.</p>
          <pre><code>&lt;r:contacts type=&quot;client, plaintiff&quot;&gt;
  &lt;r:display_name/&gt;
&lt;/r:contacts&gt;</code></pre>

          <h5>Client Portal Password</h5>
          <p>Use <code>&lt;r:share_password/&gt;</code> inside a contact scope to print that contact&rsquo;s client-portal password &mdash; useful in welcome letters or fee agreements where you tell the client how to sign in. Returns nothing if the contact has no password set.</p>
          <pre><code>&lt;r:contact type=&quot;client&quot;&gt;
  Your temporary client-portal password is &lt;strong&gt;&lt;r:share_password/&gt;&lt;/strong&gt;.
  Sign in at https://v7.attymatter.com/share_sessions/new with your email address.
&lt;/r:contact&gt;</code></pre>

          <div class="alert alert-info"><strong>Tip:</strong> When debugging tag issues, check the generated HTML output. Tags that fail to resolve show an error in red text (in development) or are silently replaced with empty strings (in production). Common causes: misspelled tag name, no contact with the specified role, or missing matter data.</div>
        `
      },
      {
        id: 'stylesheets',
        title: 'Stylesheets & Fonts',
        audience: 'admin',
        content: `
          <h4>Document Styling</h4>
          <p>Stylesheets control the formatting of generated documents (fonts, margins, spacing, etc.):</p>
          <ul>
            <li><strong>Stylesheets</strong> (<strong>Documents &rarr; Stylesheets</strong>) &mdash; CSS rules applied to generated documents. Each DocTemplate can reference one or more stylesheets.</li>
            <li><strong>Stylesheet Groups</strong> (<strong>Documents &rarr; Stylesheet Groups</strong>) &mdash; Organize stylesheets by purpose (e.g., &ldquo;Letters&rdquo;, &ldquo;Court Filings&rdquo;, &ldquo;Internal&rdquo;).</li>
            <li><strong>Fonts</strong> (<strong>Documents &rarr; Fonts</strong>) &mdash; Upload custom fonts for use in documents. Fonts are embedded in the generated PDFs.</li>
          </ul>
          <p>Since documents are rendered as HTML before PDF conversion, standard CSS properties work for styling (font-family, font-size, margins, line-height, etc.).</p>
        `
      }
    ]
  },

  // ============================================================
  // 23. AUTO RULES
  // ============================================================
  {
    id: 'auto-rules',
    title: 'Auto Rules',
    icon: 'fa-robot',
    audience: 'admin',
    subsections: [
      {
        id: 'auto-rules-overview',
        title: 'Overview',
        audience: 'admin',
        content: `
          <h4>Automation Rules</h4>
          <p>Auto rules trigger actions automatically when scans are released (filed to matters). They are configured per document type. Access from <strong>Documents &rarr; Auto Rules</strong>.</p>

          <table class="table table-sm table-bordered">
            <thead><tr><th>Rule Type</th><th>Trigger</th><th>Action</th><th>Example Use</th></tr></thead>
            <tbody>
              <tr><td><strong>Auto Email Rules</strong></td><td>Scan released with matching document type</td><td>Send notification email to matter users</td><td>&ldquo;Email the lead attorney when a Medical Record is filed&rdquo;</td></tr>
              <tr><td><strong>Auto Outgoing Rules</strong></td><td>Scan released with matching document type</td><td>Create an outgoing job (fax, mail, or email)</td><td>&ldquo;Fax a copy to the insurance company when a Doctor&rsquo;s Report is filed&rdquo;</td></tr>
              <tr><td><strong>Auto Redirect Rules</strong></td><td>Scan released with matching document type</td><td>Redirect user to a page (new event, receipt, or payable)</td><td>&ldquo;Open the New Receipt form when an Invoice is released&rdquo;</td></tr>
              <tr><td><strong>Auto Todo Rules</strong></td><td>Scan released with matching document type</td><td>Create a reminder/todo N days after release</td><td>&ldquo;Remind the paralegal in 30 days to follow up on filed Medical Records&rdquo;</td></tr>
            </tbody>
          </table>

          <h5>Common Concepts</h5>
          <p>All four rule types share these fields:</p>
          <ul>
            <li><strong>Matter (Scope)</strong> &mdash; Leave blank for a <em>Global</em> rule that applies to all matters. Set a specific matter to limit the rule to that matter only.</li>
            <li><strong>Document Type</strong> &mdash; The document type(s) that trigger the rule. The scan&rsquo;s document type must match.</li>
            <li><strong>Minimum Priority</strong> &mdash; Scale of 0 (highest) to 10 (lowest). The rule only fires when the scan&rsquo;s priority is <strong>at or above</strong> (numerically &le;) this threshold. Default is 10 (fires for all priorities).</li>
          </ul>
          <div class="alert alert-info"><strong>Priority example:</strong> A rule with minimum priority 3 will fire for scans with priority 0, 1, 2, or 3 but NOT for 4&ndash;10.</div>

          <h5>How Auto Rules Execute</h5>
          <ul>
            <li>Rules fire when scans are released (filed to matters).</li>
            <li>Multiple rules can fire for the same scan &mdash; they are independent.</li>
            <li>Rules apply per intranet. Each intranet has its own set of auto rules.</li>
            <li>All rule changes are tracked via Paper Trail versioning for audit purposes.</li>
          </ul>

          <h5>Scan File-Management Billing</h5>
          <p>A scan upload creates a non-billable <strong>Staff - File Management</strong> entry. When the scan is successfully released, V7 assigns that entry to the user who completed the release. The original scanner/API user remains in the entry&rsquo;s creation audit trail.</p>
        `
      },
      {
        id: 'auto-email-rules',
        title: 'Auto Email Rules',
        audience: 'admin',
        content: `
          <h4>Auto Email Rules</h4>
          <p>Automatically send email notifications to matter users when a scan with a matching document type is released.</p>
          <p><strong>Location:</strong> <strong>Documents &rarr; Auto Rules &rarr; Auto Email Rules</strong></p>

          <h5>Form Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Required</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Matter</strong></td><td>No</td><td>Scope the rule to a specific matter, or leave blank for all matters (Global).</td></tr>
              <tr><td><strong>Matter User Type</strong></td><td>No</td><td>Send only to users of a specific role (e.g., Lead Attorney, Paralegal). Choose &ldquo;All&rdquo; to notify everyone on the matter.</td></tr>
              <tr><td><strong>Send to All-Notifications Users</strong></td><td>No</td><td>Checkbox. Additionally send to users flagged as &ldquo;included in all notifications&rdquo; on the matter.</td></tr>
              <tr><td><strong>Minimum Priority</strong></td><td>No</td><td>0 (high) to 10 (low). Default: 10. Only scans at or above this priority trigger the rule.</td></tr>
              <tr><td><strong>Document Types</strong></td><td>Yes</td><td>Select one or more document types. Supports List or Tree view with prefix filter. Selecting a parent selects all children.</td></tr>
            </tbody>
          </table>

          <h5>How It Works</h5>
          <ol>
            <li>A scan is released with a matching document type and sufficient priority.</li>
            <li>The system finds all email rules matching the document type and matter (or global rules).</li>
            <li>For each rule, it identifies the recipients:
              <ul>
                <li>If <strong>Matter User Type</strong> = &ldquo;All&rdquo; &rarr; all active users on the matter.</li>
                <li>If a specific user type is selected &rarr; only users of that type.</li>
                <li>If <strong>Send to All-Notifications Users</strong> is checked &rarr; additionally includes those users.</li>
              </ul>
            </li>
            <li>A notification email is sent to each recipient with scan details.</li>
          </ol>

          <h5>Example Setup</h5>
          <p><em>&ldquo;Notify the lead attorney whenever a Medical Record is filed on any matter.&rdquo;</em></p>
          <table class="table table-sm table-bordered">
            <tbody>
              <tr><td>Matter</td><td><em>(blank &mdash; Global)</em></td></tr>
              <tr><td>Matter User Type</td><td>Lead Attorney</td></tr>
              <tr><td>All-Notifications Users</td><td>Unchecked</td></tr>
              <tr><td>Minimum Priority</td><td>10 (all priorities)</td></tr>
              <tr><td>Document Types</td><td>Medical Record</td></tr>
            </tbody>
          </table>

          <h5>Index View Columns</h5>
          <p><strong>Id</strong> &bull; <strong>Scope</strong> (matter name or &ldquo;Global&rdquo;) &bull; <strong>Document Type(s)</strong> &bull; <strong>User Type</strong> (with &ldquo;Notify&rdquo; badge if all-notifications is on) &bull; <strong>Actions</strong> (delete)</p>
        `
      },
      {
        id: 'auto-outgoing-rules',
        title: 'Auto Outgoing Rules',
        audience: 'admin',
        content: `
          <h4>Auto Outgoing Rules</h4>
          <p>Automatically create outgoing jobs to send documents to contacts involved in a matter when a scan with a matching document type is released.</p>
          <p><strong>Location:</strong> <strong>Documents &rarr; Auto Rules &rarr; Auto Outgoing Rules</strong></p>

          <h5>Form Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Required</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Matter</strong></td><td>No</td><td>Scope to a specific matter, or leave blank for Global.</td></tr>
              <tr><td><strong>Document Type</strong></td><td>Yes</td><td>The document type that triggers this rule.</td></tr>
              <tr><td><strong>Involvement Role</strong></td><td>Yes</td><td>Which role on the matter to send to (e.g., &ldquo;Insurance Company&rdquo;, &ldquo;Opposing Counsel&rdquo;).</td></tr>
              <tr><td><strong>Send to</strong></td><td>Yes</td><td>
                <ul class="mb-0">
                  <li><strong>First contact of that role</strong> &mdash; only the first involvement matching the role.</li>
                  <li><strong>All contacts of that role</strong> &mdash; every involvement matching the role.</li>
                </ul>
              </td></tr>
              <tr><td><strong>Send via</strong></td><td>Yes</td><td>
                <ul class="mb-0">
                  <li><strong>Automatically select best method</strong> &mdash; tries email first, then fax, then mail.</li>
                  <li><strong>Postal mail only</strong> &mdash; always sends via print/postal mail.</li>
                  <li><strong>Fax only</strong> &mdash; always sends via fax.</li>
                </ul>
              </td></tr>
              <tr><td><strong>Minimum Priority</strong></td><td>No</td><td>0 (high) to 10 (low). Default: 10.</td></tr>
            </tbody>
          </table>

          <h5>How It Works</h5>
          <ol>
            <li>A scan is released with a matching document type and sufficient priority.</li>
            <li>The system finds the involvement(s) on the matter matching the specified role.</li>
            <li>For each contact, it determines the delivery method:
              <ul>
                <li><strong>Auto:</strong> Email (share) &rarr; Fax &rarr; Mail &rarr; skip if none available.</li>
                <li><strong>Mail:</strong> Uses the contact&rsquo;s primary address. Skips if no address.</li>
                <li><strong>Fax:</strong> Uses the contact&rsquo;s fax number. Skips if no fax.</li>
              </ul>
            </li>
            <li>An <strong>Outgoing Job</strong> is created with status &ldquo;waiting&rdquo; and the document attached.</li>
            <li>For email shares, the share email is sent immediately. For mail/fax, the job appears in the outgoing queue.</li>
            <li>The document is marked as <strong>non-editable</strong> once sent.</li>
          </ol>

          <h5>Uniqueness Constraint</h5>
          <p>Each combination of document type + role + send method + send-to + matter must be unique. You cannot create duplicate rules.</p>

          <h5>Example Setup</h5>
          <p><em>&ldquo;Fax a copy of every Doctor&rsquo;s Report to the insurance company on any matter.&rdquo;</em></p>
          <table class="table table-sm table-bordered">
            <tbody>
              <tr><td>Matter</td><td><em>(blank &mdash; Global)</em></td></tr>
              <tr><td>Document Type</td><td>Doctor&rsquo;s Report</td></tr>
              <tr><td>Involvement Role</td><td>Insurance Company</td></tr>
              <tr><td>Send to</td><td>First contact of that role</td></tr>
              <tr><td>Send via</td><td>Fax only</td></tr>
              <tr><td>Minimum Priority</td><td>10 (all priorities)</td></tr>
            </tbody>
          </table>

          <h5>Index View Columns</h5>
          <p><strong>Id</strong> &bull; <strong>Scope</strong> (matter or &ldquo;Global&rdquo;) &bull; <strong>Document Type</strong> &bull; <strong>To</strong> (e.g., &ldquo;First Insurance Company&rdquo; or &ldquo;All Opposing Counsel&rdquo;) &bull; <strong>Via</strong> (Auto/Mail/Fax) &bull; <strong>Actions</strong> (delete)</p>
        `
      },
      {
        id: 'auto-redirect-rules',
        title: 'Auto Redirect Rules',
        audience: 'admin',
        content: `
          <h4>Auto Redirect Rules</h4>
          <p>Automatically redirect the user to a specific page (new event, receipt, or payable) after releasing a scan with a matching document type. Useful for documents that always require a follow-up action.</p>
          <p><strong>Location:</strong> <strong>Documents &rarr; Auto Rules &rarr; Auto Redirect Rules</strong></p>

          <h5>Form Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Required</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Matter</strong></td><td>No</td><td>Scope to a specific matter, or leave blank for Global.</td></tr>
              <tr><td><strong>Document Type</strong></td><td>Yes</td><td>The document type that triggers the redirect. <strong>Only one rule per document type per intranet.</strong></td></tr>
              <tr><td><strong>Destination</strong></td><td>Yes</td><td>Where to redirect the user:
                <ul class="mb-0">
                  <li><strong>New Event</strong> &mdash; opens the new event form</li>
                  <li><strong>New Payable</strong> &mdash; opens the new payable form</li>
                  <li><strong>New Receipt</strong> &mdash; opens the new receipt form</li>
                </ul>
              </td></tr>
              <tr><td><strong>Special Instructions</strong></td><td>No</td><td>Free-text notes about this rule (informational only, not shown to the user on redirect).</td></tr>
              <tr><td><strong>Minimum Priority</strong></td><td>No</td><td>0 (high) to 10 (low). Default: 10.</td></tr>
            </tbody>
          </table>

          <h5>How It Works</h5>
          <ol>
            <li>A user releases a scan with a matching document type and sufficient priority.</li>
            <li>Instead of returning to the scan queue, the user is redirected to the destination page.</li>
            <li>The destination form is pre-populated with context from the scan:
              <ul>
                <li>Matter ID and matter list</li>
                <li>Contact and vendor from the scan&rsquo;s first author</li>
                <li>Amount from the scan</li>
                <li>Scan ID for linking</li>
              </ul>
            </li>
            <li>Only the <strong>first matching rule</strong> is used (one redirect per scan release).</li>
          </ol>

          <h5>Uniqueness Constraint</h5>
          <p>Only <strong>one redirect rule per document type</strong> per intranet is allowed. If you need different behavior per matter, create matter-specific rules.</p>

          <h5>Example Setup</h5>
          <p><em>&ldquo;When an Invoice is released, open the New Receipt form so the user can record the payment immediately.&rdquo;</em></p>
          <table class="table table-sm table-bordered">
            <tbody>
              <tr><td>Matter</td><td><em>(blank &mdash; Global)</em></td></tr>
              <tr><td>Document Type</td><td>Invoice</td></tr>
              <tr><td>Destination</td><td>New Receipt</td></tr>
              <tr><td>Special Instructions</td><td>Record payment from vendor invoice</td></tr>
              <tr><td>Minimum Priority</td><td>10 (all priorities)</td></tr>
            </tbody>
          </table>

          <h5>Index View Columns</h5>
          <p><strong>Id</strong> &bull; <strong>Scope</strong> (matter or &ldquo;Global&rdquo;) &bull; <strong>Document Type</strong> &bull; <strong>To</strong> (destination page) &bull; <strong>Actions</strong> (delete)</p>
        `
      },
      {
        id: 'auto-todo-rules',
        title: 'Auto Todo Rules',
        audience: 'admin',
        content: `
          <h4>Auto Todo Rules</h4>
          <p>Automatically create a reminder (todo) a specified number of days after a scan with a matching document type is released. The reminder is assigned to users on the matter.</p>
          <p><strong>Location:</strong> <strong>Documents &rarr; Auto Rules &rarr; Auto Todo Rules</strong></p>

          <h5>Form Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Required</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>Matter</strong></td><td>No</td><td>Scope to a specific matter, or leave blank for Global.</td></tr>
              <tr><td><strong>Days After Scan</strong></td><td>Yes</td><td>Number of days after the scan release date to create the reminder. Use 0 for immediate.</td></tr>
              <tr><td><strong>Subject</strong></td><td>Yes</td><td>Title of the reminder that will be created.</td></tr>
              <tr><td><strong>Message</strong></td><td>Yes</td><td>Body/description of the reminder.</td></tr>
              <tr><td><strong>Minimum Priority</strong></td><td>No</td><td>0 (high) to 10 (low). Default: 10.</td></tr>
              <tr><td><strong>Document Types</strong></td><td>Yes</td><td>Select one or more document types. Supports List or Tree view with prefix filter.</td></tr>
            </tbody>
          </table>

          <h5>How It Works</h5>
          <ol>
            <li>A scan is released with a matching document type and sufficient priority.</li>
            <li>The system calculates the reminder date: scan release date + <strong>Days After Scan</strong> days (scheduled for 7:00 AM).</li>
            <li>Reminders are created for the appropriate users on the matter:
              <ul>
                <li>Users flagged as &ldquo;included in all notifications&rdquo; on the matter.</li>
                <li>Users matching a specified matter user type (if configured).</li>
                <li>Falls back to the matter&rsquo;s <strong>lead attorney</strong> if no other users match.</li>
              </ul>
            </li>
            <li>Each user may receive up to three reminder delivery methods: browser notification, email, and/or SMS &mdash; based on their personal notification settings.</li>
          </ol>

          <h5>Example Setup</h5>
          <p><em>&ldquo;Remind the team in 30 days to follow up on any Medical Record that gets filed.&rdquo;</em></p>
          <table class="table table-sm table-bordered">
            <tbody>
              <tr><td>Matter</td><td><em>(blank &mdash; Global)</em></td></tr>
              <tr><td>Days After Scan</td><td>30</td></tr>
              <tr><td>Subject</td><td>Follow up on Medical Record</td></tr>
              <tr><td>Message</td><td>A medical record was filed 30 days ago. Please review and take any necessary action.</td></tr>
              <tr><td>Minimum Priority</td><td>10 (all priorities)</td></tr>
              <tr><td>Document Types</td><td>Medical Record</td></tr>
            </tbody>
          </table>

          <h5>Index View Columns</h5>
          <p><strong>Id</strong> &bull; <strong>Scope</strong> (matter or &ldquo;Global&rdquo;) &bull; <strong>Document Type(s)</strong> &bull; <strong>Days After</strong> &bull; <strong>Subject</strong> &bull; <strong>Message</strong> &bull; <strong>Actions</strong> (delete)</p>
        `
      },
      {
        id: 'auto-rules-tips',
        title: 'Tips & Troubleshooting',
        audience: 'admin',
        content: `
          <h4>Auto Rules Tips &amp; Troubleshooting</h4>

          <h5>Best Practices</h5>
          <ul>
            <li><strong>Start with Global rules.</strong> Only create matter-specific rules when a matter needs different behavior from the norm.</li>
            <li><strong>Use priority filtering</strong> to avoid low-priority scans (like junk mail) from triggering automations. Setting minimum priority to 2 or 3 is a good default for most rules.</li>
            <li><strong>Test with a single matter first.</strong> Create the rule scoped to one matter, release a test scan, and verify the behavior before making it Global.</li>
            <li><strong>Check the outgoing queue</strong> after setting up auto outgoing rules. Verify that jobs are being created with the correct delivery method and recipient.</li>
          </ul>

          <h5>Common Issues</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Problem</th><th>Cause</th><th>Fix</th></tr></thead>
            <tbody>
              <tr><td>Rule doesn&rsquo;t fire</td><td>Scan priority is lower than rule&rsquo;s minimum priority</td><td>Lower the minimum priority on the rule, or increase the scan&rsquo;s priority</td></tr>
              <tr><td>Rule doesn&rsquo;t fire</td><td>Document type doesn&rsquo;t match exactly</td><td>Verify the scan&rsquo;s document type matches the rule&rsquo;s document type (check for parent vs. child types)</td></tr>
              <tr><td>Email rule sends to wrong people</td><td>Matter user type is set incorrectly</td><td>Edit the rule and change the Matter User Type, or set to &ldquo;All&rdquo;</td></tr>
              <tr><td>Outgoing rule skips a contact</td><td>Contact has no address/fax/email for the selected send method</td><td>Update the contact&rsquo;s information, or switch send method to &ldquo;Auto&rdquo;</td></tr>
              <tr><td>Redirect rule doesn&rsquo;t redirect</td><td>Another rule for the same document type already exists</td><td>Only one redirect rule per document type is allowed. Edit or delete the existing one.</td></tr>
              <tr><td>Todo/reminder not created</td><td>No users on the matter match the notification criteria</td><td>Ensure the matter has a lead attorney or users flagged for notifications</td></tr>
            </tbody>
          </table>

          <h5>Searching &amp; Filtering</h5>
          <p>All four rule index pages have a sidebar with:</p>
          <ul>
            <li><strong>Matter filter</strong> &mdash; type 3+ characters to search by matter name/number.</li>
            <li><strong>Document Type filter</strong> &mdash; type 2+ characters to search by document type name or prefix.</li>
            <li><strong>Clear button</strong> &mdash; resets all filters.</li>
            <li><strong>Pagination</strong> &mdash; navigate through large rule sets.</li>
          </ul>
        `
      }
    ]
  },

  // ============================================================
  // 24. REPORTS & GRAPHS
  // ============================================================
  {
    id: 'reports',
    title: 'Reports & Graphs',
    icon: 'fa-chart-bar',
    audience: 'admin',
    subsections: [
      {
        id: 'financial-reports',
        title: 'Financial Reports',
        audience: 'admin',
        content: `
          <h4>Available Reports</h4>
          <p>Access from <strong>Administration &rarr; Reports</strong>:</p>

          <h5>Balance Sheet</h5>
          <p>Shows assets, liabilities, and equity at a point in time. Accounts are organized by COA type. Totals should satisfy: Assets = Liabilities + Equity.</p>

          <h5>Profit &amp; Loss (P&amp;L)</h5>
          <p>Income vs expenses for a time period. Supports <strong>up to 4-year comparison</strong> side by side.</p>
          <p><strong>Reversed transactions are ignored.</strong> When a transaction is reversed, V7 leaves both accounting entries in the ledger for the audit trail but omits the original entry and its reversal from the P&amp;L. This is the same behavior as a ledger search with <strong>Hide Reversed</strong> selected. As a result, an account&rsquo;s P&amp;L amount equals its non-reversed credits minus its non-reversed debits, even when the original transaction and reversal fall in different years.</p>

          <h5>Ledger Detail</h5>
          <p>Line-item view of every transaction for a specific account or date range. Shows individual debits and credits.</p>

          <h5>End of Year Reports</h5>
          <p>Comprehensive annual financial summaries. Available for: this year, last year, 2 years ago. Can be sent to:</p>
          <ul>
            <li><strong>Accountant</strong> &mdash; Sends the report to the configured accountant contact</li>
            <li><strong>Self</strong> &mdash; Sends to the current user</li>
            <li><strong>Both</strong> &mdash; Sends to accountant and current user</li>
          </ul>

          <h5>Other Reports</h5>
          <ul>
            <li><strong>Wage Reports</strong> &mdash; Employee wage summaries by period</li>
            <li><strong>Workers&rsquo; Compensation Report</strong> &mdash; WC insurance reporting data</li>
          </ul>

          <p>All major reports support <strong>CSV export</strong>.</p>
        `
      },
      {
        id: 'graphs',
        title: 'Graphs & Analytics',
        audience: 'admin',
        content: `
          <h4>Visual Analytics</h4>
          <p>Access from <strong>Administration &rarr; Graphs</strong>:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Graph</th><th>Shows</th><th>Useful For</th></tr></thead>
            <tbody>
              <tr><td><strong>Signed Fee Agreements</strong></td><td>New client sign-ups over time</td><td>Tracking intake trends, marketing effectiveness</td></tr>
              <tr><td><strong>Receipts</strong></td><td>Revenue received over time</td><td>Cash flow tracking, revenue trends</td></tr>
              <tr><td><strong>Attorney Graphs</strong></td><td>Per-attorney metrics</td><td>Productivity tracking, billing comparisons. Each attorney has their own graph page.</td></tr>
            </tbody>
          </table>
          <p>Attorney graphs are broken down by the <code>graph_group</code> and <code>graph_label</code> configured on matter types.</p>
        `
      }
    ]
  },

  // ============================================================
  // ============================================================
  // EMAIL PROCESSING (MAILMAN2)
  // ============================================================
  {
    id: 'email-processing',
    title: 'Email Processing (Mailman2)',
    icon: 'fa-envelope',
    audience: 'admin',
    subsections: [
      {
        id: 'email-overview',
        title: 'Architecture Overview',
        audience: 'admin',
        content: `
          <h4>Email Processing Architecture</h4>
          <p><strong>Mailman2</strong> is a standalone Rails 8 application that runs on the mail/storage server (<code>storage.princelaw.com</code>). It receives inbound email from Postfix and processes it into V7&rsquo;s data structures &mdash; archiving emails, filing documents to matters, creating matter history entries, generating support tickets, and handling court notices.</p>

          <div class="alert alert-info"><strong>Key Point:</strong> Mailman2 is <em>not</em> the GNU Mailman mailing list software. It is a custom Rails application that uses Rails Action Mailbox to receive email from Postfix and route it to specialized mailbox processors.</div>

          <h5>How Email Flows Through the System</h5>
          <ol>
            <li><strong>Email arrives</strong> at the mail server (Postfix on <code>storage.princelaw.com</code>).</li>
            <li><strong>Postfix <code>always_bcc</code></strong> sends a copy of every email to <code>archive@attymatter.com</code>.</li>
            <li><strong>Postfix transport map</strong> routes <code>attymatter.com</code> and <code>archive@attymatter.com</code> addresses to the <code>etof</code> transport.</li>
            <li>The <code>etof</code> transport pipes the email to <strong><code>/etc/postfix/send_email_to_m2.sh</code></strong>.</li>
            <li>The script calls <strong>Rails Action Mailbox ingress</strong>: <code>bin/rails action_mailbox:ingress:postfix</code>.</li>
            <li><strong>ApplicationMailbox</strong> routes the email to the correct processor based on the recipient address.</li>
            <li>The processor archives the email, creates documents, files to matters, etc.</li>
          </ol>

          <h5>System Components</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Component</th><th>Location</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><strong>Postfix</strong></td><td><code>storage.princelaw.com</code></td><td>Mail Transfer Agent &mdash; receives, routes, and delivers email</td></tr>
              <tr><td><strong>send_email_to_m2.sh</strong></td><td><code>/etc/postfix/send_email_to_m2.sh</code></td><td>Postfix pipe script that passes email to Mailman2 via Action Mailbox</td></tr>
              <tr><td><strong>Mailman2 app</strong></td><td><code>/web/mailman2/site/current/</code></td><td>Rails 8 application with Action Mailbox processors</td></tr>
              <tr><td><strong>V7 database</strong></td><td><code>mx2.princelaw.com</code></td><td>Primary database (matters, contacts, history, documents)</td></tr>
              <tr><td><strong>Archive database</strong></td><td><code>mx2.princelaw.com</code></td><td>Email archive metadata (email_archives, email_attachments)</td></tr>
              <tr><td><strong>Email Archive on disk</strong></td><td><code>storage.princelaw.com</code></td><td>Raw email and attachment files; mounted on the V7 server beneath <code>/mnt/email_archive</code></td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'postfix-config',
        title: 'Postfix Configuration',
        audience: 'admin',
        content: `
          <h4>Postfix Email Routing</h4>
          <p>Postfix on the storage server is configured to route specific email domains and addresses to Mailman2 for processing.</p>

          <h5>Key Settings in <code>/etc/postfix/main.cf</code></h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Setting</th><th>Value</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><strong>always_bcc</strong></td><td><code>archive@attymatter.com</code></td><td>Every email sent/received gets a blind copy to the archive address. This is how <em>all</em> firm email is captured.</td></tr>
              <tr><td><strong>virtual_transport</strong></td><td><code>dovecot</code></td><td>Normal email delivery goes to Dovecot IMAP.</td></tr>
              <tr><td><strong>transport_maps</strong></td><td><code>hash:/etc/postfix/transport</code></td><td>Overrides delivery for specific domains/addresses.</td></tr>
              <tr><td><strong>virtual_mailbox_maps</strong></td><td>MySQL lookups</td><td>Mailbox configuration stored in the Postfix database.</td></tr>
            </tbody>
          </table>

          <h5>Transport Map (<code>/etc/postfix/transport</code>)</h5>
          <p>The transport map routes specific addresses and domains to custom transports instead of normal Dovecot delivery:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Address/Domain</th><th>Transport</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><code>attymatter.com</code></td><td><code>etof:</code></td><td>All attymatter.com email &rarr; Mailman2 production</td></tr>
              <tr><td><code>archive@attymatter.com</code></td><td><code>etof:</code></td><td>Archive BCC copies &rarr; Mailman2 production</td></tr>
              <tr><td><code>court_notices@princelaw.com</code></td><td><code>etof:</code></td><td>Court filing notices &rarr; Mailman2 production</td></tr>
              <tr><td><code>development.attymatter.com</code></td><td><code>etof_dev:</code></td><td>Dev emails &rarr; Mailman2 dev instance</td></tr>
              <tr><td><code>development.princelaw.com</code></td><td><code>etof_dev:</code></td><td>Dev emails &rarr; Mailman2 dev instance</td></tr>
            </tbody>
          </table>
          <p>After changing the transport file, run: <code>postmap /etc/postfix/transport &amp;&amp; systemctl reload postfix</code></p>

          <h5>Transports in <code>/etc/postfix/master.cf</code></h5>
          <p>The <code>etof</code> transport is defined as a Postfix <strong>pipe</strong> that invokes the shell script:</p>
          <pre><code>etof  unix  -  n  n  -  10  pipe
  flags=Rq user=archive null_sender=
  argv=/etc/postfix/send_email_to_m2.sh -e live -f \${sender} -t \${recipient}</code></pre>
          <p>Key settings:</p>
          <ul>
            <li><strong>user=archive</strong> &mdash; Runs as the <code>archive</code> Unix user</li>
            <li><strong>flags=Rq</strong> &mdash; R = prepend Return-Path header; q = quote special characters</li>
            <li><strong>maxproc=10</strong> &mdash; Up to 10 concurrent deliveries</li>
            <li><strong>null_sender=</strong> &mdash; Handle bounce messages (empty sender)</li>
          </ul>

          <h5>The Pipe Script: <code>/etc/postfix/send_email_to_m2.sh</code></h5>
          <pre><code>#!/bin/bash
source /etc/profile.d/rvm.sh
source /etc/profile.d/environ.sh
export PATH=\$PATH:/usr/local/rvm/rubies/ruby-3.4.1/bin/

cd /web/mailman2/site/current/
rvm use 3.4.1
RAILS_ENV=production bin/rails action_mailbox:ingress:postfix \\
  URL=http://localhost:83/rails/action_mailbox/relay/inbound_emails \\
  INGRESS_PASSWORD=4*5=Twenty</code></pre>
          <p>This script:</p>
          <ol>
            <li>Loads RVM and environment variables.</li>
            <li>Changes to the Mailman2 app directory.</li>
            <li>Calls <code>action_mailbox:ingress:postfix</code> which reads the email from stdin and POSTs it to the Action Mailbox relay endpoint.</li>
          </ol>
          <div class="alert alert-warning"><strong>Note:</strong> There are also <code>send_email_to_whp.sh</code> (development) and <code>send_email_to_devel.sh</code> (testing) scripts that route to different servers/ports for development work.</div>
        `
      },
      {
        id: 'email-routing',
        title: 'Email Routing (Mailboxes)',
        audience: 'admin',
        content: `
          <h4>ApplicationMailbox Routing</h4>
          <p>Once Mailman2 receives an email via Action Mailbox, the <strong>ApplicationMailbox</strong> routes it to the appropriate processor based on the recipient address:</p>

          <table class="table table-sm table-bordered">
            <thead><tr><th>Pattern</th><th>Mailbox</th><th>What It Does</th></tr></thead>
            <tbody>
              <tr><td><code>support@princelaw.com</code><br><code>support@attymatter.com</code></td><td><strong>SupportMailbox</strong></td><td>Creates or updates support tickets. Handles ticket commands (status, reassignment, merge) via email subject keywords.</td></tr>
              <tr><td><code>court_notices@princelaw.com</code><br><code>court_filings@princelaw.com</code></td><td><strong>CourtNoticeMailbox</strong></td><td>Parses court filing notices. Extracts case/docket numbers, matches to matters, creates reminders for lead attorneys with 3-day delivery.</td></tr>
              <tr><td><code>12345@firm.attymatter.com</code><br>(5-digit number)</td><td><strong>MatterMailbox</strong></td><td>Files email directly to a matter. Creates MatterHistory entries with threading. When unified document intake is enabled, attachments are staged for release review before becoming final Documents. The 5-digit number is the matter number and the subdomain identifies the intranet.</td></tr>
              <tr><td><code>archive@attymatter.com</code><br><code>*@attymatter.com</code><br><code>*@princelaw.com</code><br>(catch-all)</td><td><strong>ArchiveMailbox</strong></td><td>Archives the email for search. Stores in the archive database and indexes in Solr. Does NOT file to any specific matter.</td></tr>
            </tbody>
          </table>

          <h5>Pre-Processing (All Mailboxes)</h5>
          <p>Before any mailbox processor runs, the <code>show_me</code> callback in ApplicationMailbox performs these steps for every email:</p>
          <ol>
            <li><strong>Spam filtering</strong> &mdash; Checks sender, subject, and body against 40+ spam patterns. Junk emails are saved to a subfolder but not processed.</li>
            <li><strong>Archive copy</strong> &mdash; Saves the raw .eml file to disk at <code>/email_archive/maildir/{subfolder}/{year}/{month}/{day}/{message_id}.eml</code></li>
            <li><strong>Author detection</strong> &mdash; Looks up the sender in V7&rsquo;s email addresses to identify the Contact or User who sent it.</li>
            <li><strong>Email text extraction</strong> &mdash; Parses the email body, handling nested multipart structures (e.g., Apple Mail).</li>
            <li><strong>Matter email detection</strong> &mdash; Identifies any recipients matching the <code>5-digit@subdomain</code> pattern for matter filing.</li>
          </ol>

          <h5>Spam Filter Patterns</h5>
          <p>Emails are rejected (saved but not processed) if they match:</p>
          <ul>
            <li><strong>Subject keywords</strong>: &ldquo;click here&rdquo;, &ldquo;free shipping&rdquo;, &ldquo;offer ends&rdquo;, &ldquo;% off&rdquo;, spam markers (<code>***spam***</code>), Rollbar/fail2ban notifications, etc.</li>
            <li><strong>Sender patterns</strong>: <code>donotreply@wordpress.com</code>, <code>rollbar.com</code>, <code>facebook</code>, <code>spamtitan</code>, <code>root</code>, newsletters, notifiers, etc.</li>
            <li><strong>List headers</strong>: Any email with <code>List-Unsubscribe</code> or <code>List-Id</code> headers.</li>
            <li><strong>Body spam words</strong>: &ldquo;unsubscribe&rdquo;, &ldquo;money back&rdquo;, &ldquo;order now&rdquo;, etc.</li>
            <li><strong>Invalid dates</strong>: Emails with future dates or missing dates.</li>
          </ul>
          <p>Filtered emails are saved to subfolders like <code>undesireable_subject/</code>, <code>unwanted_from/</code>, <code>mail_list/</code>, <code>body_spam/</code>, <code>predated/</code>.</p>

          <h5>SpamTitan Recommendation Automation</h5>
          <p>The operational SpamTitan recommender is run from <code>scripts/spamtitan-recommend-rules.sh</code>. Each run writes Markdown and JSON reports under <code>tmp/spamtitan-recommendations/</code>, then runs the conservative auto-scoring gate.</p>
          <ul>
            <li>Default mode is <strong>dry-run</strong>; it logs whether any low-risk scoring rule would sync.</li>
            <li>Live auto-sync requires <code>SPAMTITAN_AUTO_SYNC_SCORING=live</code> or the <code>--auto-sync-scoring</code> flag.</li>
            <li>Disable the gate with <code>--no-auto-sync-scoring</code>.</li>
            <li>The auto path only permits scoring rules that meet the strict threshold checks in <code>scripts/spamtitan_approval.py</code>. Hard-block rules remain manual approval only.</li>
          </ul>
        `
      },
      {
        id: 'matter-email',
        title: 'Emailing to a Matter',
        audience: 'both',
        content: `
          <h4>Filing Email to a Matter</h4>
          <p>To file an email directly to a matter, send or forward it to: <code><strong>{matter_number}@{firm}.attymatter.com</strong></code></p>
          <p>For example: <code>12345@princelaw.attymatter.com</code> files the email to matter 12345 in the Prince Law intranet.</p>

          <h5>What Happens</h5>
          <ol>
            <li>The <strong>MatterMailbox</strong> receives the email.</li>
            <li>It identifies the sender (matches the &ldquo;From&rdquo; address to a Contact or User in V7).</li>
            <li>It creates a <strong>MatterHistory</strong> entry on the matter with the email subject as the title and the email body as the message.</li>
            <li>The email body and attachments are preserved with the history entry. When unified document intake is enabled, meaningful body text and document-like attachments become separate items in the scan/release queue before becoming final documents; supported audio and video files follow the documented direct-release path.</li>
            <li>Repeated inline email images, such as a sender&rsquo;s logo or signature graphic, are compared by their actual file contents within the matter. If the identical embedded image was already received for that matter, the new copy is omitted from the scan queue. It remains preserved with the newly archived email, and the matter history explains that the duplicate inline image was omitted. Images deliberately attached as files, changed images, and images sent to another matter are not suppressed.</li>
            <li>Apple Mail Drop files are recognized from Apple&rsquo;s signed remote-attachment header, downloaded while the link is valid, preserved in the email archive, and staged like ordinary attachments. This applies to genuine Apple Mail Drop files, not arbitrary cloud-storage links pasted into a message.</li>
            <li>The email body is rendered for release review. Staff select its final document type, author, authored date, and notes just as they do for an attachment. This permits bills, notices, and other substantive body content to run the appropriate document rules after release.</li>
            <li>The email is archived in the archive database with a Solr index for searchability.</li>
          </ol>
          <p><strong>Review and typing:</strong> A staged email body or attachment does not appear in the matter&rsquo;s final document list until it is released. In the scan queue, confirm or change the suggested document type, author, authored date, and notes. Document-type rules run when each item is released using the final selected type. If intake capture fails, MM2 falls back to the legacy filing path so body content is not silently lost.</p>
          <p><strong>Mail Drop size limit:</strong> Mail Drop files may be much larger than ordinary email attachments. MM2 accepts a Mail Drop file up to 300 MB by default and downloads it without holding the entire file in memory. In lay terms, the limit prevents a very large or misleading download from consuming all available storage or tying up email processing. Administrators can change the limit with <code>MM2_MAIL_DROP_MAX_BYTES</code>. Files over the limit, expired links, failed downloads, and non-Apple download hosts are rejected and reported as processing failures instead of disappearing silently.</p>
          <p>Allow a short processing interval after forwarding an email. If an expected attachment does not appear in the scan queue, check the matter history for the archived email and intake status before forwarding it again. A logo or signature image may have been intentionally omitted as an exact duplicate; a normal attached document should still appear or have an intake status explaining what happened.</p>
          <div class="alert alert-info"><strong>Ethical recordkeeping:</strong> The original email remains archived and linked where available. This preserves the communication even if individual attachments are later released, rejected, or returned to the intake queue.</div>

          <h5>Email Threading</h5>
          <p>When replying to a matter email, the system maintains threading:</p>
          <ul>
            <li>Replies include a hidden history ID marker (<code>*****12345*****</code>) or an <code>X-History-Id</code> header.</li>
            <li>If the marker is found, the new email is added as a <strong>child</strong> of the original MatterHistory entry.</li>
            <li>If no marker is found, a new top-level history entry is created.</li>
            <li>The reply-to address is set to <code>{matter_number}@{firm}.attymatter.com</code> so subsequent replies continue to file to the same matter.</li>
          </ul>

          <h5>Multi-Matter Filing</h5>
          <p>An email can be filed to <strong>multiple matters</strong> by including multiple matter addresses in the To/CC fields. Each matter gets its own history entry and documents.</p>
        `
      },
      {
        id: 'court-notices',
        title: 'Court Notice Processing',
        audience: 'both',
        content: `
          <h4>Automatic Court Notice Handling</h4>
          <p>Emails sent to <code>court_notices@princelaw.com</code> or <code>court_filings@princelaw.com</code> are automatically processed by the <strong>CourtNoticeMailbox</strong>.</p>

          <h5>How It Works</h5>
          <ol>
            <li>The system extracts the <strong>case/docket number</strong> from the email body using regex pattern matching (<code>Case Number: XXXX-XXXX</code>).</li>
            <li>It searches V7&rsquo;s <strong>Matter Details</strong> for matching docket numbers (Docket Number, Agency Case Number, Custody Case Number, Magisterial District Docket Number, Prior CP Docket Number, Divorce Docket Number).</li>
            <li>For each matched matter, it creates a <strong>Reminder</strong>:
              <ul>
                <li>Delivered via: Browser Notification, Email, and Phone Text</li>
                <li>Start: 3 days from now at 7 AM</li>
                <li>End: 10 days from now at 7 AM</li>
                <li>Recipients: attorneys mentioned in the email body + the matter&rsquo;s lead attorney</li>
              </ul>
            </li>
            <li>URLs found in the email are included in the reminder message for quick access to the filing.</li>
          </ol>

          <div class="alert alert-warning"><strong>Safety check:</strong> If a case number matches more than 20 matters, it&rsquo;s likely a bad pattern match. The system skips processing and logs a warning to prevent reminder floods.</div>
        `
      },
      {
        id: 'support-email',
        title: 'Support Ticket Email',
        audience: 'both',
        content: `
          <h4>Support Tickets via Email</h4>
          <p>Emails to <code>support@princelaw.com</code> or <code>support@attymatter.com</code> are processed by the <strong>SupportMailbox</strong>.</p>

          <h5>Creating a New Ticket</h5>
          <p>Send an email to the support address. The system:</p>
          <ol>
            <li>Identifies the sender from V7&rsquo;s contact/user records.</li>
            <li>Creates a new <strong>Ticket</strong> with the email subject as the name and the email body as the description.</li>
            <li>Sets the ticket condition to &ldquo;Unassigned&rdquo; and queue to &ldquo;Unassigned&rdquo;.</li>
            <li>Sends an acknowledgment email back with the ticket ID.</li>
            <li>Notifies watchers of the new ticket.</li>
          </ol>

          <h5>Replying to a Ticket</h5>
          <p>The reply marker (<code>**************Please reply above this line**************</code>) separates new content from quoted text. Only text above the marker is captured as a new comment.</p>

          <h5>Email Commands (via Subject Line)</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Subject Contains</th><th>Action</th></tr></thead>
            <tbody>
              <tr><td><code>show me unassigned</code></td><td>Sends a list of all unassigned tickets.</td></tr>
              <tr><td><code>show me my [condition]</code></td><td>Sends a list of your tickets filtered by condition (e.g., &ldquo;show me my open&rdquo;).</td></tr>
              <tr><td><code>status request</code></td><td>Sends the current status of the ticket.</td></tr>
              <tr><td><code>open [username]</code></td><td>Reopens the ticket and assigns it to the named user.</td></tr>
              <tr><td><code>closed</code> / <code>resolved</code></td><td>Changes the ticket condition to closed/resolved.</td></tr>
              <tr><td><code>merge with [ticket_id]</code></td><td>Merges this ticket into another (moves comments, attachments, watchers).</td></tr>
            </tbody>
          </table>

          <div class="alert alert-info"><strong>Unknown Senders:</strong> If the sender&rsquo;s email address is not found in V7&rsquo;s contact database, the system sends a notification back and the email goes to the dead letter queue for manual review.</div>
        `
      },
      {
        id: 'dead-letter-queue',
        title: 'Dead Letter Queue',
        audience: 'admin',
        content: `
          <h4>Dead Letter Queue</h4>
          <p>When email processing fails (any unhandled exception), the email is captured in the <strong>Dead Letter Queue</strong> rather than being lost. This provides a safety net for troubleshooting and retry.</p>

          <h5>How It Works</h5>
          <ol>
            <li>Any exception during mailbox processing is caught by <code>ApplicationMailbox#handle_processing_failure</code>.</li>
            <li>A <strong>DeadLetter</strong> record is created with the error details, email metadata, and the path to the saved .eml file.</li>
            <li>The error is reported to <strong>Rollbar</strong> with a UUID for cross-referencing.</li>
            <li>A <strong>notification email</strong> is sent immediately to <code>tech@princelaw.com</code>.</li>
            <li>The <strong>DeadLetterRetryJob</strong> automatically retries failed emails on a schedule.</li>
          </ol>

          <h5>Dead Letter Fields</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>message_id</strong></td><td>Unique email message ID (RFC 2822)</td></tr>
              <tr><td><strong>mailbox_class</strong></td><td>Which mailbox was processing it (ArchiveMailbox, MatterMailbox, etc.)</td></tr>
              <tr><td><strong>status</strong></td><td><code>pending</code>, <code>retrying</code>, or <code>dead</code></td></tr>
              <tr><td><strong>retry_count</strong></td><td>Number of retry attempts (max 3)</td></tr>
              <tr><td><strong>next_retry_at</strong></td><td>When the next automatic retry will occur</td></tr>
              <tr><td><strong>error_class / error_message</strong></td><td>The exception class and message that caused the failure</td></tr>
              <tr><td><strong>error_backtrace</strong></td><td>First 20 lines of the error backtrace</td></tr>
              <tr><td><strong>stored_email_path</strong></td><td>Path to the raw .eml file on disk</td></tr>
              <tr><td><strong>email_metadata</strong></td><td>JSON with from, to, subject, and date</td></tr>
              <tr><td><strong>rollbar_uuid</strong></td><td>Rollbar error occurrence UUID for cross-referencing</td></tr>
            </tbody>
          </table>

          <h5>Retry Schedule</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Attempt</th><th>Delay</th></tr></thead>
            <tbody>
              <tr><td>1st retry</td><td>1 minute after failure</td></tr>
              <tr><td>2nd retry</td><td>5 minutes after 1st retry failure</td></tr>
              <tr><td>3rd retry</td><td>15 minutes after 2nd retry failure</td></tr>
              <tr><td>After 3 failures</td><td>Status set to <code>dead</code> &mdash; requires manual intervention</td></tr>
            </tbody>
          </table>

          <h5>Admin Interface</h5>
          <p>Access the dead letter queue at <code>http://storage.princelaw.com:83/admin/dead_letters</code>.</p>
          <p>Available actions:</p>
          <ul>
            <li><strong>View</strong> &mdash; See error details, email metadata, and Rollbar link</li>
            <li><strong>Retry</strong> &mdash; Manually retry a single dead letter</li>
            <li><strong>Retry All</strong> &mdash; Retry all pending dead letters</li>
            <li><strong>Forward</strong> &mdash; Forward the original email to a different address for manual handling</li>
            <li><strong>Download EML</strong> &mdash; Download the raw email file</li>
            <li><strong>Delete</strong> &mdash; Remove from the queue</li>
            <li><strong>Bulk operations</strong> &mdash; Bulk retry, bulk delete, bulk forward</li>
          </ul>
        `
      },
      {
        id: 'email-archive-ui',
        title: 'Email Archive (V7)',
        audience: 'both',
        content: `
          <h4>Email Archive</h4>
          <p>Navigate to <strong>Administration &rarr; Email Archive</strong> to search and view archived emails.</p>

          <h5>Searching</h5>
          <p>The email archive search is powered by <strong>Apache Solr</strong> and supports the following search fields:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Field</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><strong>From</strong></td><td>Sender email address (wildcard search)</td></tr>
              <tr><td><strong>To</strong></td><td>Recipient email address (wildcard search)</td></tr>
              <tr><td><strong>Subject</strong></td><td>Email subject line (wildcard search)</td></tr>
              <tr><td><strong>Contents</strong></td><td>Full-text search of email body and attachments</td></tr>
              <tr><td><strong>Header Content</strong></td><td>Search within email headers</td></tr>
              <tr><td><strong>Message ID</strong></td><td>Exact RFC 2822 message ID</td></tr>
              <tr><td><strong>Date From / Date To</strong></td><td>Date range filter (MM/DD/YYYY)</td></tr>
            </tbody>
          </table>
          <p>Results can be sorted by: sender, recipients, ID, date, subject, or archive date.</p>

          <h5>Viewing an Email</h5>
          <p>The email detail page shows:</p>
          <ul>
            <li><strong>Headers</strong> &mdash; From, To, CC, Date, Subject, and other email headers</li>
            <li><strong>Body</strong> &mdash; HTML and/or plain text content with search term highlighting</li>
            <li><strong>Attachments</strong> &mdash; Tabbed view of each attachment with download links</li>
          </ul>
          <p>Links labeled <strong>Original email</strong> open this detail page in read-only mode. If the archive database is temporarily unavailable, V7 displays the copy already indexed in Solr instead of returning a blank page. A warning identifies the fallback view because attachment metadata or binary attachment data may be unavailable until the archive database reconnects.</p>
          <p>Attachment download links use the attachment metadata stored in the archive database on <code>mx2.princelaw.com</code>, then read the corresponding file from the archive filesystem mounted on the V7 server at <code>/mnt/email_archive</code>. In ordinary terms, mx2 tells V7 which file belongs to the email, while the storage server holds the file itself. Downloads use that database path directly and do not require a second matching Solr search result. This keeps a valid archive download working even if an older Solr attachment identifier is missing or differs from the current archive record.</p>

          <h5>Email Storage</h5>
          <p>Archived emails are stored in multiple locations:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Storage</th><th>Location</th><th>Contents</th></tr></thead>
            <tbody>
              <tr><td><strong>Database</strong></td><td><code>mx2.princelaw.com</code>, archive database (<code>email_archives</code> table)</td><td>Message ID, HTML body, text body, intranet ID</td></tr>
              <tr><td><strong>Database</strong></td><td><code>mx2.princelaw.com</code>, archive database (<code>email_attachments</code> table)</td><td>Attachment records and filenames</td></tr>
              <tr><td><strong>Disk</strong></td><td>Storage server, mounted on V7 beneath <code>/mnt/email_archive</code></td><td>Raw email and attachment files organized by date</td></tr>
              <tr><td><strong>Solr Index</strong></td><td><code>solr.princelaw.com:8983/solr/email</code></td><td>Full-text searchable index of all email content and attachments</td></tr>
            </tbody>
          </table>

          <h5>Database Architecture</h5>
          <p>The email archive uses <strong>separate databases</strong> from the main V7 application:</p>
          <ul>
            <li><strong>Archive database</strong> (<code>Archive</code> base class) &mdash; Legacy email archives</li>
            <li><strong>New Archive database</strong> (<code>NewArchive</code> base class) &mdash; Current email archives from Mailman2</li>
          </ul>
          <p>Both use read replicas for query performance. The <code>NewEmailArchive</code> model (used by Mailman2) writes to the new archive database, while the <code>EmailArchive</code> model in V7 reads from the legacy archive database for display.</p>

          <h5>Troubleshooting an &ldquo;archive database unavailable&rdquo; warning</h5>
          <p>The warning means V7 could search the Solr copy but could not read the full email record from MySQL. Verify that the archive connection targets <code>mx2.princelaw.com</code>, not the storage server at <code>10.1.1.11</code>. The storage server no longer runs MySQL. After database access is restored, verify that the requested file is readable through <code>/mnt/email_archive</code>. A missing database connection and a missing filesystem mount are separate failures and should be checked independently.</p>

          <h5>Attachment Exclusions</h5>
          <p>The following file types are excluded from archiving to save storage and avoid malware:</p>
          <ul>
            <li><code>.exe</code>, <code>.dmg</code>, <code>.pkg</code> &mdash; Executables/installers</li>
            <li><code>.gif</code>, <code>.tif</code> &mdash; Image formats (JPG/PNG are kept)</li>
            <li><code>.zip</code> &mdash; Archives</li>
          </ul>
        `
      },
      {
        id: 'email-troubleshooting',
        title: 'Troubleshooting Email',
        audience: 'admin',
        content: `
          <h4>Troubleshooting Email Processing</h4>

          <table class="table table-sm table-bordered">
            <thead><tr><th>Problem</th><th>Where to Look</th><th>Solution</th></tr></thead>
            <tbody>
              <tr>
                <td>Emails not being archived</td>
                <td>Check <code>always_bcc</code> in <code>/etc/postfix/main.cf</code> on storage server</td>
                <td>Ensure <code>always_bcc = archive@attymatter.com</code> is set. Check Postfix logs: <code>journalctl -u postfix</code></td>
              </tr>
              <tr>
                <td>Emails stuck in Postfix queue</td>
                <td><code>mailq</code> on storage server</td>
                <td>Check <code>/var/log/mail.log</code> for delivery errors. Flush with <code>postfix flush</code>. Check if Mailman2 is running.</td>
              </tr>
              <tr>
                <td>Mailman2 not processing</td>
                <td>Check Rails process on storage server</td>
                <td>Verify the app is running: <code>ps aux | grep rails</code>. Check logs at <code>/web/mailman2/site/current/log/production.log</code></td>
              </tr>
              <tr>
                <td>Email filed to wrong matter</td>
                <td>Check the recipient address</td>
                <td>Ensure the matter number in the email address is correct. The subdomain must match an intranet short_name.</td>
              </tr>
              <tr>
                <td>Emails going to dead letter</td>
                <td><code>http://storage.princelaw.com:83/admin/dead_letters</code></td>
                <td>View the error details. Fix the underlying issue. Use &ldquo;Retry&rdquo; or &ldquo;Forward&rdquo; to reprocess.</td>
              </tr>
              <tr>
                <td>Search not returning results</td>
                <td>Check Solr on <code>solr.princelaw.com:8983</code></td>
                <td>Verify the email Solr core is running. Check if indexing is happening. Solr outage = no search, but emails are still stored in the database.</td>
              </tr>
              <tr>
                <td>Court notices not creating reminders</td>
                <td>Check <code>CourtNoticeMailbox</code> logs</td>
                <td>Verify the case number format in the email matches the regex. Check that Matter Details contain matching docket numbers.</td>
              </tr>
              <tr>
                <td>Unknown sender on support email</td>
                <td>Dead letter queue</td>
                <td>The sender&rsquo;s email isn&rsquo;t in V7&rsquo;s contact database. Add their email address to their Contact record, then retry from dead letter queue.</td>
              </tr>
              <tr>
                <td>Abort file blocking processing</td>
                <td><code>/tmp/abort</code> on storage server</td>
                <td>A critical error created the abort file. Check Rollbar for the error. Remove the file to resume: <code>rm /tmp/abort</code></td>
              </tr>
            </tbody>
          </table>
        `
      }
    ]
  },

  // 25. INTEGRATIONS
  // ============================================================
  {
    id: 'integrations',
    title: 'Integrations',
    icon: 'fa-plug',
    audience: 'admin',
    subsections: [
      {
        id: 'lawpay-integration',
        title: 'LawPay Integration',
        audience: 'admin',
        content: `
          <h4>LawPay Payment Processing</h4>
          <p>V7 integrates with LawPay for secure online payments.</p>

          <h5>Authentication</h5>
          <p>LawPay uses <strong>OAuth</strong> for API authentication. The API key, secret, merchant ID, and OAuth tokens are stored in the <code>lawpay</code> settings group.</p>

          <h5>Test vs Production Mode</h5>
          <p>The <code>test_mode</code> setting (&ldquo;true&rdquo;/&ldquo;false&rdquo;) controls whether LawPay API calls go to the sandbox or production endpoints. Always test with <code>test_mode = true</code> first.</p>

          <h5>Two Payment Paths</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Path</th><th>When Used</th><th>Features</th></tr></thead>
            <tbody>
              <tr><td><strong>Invoice API</strong></td><td>Single-matter email invoices</td><td>Line items, contact sync, detailed invoice, PDF attachment</td></tr>
              <tr><td><strong>Hosted Payment Page (HPP)</strong></td><td>Multi-matter invoices, mailed invoices</td><td>Pre-filled URL with reference and amount only. No line items.</td></tr>
            </tbody>
          </table>

          <h5>Account IDs</h5>
          <ul>
            <li>Current routing comes from V7 <strong>Bank Accounts</strong>, not matter type names.</li>
            <li>Eligible LawPay deposit accounts must be active and have an active LawPay mapping for the current live/test environment.</li>
            <li>Enter the corresponding LawPay account ID under <strong>Remote payment accounts</strong>; there is no separate LawPay checkbox.</li>
            <li>Use <code>is_escrow = true</code> for trust/IOLTA LawPay accounts and <code>is_escrow = false</code> for operating LawPay accounts.</li>
            <li><code>trust_account_id</code> and <code>operating_account_id</code> settings are legacy fallbacks and should not be the primary configuration path.</li>
          </ul>
        `
      },
      {
        id: 'payload-integration',
        title: 'Payload Integration',
        audience: 'admin',
        content: `
          <h4>Payload Payment Processing</h4>
          <p>V7 integrates with Payload for online invoice payments, retainer payment links, payment webhooks, and deposit reconciliation.</p>

          <h5>How Payload Is Selected</h5>
          <ul>
            <li>New matters default to <code>billing_method = "payload"</code>. Staff can change a matter to LawPay or Traditional (V7) when a specific matter should not use PayLoad.</li>
            <li>A matter using <code>billing_method = "payload"</code> uses Payload unless <code>payload.enabled</code> is explicitly off.</li>
            <li>For invoice links, PayLoad must also have a usable API key and general/operating processing account configured for the matter&rsquo;s intranet. If those settings are missing, V7 falls back to manual handling rather than creating an unusable PayLoad link.</li>
            <li>If the matter does not specify LawPay or Payload, V7 checks global provider settings in this order: <code>payments.provider</code>, <code>payment_provider.provider</code>, <code>payload.provider</code>, then <code>payload.enabled</code>.</li>
            <li><code>payload.enabled</code> is considered on when its value is <code>1</code>, <code>true</code>, <code>yes</code>, <code>enabled</code>, or <code>payload</code>.</li>
            <li><code>payload.enabled</code> is considered off when its value is <code>false</code>, <code>0</code>, <code>no</code>, <code>off</code>, or <code>disabled</code>.</li>
            <li>If none of those select Payload, V7 uses manual payment handling.</li>
            <li>If Payload is disabled, V7 treats Payload matters as manual during invoicing and does not process Payload callbacks, deposit polling, retainer links, refunds, or reconciliation.</li>
          </ul>

          <h5>Credentials and Mode</h5>
          <ul>
            <li>Payload credentials are intranet-specific. PLO and CRDF may use different Payload API keys; V7 always reads the key and account settings from the current matter&rsquo;s intranet.</li>
            <li><code>payload.test_mode = true</code> makes V7 use <code>test_api_key</code> and test processing account settings.</li>
            <li><code>payload.test_mode = false</code> makes V7 use <code>api_key</code> and live processing account settings.</li>
            <li>The Payload deposit poller considers Payload configured when Payload is enabled and either <code>payload.api_key</code> or <code>payload.test_api_key</code> is present.</li>
          </ul>

          <h5>Processing Accounts</h5>
          <ul>
            <li>Invoice payments use the general/operating account settings. In test mode the lookup order is <code>test_general_processing_account_id</code>, <code>test_processing_account_id</code>, <code>general_processing_account_id</code>, then <code>processing_account_id</code>. In live mode it uses <code>general_processing_account_id</code>, then <code>processing_account_id</code>.</li>
            <li>Retainer payment links use IOLTA/trust account settings. In test mode the lookup order is <code>test_iolta_processing_account_id</code>, then <code>iolta_processing_account_id</code>. In live mode it uses <code>iolta_processing_account_id</code>.</li>
            <li>Before creating a Payload customer, invoice, or retainer link, V7 asks Payload for the configured processing account with the selected intranet&rsquo;s API key. If Payload rejects the account or the account is not a processing account, V7 stops and reports the configuration problem instead of creating stray Payload records under the wrong organization.</li>
            <li><code>allow_card_payments</code> and <code>allow_bank_account_payments</code> control checkout funding options. Blank values default to enabled.</li>
          </ul>

          <h5>Invoice Links in the Client Portal</h5>
          <ul>
            <li>The Billing &amp; Payments tab checks for unpaid invoices every time the portal page loads.</li>
            <li><strong>In plain terms:</strong> PayLoad checkout links are temporary for security. A link saved days or weeks ago can expire even though the invoice is still unpaid. V7 therefore does not send the client directly to an old saved checkout address. The portal asks PayLoad for a fresh checkout session only when the client clicks <strong>Pay Now</strong>.</li>
            <li>Normally V7 keeps the same PayLoad invoice and replaces only its temporary checkout token. This does not create another V7 invoice and does not email another payment request.</li>
            <li>If PayLoad reports that the remote invoice no longer exists, V7 creates a stand-alone checkout session for the amount recorded on the V7 invoice without asking PayLoad to send an email. The original V7 invoice remains the accounting record, and its identifiers are carried with the payment so V7 can reconcile it. V7 does not create another remote invoice because PayLoad may automatically email newly created invoices.</li>
            <li>A stand-alone recovery checkout is refreshed on every <strong>Pay Now</strong> click just like an invoice checkout. In lay terms, the client always receives a current secure payment page, but V7 does not create a PayLoad invoice or payment-link email behind the scenes.</li>
            <li>The recovery checkout is for the invoice&rsquo;s fixed requested amount. PayLoad requires that amount to be supplied directly on the checkout page; if the V7 invoice has no positive amount, V7 stops and displays an error instead of opening an unusable payment page.</li>
            <li>For hourly billing entries, V7 sends the PayLoad invoice line item with the billing hours as PayLoad quantity and the hourly rate as PayLoad rate, so PayLoad renders quantity, rate, and total consistently with the V7 invoice. Previous balances, credits, expenses, and other flat charges are sent with quantity one.</li>
            <li>V7 sends PayLoad invoice and checkout descriptions in the form <code>Matter #12345 - Matter Name</code> so PayLoad dashboard/payment records are identifiable even when the email click-tracking URL is opaque. Multi-matter invoices include all matter numbers in the description and prefix line items with the matter number when needed.</li>
            <li>PayLoad limits custom attributes, so V7 sends a compact metadata set: root matter id/number, all invoice matter ids/numbers, client name, V7 invoice id, PayLoad invoice id, intranet id, and processing-account role. V7 limits the client-name metadata value to 40 characters so long estate or organization names do not prevent invoice creation; the full matter name remains in the PayLoad invoice description.</li>
            <li>Refreshing the portal after a new invoice is issued will show its payment option. Repeatedly clicking <strong>Pay Now</strong> refreshes the checkout session but does not create duplicate V7 invoices.</li>
          </ul>

          <h5>Reissuing Provider Invoice Requests</h5>
          <ul>
            <li>On a matter&rsquo;s <strong>Accounting &gt; Invoices</strong> tab, accountants and managers can use <strong>Reissue PayLoad</strong> or <strong>Resend LawPay</strong> for an existing V7 invoice.</li>
            <li><strong>Reissue PayLoad</strong> creates a fresh PayLoad invoice from the current V7 invoice data, including bills already attached to that V7 invoice or invoice group. It then creates and stores the same open-amount PayLoad checkout-page URL used by normal invoice syncs. It does not resend the V7 invoice email or PDF.</li>
            <li>When creating the fresh PayLoad invoice, V7 sends V7 matter/client/invoice metadata only. The new PayLoad invoice id is added to the checkout-page metadata after PayLoad returns it, so the old PayLoad invoice id is not reused in the new invoice request.</li>
            <li><strong>Resend LawPay</strong> sends the LawPay provider invoice request again, or creates it first if the V7 invoice did not already have a LawPay invoice id. It does not resend the V7 invoice email or PDF.</li>
            <li>When reissuing a corrected PayLoad request, review the old PayLoad request in the PayLoad dashboard if it should be cancelled or withdrawn there. V7 updates the invoice to point at the newest PayLoad invoice/payment URL.</li>
          </ul>

          <h5>Payment Activity Reports</h5>
          <ul>
            <li>The general <strong>Accounting &gt; Online Payments</strong> report shows firm-wide LawPay and PayLoad activity.</li>
            <li>The matter <strong>Accounting &gt; Online Payments</strong> tab is scoped to that matter only.</li>
            <li>PayLoad payments created from V7 invoice or retainer links carry V7 matter/payment metadata, including root matter number, all included matter numbers, client name, V7 invoice id, PayLoad invoice id, intranet id, and processing-account role. Manually keyed PayLoad payments may not include that metadata, so V7 records them without attaching them to an invoice unless PayLoad supplied a real invoice id, checkout-token id, or V7 invoice id.</li>
            <li>For manually keyed payments that lack V7 metadata, staff should verify the matter and processing account against PayLoad before reconciling.</li>
          </ul>

	          <h5>PayLoad Webhook Updates</h5>
	          <ul>
	            <li>PayLoad sends separate webhook triggers for payments, processed/authorized status changes, refunds, reversals, credits, voids, declines, rejects, deposits, payment-link status, and processing-account status.</li>
	            <li>Payment-related webhooks update the existing Online Payments row for the original transaction. For example, a refund does not create a second refund row; V7 marks the original payment row as refunded or partially refunded and stores the refund transaction id, cumulative refunded amount, date, and PayLoad details on that row.</li>
	            <li>When a PayLoad payment reaches a paid status (<code>processed</code>, <code>completed</code>, or <code>paid</code>), V7 saves or updates the <code>PayloadPayment</code> row immediately, then queues <code>PayloadPaymentProcessorWorker</code> to create the V7 accounting records. The processor creates the PayLoad payment entry group, deposit child, receipt child, ledgers, and a receipt distribution for the matter.</li>
	            <li>For operating/general account payments, V7 creates payment bill rows and applies them against invoice charges when possible. For IOLTA/trust account payments, V7 expects exactly one matter and creates an escrow balance entry plus an escrow receipt distribution, without requiring an invoice bill to exist.</li>
	            <li>The <code>PayloadPayment</code> row stores <code>processing_status</code>, <code>entry_group_id</code>, <code>receipt_id</code>, <code>bank_account_id</code>, and <code>processed_at</code>. These fields are the audit link between the PayLoad callback and the V7 receipt. The processor is idempotent: once an entry group exists, duplicate callbacks must not create another receipt.</li>
	            <li>Receipt bank routing uses the active V7 bank account marked <strong>Credit Card</strong>. In plain terms, the client&rsquo;s PayLoad payment is first money due from the card processor, so V7 credits the Credit Card clearing account even when PayLoad says it will later deposit the money into Ambler General or an Ambler escrow account. PayLoad&rsquo;s processing-account ID remains on the payment as the settlement destination and is used only as a fallback when the firm has no active Credit Card account.</li>
	            <li>If PayLoad sends multiple partial refund webhooks for one original payment, V7 totals those refunds on the original payment row and treats the row as fully refunded once the cumulative refunded amount equals the original payment amount. V7 blocks staff from submitting another PayLoad refund request for a payment already in a refund or reversal state.</li>
	            <li>Deposit webhooks update reconciliation/deposit status for matching PayLoad payments.</li>
	            <li>Status-only operational webhooks, such as payment activation or processing-account status, are acknowledged for audit purposes but do not create Online Payments rows because they are not client payments.</li>
	          </ul>

	          <h5>Backfilling Missing PayLoad Receipts</h5>
	          <ul>
	            <li>If V7 received PayLoad payment callbacks before receipt posting was enabled, the Online Payments row may exist while <code>processing_status</code> is still <code>unprocessed</code> and <code>receipt_id</code> is blank.</li>
	            <li>Use <code>bundle exec rake payload:process_payments</code> to preview paid PayLoad rows that still need V7 receipt/accounting records. The task is a dry run by default.</li>
	            <li>Use <code>bundle exec rake payload:process_payments DRY_RUN=false</code> to create the missing receipts. Add <code>INTRANET_ID=1</code> or <code>PAYMENT_ID=123</code> to limit the run.</li>
	            <li>If a row remains unprocessed after the task, check whether the payment has a resolvable matter and whether the PayLoad processing account id is mapped to an active V7 bank account for the correct live/test environment.</li>
	          </ul>
	        `
	      },
      {
        id: 'sms-sending',
        title: 'SMS &mdash; Sending Texts',
        audience: 'both',
        content: `
          <h4>Sending Text Messages (SMS)</h4>
          <p>V7 can send SMS text messages to clients and other contacts involved in a matter. Texts are sent from the firm&rsquo;s dedicated phone number <strong>(610) 702-9296</strong> via the <strong>Telnyx</strong> messaging service.</p>

          <div class="alert alert-info">
            <i class="fas fa-info-circle mr-1"></i>
            <strong>Caller ID:</strong> Every outgoing text is automatically prefixed with the firm name (e.g., &ldquo;Prince Law Offices: your message here&rdquo;) so recipients know who sent it. The firm name is based on the intranet you are logged into.
          </div>

          <h5>How to Send a Text</h5>
          <ol>
            <li>Open the matter and click the <strong>Texts</strong> tab.</li>
            <li>In the <strong>Send To</strong> section, use the <strong>Add Recipient</strong> search box to find an involvement by name.</li>
            <li>Select one or more recipients. Each recipient shows a status icon:
              <ul>
                <li><i class="fas fa-mobile-alt fa-fw text-success"></i> <strong>Green phone</strong> &mdash; texting is enabled and a mobile number is on file.</li>
                <li><i class="fas fa-mobile-alt fa-fw text-danger"></i> <strong>Red phone</strong> &mdash; texting is disabled or no mobile number. Hover over the icon to see the reason.</li>
              </ul>
            </li>
            <li>Optionally load a <strong>Text Template</strong> from the dropdown to pre-fill the message.</li>
            <li>Type or edit your message in the <strong>Text</strong> field. Messages are limited to <strong>150 characters</strong>. A character counter is displayed below the field.</li>
            <li>Use <strong>Shortcuts</strong> to insert variables at the cursor position (e.g., <code>~client~</code>, <code>~outstanding~</code>). Click a shortcut badge and it will be inserted wherever your cursor is in the message.</li>
            <li>Click <strong>Preview</strong> to see the message with variables replaced for the first recipient.</li>
            <li>Click <strong>Send</strong> to send the text to all selected recipients.</li>
          </ol>

          <h5>Available Shortcuts (Variables)</h5>
          <p>These placeholders are replaced with actual values when the message is sent:</p>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Variable</th><th>Replaced With</th></tr></thead>
            <tbody>
              <tr><td><code>~client~</code></td><td>Client&rsquo;s full name</td></tr>
              <tr><td><code>~email~</code></td><td>Client&rsquo;s email address</td></tr>
              <tr><td><code>~escrow~</code></td><td>Escrow/trust balance</td></tr>
              <tr><td><code>~event~</code></td><td>Next event date</td></tr>
              <tr><td><code>~id~</code></td><td>Matter ID number</td></tr>
              <tr><td><code>~last~</code></td><td>Client&rsquo;s last name</td></tr>
              <tr><td><code>~lead~</code></td><td>Lead attorney name</td></tr>
              <tr><td><code>~needed~</code></td><td>Amount needed (retainer shortfall)</td></tr>
              <tr><td><code>~number~</code></td><td>Matter number</td></tr>
              <tr><td><code>~outstanding~</code></td><td>Outstanding balance</td></tr>
              <tr><td><code>~phone~</code></td><td>Firm phone number</td></tr>
              <tr><td><code>~retainer~</code></td><td>Retainer amount</td></tr>
              <tr><td><code>~url_crdf~</code></td><td>Client retainer deposit form URL</td></tr>
              <tr><td><code>~url_plo~</code></td><td>Prince Law Offices website URL</td></tr>
            </tbody>
          </table>

          <h5>Viewing Sent Texts</h5>
          <p>Below the send form, the <strong>Texts sent</strong> table shows all texts sent for this matter with their delivery status:</p>
          <ul>
            <li><strong>queued</strong> &mdash; Message accepted by Telnyx, waiting to send.</li>
            <li><strong>sent</strong> &mdash; Message sent to the carrier.</li>
            <li><strong>delivered</strong> &mdash; Message confirmed delivered to the recipient&rsquo;s phone.</li>
            <li><strong>failed</strong> &mdash; Message could not be delivered (invalid number, carrier rejection, etc.).</li>
          </ul>

          <h5>Receiving Replies</h5>
          <p>When a recipient replies to a text, V7 captures the response automatically:</p>
          <ul>
            <li>The reply appears in the <strong>Texts sent</strong> table as an <strong>inbound</strong> message, linked to the original outbound text.</li>
            <li>The texts page <strong>auto-refreshes every 10 seconds</strong>, so new replies and status changes appear without reloading the page. You will also see a notification toast when a new message arrives.</li>
            <li>An <strong>email notification</strong> is sent to the user who sent the original outbound text, plus all matter users with &ldquo;include in all notifications&rdquo; enabled.</li>
            <li>The email subject clearly identifies it as a reply: <strong>&ldquo;[SMS Reply] Contact Name: message preview&rdquo;</strong>.</li>
            <li>A <strong>MatterHistory</strong> entry is created in the matter&rsquo;s timeline recording the inbound message.</li>
          </ul>
          <div class="alert alert-info">
            <i class="fas fa-info-circle mr-1"></i>
            <strong>Tip:</strong> If you don&rsquo;t see a reply you expect, click <strong>Reset</strong> to force a reload. The auto-refresh polls every 10 seconds but a manual refresh is instant.
          </div>

          <h5>Reset Button</h5>
          <p>The <strong>Reset</strong> button clears the form (recipients, message, template selection) and reloads the recipient list. Use this after enabling texting for an involvement &mdash; reset will allow you to re-search and find the newly enabled recipient.</p>

          <h5>Opt-Out / Opt-In</h5>
          <p>Recipients can control whether they receive text messages:</p>
          <ul>
            <li>Texting <strong>STOP</strong> (or STOPALL, UNSUBSCRIBE, CANCEL, END, QUIT) to the firm&rsquo;s number will opt out the recipient. All text notifications for that phone number are disabled across all matters.</li>
            <li>Texting <strong>START</strong> (or UNSTOP, SUBSCRIBE) will opt back in and re-enable text notifications.</li>
            <li>The carrier automatically sends a confirmation message to the recipient when they opt out or opt in.</li>
            <li>Once opted out, <strong>no texts can be sent</strong> to that number until the recipient texts START.</li>
          </ul>
          <div class="alert alert-warning"><strong>Important:</strong> Opt-out is enforced at the carrier level. Even if you re-enable texting in V7, messages will not be delivered until the recipient texts START from their phone.</div>
        `
      },
      {
        id: 'sms-templates',
        title: 'SMS &mdash; Templates',
        audience: 'admin',
        content: `
          <h4>Text Message Templates</h4>
          <p>Text templates allow you to save and reuse common message formats. Template management is restricted to <strong>admin</strong> and <strong>tech</strong> users.</p>

          <h5>Creating a Template</h5>
          <ol>
            <li>On the <strong>Texts</strong> tab of any matter, type the desired message in the Text field. You can include shortcut variables (e.g., <code>~client~</code>, <code>~outstanding~</code>).</li>
            <li>Enter a <strong>Template Name</strong> and optional <strong>Template Description</strong>.</li>
            <li>Click <strong>Save as New Template</strong>.</li>
          </ol>

          <h5>Using a Template</h5>
          <ol>
            <li>In the <strong>Load Text Template</strong> dropdown, search for and select a template.</li>
            <li>The message, name, and description fields are populated automatically.</li>
            <li>Edit the message if needed, then send as usual.</li>
          </ol>

          <h5>Updating a Template</h5>
          <p>After loading a template, make changes to the text, name, or description and click <strong>Update Template</strong>. The button label changes from &ldquo;Save as New&rdquo; to &ldquo;Update&rdquo; when an existing template is loaded.</p>

          <h5>Deleting a Template</h5>
          <p>Load the template, then click <strong>Delete Template</strong>. A confirmation prompt will appear before deletion.</p>

          <div class="alert alert-info"><strong>Note:</strong> The template controls (Save/Update, Delete, Template Name, Template Description) are only visible to admin and tech users. All users can load and use existing templates.</div>
        `
      },
      {
        id: 'sms-configuration',
        title: 'SMS &mdash; Configuration &amp; Troubleshooting',
        audience: 'admin',
        content: `
          <h4>Telnyx SMS Configuration</h4>
          <p>SMS is powered exclusively by the <strong>Telnyx</strong> API (Twilio was removed in April 2026). The firm&rsquo;s SMS number is <strong>(610) 702-9296</strong>. The following environment variables control SMS behavior:</p>

          <h5>Environment Variables</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Variable</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><code>TELNYX_API_KEY</code></td><td>API key for authenticating with the Telnyx API.</td></tr>
              <tr><td><code>TELNYX_NUMBER</code></td><td>The firm&rsquo;s SMS phone number (the &ldquo;from&rdquo; number on outbound texts).</td></tr>
              <tr><td><code>TELNYX_PUBLIC_KEY</code></td><td>Public key for verifying inbound webhook signatures.</td></tr>
              <tr><td><code>TELNYX_SMS_DISABLED</code></td><td>Set to <code>true</code> to disable all outbound SMS (useful during setup or testing). Any other value allows sending.</td></tr>
            </tbody>
          </table>
          <p>These are set in the Passenger environment file on the production server: <code>/web/v7.attymatter.com/site/shared/config/passenger.env</code></p>

          <h5>10DLC Registration</h5>
          <p>US carriers require <strong>10DLC (10-Digit Long Code)</strong> registration for application-to-person (A2P) SMS. Without registration, messages will be rejected by carriers.</p>
          <ul>
            <li><strong>Brand</strong>: Prince Law Offices (registered with The Campaign Registry / TCR).</li>
            <li><strong>Campaign</strong>: Registered in the Telnyx Mission Control portal under Messaging &rarr; Campaigns.</li>
            <li>The campaign configuration includes auto-reply messages for STOP/START keywords.</li>
            <li>If messages fail with error <strong>40010 &ldquo;Not 10DLC registered&rdquo;</strong>, verify the campaign status in Telnyx Mission Control.</li>
          </ul>

          <h5>Webhook Configuration</h5>
          <p>Telnyx sends status updates and inbound messages via webhooks to:</p>
          <ul>
            <li><strong>Callback URL</strong>: <code>https://v7.attymatter.com/telnyx/callback</code></li>
            <li>The webhook handles:
              <ul>
                <li><strong>Status updates</strong> &mdash; delivery confirmations, failures, etc. Updates the <code>service_status</code> on the text record.</li>
                <li><strong>Inbound messages</strong> &mdash; STOP/START keyword processing and inbound message storage.</li>
              </ul>
            </li>
            <li>Webhook signatures are verified using <code>TELNYX_PUBLIC_KEY</code> to prevent spoofing.</li>
          </ul>

          <h5>How Opt-Out Works (Technical)</h5>
          <ol>
            <li>Recipient texts <strong>STOP</strong> to the firm&rsquo;s number.</li>
            <li>Telnyx delivers the inbound message to V7 via webhook.</li>
            <li>V7 finds the most recent outbound text to that phone number to determine matter/involvement context.</li>
            <li>V7 stores the inbound message as a <code>TextDocument</code> with <code>direction = &lsquo;inbound&rsquo;</code>, linked to the outbound text via <code>parent_id</code>.</li>
            <li>V7 sets <code>allow_phone_text = false</code> on <strong>all involvements</strong> for contacts with that phone number.</li>
            <li>A <code>MatterHistory</code> entry is created to log the opt-out.</li>
            <li>Telnyx blocks all future messages to this number at the carrier level and sends an automatic confirmation to the recipient.</li>
          </ol>
          <p>The <strong>START</strong> process works identically in reverse: <code>allow_phone_text</code> is set to <code>true</code> on all involvements for that contact.</p>

          <h5>Conversation Threading</h5>
          <p>Text messages are stored with conversation threading support:</p>
          <ul>
            <li>Outbound texts have <code>direction = &lsquo;outbound&rsquo;</code> (the default).</li>
            <li>Inbound texts have <code>direction = &lsquo;inbound&rsquo;</code> and a <code>parent_id</code> linking to the most recent outbound text to that number.</li>
            <li>This allows tracking which outbound message prompted a reply.</li>
          </ul>

          <h5>Troubleshooting</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Problem</th><th>Cause</th><th>Solution</th></tr></thead>
            <tbody>
              <tr>
                <td>Messages show <strong>failed</strong> status</td>
                <td>Invalid phone number, carrier rejection, or 10DLC issue</td>
                <td>Check the Telnyx Mission Control dashboard for detailed error codes. Verify the recipient&rsquo;s phone number is correct and is a mobile number.</td>
              </tr>
              <tr>
                <td>Error 40010 &ldquo;Not 10DLC registered&rdquo;</td>
                <td>Campaign not registered or registration not propagated</td>
                <td>Check Telnyx Mission Control &rarr; Messaging &rarr; Campaigns. Verify <code>campaign_registered</code> shows &ldquo;REGISTERED&rdquo;. Registration can take up to 24 hours to propagate.</td>
              </tr>
              <tr>
                <td>Error 40300 &ldquo;Blocked due to STOP message&rdquo;</td>
                <td>Recipient previously texted STOP</td>
                <td>The recipient must text <strong>START</strong> from their phone to re-enable messaging. V7 cannot override carrier-level blocks.</td>
              </tr>
              <tr>
                <td>All SMS disabled / messages not sending</td>
                <td><code>TELNYX_SMS_DISABLED</code> set to <code>true</code></td>
                <td>Set <code>TELNYX_SMS_DISABLED=false</code> in the Passenger environment file and restart Passenger.</td>
              </tr>
              <tr>
                <td>Recipient shows red phone icon</td>
                <td>Texting not enabled or no mobile number</td>
                <td>Edit the involvement and check <strong>Allow Phone Text</strong>. Ensure the contact has a <strong>mobile</strong> phone number (not just home/work).</td>
              </tr>
              <tr>
                <td>STOP received but involvements not updated</td>
                <td>Phone number format mismatch or no matching outbound text</td>
                <td>Check that the contact&rsquo;s phone number in V7 matches the format <code>1-XXX-XXX-XXXX</code>. Check Rollbar for webhook processing errors.</td>
              </tr>
              <tr>
                <td>Webhook errors / 500 responses</td>
                <td>Server error processing the callback</td>
                <td>Check Rollbar for error details. Verify <code>TELNYX_PUBLIC_KEY</code> is set correctly. Check that the webhook URL is accessible from the internet.</td>
              </tr>
            </tbody>
          </table>

          <h5>Enabling SMS for a Contact</h5>
          <ol>
            <li>Ensure the contact has a <strong>mobile phone number</strong> on file.</li>
            <li>On the matter, edit the contact&rsquo;s <strong>involvement</strong>.</li>
            <li>Check <strong>Allow Phone Text</strong>.</li>
            <li>Save the involvement.</li>
            <li>The contact will now appear with a green phone icon in the Texts tab recipient search.</li>
          </ol>
        `
      },
      {
        id: 'fax-integration',
        title: 'Fax Services (Phaxio)',
        audience: 'both',
        content: `
          <h4>Phaxio Fax Integration</h4>
          <p>PLO does <strong>not have a physical fax machine</strong>. All faxes are sent and received electronically through <strong>Phaxio</strong>, a third-party fax API. The only way to send a fax is through a matter&rsquo;s outgoing mail system.</p>

          <h5>Sending Faxes (Step-by-Step)</h5>
          <ol>
            <li>Ensure the recipient contact has a <strong>fax number</strong> in V7 and is attached to the matter.</li>
            <li>Any attachments to fax must be <strong>already scanned/uploaded</strong> into the matter.</li>
            <li>In the matter, click <strong>New &rarr; Document &rarr; Miscellaneous Letter</strong>.</li>
            <li>Tag the document. Select the recipient contact.</li>
            <li>Check <strong>&ldquo;Send By Fax&rdquo;</strong> (optionally also &ldquo;Send By Mail&rdquo; for dual delivery).</li>
            <li>Check &ldquo;Include Attachments&rdquo; if applicable.</li>
            <li>Type the fax cover/body, Preview, then Send.</li>
            <li>On the Outgoing Transaction screen, use the <strong>Documents tab</strong> to select attachments.</li>
            <li>Click <strong>Submit</strong>. The fax queues and sends immediately.</li>
          </ol>
          <div class="alert alert-warning"><strong>Cost:</strong> Faxing costs approximately <strong>$1.00 per page</strong>. Use email whenever possible. Faxing 30+ pages of records is not acceptable &mdash; use regular mail or email instead.</div>

          <h5>Fax Failures</h5>
          <p>If a fax fails to send, both the person who composed the fax and the outgoing mail person receive a <strong>Fax Failure email notification</strong>. Note that resending failed faxes through the Resend button does not work &mdash; a new outgoing mail job must be created.</p>

          <h5>Receiving Faxes</h5>
          <ul>
            <li>Incoming faxes are received via Phaxio webhook callbacks and automatically appear in the <strong>scan queue</strong>.</li>
            <li>The <code>callback_token</code> setting verifies webhook authenticity.</li>
            <li>Faxes receive a higher scan priority (configured via Scan Priorities) since they are typically time-sensitive.</li>
            <li>Many incoming faxes are delivered electronically directly to a matter via auto-processing rules.</li>
            <li>Some faxes may arrive upside down or contain multiple document types &mdash; these are printed for manual processing.</li>
          </ul>

          <h5>Processing Received Faxes (When Printed)</h5>
          <ol>
            <li>Date-stamp with red &ldquo;received&rdquo; stamper. Add the Matter Number and code.</li>
            <li>Add any contacts from the fax that aren&rsquo;t already in the matter.</li>
            <li>Scan to: <code>scanner@princelaw.com;[attorney-email]</code> (both addresses, separated by semicolon).</li>
            <li>In the scan page Note section, type the word <strong>&ldquo;FAX&rdquo;</strong> &mdash; this alerts the attorney it may be time-sensitive.</li>
            <li>Mark with an orange dot and place face-down on the scanned mail pile.</li>
          </ol>

          <h5>Configuration (Admin)</h5>
          <p>API keys (production and test), secrets, and callback tokens are stored in the <code>phaxio</code> settings group under <strong>Administration &rarr; Settings</strong>.</p>
        `
      },
      {
        id: 'email-processing',
        title: 'Email Processing',
        audience: 'admin',
        content: `
          <h4>Email System</h4>
          <p>V7 processes both incoming and outgoing emails:</p>

          <h5>Incoming Email</h5>
          <ul>
            <li>Emails are processed by <strong>Mailman</strong> (Rails Action Mailbox) on the mail server.</li>
            <li>The system matches sender/recipient email addresses to contacts and files emails to the appropriate matter.</li>
            <li>Attachments are extracted and can be filed as documents.</li>
            <li>Special handlers exist for court notices (CMECF) and other automated emails.</li>
          </ul>

          <h5>Outgoing Email</h5>
          <ul>
            <li>Emails are sent via <strong>Postfix</strong> on the application server, which relays to the mail server.</li>
            <li>All outgoing emails are logged in matter history.</li>
            <li>Delivery is asynchronous via Sidekiq background jobs.</li>
          </ul>

          <h5>Email Archive</h5>
          <p>All processed emails are stored in the <strong>Email Archive</strong> (<strong>Administration &rarr; Email Archive</strong>). The archive is searchable by sender, recipient, subject, date, and content.</p>
        `
      },
      {
        id: 'matrix-integration',
        title: 'Element/Matrix Chat',
        audience: 'both',
        content: `
          <h4>Element &mdash; Firm Messaging</h4>
          <p><strong>Element</strong> is the firm&rsquo;s secure internal messaging app, powered by our private <strong>Matrix/Synapse</strong> server. It works like Slack or Microsoft Teams but runs entirely on our own infrastructure &mdash; messages never leave our network.</p>

          <h5>What Element Is Used For</h5>
          <ul>
            <li><strong>Team messaging</strong> &mdash; Direct messages and group rooms for case discussions, admin coordination, and general communication.</li>
            <li><strong>V7 reminders</strong> &mdash; Calendar and matter reminders can be delivered as Element messages (select &ldquo;Element&rdquo; as the delivery method when creating a reminder).</li>
            <li><strong>System notifications</strong> &mdash; V7 can send automated alerts (document uploads, scan completions, etc.) to Element rooms.</li>
          </ul>

          <hr />

          <h5>Step 1: Download Element</h5>
          <p>Element is free and available on every platform. Download the version for your device:</p>
          <table class="table table-sm table-bordered" style="max-width: 600px">
            <thead><tr><th>Platform</th><th>Download Link</th></tr></thead>
            <tbody>
              <tr><td><i class="fab fa-windows mr-1"></i> Windows</td><td><a href="https://element.io/download" target="_blank">element.io/download</a> &mdash; click <strong>&ldquo;Download for Windows&rdquo;</strong></td></tr>
              <tr><td><i class="fab fa-apple mr-1"></i> Mac</td><td><a href="https://element.io/download" target="_blank">element.io/download</a> &mdash; click <strong>&ldquo;Download for macOS&rdquo;</strong>, or install from the <a href="https://apps.apple.com/app/element/id1083446067" target="_blank">Mac App Store</a></td></tr>
              <tr><td><i class="fab fa-apple mr-1"></i> iPhone / iPad</td><td><a href="https://apps.apple.com/app/element/id1083446067" target="_blank">App Store &mdash; &ldquo;Element&rdquo;</a></td></tr>
              <tr><td><i class="fab fa-android mr-1"></i> Android</td><td><a href="https://play.google.com/store/apps/details?id=im.vector.app" target="_blank">Google Play &mdash; &ldquo;Element&rdquo;</a></td></tr>
              <tr><td><i class="fas fa-globe mr-1"></i> Web Browser</td><td><a href="https://app.element.io" target="_blank">app.element.io</a> &mdash; no install needed, works in any browser</td></tr>
            </tbody>
          </table>

          <hr />

          <h5>Step 2: Configure the Homeserver</h5>
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            <strong>Important:</strong> You must change the homeserver to our private server before signing in. If you skip this step, you will be connecting to the public Matrix network instead of the firm&rsquo;s server.
          </div>

          <h6>Desktop &amp; Web</h6>
          <ol>
            <li>Open Element. On the sign-in screen, click <strong>&ldquo;Sign in&rdquo;</strong>.</li>
            <li>Below the username field you will see <strong>&ldquo;Homeserver&rdquo;</strong> showing <code>matrix.org</code>. Click <strong>&ldquo;Edit&rdquo;</strong> (or the pencil icon).</li>
            <li>Select <strong>&ldquo;Other homeserver&rdquo;</strong>.</li>
            <li>Enter: <code style="font-size: 1.1em; font-weight: bold; background: #edf2f7; padding: 4px 10px; border-radius: 4px;">https://matrix.princelaw.com</code></li>
            <li>Click <strong>&ldquo;Continue&rdquo;</strong>. Element will verify the server.</li>
            <li>Enter your username and password (provided by IT). Click <strong>&ldquo;Sign in&rdquo;</strong>.</li>
          </ol>

          <h6>iPhone / iPad</h6>
          <ol>
            <li>Open Element. Tap <strong>&ldquo;Sign in&rdquo;</strong>.</li>
            <li>Tap <strong>&ldquo;Edit&rdquo;</strong> next to the homeserver address (<code>matrix.org</code>).</li>
            <li>Clear the field and type: <code>https://matrix.princelaw.com</code></li>
            <li>Tap <strong>&ldquo;Continue&rdquo;</strong>, then enter your credentials.</li>
          </ol>

          <h6>Android</h6>
          <ol>
            <li>Open Element. Tap <strong>&ldquo;Get started&rdquo;</strong> or <strong>&ldquo;Sign in&rdquo;</strong>.</li>
            <li>Tap <strong>&ldquo;Select a server&rdquo;</strong> (or <strong>&ldquo;Edit&rdquo;</strong>).</li>
            <li>Choose <strong>&ldquo;Other&rdquo;</strong> and enter: <code>https://matrix.princelaw.com</code></li>
            <li>Tap <strong>&ldquo;Continue&rdquo;</strong>, then sign in.</li>
          </ol>

          <hr />

          <h5>Step 3: Verify Your Session</h5>
          <p>After signing in for the first time, Element may ask you to <strong>verify your session</strong> using another device or a recovery key. This is a security feature that enables encrypted messaging.</p>
          <ul>
            <li>If you have Element on another device already, follow the &ldquo;verify with another device&rdquo; prompts.</li>
            <li>If this is your first device, contact IT for a recovery key.</li>
          </ul>

          <hr />

          <h5>Quick Reference</h5>
          <table class="table table-sm table-bordered" style="max-width: 500px">
            <tbody>
              <tr><td><strong>Homeserver URL</strong></td><td><code>https://matrix.princelaw.com</code></td></tr>
              <tr><td><strong>Server Location</strong></td><td>On-premises (copies.princelaw.com / 10.1.1.16)</td></tr>
              <tr><td><strong>Encryption</strong></td><td>End-to-end encryption enabled by default for direct messages</td></tr>
              <tr><td><strong>Support</strong></td><td>Contact IT if you need your account created or password reset</td></tr>
            </tbody>
          </table>

          <h5>Troubleshooting</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Problem</th><th>Solution</th></tr></thead>
            <tbody>
              <tr><td>&ldquo;Can&rsquo;t reach homeserver&rdquo;</td><td>Make sure you typed <code>https://matrix.princelaw.com</code> (with <code>https://</code>). If on VPN, ensure it&rsquo;s connected. If outside the office, the server must be reachable via the internet.</td></tr>
              <tr><td>&ldquo;Unknown user&rdquo; or login fails</td><td>Your username is typically your first initial + last name (e.g., <code>wprince</code>). Contact IT if you don&rsquo;t have credentials.</td></tr>
              <tr><td>Messages won&rsquo;t decrypt</td><td>You need to verify your session. Go to Settings &rarr; Security &amp; Privacy &rarr; verify or import keys.</td></tr>
              <tr><td>Not receiving notifications</td><td>Check Element&rsquo;s notification settings AND your device&rsquo;s system notification permissions for the Element app.</td></tr>
            </tbody>
          </table>
        `
      }
    ]
  },

  // ============================================================
  // 26. AI FEATURES
  // ============================================================
  {
    id: 'ai-features',
    title: 'AI Features',
    icon: 'fa-robot',
    audience: 'admin',
    subsections: [
      {
        id: 'ai-document-generation',
        title: 'AI Research & Drafting',
        audience: 'admin',
        content: `
          <h4>AI Research &amp; Drafting</h4>
          <p>V7 can use either <strong>Claude</strong> (Anthropic) or <strong>OpenAI</strong> for legal research, document analysis, and document drafting. The active provider is selected per intranet in the Settings system.</p>

          <h5>Matter-Level AI Research</h5>
          <ul>
            <li>Open a matter and use the <strong>AI Research</strong> tab.</li>
            <li>The old separate Documents-tab AI tools were removed and consolidated into the AI Research workflow.</li>
            <li>Use the <strong>Query</strong> tab to enter the request, the <strong>Documents</strong> tab to select matter documents, the <strong>Temporary Files</strong> tab to provide request-only files that must not become matter documents, and the <strong>Results</strong> tab to review the answer or draft.</li>
          </ul>

          <h5>Temporary Research Uploads</h5>
          <p>Temporary research uploads are stored in <code>ai_research_uploads</code>, not <code>documents</code>. V7 accepts PDF, DOC, DOCX, RTF, ODT, TXT, XML, HTM, and HTML files up to 25 MB. The background research job extracts and retains limited text and metadata for the research discussion, removes the uploaded binary after extraction or extraction failure, and carries completed extracted text into follow-up requests. The generated response is prepended with the temporary filenames, descriptions, and an explicit notice that they are not in the matter&rsquo;s document list.</p>

          <h5>Document Creation</h5>
          <p>Document creation is based on the user&rsquo;s request. If the user asks a research question, V7 returns a research answer. Requests to draft, prepare, create, generate, write, compose, produce, make, design, or build a recognized document are routed to document creation. V7 asks for the document type and any needed follow-up details, then opens the generated draft in the document editor.</p>
          <p>Requests containing <em>template</em> or <em>reusable form</em> default to reusable-template mode, while other document-creation requests default to one-off mode. The user may override the inferred choice. Reusable-template mode displays <strong>What should this template be named?</strong>; the controller requires a nonblank name that is unique within the intranet before calling the AI provider. The persistent success page identifies the saved template by name and ID and links directly to its editor. For reusable templates, both supported AI providers receive <code>TagReferenceService.condensed_reference</code> and are instructed to return V7 Radius-tagged template HTML. Generated templates support contact lookup by involvement role and role-based conditional blocks such as <code>if_contact</code>; the saved result uses the normal DocTemplate/DocPart rendering pipeline.</p>
          <p>A drafting request may also be submitted as a follow-up after research is complete. V7 preserves the original question, prior answer, and selected matter documents as drafting context. The <strong>Send Follow-up</strong> and <strong>Reconsider With Selected Documents</strong> controls submit their respective instructions independently.</p>

          <h5>Document Analysis</h5>
          <ul>
            <li>AI can analyze uploaded documents and extract key information.</li>
            <li>Summarize lengthy documents (medical records, depositions, etc.).</li>
            <li>AI-assisted classification suggestions.</li>
          </ul>

          <h5>Successive Document Revision</h5>
          <p><strong>Revise a Document</strong> is the fourth AI Research workflow tab, alongside <strong>Query</strong>, <strong>Documents</strong>, and <strong>Results</strong>. It creates a separate editable matter document rather than overwriting the source. Initial and successive revision jobs use the intranet&rsquo;s configured AI provider and run through the background queue. Each successful job stores its AI usage record, updates the same revised copy, creates a V7 document version, adds document activity history, queues document indexing, and opens the current copy in <strong>Documents &rarr; Editing Document</strong>.</p>
          <p>The source and revised document IDs are stored on the revision request so a user can submit additional directions without resending or replacing the original. If the selected file has no editable parts, V7 attempts text extraction before submitting it to the provider. Provider output is limited to document HTML; executable and embedded elements are removed before the revision is saved.</p>
          <p>The AI Research frame notifies the matter page when a revision finishes. The matter page activates the Documents section, waits briefly if the editor component is still initializing, reloads the document list, and opens the revised document in the <strong>Editing Document</strong> sub-tab. This handoff applies to both the first revised copy and every successful follow-up revision.</p>
          <p>Complete-document revisions accept up to <strong>180,000 readable source characters</strong>, request up to <strong>65,536 output tokens</strong>, and use a revision-specific <strong>600-second provider read timeout</strong>. These higher settings apply only to document revision; ordinary AI Research and drafting calls retain their individual limits. A provider can impose a lower limit for a particular model. V7 rejects incomplete or time-limited output and does not create or update the revised document unless a complete response is received.</p>
        `
      },
      {
        id: 'ai-provider-settings',
        title: 'AI Provider Settings',
        audience: 'admin',
        content: `
          <h4>Switching Between Claude and OpenAI</h4>
          <p>AI provider choice is stored per intranet. Changing it affects AI Research, AI document drafting, AI template generation, document summaries, and background AI formatting for that intranet.</p>

          <h5>Switch Provider</h5>
          <ol>
            <li>Open <strong>Administration &rarr; Settings</strong>.</li>
            <li>Find the setting group <code>ai</code>.</li>
            <li>Set <code>provider</code> to <code>claude</code> or <code>openai</code>.</li>
            <li>If using OpenAI, confirm <code>openai_model</code> is set to the approved OpenAI model.</li>
            <li>Provider selection is intranet-specific. Confirm the intranet that owns the matter before changing the setting; other intranets keep their existing provider.</li>
            <li>Save the setting and retry the AI request.</li>
          </ol>

          <h5>Required Server Credentials</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Provider</th><th>Setting Value</th><th>Server Credential</th><th>Account Console</th></tr></thead>
            <tbody>
              <tr><td>Claude</td><td><code>ai.provider = claude</code></td><td><code>ANTHROPIC_API_KEY</code></td><td><a href="https://console.anthropic.com/settings/billing" target="_blank" rel="noopener">Anthropic billing</a></td></tr>
              <tr><td>OpenAI</td><td><code>ai.provider = openai</code></td><td><code>OPENAI_API_KEY</code></td><td><a href="https://platform.openai.com/settings/organization/billing/overview" target="_blank" rel="noopener">OpenAI billing</a></td></tr>
            </tbody>
          </table>

          <h5>Keeping Provider Accounts Current</h5>
          <ul>
            <li><strong>Claude:</strong> In the Anthropic Console, keep billing information current, monitor spend limits, and watch rate-limit usage before running large document batches.</li>
            <li><strong>OpenAI:</strong> In the OpenAI Platform, keep billing information current, review project budgets/limits, and check usage if V7 reports that quota was exceeded.</li>
            <li>After rotating a provider key, update the server environment/credentials and restart the app process that reads that key.</li>
            <li>If a pasted key has been exposed in chat, email, tickets, screenshots, or logs, rotate it immediately and replace it with a new key.</li>
            <li>When testing a new provider, run one small AI request first, then confirm the AI Usage Log records the expected provider/model and success status.</li>
          </ul>

          <h5>Troubleshooting Provider Errors</h5>
          <table class="table table-sm table-bordered">
            <thead><tr><th>Error</th><th>Likely Cause</th><th>Fix</th></tr></thead>
            <tbody>
              <tr><td><code>No OpenAI API key configured</code></td><td>The server cannot see <code>OPENAI_API_KEY</code>.</td><td>Add/replace the key in the server environment and restart V7.</td></tr>
              <tr><td><code>No Anthropic API key configured</code></td><td>The server cannot see <code>ANTHROPIC_API_KEY</code>.</td><td>Add/replace the key in the server environment and restart V7.</td></tr>
              <tr><td><code>exceeded your current quota</code></td><td>The OpenAI key&rsquo;s project or organization has no available API budget/credits.</td><td>Check OpenAI usage, billing, and project limits, then retry after the account has available quota.</td></tr>
              <tr><td>Claude rate-limit or spend-limit errors</td><td>The Anthropic workspace has reached a rate limit or spend cap.</td><td>Check Anthropic usage, limits, and billing, then retry after capacity is available.</td></tr>
              <tr><td><code>The configured ... model (...) is no longer available</code></td><td>The selected provider retired the configured model or removed access to it.</td><td>Select a supported model or switch the intranet to the other configured provider, then retry. V7 shows an actionable message to the user while retaining the provider&rsquo;s original response in the AI Usage Log for administrators.</td></tr>
              <tr><td>Unexpected provider still being used</td><td>The wrong intranet setting was changed, or the application process has stale environment credentials.</td><td>Confirm the active intranet, verify <code>ai.provider</code>, and restart V7 if credentials changed.</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'ai-dashboard',
        title: 'AI Usage Tracking',
        audience: 'admin',
        content: `
          <h4>Monitoring AI Usage</h4>

          <h5>AI Usage Logs</h5>
          <p>Navigate to <strong>Administration &rarr; AI Usage Logs</strong> to view detailed logs:</p>
          <ul>
            <li>Each AI request is logged with: timestamp, user, matter, input/output token counts, model used, and cost.</li>
            <li>Filter by user, matter, date range, or model.</li>
            <li>Failed requests are logged too, including provider account errors such as missing keys, quota exhaustion, rate limits, and invalid models.</li>
          </ul>

          <h5>AI Dashboard</h5>
          <p>Navigate to <strong>Administration &rarr; AI Dashboard</strong> for aggregate views:</p>
          <ul>
            <li>Total token usage and cost over time.</li>
            <li>Per-user usage breakdown.</li>
            <li>Per-matter cost tracking.</li>
            <li>Usage trends and patterns.</li>
          </ul>

          <div class="alert alert-info"><strong>Note:</strong> V7&rsquo;s usage logs are for internal tracking and troubleshooting. Provider billing and spend limits are controlled in the Claude or OpenAI provider console.</div>
        `
      }
    ]
  },
  // ============================================================
  // PHONE SYSTEM (3CX)
  // ============================================================
  {
    id: 'phone-system',
    title: 'Phone System (3CX)',
    icon: 'fa-phone',
    audience: 'admin',
    subsections: [
      {
        id: 'phone-overview',
        title: 'Overview',
        audience: 'admin',
        content: `
          <h4>3CX Phone System Integration</h4>
          <p>V7 integrates with the firm&rsquo;s <strong>3CX phone system</strong> using a hybrid approach:</p>
          <ul>
            <li><strong>CRM Integration</strong> (primary) &mdash; 3CX pushes call data directly into V7&rsquo;s MySQL database in real time via the built-in CRM Integration feature. Every completed call triggers an INSERT into the <code>phonecalls</code> table.</li>
            <li><strong>XAPI</strong> (enrichment) &mdash; V7&rsquo;s <code>ThreecxService</code> connects to the 3CX XAPI via OAuth2 to enrich call records with user and contact data, monitor active calls, and sync extension-to-user mappings. Runs every 5 minutes via <code>ThreecxSyncWorker</code>.</li>
          </ul>

          <h5>What This Enables</h5>
          <ul>
            <li><strong>Employee phone activity monitoring</strong> &mdash; see who is making and receiving calls.</li>
            <li><strong>Time tracking</strong> &mdash; track how much time employees spend on the phone.</li>
            <li><strong>Missed call identification</strong> &mdash; identify missed and unanswered calls.</li>
            <li><strong>Contact linking</strong> &mdash; calls are automatically linked to V7 contacts via a MySQL trigger and XAPI enrichment.</li>
            <li><strong>Contact lookup</strong> &mdash; 3CX looks up incoming callers in V7&rsquo;s contacts and displays the name on the phone screen.</li>
            <li><strong>Active call monitoring</strong> &mdash; real-time view of who is on the phone (via XAPI).</li>
            <li><strong>User status</strong> &mdash; extension registration status, queue login status (via XAPI).</li>
          </ul>

          <h5>Architecture</h5>
          <ol>
            <li><strong>3CX CRM Integration</strong> &mdash; configured in the 3CX Management Console under Integrations &rarr; CRM. Uses the &ldquo;Database MySQL&rdquo; template to connect directly to V7&rsquo;s MySQL database.</li>
            <li><strong>3CX XAPI</strong> &mdash; REST API at <code>https://3cx.princelaw.com:5001/xapi/v1/</code>. Uses OAuth2 client_credentials flow. <code>ThreecxService</code> authenticates, fetches data, and enriches phonecall records.</li>
            <li><strong><code>ThreecxSyncWorker</code></strong> &mdash; Sidekiq cron job (every 5 min) that syncs extension-to-user mappings and enriches phonecall records missing contact info.</li>
            <li><strong><code>insert_dashes</code> MySQL trigger</strong> &mdash; a BEFORE INSERT trigger on the <code>phonecalls</code> table that formats phone numbers, resolves <code>contact_id</code>, <code>phone_number_id</code>, and <code>contact_name</code>.</li>
            <li><strong><code>EnDash</code> MySQL function</strong> &mdash; a helper function used by 3CX&rsquo;s contact lookup query to normalize phone number formatting for matching.</li>
            <li><strong>Phone Calls UI</strong> &mdash; Angular page under Administration &rarr; Phone Calls with sortable columns, expandable detail rows, and search filters.</li>
          </ol>
        `
      },
      {
        id: 'phone-crm-config',
        title: 'CRM Configuration',
        audience: 'admin',
        content: `
          <h4>3CX CRM Integration Configuration</h4>
          <p>The CRM integration is configured in the 3CX Management Console. The configuration is stored in the 3CX PostgreSQL database on <code>3cx.princelaw.com</code> in the <code>database_single</code> database, <code>parameter</code> table, key <code>CRMINT_DEFAULT</code>.</p>

          <h5>Connection Settings</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Setting</th><th>Value</th></tr></thead>
            <tbody>
              <tr><td>Template</td><td>Database MySQL</td></tr>
              <tr><td>Server</td><td><code>10.1.1.5</code></td></tr>
              <tr><td>Port</td><td><code>3306</code></td></tr>
              <tr><td>Database</td><td><code>intranet_beta</code></td></tr>
              <tr><td>Username</td><td><code>intranet</code></td></tr>
            </tbody>
          </table>

          <h5>Contact Lookup SQL (caller ID &rarr; contact name on phone)</h5>
          <p>When an incoming call arrives, 3CX executes this query to look up the caller and display their name:</p>
          <pre>SELECT contacts.id AS contactid, first_name AS firstname, last_name AS lastname,
  company_name AS companyname, e.email AS email,
  p.number AS phonebusiness, m.number AS phonemobile,
  f.number AS faxbusiness, fh.number AS faxhome, h.number AS phonehome
FROM contacts
  LEFT OUTER JOIN phone_numbers p ON p.contact_id = contacts.id AND p.phone_type_id = 1
  LEFT OUTER JOIN phone_numbers h ON h.contact_id = contacts.id AND h.phone_type_id = 8
  LEFT OUTER JOIN phone_numbers fh ON fh.contact_id = contacts.id AND fh.phone_type_id = 9
  LEFT OUTER JOIN phone_numbers m ON m.contact_id = contacts.id AND m.phone_type_id = 2
  LEFT OUTER JOIN phone_numbers f ON f.contact_id = contacts.id AND f.phone_type_id = 3
  LEFT OUTER JOIN email_addresses e ON e.contact_id = contacts.id AND e.position = 1
WHERE contacts.intranet_id IN (1, 28)
  AND contacts.dod IS NULL
  AND EnDash('[Number]') COLLATE utf8mb4_general_ci
    IN (f.number, fh.number, h.number, m.number, p.number)
ORDER BY contacts.id LIMIT 1</pre>

          <h5>Call Journaling SQL (call &rarr; phonecalls table)</h5>
          <p>When a call ends, 3CX executes this INSERT to log the call. The <code>insert_dashes</code> trigger then enriches the record.</p>
          <pre>INSERT INTO phonecalls (phone_number, extension, start_time, duration, end_time, call_type)
VALUES (@Number, @Agent, @CallStartTimeUTC,
  [[DurationTimespan].get_TotalSeconds()], @CallEndTimeUTC, @CallType)</pre>
          <p>3CX variables: <code>@Number</code> = caller/callee phone number, <code>@Agent</code> = extension number, <code>@CallType</code> = Inbound/Outbound/Missed.</p>

          <h5>Unknown Caller Creation SQL</h5>
          <p>When a caller is not found in contacts, 3CX logs an entry to <code>unknown_callers</code>:</p>
          <pre>INSERT INTO unknown_callers (phone_number, attempts, updated_at)
VALUES (EnDash('[Number]'), 1, NOW())
ON DUPLICATE KEY UPDATE attempts = attempts + 1, updated_at = NOW();

SELECT id AS contactid, 'unknown' AS firstname, 'caller' AS lastname,
  phone_number AS phonebusiness
FROM unknown_callers WHERE phone_number = EnDash('[Number]');</pre>

          <h5>Contact URL</h5>
          <p>When a contact is matched, 3CX generates a clickable URL: <code>https://v7.attymatter.com/contacts/{contactid}</code></p>
        `
      },
      {
        id: 'phone-xapi-setup',
        title: 'XAPI Setup',
        audience: 'admin',
        content: `
          <h4>3CX XAPI Configuration</h4>
          <p>The XAPI provides additional capabilities beyond the CRM integration: active call monitoring, user/extension status, and phonecall data enrichment. It uses OAuth2 <code>client_credentials</code> authentication.</p>

          <h5>Current API Credentials</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Setting</th><th>Value</th></tr></thead>
            <tbody>
              <tr><td>Server</td><td><code>3cx.princelaw.com</code></td></tr>
              <tr><td>Port</td><td><code>5001</code></td></tr>
              <tr><td>Service Principal</td><td><code>89991</code></td></tr>
              <tr><td>Client ID</td><td><code>89991</code></td></tr>
              <tr><td>Client Secret</td><td><code>2zyH4PGL1YiUU9ALTHwjjiBwoXmSysI7</code></td></tr>
              <tr><td>Role</td><td>Administrator (system_admins)</td></tr>
              <tr><td>OAuth2 Token URL</td><td><code>https://3cx.princelaw.com:5001/connect/token</code></td></tr>
              <tr><td>API Base URL</td><td><code>https://3cx.princelaw.com:5001/xapi/v1/</code></td></tr>
            </tbody>
          </table>

          <h5>How to Create/Regenerate an API Key on 3CX</h5>
          <ol>
            <li>Open the <strong>3CX Admin Console</strong> at <a href="https://3cx.princelaw.com:5001" target="_blank">https://3cx.princelaw.com:5001</a>.</li>
            <li>In the left sidebar, click <strong>Integrations</strong> &rarr; <strong>API</strong>.</li>
            <li>Click on the service principal (e.g., <code>89991</code>) or click <strong>&ldquo;+ Add&rdquo;</strong> to create a new one.</li>
            <li>Under <strong>3CX Configuration API Access</strong>:
              <ul>
                <li>Check <strong>&ldquo;Enable access to the 3CX Configuration API (XAPI)&rdquo;</strong>.</li>
                <li>Set <strong>Department</strong> to <code>Administrator</code>.</li>
                <li><strong>IMPORTANT: Set Role to &ldquo;Administrator&rdquo;</strong> (not &ldquo;User&rdquo;). The &ldquo;User&rdquo; role causes all data endpoints to return 401.</li>
              </ul>
            </li>
            <li>Under <strong>3CX Call Control &amp; Chat API Access</strong>:
              <ul>
                <li>Check <strong>&ldquo;Enable access to the 3CX Call Control API&rdquo;</strong>.</li>
                <li>Add the firm&rsquo;s DID number(s) (e.g., <code>6108453803</code>).</li>
                <li>Click <strong>&ldquo;+ Select Extensions&rdquo;</strong> and add all extensions that should be accessible via the API.</li>
              </ul>
            </li>
            <li>Click <strong>&ldquo;Generate API Key&rdquo;</strong> to create a new client secret.</li>
            <li>Click <strong>&ldquo;Save&rdquo;</strong>.</li>
            <li>Copy the generated API key &mdash; this is the <strong>client_secret</strong>. The service principal number (e.g., <code>89991</code>) is the <strong>client_id</strong>.</li>
          </ol>

          <div class="alert alert-warning">
            <strong>After generating a new key:</strong> Update the V7 settings via the Rails console:
            <pre>ssh primal2
cd /web/v7.attymatter.com/site/current
bundle exec rails runner '
  intranet = Intranet.find(1)
  group = SettingGroup.find_by(name: "threecx")
  type = SettingType.find_by(setting_group: group, name: "client_secret")
  setting = Setting.find_or_initialize_by(intranet: intranet, setting_type: type)
  setting.value = "NEW_SECRET_HERE"
  setting.save!'</pre>
          </div>

          <h5>OAuth2 Authentication Flow</h5>
          <p>The API key is <strong>not</strong> a Bearer token. It is a <strong>client secret</strong> used in the OAuth2 <code>client_credentials</code> flow:</p>
          <pre># Step 1: Exchange credentials for a JWT token (expires in 60 seconds)
curl -X POST https://3cx.princelaw.com:5001/connect/token \\
  -d "grant_type=client_credentials&amp;client_id=89991&amp;client_secret=YOUR_SECRET" -k

# Response: {"token_type":"Bearer","expires_in":60,"access_token":"eyJ..."}

# Step 2: Use the JWT token for API calls
curl https://3cx.princelaw.com:5001/xapi/v1/SystemStatus \\
  -H "Authorization: Bearer eyJ..." -k</pre>
          <p>The <code>ThreecxService</code> handles this automatically, including token refresh.</p>

          <h5>Available XAPI Endpoints</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Endpoint</th><th>Description</th><th>Status</th></tr></thead>
            <tbody>
              <tr><td><code>SystemStatus</code></td><td>System info, license, version, disk usage</td><td class="text-success">Working</td></tr>
              <tr><td><code>Users</code></td><td>All extensions/users with status, settings</td><td class="text-success">Working</td></tr>
              <tr><td><code>ActiveCalls</code></td><td>Currently active calls in real time</td><td class="text-success">Working</td></tr>
              <tr><td><code>Queues</code></td><td>Call queue configuration and status</td><td class="text-success">Working</td></tr>
              <tr><td><code>Contacts</code></td><td>3CX phonebook contacts</td><td class="text-success">Working</td></tr>
              <tr><td><code>Recordings</code></td><td>Call recordings</td><td class="text-warning">Requires extended permissions</td></tr>
              <tr><td><code>CallHistoryView</code></td><td>Call history segments</td><td class="text-warning">Requires Call Control scope</td></tr>
              <tr><td><code>CallLogData</code></td><td>CDR/call log reports</td><td class="text-danger">404 &mdash; may require 3CX update</td></tr>
            </tbody>
          </table>

          <h5>V7 Settings (stored in Settings system)</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Setting</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>threecx.enabled</code></td><td>Enable/disable the XAPI integration (1 or 0)</td></tr>
              <tr><td><code>threecx.host</code></td><td>3CX server hostname</td></tr>
              <tr><td><code>threecx.port</code></td><td>3CX HTTPS port (default 5001)</td></tr>
              <tr><td><code>threecx.client_id</code></td><td>Service principal number (e.g., 89991)</td></tr>
              <tr><td><code>threecx.client_secret</code></td><td>OAuth2 client secret (API key)</td></tr>
              <tr><td><code>threecx.use_ssl</code></td><td>Use HTTPS (1 or 0)</td></tr>
            </tbody>
          </table>

          <h5>Testing the Connection</h5>
          <pre>ssh primal2
cd /web/v7.attymatter.com/site/current
bundle exec rails runner '
  service = ThreecxService.new(Intranet.find(1))
  puts service.test_connection.inspect
  puts "Active calls: " + service.active_calls.size.to_s
  puts "Users: " + service.users.size.to_s'</pre>

          <h5>Click-to-Dial (MakeCall)</h5>
          <p>Double-clicking any phone number link in V7 initiates a call via the 3CX XAPI <code>MakeCall</code> action. The call flow:</p>
          <ol>
            <li>User double-clicks a phone number link anywhere in V7.</li>
            <li>V7 sends <code>POST /phone_numbers/make_call.json</code> with the destination number.</li>
            <li>Rails uses <code>ThreecxService#make_call</code> to call <code>Users/Pbx.MakeCall</code> on the XAPI.</li>
            <li>3CX rings the user&rsquo;s desk phone/softphone. When they pick up, 3CX dials the destination.</li>
            <li>A billing modal opens simultaneously to record call duration, rate, and notes.</li>
          </ol>
          <p>Single-click still opens the 3CX app with the number pre-filled (via <code>tel:</code> protocol).</p>

          <div class="alert alert-warning">
            <strong>System Owner limitation:</strong> Extensions with the &ldquo;System Owner&rdquo; role (e.g., 81112) are invisible to the XAPI.
            MakeCall will fail for these extensions and fall back to the <code>tel:</code> protocol.
            To fix: change the extension&rsquo;s role from System Owner to a lower level, or use a separate non-admin extension.
          </div>

          <h5>Requirements for MakeCall</h5>
          <ul>
            <li>The user must have their <code>extension</code> field set in V7 (Administration &rarr; Users &rarr; Edit).</li>
            <li>The extension must be <strong>registered</strong> in 3CX (active phone/softphone connected).</li>
            <li>The extension must NOT have the &ldquo;System Owner&rdquo; role (invisible to XAPI).</li>
            <li>The extension must be in the Service Principal&rsquo;s DNLIST.</li>
          </ul>
        `
      },
      {
        id: 'phone-sql-trigger',
        title: 'MySQL Trigger &amp; Function',
        audience: 'admin',
        content: `
          <h4>MySQL Trigger: <code>insert_dashes</code></h4>
          <p>This BEFORE INSERT trigger on the <code>phonecalls</code> table runs every time 3CX (or any process) inserts a call record. It performs three tasks:</p>
          <ol>
            <li><strong>Formats phone numbers</strong> &mdash; 11-digit numbers get dashes added (e.g., <code>16105551234</code> &rarr; <code>1-610-555-1234</code>).</li>
            <li><strong>Resolves phone_number_id</strong> &mdash; looks up the formatted number in the <code>phone_numbers</code> table.</li>
            <li><strong>Resolves contact</strong> &mdash; sets <code>contact_id</code> and <code>contact_name</code> from the matched contact.</li>
          </ol>

          <h5>Trigger Code</h5>
          <pre>CREATE DEFINER='root'@'localhost' TRIGGER insert_dashes
BEFORE INSERT ON phonecalls FOR EACH ROW
SET
  new.phone_number = IF(LENGTH(new.phone_number) = 11,
    CONCAT(
      SUBSTR(new.phone_number, 1, 1), '-',
      SUBSTR(new.phone_number, 2, 3), '-',
      SUBSTR(new.phone_number, 5, 3), '-',
      SUBSTR(new.phone_number, 8, 4)
    ),
    new.phone_number),
  new.phone_number_id = (
    SELECT id FROM phone_numbers
    WHERE number = new.phone_number
    ORDER BY position LIMIT 1
  ),
  new.contact_id = (
    SELECT contact_id FROM phone_numbers
    WHERE number = new.phone_number
    ORDER BY position LIMIT 1
  ),
  new.contact_name = (
    SELECT display_name FROM contacts
    WHERE contacts.id = new.contact_id
  );</pre>

          <div class="alert alert-danger">
            <strong>Critical:</strong> The trigger&rsquo;s <code>DEFINER</code> must be a valid MySQL user.
            If the definer user doesn&rsquo;t exist (e.g., <code>'intranet'@'%'</code> when only host-specific grants exist),
            <strong>all INSERTs will fail silently</strong> and no calls will be logged.
            Use <code>SHOW TRIGGERS WHERE \`Table\` = 'phonecalls'</code> to check the current definer.
          </div>

          <h5>EnDash Function</h5>
          <p>The <code>EnDash</code> function is used by 3CX&rsquo;s contact lookup query to normalize phone numbers for matching. It converts raw digits into the dash-formatted style used in V7 (e.g., <code>16105551234</code> &rarr; <code>1-610-555-1234</code>).</p>
          <pre>CREATE DEFINER='root'@'localhost' FUNCTION EnDash(num VARCHAR(255))
RETURNS VARCHAR(255) DETERMINISTIC
BEGIN
  DECLARE cleaned VARCHAR(255);
  SET cleaned = REPLACE(REPLACE(REPLACE(REPLACE(num, '-', ''), '(', ''), ')', ''), ' ', '');
  IF LENGTH(cleaned) = 11 THEN
    RETURN CONCAT(
      SUBSTR(cleaned, 1, 1), '-',
      SUBSTR(cleaned, 2, 3), '-',
      SUBSTR(cleaned, 5, 3), '-',
      SUBSTR(cleaned, 8, 4)
    );
  END IF;
  RETURN cleaned;
END;</pre>

          <h5>To recreate the trigger (if needed)</h5>
          <p>Save the SQL above to a file and run on the MySQL server (<code>ssh mx2</code>):</p>
          <pre>DROP TRIGGER IF EXISTS insert_dashes;
-- then run the CREATE TRIGGER statement above
mysql intranet_beta &lt; /tmp/fix_trigger.sql</pre>
        `
      },
      {
        id: 'phone-data-flow',
        title: 'Data Flow',
        audience: 'admin',
        content: `
          <h4>How Call Data Flows</h4>
          <ol>
            <li>A call is made or received on the 3CX phone system.</li>
            <li>When the call ends, 3CX&rsquo;s CRM integration executes an INSERT into the <code>phonecalls</code> table with: phone number, extension, start time, end time, duration, and call type (Inbound/Outbound/Missed).</li>
            <li>The <code>insert_dashes</code> MySQL trigger fires automatically and:
              <ul>
                <li>Formats 11-digit phone numbers with dashes.</li>
                <li>Looks up the phone number in <code>phone_numbers</code> to set <code>phone_number_id</code> and <code>contact_id</code>.</li>
                <li>Sets <code>contact_name</code> from the matched contact&rsquo;s <code>display_name</code>.</li>
              </ul>
            </li>
            <li>The call appears in the Phone Calls page in V7 immediately.</li>
          </ol>

          <h5>Phonecalls Table Columns</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Column</th><th>Source</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>phone_number</code></td><td>3CX</td><td>Caller/callee number (formatted by trigger)</td></tr>
              <tr><td><code>extension</code></td><td>3CX</td><td>User&rsquo;s 3CX extension number</td></tr>
              <tr><td><code>start_time</code></td><td>3CX</td><td>Call start time (UTC)</td></tr>
              <tr><td><code>end_time</code></td><td>3CX</td><td>Call end time (UTC)</td></tr>
              <tr><td><code>duration</code></td><td>3CX</td><td>Duration in seconds</td></tr>
              <tr><td><code>call_type</code></td><td>3CX</td><td>Inbound, Outbound, or Missed</td></tr>
              <tr><td><code>phone_number_id</code></td><td>Trigger</td><td>Matched <code>phone_numbers.id</code></td></tr>
              <tr><td><code>contact_id</code></td><td>Trigger</td><td>Matched <code>contacts.id</code></td></tr>
              <tr><td><code>contact_name</code></td><td>Trigger</td><td>Contact&rsquo;s <code>display_name</code></td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'phone-click-to-call',
        title: 'Click to Call Setup',
        audience: 'admin',
        content: `
          <h4>Click to Call</h4>
          <p>Phone numbers in V7 can be rendered as clickable <code>tel:</code> links. When clicked, they will dial the number using whatever application is set as the default phone handler on the user&rsquo;s computer. This works with the <strong>3CX Desktop App</strong>, <strong>3CX Web Client</strong>, or any softphone that registers as a <code>tel:</code> handler.</p>

          <h5>macOS Setup</h5>
          <ol>
            <li>Install the <strong>3CX Desktop App</strong> if not already installed.</li>
            <li>Open <strong>FaceTime</strong>.</li>
            <li>In the menu bar, go to <strong>FaceTime &rarr; Settings</strong> (or Preferences on older macOS).</li>
            <li>Find the <strong>&ldquo;Default for calls&rdquo;</strong> dropdown.</li>
            <li>Change it from <strong>FaceTime</strong> to the <strong>3CX Desktop App</strong>.</li>
            <li>Close FaceTime Settings.</li>
          </ol>
          <div class="alert alert-info">
            <strong>Note:</strong> If the 3CX app does not appear in the dropdown, make sure it is installed and has been opened at least once. You may need to restart FaceTime for it to appear.
          </div>

          <h5>Windows Setup</h5>
          <ol>
            <li>Install the <strong>3CX Desktop App</strong> if not already installed.</li>
            <li>Open <strong>Settings &rarr; Apps &rarr; Default Apps</strong>.</li>
            <li>Scroll down and click <strong>&ldquo;Choose default apps by protocol&rdquo;</strong>.</li>
            <li>Find <strong>TEL</strong> in the list and set it to the <strong>3CX Desktop App</strong>.</li>
            <li>Find <strong>CALLTO</strong> in the list and set it to the <strong>3CX Desktop App</strong> as well.</li>
          </ol>

          <h5>Browser-Level Alternative (Chrome)</h5>
          <p>If users prefer the <strong>3CX Web Client</strong> instead of the desktop app:</p>
          <ul>
            <li>When a user first clicks a <code>tel:</code> link in Chrome, the browser will prompt which app to use. Select the 3CX Web Client and check <strong>&ldquo;Always use this app.&rdquo;</strong></li>
            <li>Alternatively, install the <a href="https://chromewebstore.google.com/detail/3cx-click2call/baipgmmeifmofkcilhccccoipmjccehn" target="_blank">3CX Click2Call Chrome Extension</a>, which automatically detects phone numbers on any page and makes them clickable.</li>
          </ul>

          <h5>Troubleshooting</h5>
          <ul>
            <li><strong>Clicking a phone number opens FaceTime (Mac):</strong> FaceTime is still set as the default. Follow the macOS steps above to change it.</li>
            <li><strong>Nothing happens when clicking:</strong> No application is registered as the <code>tel:</code> handler. Install the 3CX Desktop App and set it as the default.</li>
            <li><strong>Wrong number format:</strong> The <code>tel:</code> link uses the number as stored in V7. If the number is missing the country code, it may not dial correctly on some systems.</li>
          </ul>
        `
      },
      {
        id: 'phone-troubleshooting',
        title: 'Troubleshooting',
        audience: 'admin',
        content: `
          <h4>Troubleshooting</h4>

          <h5>No new calls appearing</h5>
          <ol>
            <li><strong>Test the INSERT directly on MySQL</strong> (<code>ssh mx2</code>):
              <pre>INSERT INTO phonecalls (phone_number, extension, start_time, duration, end_time, call_type)
VALUES ('15551234567', '99999', NOW(), 30, NOW(), 'Inbound');
-- If this fails with "definer does not exist", recreate the trigger
SELECT * FROM phonecalls WHERE extension = '99999';
-- Should show formatted number: 1-555-123-4567
DELETE FROM phonecalls WHERE extension = '99999';</pre>
            </li>
            <li><strong>Check the trigger definer</strong>:
              <pre>SHOW TRIGGERS WHERE \`Table\` = 'phonecalls';
-- Look at the Definer column. It must be a valid MySQL user.
-- If it shows 'intranet@%' but that user doesn't exist, recreate:
SELECT user, host FROM mysql.user WHERE user = 'intranet';</pre>
            </li>
            <li><strong>Check 3CX CRM config</strong> (on <code>ssh 3cx</code>):
              <pre>sudo -u postgres psql -d database_single \\
  -c "SELECT value FROM parameter WHERE name = 'CRMINT_DEFAULT';"</pre>
              Verify Server=<code>10.1.1.5</code>, Database=<code>intranet_beta</code>, ReportCallEnabled=<code>True</code>.
            </li>
            <li><strong>Check network connectivity</strong> from 3CX to MySQL:
              <pre>ssh 3cx
# MySQL client not installed, use netcat:
nc -zv 10.1.1.5 3306</pre>
            </li>
            <li><strong>Check 3CX logs</strong>:
              <pre>ssh 3cx
grep -i 'mysql\\|crm\\|error' /var/lib/3cxpbx/Instance1/Data/Logs/3cxSystemService.log | tail -20</pre>
            </li>
          </ol>

          <h5>Calls not linked to contacts</h5>
          <ul>
            <li>The trigger matches <code>phone_number</code> against <code>phone_numbers.number</code> &mdash; the format must match exactly after dash formatting.</li>
            <li>If 3CX sends <code>1-484-300-0280</code> (already formatted), the trigger leaves it as-is and looks it up. If the contact&rsquo;s number is stored differently, no match.</li>
            <li>Check: <code>SELECT * FROM phone_numbers WHERE number = '1-484-300-0280';</code></li>
          </ul>

          <h5>Backfilling missed calls from 3CX</h5>
          <p>If calls were missed due to a broken trigger or connection, they can be backfilled from 3CX&rsquo;s PostgreSQL database:</p>
          <pre>ssh 3cx
sudo -u postgres psql -d database_single -c "
COPY (
  SELECT
    COALESCE(party_callerid, party_name) AS phone_number,
    dnowner AS extension,
    to_char(start_time AT TIME ZONE 'UTC', 'YYYY-MM-DD HH24:MI:SS') AS start_time_utc,
    EXTRACT(EPOCH FROM (end_time - start_time)) AS duration_seconds,
    to_char(end_time AT TIME ZONE 'UTC', 'YYYY-MM-DD HH24:MI:SS') AS end_time_utc,
    CASE
      WHEN calltype = 1 THEN 'Outbound'
      WHEN calltype = 2 THEN 'Inbound'
      WHEN calltype = 3 THEN 'Missed'
    END AS call_type
  FROM myphone_callhistory_v14
  WHERE start_time &gt; '2025-11-05'
  ORDER BY start_time
) TO '/tmp/missing_calls.csv' WITH CSV HEADER;"</pre>
          <p>Then transfer and import via Rails runner on production, inserting each row with duplicate checking on extension + start_time.</p>

          <h5>Key infrastructure</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Component</th><th>Location</th><th>Access</th></tr></thead>
            <tbody>
              <tr><td>3CX Server</td><td><code>3cx.princelaw.com</code></td><td><code>ssh 3cx</code></td></tr>
              <tr><td>3CX Config DB</td><td>PostgreSQL on 3CX server</td><td><code>sudo -u postgres psql -d database_single</code></td></tr>
              <tr><td>3CX Call History</td><td>Table <code>myphone_callhistory_v14</code></td><td>calltype: 1=Outbound, 2=Inbound, 3=Missed</td></tr>
              <tr><td>CRM Config</td><td><code>parameter</code> table, key <code>CRMINT_DEFAULT</code></td><td>Contains MySQL connection + SQL statements</td></tr>
              <tr><td>MySQL Server</td><td><code>10.1.1.5</code> (mx2)</td><td><code>ssh mx2</code></td></tr>
              <tr><td>MySQL Trigger</td><td><code>insert_dashes</code> on <code>phonecalls</code></td><td>Definer must be valid MySQL user</td></tr>
              <tr><td>MySQL Function</td><td><code>EnDash</code></td><td>Used by 3CX lookup query</td></tr>
            </tbody>
          </table>
        `
      }
    ]
  },

  // ============================================================
  // PRODUCTION OPERATIONS
  // ============================================================
  {
    id: 'production-operations',
    title: 'Production Operations',
    icon: 'fa-server',
    audience: 'admin',
    subsections: [
      {
        id: 'production-web-runtime',
        title: 'Web Runtime',
        audience: 'admin',
        content: `
          <h4>Passenger, ActionCable, and Request Guardrails</h4>
          <p>Production serves normal V7 web/API traffic through Passenger on port <code>4100</code>. ActionCable websocket traffic should run as a separate Puma service on port <code>4101</code>, with nginx <code>/cable</code> proxying to the Cable service. This prevents websocket sessions from consuming the same Passenger capacity used by ordinary pages and JSON requests.</p>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Component</th><th>Purpose</th><th>Repo File</th></tr></thead>
            <tbody>
              <tr><td><code>v7-passenger</code></td><td>Main web/API app on port <code>4100</code></td><td><code>config/v7-passenger-with-envfile.service</code></td></tr>
              <tr><td><code>v7-cable</code></td><td>Dedicated ActionCable service on port <code>4101</code></td><td><code>config/v7-cable.service</code></td></tr>
              <tr><td>nginx v7 site</td><td>Routes <code>/</code> to Passenger and <code>/cable</code> to Cable</td><td><code>nginx/v7.attymatter.com.conf</code></td></tr>
              <tr><td>Passenger request monitor</td><td>Alerts when non-Cable requests are stuck</td><td><code>app/services/passenger_request_monitor.rb</code></td></tr>
            </tbody>
          </table>

          <h5>Timeouts</h5>
          <p>Normal web requests use <code>rack-timeout</code> so long-running requests fail and release capacity instead of occupying Passenger indefinitely. Defaults are controlled by <code>RACK_TIMEOUT_SERVICE_TIMEOUT</code>, <code>RACK_TIMEOUT_WAIT_TIMEOUT</code>, and <code>RACK_TIMEOUT_WAIT_OVERTIME</code>. The Cable service sets <code>RACK_TIMEOUT_SERVICE_TIMEOUT=0</code> because websocket sessions are intentionally long-lived.</p>

          <h5>Passenger Request Monitor</h5>
          <p>The Passenger request monitor runs every minute and ignores <code>/cable</code> requests. By default it treats non-Cable requests as stuck after 45 seconds, which is intentionally below nginx's gateway timeout so the monitor can report stalled web capacity before users see repeated <code>504 Gateway Time-out</code> errors. Override with <code>PASSENGER_MONITOR_STUCK_SECONDS</code> and <code>PASSENGER_MONITOR_STUCK_COUNT</code> when needed.</p>

          <h5>Database Restart Recovery</h5>
          <p>MySQL adapter timeouts are configured in <code>config/database.yml</code>. If a normal request hits a transient database connection failure, <code>DatabaseConnectionRecovery</code> clears stale ActiveRecord connections and returns a retryable <code>503</code> response rather than leaving the request pinned in Passenger.</p>

          <h5>Installing Runtime Files</h5>
          <p>The production runtime files can be installed with <code>scripts/install-production-web-runtime.sh</code>. Installing these files changes systemd/nginx configuration and should only be done during an approved production maintenance window.</p>
        `
      }
    ]
  },

  // ============================================================
  // TESTING
  // ============================================================
  {
    id: 'testing',
    title: 'Testing',
    icon: 'fa-flask',
    audience: 'admin',
    subsections: [
      {
        id: 'testing-overview',
        title: 'Overview',
        audience: 'admin',
        content: `
          <h4>Testing Infrastructure</h4>
          <p>V7 has a comprehensive testing setup covering both the Rails backend and the Angular frontend, plus a dedicated endpoint testing framework for validating production deployments.</p>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Layer</th><th>Framework</th><th>Test Files</th><th>Coverage Tool</th></tr></thead>
            <tbody>
              <tr><td>Rails Backend</td><td>Minitest + Shoulda Matchers</td><td>~590 files</td><td>SimpleCov &rarr; <code>public/coverage/</code></td></tr>
              <tr><td>Angular Frontend</td><td>Jasmine + Karma</td><td>~370 spec files</td><td>Karma Coverage (90% threshold)</td></tr>
              <tr><td>Endpoint Validation</td><td>Custom rake tasks + scripts</td><td>&mdash;</td><td>JSON reports in <code>tmp/</code></td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'rails-tests',
        title: 'Rails Tests',
        audience: 'admin',
        content: `
          <h4>Running Rails Tests</h4>

          <pre># Run the full test suite
make test
# or
bundle exec rails test

# Run a specific test file
bundle exec rails test test/models/matter_test.rb

# Run a specific test by name
bundle exec rails test -n test_specific_method

# Run tests in a directory
bundle exec rails test test/controllers/</pre>

          <h4>Test Directory Structure</h4>
          <p>All tests live under <code>test/</code>, organized by type:</p>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Directory</th><th>Count</th><th>What It Tests</th></tr></thead>
            <tbody>
              <tr><td><code>test/models/</code></td><td>~247</td><td>Model validations, associations, scopes, business logic</td></tr>
              <tr><td><code>test/controllers/</code></td><td>~135</td><td>Controller actions, responses, authorization</td></tr>
              <tr><td><code>test/services/</code></td><td>~76</td><td>Service objects (PDF generation, integrations, etc.)</td></tr>
              <tr><td><code>test/helpers/</code></td><td>~56</td><td>View helpers</td></tr>
              <tr><td><code>test/queries/</code></td><td>~40</td><td>Query objects for complex database operations</td></tr>
              <tr><td><code>test/mailers/</code></td><td>~11</td><td>Email delivery and content</td></tr>
              <tr><td><code>test/workers/</code></td><td>~11</td><td>Sidekiq background jobs</td></tr>
              <tr><td><code>test/integration/</code></td><td>4</td><td>Multi-step workflow tests (matter, document, billing, scan)</td></tr>
              <tr><td><code>test/channels/</code></td><td>3</td><td>ActionCable WebSocket channels</td></tr>
              <tr><td><code>test/lib/</code></td><td>4</td><td>Library utilities (PDF helpers, tag system, tools)</td></tr>
              <tr><td><code>test/jobs/</code></td><td>1</td><td>ActiveJob tests</td></tr>
            </tbody>
          </table>

          <h4>Test Fixtures</h4>
          <p>V7 uses <strong>53 YAML fixture files</strong> in <code>test/fixtures/</code>. Fixtures are auto-loaded via <code>fixtures :all</code> in the test helper. Key fixtures include: users, intranets, matters, contacts, involvements, documents, addresses, activities, billings, and conflict_checks.</p>

          <h4>Test Helper Methods</h4>
          <p>The following helpers are available in all tests (defined in <code>test/test_helper.rb</code>):</p>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Method</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><code>auth_headers(user)</code></td><td>Generates JWT bearer token headers for API request tests</td></tr>
              <tr><td><code>mock_connectivity_pass()</code></td><td>Stubs successful storage connectivity checks</td></tr>
              <tr><td><code>mock_connectivity_fail()</code></td><td>Stubs failed storage connectivity checks</td></tr>
              <tr><td><code>prepare_matter_in_storage()</code></td><td>Sets up a matter in the archive storage</td></tr>
              <tr><td><code>prepare_storage_path()</code></td><td>Creates necessary storage directories</td></tr>
              <tr><td><code>setup_document_file()</code></td><td>Copies a test PDF to the expected storage location</td></tr>
              <tr><td><code>pdf_test_file_path()</code></td><td>Returns path to the test PDF fixture file</td></tr>
            </tbody>
          </table>

          <h4>Test Libraries</h4>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Gem</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><code>minitest</code></td><td>Rails default test framework (pinned to 5.x)</td></tr>
              <tr><td><code>shoulda-matchers</code></td><td>Matcher DSL for validations and associations</td></tr>
              <tr><td><code>simplecov</code></td><td>Code coverage reporting (HTML output to <code>public/coverage/</code>)</td></tr>
              <tr><td><code>vcr</code></td><td>HTTP request recording and playback for external API tests</td></tr>
              <tr><td><code>factory_bot_rails</code></td><td>Object factories as an alternative to fixtures</td></tr>
              <tr><td><code>faker</code> / <code>ffaker</code></td><td>Fake data generation for tests</td></tr>
            </tbody>
          </table>

          <h4>Test Database</h4>
          <p>The test database is configured in <code>config/database.yml</code> under the <code>test:</code> key. It uses environment variables:</p>
          <ul>
            <li><code>HOST_DEVL</code> &mdash; database host</li>
            <li><code>INTRANET_DATABASE_TEST</code> (or falls back to <code>INTRANET_DATABASE_DEV</code>) &mdash; database name</li>
            <li>Connection pool: 25</li>
          </ul>
          <p>Optional secondary test databases exist for archive, FAQ, postfix, and prosody &mdash; these are conditionally loaded based on env vars and do <strong>not</strong> participate in transactional fixtures (tests using them must handle their own cleanup).</p>
        `
      },
      {
        id: 'angular-tests',
        title: 'Angular Tests',
        audience: 'admin',
        content: `
          <h4>Running Angular Tests</h4>

          <pre># From the angular-app/ directory
npm run test                      # Run with coverage
npx ng test                       # Watch mode
npx ng test --code-coverage       # Generate coverage report

# From the project root via Makefile
make frontend_local_test</pre>

          <p>The Karma browser UI is available at <a href="http://localhost:9876" target="_blank">http://localhost:9876</a> while tests are running in watch mode.</p>

          <h4>Test Structure</h4>
          <p>Angular tests follow the standard convention: each component, service, directive, pipe, and guard has a corresponding <code>.spec.ts</code> file alongside it (~370 spec files total). Tests use <strong>Jasmine</strong> as the assertion framework and <strong>Karma</strong> as the test runner.</p>

          <h4>Code Coverage Requirements</h4>
          <p>The Karma config enforces <strong>90% thresholds</strong> for all coverage metrics:</p>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Metric</th><th>Threshold</th></tr></thead>
            <tbody>
              <tr><td>Statements</td><td>90%</td></tr>
              <tr><td>Branches</td><td>90%</td></tr>
              <tr><td>Functions</td><td>90%</td></tr>
              <tr><td>Lines</td><td>90%</td></tr>
            </tbody>
          </table>
          <p>Coverage reports are output to <code>angular-app/coverage/</code> in HTML and text-summary formats.</p>

          <h4>Test Utilities &amp; Mocks</h4>
          <p>Shared test infrastructure lives in <code>angular-app/src/app/services/tests/common/</code>:</p>
          <table class="table table-bordered table-sm">
            <thead><tr><th>File</th><th>Purpose</th></tr></thead>
            <tbody>
              <tr><td><code>dependencies.ts</code></td><td>Comprehensive dependency builder (<code>buildDependencies()</code>) with imports for Forms, Router, Common, ngx-bootstrap modules, and service provider mappings</td></tr>
              <tr><td><code>http-dependencies.ts</code></td><td>HttpClientModule, HttpClient, and HttpClientService setup</td></tr>
              <tr><td><code>mock-http-request-interceptor.ts</code></td><td>HTTP request interception for test isolation</td></tr>
              <tr><td><code>mock-http-requests.ts</code></td><td>Pre-configured request mocks for common API calls</td></tr>
              <tr><td><code>mock-user-service.ts</code></td><td>Stubbed user service</td></tr>
              <tr><td><code>mock-matter-store-service.ts</code></td><td>Stubbed matter store service</td></tr>
            </tbody>
          </table>
          <p>Mock factories for test data are in <code>models/factories/</code>: <code>mock-user.ts</code>, <code>mock-scan.ts</code>, <code>mock-entry-group.ts</code>, <code>mock-backend-data.ts</code>.</p>

          <h4>Karma Configuration</h4>
          <ul>
            <li><strong>Browser:</strong> ChromeHeadless (default), ChromeHeadlessDebugging (remote debug on port 9222)</li>
            <li><strong>Port:</strong> 9876</li>
            <li><strong>Auto-watch:</strong> Enabled (re-runs on file changes)</li>
            <li><strong>Timeout:</strong> 11 seconds</li>
          </ul>

          <h4>E2E Testing (Playwright)</h4>
          <p>The E2E suite uses Playwright. By default it runs a local reachability smoke against the Angular dev server. Authenticated environment checks, including the matter Events smoke, use a saved browser session.</p>
          <pre># Run E2E tests
npm run e2e

# Run against devel or another authenticated v7 environment
PLAYWRIGHT_BASE_URL=http://devel.example.test PLAYWRIGHT_STORAGE_STATE=e2e/.auth/devel.json npm run e2e</pre>
        `
      },
      {
        id: 'endpoint-testing',
        title: 'Endpoint Testing',
        audience: 'admin',
        content: `
          <h4>Endpoint Testing Framework</h4>
          <p>V7 includes a custom endpoint testing framework that validates all HTTP endpoints work correctly. It can test both local development and production environments.</p>

          <h5>Quick Start</h5>
          <pre># Local testing
make test_endpoints_local
# or
./scripts/test-endpoints-local.sh

# Production testing (requires .env)
make test_endpoints_production
# or
./scripts/test-endpoints-production.sh</pre>

          <h5>Production Setup</h5>
          <p>Create a <code>.env</code> file from the example:</p>
          <pre>cp .env.example.endpoint_test .env</pre>
          <p>Required variables:</p>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Variable</th><th>Required</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>ENDPOINT_TEST_PROD_EMAIL</code></td><td>Yes</td><td>Login email for production</td></tr>
              <tr><td><code>ENDPOINT_TEST_PROD_PASSWORD</code></td><td>Yes</td><td>Login password</td></tr>
              <tr><td><code>ENDPOINT_TEST_PRODUCTION_URL</code></td><td>No</td><td>Custom URL (default: <code>https://v7.attymatter.com</code>)</td></tr>
              <tr><td><code>ENDPOINT_TEST_PROD_API_TOKEN</code></td><td>No</td><td>JWT token (preferred over password auth)</td></tr>
              <tr><td><code>ENDPOINT_TEST_PROD_INTRANET_ID</code></td><td>No</td><td>Intranet ID for multi-tenant setups</td></tr>
            </tbody>
          </table>

          <h5>Production Safety Features</h5>
          <ul>
            <li><strong>Read-only:</strong> Only GET endpoints are tested</li>
            <li><strong>Filtered:</strong> Admin, delete, download, and resource-intensive endpoints are excluded</li>
            <li><strong>Timeouts:</strong> 30s read / 10s connect</li>
            <li><strong>Sequential execution:</strong> One request at a time to avoid overloading</li>
          </ul>

          <h5>Rake Tasks</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>endpoint_test:discover</code></td><td>List all discovered GET endpoints</td></tr>
              <tr><td><code>endpoint_test:test_all</code></td><td>Test all endpoints locally</td></tr>
              <tr><td><code>endpoint_test:test_pattern[pattern]</code></td><td>Test local endpoints matching a regex pattern</td></tr>
              <tr><td><code>endpoint_test:test_production</code></td><td>Test production endpoints (read-only)</td></tr>
              <tr><td><code>endpoint_test:test_production_pattern[pattern]</code></td><td>Test production endpoints matching a pattern</td></tr>
              <tr><td><code>endpoint_test:check_production</code></td><td>Validate production credentials without running tests</td></tr>
            </tbody>
          </table>

          <h5>Reports</h5>
          <ul>
            <li><strong>Local:</strong> <code>tmp/endpoint_test_report.json</code></li>
            <li><strong>Production:</strong> <code>tmp/endpoint_test_production_report.json</code></li>
          </ul>
          <p>Reports include: timestamp, environment, summary (pass/fail counts), endpoint details, and errors.</p>

          <h5>Performance</h5>
          <ul>
            <li>Local: 2&ndash;5 minutes</li>
            <li>Production: 3&ndash;10 minutes (depends on network latency)</li>
          </ul>
        `
      }
    ]
  },

  // ============================================================
  // SCRIPTS & RAKE TASKS
  // ============================================================
  {
    id: 'scripts-rake-tasks',
    title: 'Scripts & Rake Tasks',
    icon: 'fa-terminal',
    audience: 'admin',
    subsections: [
      {
        id: 'scripts-overview',
        title: 'Overview',
        audience: 'admin',
        content: `
          <h4>Scripts &amp; Rake Tasks</h4>
          <p>V7 includes a collection of shell scripts (in <code>scripts/</code>) and Rake tasks (in <code>lib/tasks/</code>) for development, testing, deployment, and administration. This section documents all available commands.</p>
          <div class="alert alert-warning"><strong>Caution:</strong> Many of these tasks modify data or interact with production systems. Always verify you are targeting the correct environment before running any task.</div>

          <h5>Running Rake Tasks</h5>
          <p>All rake tasks are run from the project root:</p>
          <pre>bundle exec rails &lt;namespace&gt;:&lt;task_name&gt;
# or with arguments:
bundle exec rails &lt;namespace&gt;:&lt;task_name&gt;[arg1,arg2]</pre>

          <h5>Running Shell Scripts</h5>
          <pre>./scripts/&lt;script_name&gt;.sh</pre>
        `
      },
      {
        id: 'makefile-targets',
        title: 'Makefile Targets',
        audience: 'admin',
        content: `
          <h4>Makefile Targets</h4>
          <p>The project <code>Makefile</code> provides shortcuts for common operations. <strong>Note:</strong> Most Makefile targets use RVM and may not work on all setups. Prefer running the underlying commands directly when needed.</p>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Target</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>make console</code></td><td>Opens Rails console</td></tr>
              <tr><td><code>make deploy</code></td><td>Deploys to production via Capistrano</td></tr>
              <tr><td><code>make routes</code></td><td>Prints all Rails routes</td></tr>
              <tr><td><code>make migrate</code></td><td>Runs database migrations</td></tr>
              <tr><td><code>make dev_server</code></td><td>Starts dev server on port 4006 (uses RVM &mdash; prefer <code>bundle exec rails s -b 0.0.0.0 -p 3000</code> instead)</td></tr>
              <tr><td><code>make dev_server_d</code></td><td>Starts dev server daemonized on port 4006</td></tr>
              <tr><td><code>make stop_dev_server</code></td><td>Sends SIGTERM to the dev server using PID file</td></tr>
              <tr><td><code>make force_stop_dev_server</code></td><td>Sends SIGKILL to the dev server</td></tr>
              <tr><td><code>make test</code></td><td>Runs the Rails test suite</td></tr>
              <tr><td><code>make dev_db_reset</code></td><td>Resets the development database (<code>db:reset</code>)</td></tr>
              <tr><td><code>make dev_db_setup</code></td><td>Sets up the development database (<code>db:setup</code>)</td></tr>
              <tr><td><code>make dev_db_seed</code></td><td>Seeds the development database (<code>db:seed</code>)</td></tr>
              <tr><td><code>make build_dev_frontend</code></td><td>Builds Angular frontend for development (with watch)</td></tr>
              <tr><td><code>make build_dev_frontend__no_watch</code></td><td>Builds Angular frontend for development (no watch)</td></tr>
              <tr><td><code>make install_frontend_modules</code></td><td>Runs <code>npm install</code> in the Angular directory</td></tr>
              <tr><td><code>make refresh_frontend_build</code></td><td>Cleans node_modules, reinstalls, and rebuilds Angular</td></tr>
              <tr><td><code>make frontend_local_server</code></td><td>Starts Angular dev server locally</td></tr>
              <tr><td><code>make frontend_local_test</code></td><td>Runs Angular unit tests</td></tr>
              <tr><td><code>make test_endpoints_local</code></td><td>Runs local endpoint tests</td></tr>
              <tr><td><code>make test_endpoints_production</code></td><td>Runs production endpoint tests (read-only, requires <code>.env</code>)</td></tr>
              <tr><td><code>make update_prod_frontend</code></td><td>Builds Angular for production and pushes to deploy repo</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'shell-scripts',
        title: 'Shell Scripts',
        audience: 'admin',
        content: `
          <h4>Shell Scripts (<code>scripts/</code>)</h4>

          <h5>Development Scripts</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Script</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>dev_server.sh</code></td><td>Starts Rails dev server and removes stale PID files.</td></tr>
              <tr><td><code>dev_console.sh</code></td><td>Launches <code>bundle exec rails c</code>.</td></tr>
              <tr><td><code>setup_project.sh</code></td><td>Initial project setup: builds Angular frontend, then updates Ruby gems. Run once after cloning.</td></tr>
              <tr><td><code>copy_env.sh</code></td><td>Copies environment config files (<code>/etc/profile.d/environ.sh</code>) and <code>wkhtmltopdf</code> binary for local development.</td></tr>
              <tr><td><code>setup_storage.sh</code></td><td>Creates and configures the application storage directory. Uses <code>STORAGE_PATH</code> env var or defaults to <code>/opt/shared/public/storage</code>.</td></tr>
            </tbody>
          </table>

          <h5>Build Scripts</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Script</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>build_angular.sh</code></td><td>Builds Angular frontend locally. Sets <code>NODE_OPTIONS</code> for legacy OpenSSL compatibility.</td></tr>
              <tr><td><code>update_gems.sh</code></td><td>Updates Ruby gems locally, then runs <code>bundle install</code> and <code>bundle lock</code>.</td></tr>
              <tr><td><code>wv6_prod.sh</code></td><td>Builds Angular for production and force-pushes to a separate deploy git repository.</td></tr>
            </tbody>
          </table>

          <h5>Production &amp; Deployment Scripts</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Script</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>fix-passenger-env.sh</code></td><td><strong>Root only.</strong> Deploys Passenger environment config and systemd service to production. Creates config directory, sets permissions, reloads systemd, restarts and enables Passenger service.</td></tr>
              <tr><td><code>verify-production-assets.sh</code></td><td>SSH-based verification of production Angular assets after deploy or rollback. Checks file existence, validates production build sizes (detects dev builds), tests HTTPS accessibility, checks Passenger status.</td></tr>
            </tbody>
          </table>

          <h5>Testing &amp; Diagnostic Scripts</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Script</th><th>Description</th></tr></thead>
            <tbody>
              <tr><td><code>test-endpoints-local.sh</code></td><td>Runs local endpoint testing. Generates report at <code>tmp/endpoint_test_report.json</code>.</td></tr>
              <tr><td><code>test-endpoints-production.sh</code></td><td>Runs production endpoint tests (read-only GET requests). Requires <code>.env</code> file with credentials. Report at <code>tmp/endpoint_test_production_report.json</code>.</td></tr>
              <tr><td><code>test-plo6.sh</code></td><td>Health check for the <code>plo6.com</code> short URL service. Tests DNS, HTTPS, redirects, 404 handling, and PM2 process status via SSH to primal2.</td></tr>
              <tr><td><code>test_ocsp.sh</code></td><td>Tests OCSP response for the v5.attymatter.com SSL certificate.</td></tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'rake-scans',
        title: 'Rake: Scans',
        audience: 'admin',
        content: `
          <h4>Scan Management Tasks (<code>scans:</code>)</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>scans:create_test_scan</code></td>
                <td><code>[file_path]</code> (optional)</td>
                <td>Creates a test scan in the queue with hardcoded defaults: priority 1, matter 697, contact 34256, doc type <code>HA</code>, amount $200. Auto-generates a dummy PDF via <code>wkhtmltopdf</code> if none provided. Creates thumbnail via ImageMagick.</td>
              </tr>
              <tr>
                <td><code>scans:create_dummy_pdf</code></td>
                <td>none</td>
                <td>Generates a test PDF at <code>lib/tasks/test_scan.pdf</code> using <code>wkhtmltopdf</code>. Automatically called by <code>create_test_scan</code> if no file exists.</td>
              </tr>
              <tr>
                <td><code>scans:upload_scan</code></td>
                <td><code>[file_path, priority, user_id, matter_ids, document_type, author_ids, authored_on, seconds]</code></td>
                <td>Uploads a scan programmatically via <code>ScanService</code>. Accepts document type as prefix string or numeric ID. Example:<br><pre>rails scans:upload_scan[/path/to/file.pdf,1,128,"697","HF","97481","08-11-2019",0]</pre></td>
              </tr>
              <tr>
                <td><code>scans:release_scan</code></td>
                <td><code>[scan_id, user_id, amount, exhibit_number, status, tags]</code></td>
                <td>Releases a scan from the queue. Processes auto rules (email, outgoing, redirect). Status defaults to &ldquo;released&rdquo;. Example:<br><pre>rails scans:release_scan[123,17,200,"","released",""]</pre></td>
              </tr>
              <tr>
                <td><code>scans:validate_storage</code></td>
                <td>none</td>
                <td>Validates storage paths for all intranets: checks directory existence, write permissions (0775), and test file creation.</td>
              </tr>
              <tr>
                <td><code>scans:fix_permissions</code></td>
                <td>none</td>
                <td>Creates missing scan directories and fixes permissions to 0775 for all intranets.</td>
              </tr>
            </tbody>
          </table>

          <div class="alert alert-info"><strong>Tip &mdash; Testing Check &rarr; Receipt Flow:</strong> To test the full scan-to-receipt redirect, create a test scan with a check document type (one with <code>is_check = true</code>), ensure a matching <code>AutoRedirectRule</code> exists for that document type pointing to &ldquo;New Receipt&rdquo;, then release the scan. The system will redirect to the receipt creation page with the scan amount pre-filled.</div>
        `
      },
      {
        id: 'rake-accounting',
        title: 'Rake: Accounting & Audit',
        audience: 'admin',
        content: `
          <h4>Accounting Audit Tasks (<code>accounting:</code>)</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>accounting:audit</code></td>
                <td><code>[intranet_id]</code> (optional)</td>
                <td>Full accounting audit: checks in transit, escrow ledger vs bills, escrow bills vs bank, overall ledger balance. Runs for all intranets if no ID specified.</td>
              </tr>
              <tr>
                <td><code>accounting:transit_orphans</code></td>
                <td><code>[intranet_id]</code> (required)</td>
                <td>Finds orphan/unmatched debit and credit entries in checks-in-transit accounts by amount.</td>
              </tr>
              <tr>
                <td><code>accounting:escrow_discrepancies</code></td>
                <td><code>[intranet_id]</code> (required)</td>
                <td>Identifies matters with mismatches between escrow ledger balance and escrow bills.</td>
              </tr>
              <tr>
                <td><code>accounting:reconciliation_report</code></td>
                <td><code>[intranet_id]</code> (required)</td>
                <td>Generates detailed reconciliation report comparing bank balances, transit, escrow ledger, and overall ledger.</td>
              </tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'rake-payroll',
        title: 'Rake: Payroll',
        audience: 'admin',
        content: `
          <h4>Payroll Tasks (<code>payroll:</code>)</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>payroll:update_wit_brackets</code></td>
                <td>none</td>
                <td>Updates Withheld Income Tax brackets to IRS 2026 values from Publication 15-T percentage method tables for weekly payroll.</td>
              </tr>
              <tr>
                <td><code>payroll:send_wit_update_reminder</code></td>
                <td>none</td>
                <td>Checks if current year &gt; configured bracket year and sends reminder email if brackets are outdated.</td>
              </tr>
              <tr>
                <td><code>payroll:validate_wit_brackets</code></td>
                <td>none</td>
                <td>Validates bracket coverage for single/married filing statuses, shows summary, and spot-checks common wage amounts.</td>
              </tr>
              <tr>
                <td><code>payroll:show_irs_limits</code></td>
                <td>none</td>
                <td>Displays configured IRS limits (401k max, match %, WIT exemption, etc.) for all intranets.</td>
              </tr>
              <tr>
                <td><code>payroll:audit_user_taxes</code></td>
                <td><code>[user_id, gross]</code> (gross optional)</td>
                <td>Shows detailed tax breakdown for a user: WIT bracket, base, rate, calculated amount. Optionally specify a gross amount.</td>
              </tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'rake-lawpay',
        title: 'Rake: LawPay',
        audience: 'admin',
        content: `
          <h4>LawPay Tasks (<code>lawpay:</code>)</h4>

          <h5>Transaction Management</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>lawpay:test_webhook</code></td>
                <td><code>[amount, payer_name, payer_email]</code> (all optional)</td>
                <td>Simulates a <code>charge.succeeded</code> webhook for testing. Creates a test <code>LawpayPayment</code> record.</td>
              </tr>
              <tr>
                <td><code>lawpay:test_refund</code></td>
                <td><code>[payment_id]</code> (required)</td>
                <td>Simulates a <code>refund.created</code> webhook for an existing LawpayPayment.</td>
              </tr>
              <tr>
                <td><code>lawpay:list</code></td>
                <td>none</td>
                <td>Lists the 20 most recent LawpayPayment records with ID, amount, payer name, status, and date.</td>
              </tr>
              <tr>
                <td><code>lawpay:clear_test</code></td>
                <td>none</td>
                <td>Deletes all unprocessed test payments (those with transaction_id matching <code>test_txn_%</code>).</td>
              </tr>
              <tr>
                <td><code>lawpay:import</code></td>
                <td><code>[days_back, limit]</code> (optional, default 30 days / 50 records)</td>
                <td>Fetches recent transactions from LawPay API and imports new ones into the database.</td>
              </tr>
              <tr>
                <td><code>lawpay:fetch</code></td>
                <td><code>[days_back, limit]</code> (optional, default 7 days / 20 records)</td>
                <td>Displays recent LawPay API transactions without importing (dry run).</td>
              </tr>
            </tbody>
          </table>

          <h5>Setup &amp; Configuration</h5>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>lawpay:setup_invoice_settings</code></td>
                <td>none</td>
                <td>Creates the lawpay SettingGroup and Settings for <code>operating_account_id</code> and <code>trust_account_id</code>. Run once during initial setup.</td>
              </tr>
              <tr>
                <td><code>lawpay:show_settings</code></td>
                <td>none</td>
                <td>Displays all LawPay settings for the intranet (sensitive values masked) and the webhook URL.</td>
              </tr>
              <tr>
                <td><code>lawpay:generate_webhook_token</code></td>
                <td><code>[intranet_id]</code> (optional)</td>
                <td>Generates or regenerates the <code>webhook_token</code> setting used to authenticate incoming LawPay webhooks.</td>
              </tr>
              <tr>
                <td><code>lawpay:webhook_urls</code></td>
                <td>none</td>
                <td>Lists webhook URLs for all intranets. Shows instructions for generating tokens where missing.</td>
              </tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'rake-endpoint-testing',
        title: 'Rake: Endpoint Testing',
        audience: 'admin',
        content: `
          <h4>Endpoint Testing Tasks (<code>endpoint_test:</code>)</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>endpoint_test:discover</code></td>
                <td>none</td>
                <td>Discovers and lists all GET endpoints in the application.</td>
              </tr>
              <tr>
                <td><code>endpoint_test:test_all</code></td>
                <td>none</td>
                <td>Tests all GET endpoints locally for 200 status and valid JSON. Generates report at <code>tmp/endpoint_test_report.json</code>.</td>
              </tr>
              <tr>
                <td><code>endpoint_test:test_pattern</code></td>
                <td><code>[pattern]</code> (regex)</td>
                <td>Tests local endpoints matching a pattern. Example: <code>rails endpoint_test:test_pattern["/api/v1"]</code></td>
              </tr>
              <tr>
                <td><code>endpoint_test:test_production</code></td>
                <td>none</td>
                <td>Tests production endpoints (read-only GET only). Requires <code>.env</code> with <code>ENDPOINT_TEST_PROD_EMAIL</code> and <code>ENDPOINT_TEST_PROD_PASSWORD</code>. Safety filters exclude admin/delete/unsafe endpoints.</td>
              </tr>
              <tr>
                <td><code>endpoint_test:test_production_pattern</code></td>
                <td><code>[pattern]</code> (regex)</td>
                <td>Tests production endpoints matching a pattern, with the same safety filters.</td>
              </tr>
              <tr>
                <td><code>endpoint_test:check_production</code></td>
                <td>none</td>
                <td>Validates production credentials from <code>.env</code> and confirms connectivity without running tests.</td>
              </tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'rake-solr',
        title: 'Rake: Solr Search',
        audience: 'admin',
        content: `
          <h4>Solr Sync Tasks (<code>solr:</code>)</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>solr:sync_from_production</code></td>
                <td><code>[core]</code> (optional)</td>
                <td>Syncs Solr development cores from production using cursorMark pagination. Available cores: email, contacts, involvements, main, users. Syncs all cores if none specified.</td>
              </tr>
              <tr>
                <td><code>solr:status</code></td>
                <td>none</td>
                <td>Shows document counts for all Solr cores in both production and development environments with diff.</td>
              </tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'rake-email',
        title: 'Rake: Email & Newsletters',
        audience: 'admin',
        content: `
          <h4>Newsletter Email Tasks (<code>smailer:</code>)</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>smailer:send_batch</code></td>
                <td><code>[smtp_user, smtp_pass]</code> (optional)</td>
                <td>Enqueues a Sidekiq job to send a batch of queued newsletter emails.</td>
              </tr>
              <tr>
                <td><code>smailer:retry_batch</code></td>
                <td><code>[condition, smtp_user, smtp_pass]</code></td>
                <td>Retries sending emails matching a SQL condition.</td>
              </tr>
              <tr>
                <td><code>smailer:process_bounces</code></td>
                <td><code>[smtp_user, smtp_pass]</code> (optional)</td>
                <td>Processes email bounces via Sidekiq job.</td>
              </tr>
            </tbody>
          </table>

          <h4>Fax Testing</h4>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>send_fax</code></td>
                <td>none</td>
                <td>Sends a test fax via the Phaxio API <code>testReceive</code> endpoint (hardcoded credentials and file path).</td>
              </tr>
            </tbody>
          </table>

          <h4>Email Archive</h4>
          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>check_archive</code></td>
                <td>none</td>
                <td>Processes email in archive by calling <code>CheckArchive::collect_archive</code>.</td>
              </tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'rake-data-maintenance',
        title: 'Rake: Data Maintenance',
        audience: 'admin',
        content: `
          <h4>Data Cleanup Tasks</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>clean:clear_duplicate_address_emails</code></td>
                <td><code>[intranet_id, trial_run]</code> (both optional)</td>
                <td>Finds and removes duplicate addresses and emails from user contacts. Updates involvement references to point to canonical records. Supports trial run mode (no changes made).</td>
              </tr>
              <tr>
                <td><code>clean:doc_template_md_to_html</code></td>
                <td>none</td>
                <td>Converts RedCloth markdown descriptions in all DocTemplate records to HTML.</td>
              </tr>
              <tr>
                <td><code>prune_versions:prune</code></td>
                <td>none</td>
                <td>Moves PaperTrail Version records older than 6 months to StorageVersion table in batches of 100, then deletes originals. Reduces main database size.</td>
              </tr>
              <tr>
                <td><code>update_scans</code></td>
                <td>none</td>
                <td>Sets <code>scanned_at = created_at</code> for scans that are missing a <code>scanned_at</code> timestamp (excludes incoming scans).</td>
              </tr>
              <tr>
                <td><code>update_cost</code></td>
                <td>none</td>
                <td>Updates fax cost field: <code>cost = num_pages &times; $0.07</code> for all successful faxes.</td>
              </tr>
              <tr>
                <td><code>documents:update_uploaded</code></td>
                <td>none</td>
                <td>Sets <code>uploaded = true</code> for documents with document_parts, <code>false</code> for others without a value.</td>
              </tr>
            </tbody>
          </table>

          <h4>Data Import &amp; Migration Tasks</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>involvements:support</code></td>
                <td>none</td>
                <td>Updates involvement communication flags (<code>use_mail</code>, <code>use_email</code>, <code>use_fax</code>, <code>use_phone</code>) and role flags (<code>is_plaintiff</code>, <code>is_defendant</code>, <code>is_intervenor</code>) based on role types.</td>
              </tr>
              <tr>
                <td><code>involvements:support_v1</code></td>
                <td>none</td>
                <td>Updates only the role flags (<code>is_plaintiff</code>, <code>is_defendant</code>, <code>is_intervenor</code>).</td>
              </tr>
              <tr>
                <td><code>involvements:support_fileshare</code></td>
                <td>none</td>
                <td>Updates only the communication flags where null.</td>
              </tr>
              <tr>
                <td><code>import_involvements:data</code></td>
                <td>none</td>
                <td>Imports contacts and creates involvements from an ODS spreadsheet. Creates/matches contacts with gender detection and address geocoding, assigns &ldquo;Class Member&rdquo; role.</td>
              </tr>
              <tr>
                <td><code>linked_accounts:link_possible_accounts</code></td>
                <td>none</td>
                <td>Audits human user identities automatically associated across intranets by normalized SSN. The legacy task name is retained, but the obsolete linked-accounts table and records have been removed.</td>
              </tr>
              <tr>
                <td><code>matters:write_documents</code></td>
                <td><code>[number]</code> (matter number)</td>
                <td>Regenerates PDF files for all documents in a specific matter that have document_part records.</td>
              </tr>
            </tbody>
          </table>
        `
      },
      {
        id: 'rake-dev-database',
        title: 'Rake: Development & Database',
        audience: 'admin',
        content: `
          <h4>Development Database Tasks</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>dev:load_fixtures</code></td>
                <td>none</td>
                <td>Loads YAML fixture files from <code>test/fixtures</code> into the development database, bypassing validations and preserving IDs.</td>
              </tr>
              <tr>
                <td><code>dev:reset_with_fixtures</code></td>
                <td>none</td>
                <td>Drops, recreates, and migrates the database, then loads all test fixtures. <strong>Destructive &mdash; destroys all data.</strong></td>
              </tr>
              <tr>
                <td><code>db:update_users</code></td>
                <td>none</td>
                <td>Resets all user passwords and salts to predefined values. Legacy migration task.</td>
              </tr>
              <tr>
                <td><code>db:update_users_random_field</code></td>
                <td>none</td>
                <td>Generates and assigns random 50-character strings to the <code>random</code> field for all users.</td>
              </tr>
            </tbody>
          </table>

          <h4>Asset Management Tasks</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>assets_manage:copy_dev_application_js</code></td>
                <td>none</td>
                <td>Copies the most recent <code>application-*.js</code> and <code>.gz</code> files from <code>public/assets</code> to <code>app/views/layouts</code>.</td>
              </tr>
              <tr>
                <td><code>assets_manage:copy_prod_application_js</code></td>
                <td>none</td>
                <td>Copies cached <code>application.js</code> files from <code>app/views/layouts</code> back to <code>public/assets</code>.</td>
              </tr>
            </tbody>
          </table>

          <h4>Documentation</h4>

          <table class="table table-bordered table-sm">
            <thead><tr><th>Task</th><th>Arguments</th><th>Description</th></tr></thead>
            <tbody>
              <tr>
                <td><code>code_docs:angular_typescript_docs</code></td>
                <td>none</td>
                <td>Generates TypeDoc documentation for the Angular TypeScript source code (excludes tests and environment files).</td>
              </tr>
              <tr>
                <td><code>exceptions:list</code></td>
                <td>none</td>
                <td>Lists all Exception subclasses in the application, sorted alphabetically. Useful for debugging.</td>
              </tr>
            </tbody>
          </table>
        `
      }
    ]
  }
];
